import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Routes, Route, Navigate, useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db, storage, functions } from './firebase';
import { doc, onSnapshot, setDoc, collection, getDoc, getDocs, deleteDoc, addDoc, serverTimestamp, increment } from 'firebase/firestore';
import { subscribeToProducts, saveProducts, saveProduct, syncProducts, replaceAllProducts } from './services/productService';
import { loadLibraryPayload } from './services/libraryPayloadService';
import { getR2UsedBytes } from './services/r2VideoService';
import { logCrash, laporGangguanDom } from './services/errorLog';
import { httpsCallable } from 'firebase/functions';
import { ref, listAll, getMetadata, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import TranscribeModal from './components/TranscribeModal';
import CoinDisplay from './components/CoinDisplay';
import RewriteView from './components/RewriteView';
import WorkspaceView from './components/WorkspaceView';
/* Had hari TikTok dibaca daripada satu tempat sahaja — kotak Start di sini
   dan pagar julat dalam WorkspaceView mesti bersetuju, kalau tidak pengguna
   boleh memilih tarikh yang kemudiannya ditolak semasa Run. */
import { HAD_HARI_TIKTOK } from './components/scheduleV39';
/* Control Panel tab Agent AI (v3.9). Badan dan jalur kaki berasingan
   kerana panel ini memang dua bekas berbeza — badan menatal, jalur kaki
   melekat di bawah. */
import { AgentPanelBodyV39, AgentPanelFootV39 } from './components/AgentPanelV39';
/* Pemilih v3.9 — pengganti <select> asli dalam panel. Lihat PilihV39.jsx. */
import PilihV39 from './components/PilihV39';
import WorkflowV39 from './components/WorkflowV39'; // ⭐ Workflow Control Panel tab Flow — admin sahaja (2026-09-15), lihat nota dlm fail
import WorkflowDropdownV4 from './components/WorkflowDropdownV4'; // ★ [3 Nov] dropdown Workflow — Workspace v4 mod New sahaja (data page /workflow)
import HostedBetaSuisV39 from './components/HostedBetaSuisV39'; // ★ Hosted Flow Beta — pemilih mode (pelanggan layak)
import HostedStatusKad from './components/HostedStatusKad'; // ★ Hosted Flow Beta — status job customer
import HostedNotifBell from './components/HostedNotifBell'; // ★ Hosted Flow Beta — loceng notifikasi batch siap
import { bacaHostedBeta, simpanHostedBeta } from './utils/hostedBeta';
import { hostedBetaBolehAktif } from './services/centralWorkerService';
import HasilSceneV39 from './components/HasilSceneV39'; // ★ Scene output — enjin Flow RESIPI (17 Okt, PLAN_FLOW_RESIPI.md)
import { bacaHasilScene, simpanHasilScene } from './utils/hasilScene';
import RujukanProdukV39, { SimpanGambarV39, BilOutputV39 } from './components/RujukanProdukV39'; // ★ Gambar produk — resipi gambar berlainan (31 Okt, PLAN_FLOW_RESIPI.md)
import { bacaRujukanProduk, simpanRujukanProduk, bacaSimpanGambar, simpanSimpanGambar, bacaBilOutput, simpanBilOutput } from './utils/rujukanProduk';
import PadamModuleSeksyen from './components/PadamModuleSeksyen'; // ★ Padam module ujian admin (17 Okt)
import { padamModule } from './services/padamModuleService';
import AIInstructionsView from './components/AIInstructionsView';
import AdminView from './components/AdminView';
import AdminAffiliateView from './components/AdminAffiliateView';
import AdminPinGate from './components/AdminPinGate';
import ScriptVersionsView from './components/ScriptVersionsView';
import AnnounceAdminView from './components/AnnounceAdminView';
import AdminPartnerView from './components/AdminPartnerView'; // urus Partner (PLAN_PARTNER.md)
import PartnerStudioView from './components/PartnerStudioView'; // /partner — pintu di server (senarai Partner + PIN Partner)
/* BOOST — himpunan MCN / TAP TikTok. RUPA SAHAJA, tiada logik (2026-09-06).
   Kedua-dua route dipagar `isAdmin` — pelanggan langsung tidak nampak.
   Lihat PLAN_BOOST.md sebelum membuka kepada pelanggan. */
import BoostV39View from './components/BoostV39View';
import BoostAdminView from './components/BoostAdminView';
import BoostPartnerView from './components/BoostPartnerView'; // /mcn — staff MCN (pintu = email dalam boost_mcn, disemak server)
import useBoostBuka from './hooks/useBoostBuka';
import PricingBaruView from './components/PricingBaruView';
import PricingV4View from './components/PricingV4View';
import LandingV4Lab from './components/LandingV4Lab';
import ProfileView from './components/ProfileView';
import SpaceSettingsView from './components/SpaceSettingsView';
import ManualProductModal from './components/ManualProductModal';
import ProductLibraryModal from './components/ProductLibraryModal';
import BulkImportModal from './components/BulkImportModal';
import StorageCleanupOverlay from './components/StorageCleanupOverlay';
import TemplateSelector from './components/TemplateSelector';
import TemplateEditorModal from './components/TemplateEditorModal';
import FragmentEditorModal from './components/FragmentEditorModal';
import { listSystemTemplates, listFragments } from './services/templateService';
import LibraryView from './components/LibraryView'; // HIDDEN — not ready for production
import WorkspaceV38View from './components/WorkspaceV38View'; // PROTOTAIP UI schedule baru — /workspace-v3.8 (menu dibuang; route kekal untuk rujukan)
// PROTOTAIP Studio v3.9 — semua halaman ini dibalut StudioShell yang sama
// macam /formula. Data contoh sahaja; tiada kesan pada halaman sebenar.
/* `CP_KEY` — kunci simpanan "Control Panel tersorok". Ia diimport dari sini
   (bukan ditulis semula) supaya prototaip dan halaman sebenar berkongsi SATU
   tetapan; lihat nota pada takrifannya. */
import WorkspaceV39View, { CP_KEY } from './components/WorkspaceV39View';
// PILOT admin: composer Workspace disambung ke Prompt Compiler V2 (/workspace-v4). Generate lama tidak disentuh.
import WorkspaceV4PilotView from './components/WorkspaceV4PilotView';
import CaraPostV39 from './components/CaraPostV39';
import { KotakBegKuningV39, KotakAddProductFbV39 } from './components/PautanProdukV39';
import SemakanTiktokV39 from './components/SemakanTiktokV39';
import JenisPostTiktokV39 from './components/JenisPostTiktokV39';   // ★ 2026-11-04 Photo post TikTok (admin sahaja)
import LaluanFbV39 from './components/LaluanFbV39';
import PostToV39 from './components/PostToV39';
import PhoneControlPanelV4 from './components/PhoneControlPanelV4';
import PhoneAutomationView from './components/PhoneAutomationView';
import ProductsV39View from './components/ProductsV39View';
import ExtensionV39View from './components/ExtensionV39View';
import AffiliateV39View from './components/AffiliateV39View';
import SalesV39View from './components/SalesV39View';
import HelpV39View from './components/HelpV39View';
import SettingsV39View from './components/SettingsV39View';
// ⛔ Tiga ini PELAN reka bentuk sahaja (rupa tanpa logic) untuk /dashboard,
//    /marketplace dan /notes. Halaman asal TIDAK disentuh dan masih hidup.
import DashboardV39View from './components/DashboardV39View';
import MarketplaceV39View from './components/MarketplaceV39View';
import NotesV39View from './components/NotesV39View';
/* PRO Studio v3.9 — susunan dua tingkat (hub → halaman fokus)
   menggantikan tab bertindan. Sepasang dengan ProStudioView lama:
   suis v3.9 hidup → rupa ini, suis mati → rupa lama. */
import ProStudioV39View from './components/ProStudioV39View';
/* ⛔ PROTOTAIP LEAD MAGNET HOOK (2026-08-17) — halaman `/hooks`, ADMIN
   SAHAJA. Rupa sahaja: tiada Firestore, tiada service, setiap butang
   mati. Ia dirender DALAM StudioShell (bersama menu tepi) seperti
   halaman v3.9 yang lain — arahan user 2026-08-17: "align guna
   interface v3.9, ada menu, jangan full width".
   Nak padam: fail komponen + import ini + baris route '/hooks' dalam
   senarai StudioShell + item menu 'hooks' dalam design/StudioShell.jsx.
   Rujuk PLAN_HOOK_LEADMAGNET.md di root. */
import HooksV39View from './components/HooksV39View';
/* WORKFLOW — galeri semua jenis workflow Flow (ADMIN SAHAJA, 26 Okt 2026). Data sebenar dalam
   config/workflow_galeri (admin edit sendiri). Pagar route '/workflow' BERPASANGAN dengan `admin: true`
   pada item menu 'workflow' dalam design/StudioShell.jsx. Lihat kepala WorkflowV39View.jsx. */
import WorkflowV39View from './components/WorkflowV39View';
/* ADMIN → FORMULAS — PROTOTAIP DESIGN (27 Okt 2026): galeri rupa Workspace v4 + gear → page edit block.
   Data contoh sahaja (utils/formulaAdminContoh.js), tiada Firestore. Route '/admin/formulas*' dipagar isAdmin;
   BELUM PIN (tiada data sebenar) — bungkus AdminPinGate bila disambung. Lihat kepala FormulaAdminView.jsx. */
import FormulaAdminView from './components/FormulaAdminView';
import FormulaAdminEditView from './components/FormulaAdminEditView';
/* Android Auto Publisher — POC ujian WebUSB/ADB (ADMIN SAHAJA, Fasa 1).
   Route '/android-test' dipagar isAdmin + AdminPinGate (lihat di bawah).
   Library ya-webadb dimuat dinamik dalam utils/androidAdb.js. */
import AndroidBuildView from './components/AndroidBuildView';
/* Pemilih group v3.9 — dipakai pada header webapp bila suis v3.9 hidup.
   Komponen yang SAMA dengan prototaip /workspace-v3.9, bukan salinan. */
import GroupSwitcherV39, { GROUP_UTAMA, PadamGroupModalV39 } from './components/GroupSwitcherV39';
/* Komposer tab Generate — komponen PROTOTAIP yang SAMA, dijalankan dalam
   mod sebenar melalui prop `nyata`. Bukan salinan: rupa composer masih
   berubah, dan dua salinan bermakna setiap pelarasan kena dibuat dua kali. */
import PromptComposerV39 from './components/PromptComposerV39';
/* Generate V2 (23 Okt 2026) — rupa baharu tab Generate, ADMIN SAHAJA di belakang toggle.
   Memakai `komposerNyata` yang SAMA; lihat nota dalam GenerateV2Nyata.jsx. */
import GenerateV2Nyata, { GenerateV2Suis } from './components/GenerateV2Nyata';
import useGenerateV2 from './hooks/useGenerateV2';
import { matlamatModule, sceneModule, GOAL_V2, FORMAT_V2, PIL_V2, MAKS_SCENE_V2 } from './utils/generateV2Matlamat';
import FormulaEditorV39View from './components/FormulaEditorV39View';
import MarketplaceView from './components/MarketplaceView';
import MarketplaceProductView from './components/MarketplaceProductView';
import MyPurchasesView from './components/MyPurchasesView';
/* Pasangan rupa v3.9 bagi dua halaman di atas. Tiada route -v3.9 untuk
   kedua-duanya — ia hidup sahaja, dipilih oleh suis pada route sebenar. */
import MarketplaceProductV39View from './components/MarketplaceProductV39View';
import TroliV39View from './components/TroliV39View';
import MyPurchasesV39View from './components/MyPurchasesV39View';
import FlowCustomerView from './components/FlowCustomerView';
import FlowPayView, { FlowPaySuccessView } from './components/FlowPayView';
import PromoAiProView, { PromoAiProSuccessView } from './components/PromoAiProView'; // Promo Kembali Premium + Google AI Pro (/promo)
import AdminMarketplaceView from './components/AdminMarketplaceView';
import AdminMarketplaceV39View from './components/AdminMarketplaceV39View';
import AutomationView from './components/AutomationView';
import PipelineTestView from './components/PipelineTestView'; // MOCKUP — makmal ujian jimat OpenAI (/pipeline-test)
import GettingStartedView from './components/GettingStartedView';
import NotesView from './components/NotesView';
import ExtensionView from './components/ExtensionView'; // Halaman khas muat turun + cara pasang extension (route /extension)
import Feed1View from './components/Feed1View'; // MOCKUP UI feed komuniti (route /feed1)
import PilihanGayaMockPanel from './components/PilihanGayaMockPanel'; // MOCKUP UI "MODE PRO" semua module (hook + gaya; pakej Premium Pro / Fighter Pro) — admin-only, tiada logic
import ProStudioView from './components/ProStudioView'; // MOCKUP UI "PRO STUDIO" page penuh (route /pro-studio) — admin-only, tiada logic
import FormulaShelfView from './components/FormulaShelfView'; // FASA 1 — Rak Formula (route /formula), guna module sedia ada
import { ContentPanel, PageHeader, SuisBahasa } from './design/ui';
import { useT, cari } from './i18n';
/* ★ [v3.9.3] CTA beg kuning — senarai template (akaun) + pilihan (browser ini). */
import { useSenaraiCta } from './hooks/useSenaraiCta';
/* Pro Mode (PLAN_PROSTUDIO_BLOCKS.md) — suis per-browser + kiraan blocks. */
import { useProMode } from './hooks/useProMode';
import { bacaCtaPilihan, tulisCtaPilihan, ctaTeksUntukLarian } from './utils/ctaStore';
import { useCiriPercuma } from './hooks/useCiriPercuma';
import { CIRI, bolehGunaCiri } from './utils/ciriPercuma';
import { KUNCI_BEG_KUNING, KUNCI_FB_ADD_PRODUCT, bacaSuisProduk, tulisSuisProduk } from './utils/pautanProduk';
import { bacaLaluanFb, tulisLaluanFb } from './utils/laluanFb';
import { KUNCI_HANTAR_WORKSPACE } from './utils/hantarKeWorkspace';
import { kunciShopee } from './utils/shopeeImport';
import { tarikhPapar } from './utils/tarikhPapar';
/* ⚠️ `cari(bahasa, kunci)` dipakai di sini, BUKAN `useT()`. App.jsx dikongsi
   dengan rupa LAMA, jadi ia tidak boleh mengikut bahasa pilihan pengguna.
   Setiap panggilan di bawah menyebut bahasanya sendiri ('ms' / 'en') kerana
   ia mengisi DUA kotak admin yang berasingan, bukan memaparkan ayat. */
/* Ikon butang `Hide` pada kepala Control Panel. Pasangan yang SAMA dengan
   butang kuncup rel menu StudioShell (PanelLeftClose/Open), cuma menghadap ke
   kanan — supaya dua kawalan ruang skrin itu kelihatan sekeluarga. */
import { PanelRightClose } from 'lucide-react';
import StudioShell, { StudioRail, idAktifDariLaluan } from './design/StudioShell'; // FASA 1 — shell penuh SESAAT Studio (menutup shell lama; lihat nota dalam fail)
import { useUiV39 } from './config/uiVersion'; // suis reka bentuk v3.9 — FAIL-CLOSED, lihat nota dalam fail
import FormulaOpenRoute from './components/FormulaOpenRoute';  // FASA 1 — buka formula (route /formula/:formulaId) → set module aktif → workspace
import TutorialView from './components/TutorialView'; // Tutorial (route /tutorial) — rupa v3.9, kandungan dari config/tutorial
import TutorialLessonView from './components/TutorialLessonView'; // Halaman lesson (/tutorial/:videoId) — rel kurikulum + Lesson seterusnya
import AffiliateView from './components/AffiliateView';
import CheckoutSuccessView from './components/CheckoutSuccessView';
import UpgradeSuccessView from './components/UpgradeSuccessView';
import BillplzPaymentReturnView from './components/BillplzPaymentReturnView';
import AffiliateClaimView from './components/AffiliateClaimView';
import SalesPageView from './components/SalesPageView';

import { setRefCookie } from './services/affiliateService';
import { withRef } from './utils/refLink';
import { productDisplayNo } from './utils/productDisplayId';
import { getModule, listRegistryOnlyModules, registerRuntimeModules, MODULES, getSceneCount } from './config/modules';
/* Penjaga lesen module — satu-satunya tempat peraturan itu dikira.
   Dipakai oleh Rak Formula, route pembuka formula, dan PRO Studio. */
import { computeFormulaAccess } from './config/formulaAccess';
import { toast } from './components/toastBus';

import NewSidebar from './components/NewSidebar';
import Login from './components/Login';
import CompleteRegistration from './components/CompleteRegistration';
import LicenseGate from './components/LicenseGate';
import StartGate from './components/StartGate';
import SupportGroupView from './components/SupportGroupView';
import { aiService } from './services/aiService';
import LockedPage from './components/LockedPage';
import SmartCustomChatGPTTemplates from './components/SmartCustomChatGPTTemplates';
import FlownityScrapeConfig from './components/FlownityScrapeConfig';
import { getUserRole, canAccessAdmin, ROLES, ROLE_DISPLAY } from './utils/roles';
import { MODE_LABELS, MODE_HINTS } from './utils/promptResolver';

// ★ Kunci penyelenggaraan (maintenance) — module yang di-lock utk SEMUA non-admin,
// WALAUPUN lesen mereka ada akses (allowedModules) atau wildcard '*'. Admin tetap boleh test.
// ✅ SMART DIBUKA 2026-08-17 (arahan user). Ia dikunci di sini sejak
// 2026-07-09 kerana automation belum stabil; kunci itu kini ditanggalkan,
// jadi akses kembali mengikut `allowedModules` pada lesen seperti module lain.
// Kunci kedua (FLOW_LOCKED_MODULES dalam WorkspaceView.jsx) dibuka serentak —
// dua-dua mesti kosong, kalau tidak module boleh dipilih tetapi tidak boleh Run.
//
// ⚠️ SMART bertanda `isPremiumAddon: true` dalam config/modules.js. Itu bermakna
//    lesen dengan wildcard '*' TIDAK auto-dapat SMART — hanya lesen yang
//    menyenaraikan 'smartcustom' secara eksplisit. Buka senarai ini TIDAK sama
//    dengan memberi SMART kepada semua pelanggan berbayar.
//
// Nak kunci semula: masukkan balik id module ke dalam senarai ini DAN
// FLOW_LOCKED_MODULES. Berkesan serta-merta selepas deploy.
const MAINTENANCE_LOCKED_MODULES = [];

// Halaman bayar yang duduk SELEPAS log masuk tetapi SEBELUM dinding lesen:
// /flow (Akaun Flow Ultra) dan /promo (Kembali Premium + Google AI Pro).
// Satu takrifan untuk tiga tempat: lencongan sesaat.ai, ingat destinasi semasa
// log masuk, dan pulihkan destinasi selepas log masuk.
// ⚠️ '/flow' kekal startsWith seperti asal (kod stabil); '/promo' dipadan ikut segmen
//    supaya laluan lain yang kebetulan bermula "/promo..." tidak tertangkap.
const laluanBayarLuarGate = (p) => p.startsWith('/flow') || p === '/promo' || p.startsWith('/promo/');

// ★ Jalur amber "Run Flow sedang dalam penyelenggaraan" (kaki panel kanan).
// Senarai id module yang jalur itu DIPAPARKAN. Pelanggan sahaja — admin tidak
// pernah nampak, supaya admin boleh terus menguji tanpa gangguan.
//
// ⛔ KOSONG = jalur itu tidak muncul pada mana-mana module (arahan user
//    2026-08-23). Sebelum ini ia DIPAKU kepada 'smartcustom' sejak 2026-06-10;
//    kunci SMART sebenar sudah ditanggalkan 2026-08-17 tetapi jalur ini
//    tertinggal, jadi pelanggan membaca "penyelenggaraan" pada module yang
//    sebenarnya boleh di-Run. Jalur dikekalkan (bukan dipadam) kerana user
//    mahu memakainya semula kelak.
//
// Nak hidupkan semula: masukkan id module ke dalam senarai ini, cth
//    const RUN_FLOW_MAINTENANCE_MODULES = ['smartcustom'];
// Berkesan selepas deploy hosting.
//
// ⚠️ Ini jalur PAPARAN sahaja — ia TIDAK menyekat butang Run. Kunci sebenar
//    ialah MAINTENANCE_LOCKED_MODULES (atas) + FLOW_LOCKED_MODULES
//    (WorkspaceView.jsx). Nak betul-betul kunci: isi ketiga-tiganya, kalau
//    tidak pelanggan baca "penyelenggaraan" tetapi butang tetap boleh ditekan.
const RUN_FLOW_MAINTENANCE_MODULES = [];

/* Bekas satu jalur tetapan formula dalam komposer tab Generate (v3.9).
   ⛔ WAJIB di paras fail, BUKAN di dalam App. Komponen yang ditakrifkan
      dalam badan komponen lain dicipta SEMULA setiap render, dan React
      menganggapnya jenis baharu — seluruh isinya dipasang semula setiap
      kali. Pemilih template GOYANG duduk di dalam sini; dipasang semula
      bermakna ia memuat turun senarai template berulang kali dan
      pilihan pengguna boleh terpadam di tengah jalan. */
const JalurTetapan = ({ tajuk, wajib, children }) => (
  <div style={{
    marginTop: 12, padding: 10, borderRadius: 10,
    border: `1px solid ${wajib ? 'var(--s-stop)' : 'var(--s-line-strong)'}`,
    background: 'var(--s-fill)',
    /* ⛔ HAD LEBAR WAJIB (aduan user 2026-08-12: "terlalu lebar").
       Komposer mengisi seluruh lajur — pada skrin lebar itu bermakna
       1,700px. Satu kotak pilihan atau dua butang yang diregang selebar
       itu bukan sahaja hodoh, ia menyusahkan: mata terpaksa berjalan
       dari label di kiri ke kawalan di kanan. 560px lebih kurang lebar
       empat petak formula, jadi jalur ini duduk sebaris dengan galeri
       di atasnya dan bukan merentas skrin. */
    maxWidth: 560,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <span style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '.06em',
        textTransform: 'uppercase', color: wajib ? 'var(--s-stop)' : 'var(--s-ink-3)',
      }}>{tajuk}</span>
      {wajib && (
        <span style={{
          fontSize: 9, fontWeight: 700, letterSpacing: '.04em', color: 'var(--s-stop)',
          border: '1px solid var(--s-stop)', borderRadius: 4, padding: '1px 5px',
        }}>WAJIB</span>
      )}
    </div>
    {children}
  </div>
);

// SpaceRoute wrapper: resolve spaceName from URL (or fallback prop) to spaceId
function SpaceRoute({ spaces, onResolveSpace, children, defaultSpaceName }) {
  const { spaceName: paramSpaceName } = useParams();
  const spaceName = paramSpaceName || defaultSpaceName;
  const navigate = useNavigate();

  useEffect(() => {
    if (!spaceName || !spaces || spaces.length === 0) return;
    const decoded = decodeURIComponent(spaceName);
    const space = spaces.find(s => s.name.toLowerCase() === decoded.toLowerCase());
    if (space) {
      onResolveSpace(space.id);
    } else {
      // Space not found, redirect to home
      navigate('/', { replace: true });
    }
  }, [spaceName, spaces]);

  return children;
}

// [2026-10-08] Laluan pendek ejen /r/:refCode DIBUANG (izin user). Ia memanggil trackClick yang tidak
// pernah diimport (ReferenceError → halaman kosong) dan tiada sesiapa memakainya: semua link ejen = ?ref=
// (AffiliateV39View / AffiliateView), 0 bahan pemasaran, 0 klik dari /r/ dalam 60 hari (disemak 8 Okt).
// /r/support KEKAL — dikendalikan berasingan (lihat "/r/support renders fullscreen").

/* ── ADMIN — lencongan alamat lama ke tab pertama ─────────────────────────
   Setiap tab admin kini ada alamatnya sendiri (`/admin/system/dashboard`,
   `/admin/affiliate/overview`, …), jadi `/admin` dan rakan-rakannya tiada
   halaman sendiri lagi. Lencongan ini yang menjaga bookmark lama.
   ⚠️ Query string DIKEKALKAN — pautan `/admin/affiliate?ref=AFR-X` (dari
      tab Users) mesti sampai dengan kod ejennya masih utuh; `<Navigate>`
      biasa menggugurkannya secara SENYAP dan carian ejen jadi kosong. */
function AdminRedirect({ ke }) {
  const { search } = useLocation();
  return <Navigate to={`${ke}${search}`} replace />;
}

// Error Boundary to catch and display rendering errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
    /* ⭐ Hantar ke `error_logs` + Sentry. Sebelum ini kemalangan di sini
       BERHENTI di sini: sempadan ini menangkap sebelum sempadan Sentry di
       akar aplikasi, jadi kita tidak pernah tahu seorang pelanggan pun
       terkena. Aduan 2026-09-01 tidak dapat dijawab dengan angka kerana
       angka itu memang tidak pernah dikumpul. Lihat services/errorLog.js. */
    logCrash(error, errorInfo, 'react_crash');
    this.setState({ errorInfo });
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, fontFamily: 'monospace', background: '#1e1e2e', color: '#f5f5f5', minHeight: '100vh' }}>
          <h2 style={{ color: '#f38ba8' }}>⚠️ Component Crash</h2>
          <pre style={{ whiteSpace: 'pre-wrap', color: '#fab387', margin: '16px 0', padding: 16, background: '#313244', borderRadius: 8 }}>
            {this.state.error?.toString()}
          </pre>
          <details style={{ cursor: 'pointer', marginTop: 16 }}>
            <summary style={{ color: '#89b4fa' }}>Stack Trace</summary>
            <pre style={{ whiteSpace: 'pre-wrap', color: '#a6adc8', fontSize: 11, marginTop: 8 }}>
              {this.state.errorInfo?.componentStack}
            </pre>
          </details>
          <button onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
            style={{ marginTop: 20, padding: '10px 20px', background: '#89b4fa', color: '#1e1e2e', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold' }}>
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ════════════════════════════════════════════════════════════════════
   BINGKAI KANDUNGAN v3.9 — SATU tempat untuk seluruh app
   ════════════════════════════════════════════════════════════════════
   Bingkai gelap bertepi bulat (jurang 10px, panel --s-panel, garis
   rambut, bucu 16px) dipasang SEKALI di sini, bukan oleh setiap halaman
   sendiri. Halaman baharu mendapatnya automatik; tiada siapa perlu ingat.

   Sebelum ini setiap halaman membalut dirinya, dan halaman Products
   akhirnya menulis bingkai SALINAN sendiri — dua takrifan yang boleh
   terpesong. Itu masalah yang pemusatan ini tamatkan.

   ⚠️ Ditakrif di peringkat MODUL, bukan dalam App(). Komponen yang
      dicipta semula setiap render membawa identiti baharu, jadi React
      membuang dan membina semula seluruh halaman di dalamnya — medan
      carian hilang fokus dan kedudukan skrol melompat ke atas.

   ⛔ Halaman yang TIDAK dibingkai, dan sebabnya:
      · /profile/*, /pricing*  — arahan user 2026-08-11
      · /formula*              — sudah dibalut StudioShell, yang membawa
                                 bingkai yang sama; dua lapis = kotak
                                 dalam kotak
      (⚠️ /tutorial DIKELUARKAN 2026-08-16: ia tidak lagi dibalut
       StudioShell, jadi ia perlukan bingkai App seperti halaman biasa)
      · *-v3.9                 — halaman prototaip, sebab yang sama
      · /workspace-v3.8        — prototaip lama
      Halaman SEBELUM login (Login, /flow, salespage, /r/:ref) tidak
      perlu disenaraikan: ia hidup dalam pokok route yang berlainan. */
const LALUAN_TANPA_BINGKAI = [
    '/profile', '/pricing', '/formula',
    '/workspace-v3.8',
];

function tiadaBingkai(pathname) {
    if (!pathname) return true;
    /* `-v3.9` di hujung ATAU diikuti seksyen (/prostudio-v3.9/gaya).
       Tanpa cabang kedua, seksyen halaman prototaip dapat bingkai App
       DI ATAS bingkai StudioShell — kotak dalam kotak. */
    if (/-v3\.9(\/|$)/.test(pathname)) return true;
    return LALUAN_TANPA_BINGKAI.some((p) => pathname === p || pathname.startsWith(p + '/') || pathname.startsWith(p + '-'));
}

function BingkaiHalaman({ aktif, children }) {
    if (!aktif) return children;
    return <ContentPanel berlatar>{children}</ContentPanel>;
}

/* ════════════════════════════════════════════════════════════════════
   KEPALA DI DALAM BINGKAI — untuk halaman yang tajuknya dipegang oleh
   App, bukan oleh halaman itu sendiri (setakat ini: Workspace)
   ════════════════════════════════════════════════════════════════════
   Halaman v3.9 yang biasa membawa tajuknya sendiri di dalam bekasnya,
   jadi jarak tepinya datang daripada bekas itu. Workspace tidak boleh
   buat begitu: tajuknya perlu pemilih group, dan group diuruskan oleh
   App (rename, padam, sync ke extension), bukan oleh WorkspaceView.

   Jadi tajuk itu dipasang di sini — DI DALAM bingkai, dalam aliran biasa,
   supaya jaraknya diukur daripada tepi bingkai yang sama seperti setiap
   halaman lain. Sebelum ini ia blok terapung di luar bingkai, dan itulah
   punca jaraknya tidak pernah sepadan.

   ⚠️ Lapisan flex ini WAJIB. Kandungan bingkai setinggi 100%; tambah
      kepala di atasnya tanpa lapisan ini bermakna kepala + kandungan
      melebihi 100%, jadi seluruh bingkai menatal dan tatal DALAMAN
      Workspace (senarai produk dan Control Panel menatal berasingan)
      hilang.

   Bila `berkepala` mati ia memulangkan kandungan SEPERTI ADANYA — tiada
   elemen tambahan langsung. Itu yang menjadikan rupa lama dan semua
   halaman lain benar-benar tidak tersentuh.                            */
/* ⛔ BENTUK POKOK MESTI KEKAL SAMA pada kedua-dua keadaan `berkepala`.
      JANGAN tukar kepada `if (!berkepala) return children`.

      Versi pertama berbuat begitu, dan ia menyebabkan gelung pasang-semula
      yang membekukan halaman:

        berkepala bergantung pada `workspaceLocked` → yang bergantung pada
        status PIN → yang dilaporkan oleh AdminPinGate, dan AdminPinGate
        hidup DI DALAM `children`.

      Jadi memulangkan bentuk yang berbeza akan membuang dan membina semula
      seluruh subpokok setiap kali PIN disahkan; AdminPinGate yang baharu
      melaporkan statusnya semula, `berkepala` bertukar lagi, dan kitaran
      berulang. Kesannya di skrin: tab Workspace melompat balik ke Generate
      dengan sendirinya, Control Panel tidak pernah muncul, dan renderer
      tersekat sehingga screenshot pun gagal.

      `display: contents` menjadikan pembalut ini tiada kesan pada susun
      atur ketika dimatikan, tanpa mengubah bentuk pokok. React tidak
      pernah membuang apa-apa. */
function BingkaiIsi({ berkepala, kepala, refKanan, kananAktif, kananSorok = false, tanpaGaris = false, children }) {
    const telus = { display: 'contents' };
    /* Lajur kawalan — salinan `.s-work__left--kanan` prototaip: garis nipis
       memisahkannya daripada senarai, padding di sekeliling supaya kad di
       dalamnya tidak melekat pada tepi, dan lajur INI yang menatal (bukan
       kad di dalamnya — dua tatal bersarang memaksa pengguna meneka mana
       satu yang akan bergerak). */
    const lajurKanan = kananAktif
        ? {
            flex: 'none', display: 'flex', minHeight: 0,
            /* ⚠️ overflowY: hidden SEMASA TERSOROK (fix 2-scrollbar, aduan user
               2026-08-20). Bila panel tersorok, lebarnya mengecut ke ~0 TAPI
               `overflowY:auto` + kandungan panjang membuat browser mengekalkan
               scrollbar menegak (~8px) — jadi pengguna nampak DUA scrollbar
               (kandungan + jalur 8px hantu ini). `hidden` semasa tersorok
               membuang jalur itu; `auto` semula bila panel dibuka supaya
               kandungan panjang tetap boleh ditatal. */
            overflowY: kananSorok ? 'hidden' : 'auto',
            /* ⚠️ `flex-start` WAJIB. Tanpanya kad di dalam diregangkan
               kepada tinggi lajur (kelakuan lalai `align-items: stretch`),
               dan kerana kad membawa `overflow: hidden` untuk bucu bulatnya,
               isi yang melebihi tinggi lajur TERKERAT — bukan menatal.
               Aduan user 2026-08-12: panel terpotong di tengah ayat dan
               butang Run Flow langsung tidak dapat dicapai.
               Dengan `flex-start` kad mengambil tinggi sebenarnya, dan
               LAJUR ini yang menatal. */
            alignItems: 'flex-start',

            /* ── Sorok/papar bergerak (arahan user 2026-08-15) ──────────────
               Semasa tersorok lajur ini KEKAL dirender; hanya paddingnya
               mengecut ke sifar dan garis pemisahnya dipudarkan. Panel di
               dalamnya (`<aside>`) sudah membawa peralihan `width`/`opacity`
               sendiri, jadi kedua-duanya bergerak serentak: panel gelongsor
               keluar sementara senarai mengembang mengisi ruangnya.

               Kalau lajur ini di-`display: none` semasa tersorok, peralihan
               pada panel itu tidak akan kelihatan langsung — kotak yang
               `display: none` tidak menganimasikan apa-apa.

               `overflowX: hidden` WAJIB: semasa mengecut, kandungan panel
               masih selebar asalnya untuk seketika dan akan terjulur keluar.
               `pointerEvents: none` menghalang medan yang sudah tidak
               kelihatan daripada masih boleh ditekan. */
            overflowX: 'hidden',
            padding: kananSorok ? '22px 0 48px' : '22px 20px 48px',
            borderLeft: '1px solid',
            /* Workspace v4 (`tanpaGaris`, ujian 23 Okt atas arahan user): tiada garis pemisah — Control Panel
               sudah berkotak sendiri, garis memotong kepala halaman. Classic: garis kekal. */
            borderLeftColor: (kananSorok || tanpaGaris) ? 'transparent' : 'var(--s-line-strong)',
            pointerEvents: kananSorok ? 'none' : undefined,
            transition: 'padding 0.22s ease-in-out, border-color 0.22s ease-in-out',
        }
        : { display: 'none' };
    return (
        <div style={berkepala
            ? { height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }
            : telus}
        >
            {berkepala ? kepala : null}
            {/* ⚠️ `position: relative` WAJIB — ia menetapkan sempadan bagi
                Control Panel, yang pada TELEFON menjadi lapisan penuh skrin
                (`position: absolute` — lihat <aside> panel di bawah).

                Tanpa baris ini, lapisan itu mencari kotak berkedudukan
                terdekat, terlepas bingkai ini, dan mendarat pada pembalut
                halaman — yang TERMASUK baris kepala. Akibatnya butang menu,
                pemilih group dan pil bahasa tertimbus di bawah panel: satu-
                satunya jalan keluar ialah butang "Hide" di dalam panel itu.
                Dengan baris ini, panel bermula TEPAT di bawah kepala, jadi
                menu kekal boleh ditekan sepanjang masa.

                Ia tidak mengubah apa-apa pada desktop: di sana panel ialah
                lajur biasa dalam baris ini, bukan lapisan. */}
            <div style={berkepala ? { flex: 1, minHeight: 0, display: 'flex', position: 'relative' } : telus}>
                {children}
                {/* Slot lajur KANAN — tempat Control Panel mendarat.
                    Ia sentiasa dirender (disorok bila tidak digunakan) supaya
                    bentuk pokok kekal sama; lihat amaran gelung di atas. */}
                <div ref={refKanan} style={berkepala ? lajurKanan : { display: 'none' }} />
            </div>
        </div>
    );
}

/* Memindahkan `children` ke dalam nod `ke` tanpa memindahkan kodnya.
   Kegunaannya: Control Panel ditulis sebagai adik-beradik <main> (1,600+
   baris, dikongsi dengan rupa lama), tetapi dalam v3.9 ia mesti muncul DI
   DALAM bingkai halaman — sebagai lajur kanan, sama seperti prototaip.

   Portal memindahkan DOM sahaja; kedudukan komponen dalam pokok React
   kekal, jadi state, context dan semua handler tidak terjejas. Alternatifnya
   ialah memindahkan 1,600 baris JSX itu secara fizikal, yang membawa risiko
   besar tanpa faedah tambahan.

   `ke` kosong → children dirender di tempat asalnya (rupa lama). */
function Portal({ ke, children }) {
    return ke ? createPortal(children, ke) : children;
}

/* Pilihan model Google Flow — SATU senarai, dipakai oleh kedua-dua rupa.
   Dipisahkan ke sini semasa blok "Model" dibentuk semula mengikut prototaip
   (2026-08-12): tanpa ini senarai option terpaksa ditulis dua kali, dan
   menambah model baharu nanti bermakna mengingat untuk mengubah dua tempat.

   ⚠️ Nilai `v` bagi imej MESTI sama persis dengan nama dalam dropdown Google
      Flow — skrip automasi memadankan nama, dan "Nano Banana 2" berbeza
      daripada "Nano Banana 2 Lite" (lihat flow-common.js).

   Angka credit sengaja tiada pada label video (keputusan user 2026-08-04):
   kos berbeza ikut pakej Google pengguna dan berubah dari masa ke masa, jadi
   satu label tidak boleh betul untuk semua. Flow sendiri memaparkan kos
   sebenar sebelum generate. */
/* ⭐ 2026-09-03 — MODEL, TEMPOH & INGREDIENTS DIPISAH JADI TIGA KAWALAN.
   Dulu satu dropdown "Video" mengunci ketiga-tiganya: "Omni Flash 8s" = model Omni +
   tempoh 8s + Ingredients, sekaligus. Pelanggan tak boleh tukar tempoh tanpa tukar model,
   dan tak boleh guna Ingredients dengan Veo. Kini model sahaja di sini; tempoh & Ingredients
   jadi kawalan berasingan (lihat flowVideoDuration / flowIngredientsMode).
   ⚠️ MIGRASI: nilai LAMA tersimpan (`omni_flash_6s/8s/10s`) dileraikan kepada
      model `omni_flash` + tempoh masa dibaca — lihat leraiModVideoLama() di WorkspaceView. */
const PILIHAN_MODEL_VIDEO = [
    { v: 'omni_flash', t: 'Omni Flash' },
    { v: 'lite', t: 'Veo 3.1 - Lite' },
    { v: 'fast', t: 'Veo 3.1 - Fast' },
    { v: 'lower_priority', t: 'Veo 3.1 - Lite [Lower Priority] · Ultra only' },
];
// Tempoh video. ⚠️ Omni sahaja ada 10s — Veo terhad 4s/6s/8s (disahkan pada Flow sebenar
//   2026-09-03). DURASI_UNTUK_MODEL() memangkas 10s bila model bukan Omni.
const PILIHAN_DURASI_VIDEO = ['4s', '6s', '8s', '10s'];
const DURASI_UNTUK_MODEL = (model) => (model === 'omni_flash' ? PILIHAN_DURASI_VIDEO : PILIHAN_DURASI_VIDEO.filter((d) => d !== '10s'));
// ⭐ 2026-09-13 — Resolusi video. HANYA Omni ada pilihan kualiti video (Veo tiada langsung).
//   Lalai 720p = kelakuan Flow sebelum ciri ini. Diukur hidup flow.google.com 2026-09-13.
const PILIHAN_RESOLUSI_VIDEO = ['360p', '720p'];
/* ⭐ 2026-09-16 — SAIZ FAIL VIDEO (menu Download dlm Flow). Disimpan sebagai perkataan,
   bukan nombor: nombor dalam menu Flow berubah ikut cara video dijana. ⛔ 4K (50 kredit
   setiap video) SENGAJA tiada di sini — keputusan user 2026-09-16. */
const PILIHAN_SAIZ_VIDEO = ['asal', 'naik'];
/* ⛔ 2026-09-16 — SKRIP YANG TIDAK MENGHORMATI "Saiz fail video".
   Pilihan ini hidup dalam menu Download TILE video (flowDownloadVideoSafe, flow-common).
   Modul 2-babak memuat turun SCENE GABUNGAN melalui butang "Download scene" = EKSPORT
   TERUS, dan eksport itu LANGSUNG TIADA menu saiz — jadi pilihan ini tidak boleh
   berkesan di sana. Dikunci di UI supaya ia bukan kawalan palsu.
   ⚠ 'smartcustom' SENGAJA TIADA dalam senarai: skripnya ditentukan PER KAD
     (1 babak → enjin AI8SAAT = berkesan; 2 babak → AI16SAAT = tidak). Module-level
     tidak dapat mengetahuinya, jadi ia dibiar terbuka dan notanya menyebut syarat itu. */
const SKRIP_TANPA_PILIHAN_SAIZ = ['ai16saat', 'nscene', 'dinamik'];
/* ⭐ 2026-09-16 — LABEL = teks SEBENAR menu Download dalam Flow, DIUKUR hidup:
     · video dijana 360p  → "270p Animated GIF" · "360p Original size" · "720p Upscaled"
     · video dijana 720p  → "270p Animated GIF" · "720p Original size" · "1080p Upscaled" · "4K Upscaled · 50 credits"
   Hanya Omni pada 360p menghasilkan video 360p; Omni 720p DAN semua model Veo = 720p
   (Veo langsung tiada pilihan resolusi — disahkan pada Veo 3.1 Lite [LP]).
   ⛔ "270p Animated GIF" TIDAK ditawarkan — ia GIF, bukan video (akan rosakkan kad + TikTok).
   ⛔ "4K Upscaled · 50 credits" TIDAK ditawarkan — keputusan user 2026-09-16 (50 kredit/video).
   ⚠ Nilai TERSIMPAN kekal 'asal'/'naik', bukan nombor: nombor berubah bila pelanggan tukar
     resolusi/model, jadi menyimpan "1080p" akan gagal SENYAP pada video 360p. */
const labelSaizVideo = (ultraMode, resolusi) => (
  (ultraMode === 'omni_flash' && resolusi === '360p')
    ? { asal: '360p (Original size)', naik: '720p (Upscaled)' }
    : { asal: '720p (Original size)', naik: '1080p (Upscaled)' }
);
const PILIHAN_MODEL_IMEJ = ['Nano Banana Pro', 'Nano Banana 2', 'Nano Banana 2 Lite'];

function App() {
  const [user, setUser] = useState(null);
  // Gate "Lengkapkan Pendaftaran" — pengguna BARU via Google wajib ada ref ejen + isi WhatsApp
  // sebelum masuk app. 'ok' | 'checking' | 'incomplete'. Lihat CompleteRegistration.jsx.
  const [regGate, setRegGate] = useState('ok');
  // Status PIN admin verified — dikongsi dengan AdminPinGate supaya chrome workspace
  // (panel kiri + toolbar) boleh dikunci untuk admin/mod yang belum masuk PIN.
  const [adminPinVerified, setAdminPinVerified] = useState(() => {
    try {
      const s = sessionStorage.getItem('admin_pin_verified');
      const t = sessionStorage.getItem('admin_pin_time');
      return s === 'true' && !!t && (Date.now() - parseInt(t)) < 30 * 60 * 1000;
    } catch { return false; }
  });
  // ★ Role derivation — dipindahkan ke atas supaya useEffect awal boleh rujuk isAdmin
  const userRole = getUserRole(user?.email);
  const isAdmin = userRole === ROLES.ADMIN;
  /* ★ Suis reka bentuk v3.9 (config/ui_v39). FAIL-CLOSED: doc tiada, rules
     menolak, atau ralat baca → `false` → rupa lama. Ia mesti dipanggil di
     sini — SEBELUM sebarang `return` awal di bawah — kerana hook React tidak
     boleh dilangkau pada sebahagian render sahaja.
     Bila `true`: rel menu v3.9 menggantikan NewSidebar dan tema gelap
     dipasang pada bekas app. HALAMAN dan enjinnya TIDAK disentuh. */
  const uiV39 = useUiV39(isAdmin);
  /* Boost dibuka kepada pelanggan? (config/boost.open, FAIL-CLOSED) — route /boost + menu rel BERPASANGAN. */
  const boostBuka = useBoostBuka(user);
  /* Workspace V2 — SATU suis untuk rupa baharu SEMUA tab Workspace (arahan user 23 Okt). Kini hanya
     tab Generate ada rupa baharu; tab lain yang siap kelak membaca `generateV2.aktif` yang sama.
     Admin sahaja; pelanggan sentiasa rupa lama (hooks/useGenerateV2.js). */
  const generateV2 = useGenerateV2(isAdmin);
  /* ★ HOSTED FLOW BETA — flag BERASINGAN daripada v4 (approve owner 2026-10-30).
     DAHULU terikat generateV2.aktif → tak boleh lancar Hosted tanpa lancar SELURUH v4. Kini gate =
     `hostedBetaBolehAktif()` (env VITE_HOSTED_BETA_API, fail-closed, DEFAULT OFF sehingga release +
     deploy) × kelayakan server (Lite LP, /quota.eligible) × mode bukan 'biasa'. v4/generateV2 TIDAK
     lagi terlibat — module v4 lain tak terjejas; dashboard admin (FlowUltraTab) gate berasingan sendiri.
     mode 'biasa' / bukan-layak / env tiada → null → Run Flow lama tepat. */
  const [hostedBeta, setHostedBeta] = useState(() => bacaHostedBeta());
  const ubahHostedBeta = (patch) => setHostedBeta(simpanHostedBeta({ ...hostedBeta, ...patch }));
  const [hostedEligible, setHostedEligible] = useState(false); // disahkan server (HostedBetaSuisV39 → onEligible)
  const hostedBetaAktif = hostedBetaBolehAktif() && hostedEligible && (hostedBeta.mode || 'hosted') !== 'biasa';
  /* Slot kepala Workspace untuk v4 (arahan user 23 Okt, ikut prototaip): jalur tab sebaris pemilih
     group + alat kanan (susun · buka/tutup · Show Control Panel) sebelah pil bahasa. WorkspaceView
     merender elemen ASALNYA ke slot ini melalui portal; slot null (Classic) = rupa lama. */
  const [slotTabV4, setSlotTabV4] = useState(null);
  const [slotAlatV4, setSlotAlatV4] = useState(null);

  /* Control Panel (lajur kanan) hidup dalam App.jsx dan DIKONGSI oleh
     rupa lama + v3.9 — ada cabang `!uiV39 &&` (lama) dan `uiV39 &&` (v3.9)
     dalam blok yang sama. Jadi terjemahan mesti berpagar: v3.9 ikut suis
     bahasa, rupa lama memulangkan ayat asal (parameter kedua). Corak sama
     seperti LibraryView. Kunci Control Panel (`workspace.*`) sudah dicipta
     semasa WorkspaceView diterjemah — cuma tidak dapat disambung ketika itu
     kerana panel duduk di sini, bukan dalam WorkspaceView. */
  const t = useT();
  const tCP = useCallback((kunci, lalai, vars) => (uiV39 ? t(kunci, vars) : lalai), [uiV39, t]);

  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  /* Penanda tema pada <body> — BUKAN pada shell.
     Sebabnya: sebahagian modal dirender melalui `createPortal` terus ke
     `document.body` (CompleteProductDataModal, satu dropdown LibraryView).
     Elemen itu duduk DI LUAR pokok `.s-root`, jadi mana-mana peraturan
     yang berskop `.s-root` langsung tidak sampai kepadanya — ia akan
     kekal putih di atas halaman gelap. Penanda pada body meliputi
     kedua-duanya sekali.

     ⚠️ Kesan ini WAJIB duduk SELEPAS `useLocation()` di atas. Diletak
        lebih awal, ia merujuk `location` sebelum pemboleh ubah itu wujud —
        sintaksnya sah (jadi pembina tidak mengadu) tetapi halaman terus
        mati masa jalan.

     Lihat blok "Lapisan tema v3.9" dalam design/ui.css. */
  useEffect(() => {
    /* ⛔ Halaman JUALAN dikecualikan. Ia bukan sebahagian shell aplikasi —
       ia halaman penuh dengan navigasi dan tema sendiri (latar krim, kad
       gelap). Lapisan tema v3.9 merosakkannya: diuji pada /pricing-baru,
       kad "Fighter" kehilangan hampir seluruh teksnya kerana latar kad
       ditimpa gelap sedangkan teksnya memang direka gelap. */
    const LALUAN_JUALAN = ['/pricing', '/pricing-baru', '/pricing-lama', '/pricing-v4', '/landing-v4', '/sesaat-1'];
    const halamanJualan = LALUAN_JUALAN.some(
      (l) => location.pathname === l || location.pathname.startsWith(l + '/'),
    );
    document.body.classList.toggle('s-v39', !!uiV39 && !halamanJualan);
    return () => document.body.classList.remove('s-v39');
  }, [uiV39, location.pathname]);

  /* ⭐ LAPORAN PERISAI DOM — sekali setiap sesi browser.

     Perisai (utils/domGuard.js) menghalang halaman daripada tumbang bila
     extension pelanggan mengusik nod React. Tetapi kejayaannya bermakna
     kemalangan BERHENTI berlaku — jadi log kemalangan akan senyap dan kita
     akan menyangka masalah itu lenyap sedangkan ia cuma tidak lagi
     kelihatan. Laporan ini yang mengekalkan penglihatan itu: berapa ramai
     browser sebenarnya diusik, dan oleh apa.

     ⚠️ DUA semakan bertangguh, bukan satu. Terjemahan halaman dan
        extension muat turun video sering bertindak BEBERAPA SAAT selepas
        halaman siap (mereka menunggu kandungan muncul dahulu). Semakan
        serta-merta akan melaporkan sifar pada browser yang sebenarnya
        terkesan. Semakan kedua menangkap yang lambat.

     ⚠️ Bergantung pada `user` kerana rules `error_logs` menuntut log
        masuk — melaporkan sebelum itu bermakna tulisan pasti ditolak. */
  useEffect(() => {
    if (!user) return;
    const awal = setTimeout(() => laporGangguanDom(), 6000);
    const lewat = setTimeout(() => laporGangguanDom(), 25000);
    return () => { clearTimeout(awal); clearTimeout(lewat); };
  }, [user, location.pathname]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [transcriptionData, setTranscriptionData] = useState(null);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [headerProductInput, setHeaderProductInput] = useState('');
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // null | 'saved'
  /* MODE PRO dalam komposer — suis, MATI secara lalai (arahan user
     2026-08-12). Masih mockup: ia belum mengubah apa-apa pada janaan,
     jadi ia admin-sahaja sehingga logiknya dibina. */
  /* Pro Mode kini SEBENAR untuk GOYANG (PLAN_PROSTUDIO_BLOCKS.md bahagian 2):
     suis per-browser + kiraan blocks tersimpan (live). Digate isAdmin + log
     masuk — bukan-admin tidak melanggan doc langsung (Fasa 1 admin sahaja). */
  const proModeGoyang = useProMode('goyang', isAdmin && !!user);
  const [stagedLibraryProducts, setStagedLibraryProducts] = useState([]); // Products staged from library modal
  // --- Avatar System ---
  const [savedAvatars, setSavedAvatars] = useState([]);
  const [selectedAvatarId, setSelectedAvatarId] = useState(''); // '' = No Avatar
  const [isAvatarDropdownOpen, setIsAvatarDropdownOpen] = useState(false);
  const avatarDropdownRef = useRef(null);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [editingAvatar, setEditingAvatar] = useState(null); // null = create, object = edit
  const [avatarMaxSlots, setAvatarMaxSlots] = useState(5);
  // Controlled state for avatar modal fields (gender drives conditional hijab toggle)
  const [avatarModalGender, setAvatarModalGender] = useState('');
  const [avatarModalHijab, setAvatarModalHijab] = useState(true);
  const [avatarModalAge, setAvatarModalAge] = useState('26-35');
  // Sync modal state when opening (create vs edit existing)
  useEffect(() => {
    if (!showAvatarModal) return;
    /* Tanpa tetapan semula ini, modal yang dibuka untuk avatar BARU masih
       memapar "Replace image" daripada sesi sebelumnya. */
    setAvatarAdaImej(false);
    setAvatarUploadPct(null);
    if (editingAvatar) {
      setAvatarModalGender(editingAvatar.gender || '');
      setAvatarModalHijab(typeof editingAvatar.hijab === 'boolean' ? editingAvatar.hijab : true);
      setAvatarModalAge(editingAvatar.age || '26-35');
    } else {
      setAvatarModalGender('');
      setAvatarModalHijab(true);
      setAvatarModalAge('26-35');
    }
  }, [showAvatarModal, editingAvatar]);
  const avatarModalFileRef = useRef(null);
  /* Kemajuan muat naik gambar avatar: null = tiada muat naik berjalan,
     0-100 = peratus. Dipakai untuk bar kemajuan DAN untuk mengunci butang
     Save supaya tiada muat naik kedua dicetuskan di atas yang pertama. */
  const [avatarUploadPct, setAvatarUploadPct] = useState(null);
  /* Pratonton gambar disimpan pada nod DOM (`dataset.base64`), bukan dalam
     state — jadi React tidak tahu gambar baharu sudah dipilih. Penanda ini
     hanya untuk memunculkan butang "Replace image". */
  const [avatarAdaImej, setAvatarAdaImej] = useState(false);
  const [isAddProductOpen, setIsAddProductOpen] = useState(true);
  const [headerLogicOptions, setHeaderLogicOptions] = useState([]);
  const [headerSelectedLogicId, setHeaderSelectedLogicId] = useState('');
  const [showManualProductModal, setShowManualProductModal] = useState(false);
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const upgradeButtonRef = useRef(null);
  const [showUpgradePopup, setShowUpgradePopup] = useState(false);
  const [upgradePopupAnchor, setUpgradePopupAnchor] = useState(null);
  const upgradePopupShownOnceRef = useRef(false); // Track per-login: show popup once, don't re-open after user dismiss
  const [moduleSettingsModal, setModuleSettingsModal] = useState({ open: false, moduleId: '', moduleLabel: '' });
  const [moduleLogicMap, setModuleLogicMap] = useState({});  // { moduleId: logicId }
  // ★ CREATE MODULE (admin) — cipta module baharu via UI → Firestore modules/{id}
  const [createModuleModal, setCreateModuleModal] = useState(false);
  const [creatingModule, setCreatingModule] = useState(false);
  const [createModuleError, setCreateModuleError] = useState('');
  const [newModule, setNewModule] = useState({
    id: '', name: '', engine: 'flow1', nscene: '3', resipiGambar: 'sama',
    icon: 'auto_awesome', color: '#6366f1', badge: '', access: 'admin'
  });

  // ★ MODULE MASTER PROMPT SYSTEM (NEW — separate from AI Logic)
  const [modules, setModules] = useState([]);                    // Array of module docs from Firestore modules/ collection
  const [activeModuleId, setActiveModuleId] = useState('ai8saat'); // Currently selected module
  /* ⭐ "Pelanggan sudah memilih formula sendiri ke belum?" (arahan user 2026-08-16).
     Galeri formula v3.9 bermula KOSONG — semua kad terang, tiada satu pun
     bertanda — sehingga pelanggan menekan satu.

     ⛔ Kenapa penanda BERASINGAN dan bukan `activeModuleId` bermula null:
        `activeModuleId` ialah SATU state yang dikongsi rupa v3.9 dan rupa
        lama (tile module panel kiri). Suis `ui_v39` menyuis PAPARAN, bukan
        state — ia tidak bercabang di sini. Menukar nilai permulaannya
        bermakna pelanggan pada rupa LAMA turut kehilangan module aktif
        semasa membuka workspace, dan setiap laluan yang membaca
        `activeModuleId || 'ai8saat'` akan diam-diam jatuh ke AI8SAAT.
        Penanda ini dibaca oleh rupa v3.9 SAHAJA, jadi rupa lama tidak
        berubah walau satu piksel. */
  const [modulDipilih, setModulDipilih] = useState(false);
  // GOYANG template (Phase 1 — only goyang uses this; future modules can adopt)
  const [selectedTemplateId, setSelectedTemplateId] = useState(() => localStorage.getItem('goyang_selectedTemplateId') || '');
  const [selectedTemplate, setSelectedTemplate] = useState(null); // resolved template object
  // Sub-module selection — per module, persisted in localStorage keyed by moduleId
  const [selectedSubModuleId, setSelectedSubModuleId] = useState(null);
  const [flownityVoice, setFlownityVoice] = useState(() => localStorage.getItem('flownity_voice') || 'female');
  // Sub-module editing (module settings modal)
  const [editingHasSubModules, setEditingHasSubModules] = useState(false);
  const [editingSubModuleLabel, setEditingSubModuleLabel] = useState('Teknik');
  const [editingSubModules, setEditingSubModules] = useState([]);
  const [subModuleExpandedIdx, setSubModuleExpandedIdx] = useState(null);
  // Eager-resolve goyang template on mount — supaya legacy goyang products (yang takde templateSnapshot)
  // dapat fallback badge context walaupun user belum klik GOYANG module. Cost: 1x Firestore read on app load.
  // ★ A6 fix: Kalau templateId dalam localStorage tak jumpa dalam list (admin dah delete) — auto-clear + alert.
  useEffect(() => {
    let cancelled = false;
    if (selectedTemplateId && !selectedTemplate) {
      import('./services/templateService').then(({ listTemplates }) => {
        listTemplates('goyang').then(list => {
          if (cancelled) return;
          const found = list.find(t => t.id === selectedTemplateId);
          if (found) {
            setSelectedTemplate(found);
          } else {
            // Stale ID — template dah delete. Clean up senyap (tiada alert global —
            // user akan nampak sidebar GOYANG takda template selected bila dia buka module).
            console.warn(`[App] Stale goyang template ID "${selectedTemplateId}" — clearing localStorage.`);
            localStorage.removeItem('goyang_selectedTemplateId');
            setSelectedTemplateId('');
          }
        }).catch(() => {});
      });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [userAllowedModules, setUserAllowedModules] = useState([]); // From licenses/{uid}.allowedModules
  // 3-slot master prompt editing state (mode1/2/3)
  const [editingModulePrompts, setEditingModulePrompts] = useState({
    mode1_noAvatarNoGender: '',
    mode2_noAvatarWithGender: '',
    mode3_withAvatar: ''
  });
  const [activePromptTab, setActivePromptTab] = useState(1); // 1 | 2 | 3
  // Mandatory append fields — module-level (not per-mode). Zero token cost.
  const [editingMandatoryImageAppend, setEditingMandatoryImageAppend] = useState('');
  const [editingMandatoryVideoAppend, setEditingMandatoryVideoAppend] = useState('');
  const [editingMandatoryVideoAppend2, setEditingMandatoryVideoAppend2] = useState('');
  // Scene 2 PREPEND — admin-controlled. Veo abaikan arahan kat hujung (slow pace bug),
  // jadi admin boleh letak ayat pace/tone di ATAS Scene 2 supaya Veo baca dulu.
  // Hanya effective untuk module 2-scene (ai16saat-half, homeniche).
  const [editingPrependScene2Enabled, setEditingPrependScene2Enabled] = useState(false);
  const [editingPrependScene2Text, setEditingPrependScene2Text] = useState('');
  // GOYANG-specific — Hook + Background presets (multi-line string for textarea)
  const [editingHookPresets, setEditingHookPresets] = useState('');
  const [editingBgPresets, setEditingBgPresets] = useState('');
  // GOYANG admin — system templates managed via module settings modal
  const [adminSystemTemplates, setAdminSystemTemplates] = useState([]);
  const [adminTemplateEditor, setAdminTemplateEditor] = useState({ open: false, template: null });
  // GOYANG admin — preset fragments (Modular Composition: hooks + backgrounds)
  const [adminHookFragments, setAdminHookFragments] = useState([]);
  const [adminBgFragments, setAdminBgFragments] = useState([]);
  const [adminFragmentEditor, setAdminFragmentEditor] = useState({ open: false, type: null, fragment: null });
  // Bump key untuk trigger TemplateSelector refetch bila admin edit template/fragments
  const [templatesRefreshKey, setTemplatesRefreshKey] = useState(0);
  const [editingModuleName, setEditingModuleName] = useState('');
  /* ★ Kotak "Rupa kad formula" dalam modal tetapan module (arahan user
     2026-08-18) — ayat bawah nama (satu setiap bahasa), lencana, tempoh. */
  const [editingKadNotaMs, setEditingKadNotaMs] = useState('');
  const [editingKadNotaEn, setEditingKadNotaEn] = useState('');
  const [editingKadBadge, setEditingKadBadge] = useState('');
  const [editingKadDurasi, setEditingKadDurasi] = useState('');
  /* Generate V2 (23 Okt) — Goal / Format / Pill kad (utils/generateV2Matlamat.js). */
  const [editingKadGoal, setEditingKadGoal] = useState('');
  const [editingKadFormat, setEditingKadFormat] = useState('video');
  const [editingKadPil, setEditingKadPil] = useState('');
  const [editingKadScenes, setEditingKadScenes] = useState([1]);   // scene yang pelanggan boleh pilih (v4)
  /* ★ PAGAR MUATAN — modal tetapan module.
     `tetapanMuatGagal` diisi HANYA bila bacaan peti besi betul-betul RALAT
     (rangkaian putus / rules menolak). Bila ia berisi, butang "Simpan Semua"
     dikunci — kerana Simpan menulis apa yang ada dalam kotak, dan kotak yang
     kosong akibat muatan gagal akan MEMADAM master prompt module tersebut.
     ⚠️ Module baharu yang memang belum ada prompt BUKAN ralat — ia kekal
     boleh disimpan (peti besi tiada = bukan kegagalan bacaan). */
  const [tetapanMuatGagal, setTetapanMuatGagal] = useState(null);
  /* Cap jari simpanan terakhir peti besi (updatedAt · updatedBy) — dipapar
     dalam modal supaya admin nampak muatan memang menjadi, bukan meneka. */
  const [tetapanMeta, setTetapanMeta] = useState(null);
  /* ★ SEJARAH & PULIH — setiap Simpan menyalin versi LAMA ke doc berasingan
     `modules/{id}/private/history_{ms}` SEBELUM ditimpa. Alamat itu sudah
     dilindungi rules sedia ada (`match /private/{docId}` = admin sahaja), jadi
     TIADA perubahan firestore.rules diperlukan. Dimuat secara malas (bila admin
     tekan "Lihat sejarah") supaya membuka modal tidak jadi lambat. */
  const [sejarahTetapan, setSejarahTetapan] = useState(null);   // null = belum dimuat
  const [sejarahMemuat, setSejarahMemuat] = useState(false);
  const [sejarahRalat, setSejarahRalat] = useState(null);

  // --- Automation Settings (Lifted from WorkspaceView) ---
  const [automationTargetSets, setAutomationTargetSets] = useState(['set1', 'set2', 'set3']);
  const [automationTargetScenes, setAutomationTargetScenes] = useState(['set1', 'set2', 'set3', 'set4', 'set5']);
  // ★ LOCKED ON: Multi Browser Mode paksa sentiasa true — video wajib save ke cloud
  // Sebab: chrome.storage.local quota 10MB → senang overflow → Set 1/2 gagal upload TikTok
  const [isMultipleBrowserMode, setIsMultipleBrowserMode] = useState(true);
  const [flowProjectUrl, setFlowProjectUrl] = useState(''); // Custom Google Labs Flow project URL (boleh project ATAU collection — collection URL start dgn prefix project yg sama)
  const [flowUrlFocusField, setFlowUrlFocusField] = useState(null); // 'project' | 'collection' | null — kotak URL mana yg tengah aktif (isi satu je)
  const [continueCollectionMode, setContinueCollectionMode] = useState(false); // ★ FASA 2: Continue Collection — sambung collection tersimpan pada produk (reconcile gambar/video hilang)
  const [flowUltraMode, setFlowUltraMode] = useState('lower_priority'); // MODEL video sahaja: 'omni_flash' | 'lite' | 'fast' | 'lower_priority' (nilai LAMA omni_flash_Ns dileraikan masa dibaca)
  const [flowVideoDuration, setFlowVideoDuration] = useState('8s');     // ⭐ tempoh video: '4s'|'6s'|'8s'|'10s' (10s Omni sahaja). Default 8s = default lama.
  const [flowVideoResolution, setFlowVideoResolution] = useState('720p'); // ⭐ resolusi video Omni: '360p'|'720p'. Lalai 720p.
  const [flowDownloadSize, setFlowDownloadSize] = useState('asal');      // ⭐ saiz fail video: 'asal' (Original size) | 'naik' (Upscaled). Lalai 'asal' = kelakuan lama.
  const [flowIngredientsMode, setFlowIngredientsMode] = useState(false); // ⭐ mod Ingredients video (rujuk gambar). Default false = kelakuan lama Veo; Omni dilerai jadi true masa migrasi.
  const [flowImageModel, setFlowImageModel] = useState('Nano Banana Pro'); // model GAMBAR dlm Google Flow — nilai = nama persis yg dipapar Flow
  /* ⭐ WORKFLOW (2026-09-15) — resipi gambar video: 'generate' (Flow jana gambar → Animate) |
     'kad' (tiada generate gambar; gambar card terus jadi video, Ingredients).
     Disimpan per-browser oleh WorkspaceView (sesaat_run_config_v1, corak Resolusi video) dan dihantar
     sebagai settings.flowWorkflow → skrip Flow AI8SAAT (_jalankanWorkflowKad).
     ⛔ `workflowAktif` = pagar ADMIN sehingga laluan card disahkan hidup. Bukan admin / rupa lama →
        WorkspaceView sentiasa menghantar 'generate', walau apa pun nilai tersimpan. */
  const [flowWorkflow, setFlowWorkflow] = useState('generate');
  /* ⭐ 2026-09-16 — nama module kad yang ditanda BILA ia tidak menyokong mod card
     (null = menyokong / tiada kad ditanda). WorkspaceView yang mengira; lihat nota
     fail itu. Ia MEMALSUKAN wfKad + workflowKad, jadi larian itu berjalan cara biasa —
     tetapi nilai tersimpan pelanggan (flowWorkflow) tidak pernah ditulis semula. */
  const [kadTakSokong, setKadTakSokong] = useState(null);
  /* ★ [3 Nov] Ringkasan Formula kad ditanda [{ jenis, label, bil }] — dilapor WorkspaceView, dibaca
     dropdown Workflow mod New (WorkflowDropdownV4). Paparan sahaja; larian masih ikut flowWorkflow. */
  const [ringkasanKadWf, setRingkasanKadWf] = useState([]);
  /* ⭐ 2026-09-16 — PAGAR ADMIN DIBUANG (arahan user, untuk release v3.9.4). Sebelum ini
     `uiV39 && isAdmin`. Pelanggan kini nampak pilihan Workflow.
     ⚠️ Had yang MASIH wujud dan pelanggan akan jumpa:
        · tapak Flow LAMA (labs.google) — mod card tidak disokong; robot berhenti dgn [Sebab] jelas
        · card yang ADA avatar belum pernah dijalankan hidup (cabang avatar dlm _kadSenaraiGambar)
     Nak tutup semula = kembalikan `&& isAdmin` di sini (satu tempat). */
  const workflowAktif = uiV39;
  /* ⛔ `kadTakSokong` memalsukan wfKad juga — kalau tidak, panel akan memapar "Generate di Flow"
     (kotak atas dipilih automatik) sementara medan Image kekal berkunci dan butang Run kekal
     "· Gambar card". Dua paparan bercanggah = pelanggan tidak tahu larian mana yang berjalan. */
  const wfKad = workflowAktif && flowWorkflow === 'kad' && !kadTakSokong;
  /* ⭐ 2026-09-16 — "Saiz fail video" dikunci untuk modul 2-babak (muat turun scene
     gabungan, tiada menu saiz). Paparan sahaja — nilai flowDownloadSize TIDAK diubah. */
  /* ★ [2026-10-17] SCENE OUTPUT — enjin Flow RESIPI (module 2–5 scene): 'gabung' | 'asing'. Per-browser
     (utils/hasilScene.js, corak Cara post); dipulihkan malas + ditulis HANYA dalam pengendali (perangkap
     StrictMode). Dihantar ke KEDUA-DUA render WorkspaceView → payload `settings.flowSceneOutput`.
     ⛔ Diisytihar SEBELUM `saizKunci` (yang membacanya) — selepasnya = ralat TDZ semasa render. */
  const [hasilScene, setHasilScene] = useState(() => bacaHasilScene());
  const pilihHasilScene = (v) => setHasilScene(simpanHasilScene(v));
  /* ★ [2026-10-31] GAMBAR PRODUK — resipi "gambar berlainan setiap scene": scene mana guna gambar produk sebagai rujukan.
     Corak + perangkap sama Scene output di atas (utils/rujukanProduk.js) → payload `settings.flowRujukanProduk`. */
  const [rujukanProduk, setRujukanProduk] = useState(() => bacaRujukanProduk());
  const pilihRujukanProduk = (v) => setRujukanProduk(simpanRujukanProduk(v));
  /* ★ [2026-11-01] SIMPAN GAMBAR — Images only: folder sahaja atau folder + card → `settings.flowSimpanGambar`. */
  const [simpanGambar, setSimpanGambar] = useState(() => bacaSimpanGambar());
  const pilihSimpanGambar = (v) => setSimpanGambar(simpanSimpanGambar(v));
  /* ★ [2026-11-01] BIL OUTPUT — Images only: 1–4 gambar setiap prompt → `settings.flowBilOutput`. */
  const [bilOutput, setBilOutput] = useState(() => bacaBilOutput());
  const pilihBilOutput = (v) => setBilOutput(simpanBilOutput(v));
  /* Module aktif menuntut enjin RESIPI → bilangan scene-nya (0 = bukan resipi). */
  const babakResipiAktif = getModule(activeModuleId)?.scriptKey === 'resipi' ? getSceneCount({ moduleId: activeModuleId }) : 0;
  /* Resipi 2+ scene + "Combine": eksport Scenebuilder tiada menu saiz (sama AI16SAAT) → paparan dikunci;
     nilai flowDownloadSize TIDAK diubah (masih dipakai "Download each scene" + resipi 1 scene). */
  const gambarResipiAktif = getModule(activeModuleId)?.resipi?.gambar;
  const saizKunci = SKRIP_TANPA_PILIHAN_SAIZ.includes(getModule(activeModuleId)?.scriptKey || '')
    || (babakResipiAktif >= 2 && hasilScene === 'gabung' && gambarResipiAktif !== 'sahaja');
  /* Veo dalam mod Ingredients hanya 8s bila ada gambar (diukur 2026-09-15). Paparan dikunci 8s —
     nilai flowVideoDuration TIDAK diubah, supaya "Generate di Flow" kekal dengan pilihan asal. */
  const durasiKunci8 = wfKad && flowUltraMode !== 'omni_flash';
  const [tiktokTargetSets, setTiktokTargetSets] = useState(['set1']);
  /* ★ POST TO (v4) — tempat video dihantar: 'tiktok' atau 'facebook'.
     SATU tempat pada satu masa (keputusan user 2026-08-30) — TikTok untuk produk
     TikTok Shop, Facebook untuk produk Shopee.

     Disimpan PER-BROWSER, bukan di awan. Pelanggan menjalankan 3-5 browser
     serentak; kalau ia dikongsi melalui Firestore, menukar pilihan dalam satu
     browser akan menukar tempat post browser lain yang sedang berjalan (punca
     aduan multi-browser 2026-08-25).

     ⛔ Ditulis HANYA dalam pengendali `pilihPostTo`, tidak pernah dalam useEffect.
        Effect begitu turut berjalan pada mount dan akan menyimpan nilai LALAI
        sebelum pilihan sebenar pelanggan sempat dibaca — perangkap yang sama
        seperti `sesaat_products_paparan_v2` dan kotak Isi jadual. */
  const [postTo, setPostTo] = useState(() => {
    try {
      const v = localStorage.getItem('sesaat_post_to_v1');
      return v === 'facebook' ? 'facebook' : 'tiktok';
    } catch { return 'tiktok'; }
  });
  /* ★ v3.9.4 — medan URL page Facebook DIBUANG (keputusan user 2026-09-11): robot kini
     upload melalui Content Library, yang mengikut page AKTIF dalam browser — satu
     browser = satu page. Kunci lama `sesaat_fb_page_url` dibiar dalam browser (tiada
     pembaca, tidak berbahaya).

     ★ v3.9.4 — SUIS "AI LABEL" (TikTok + Facebook), per-browser, LALAI HIDUP (keputusan
     user 2026-09-11). Sampai ke robot melalui `tetapan.aiLabel` pada ketiga-tiga payload
     (TikTok kalendar · TikTok lama · Facebook).
     ⛔ Ditulis HANYA dalam pengendali `tukarAiLabel` (bukan effect — perangkap mount:
        effect turut berjalan semasa mount dan akan menyimpan nilai LALAI). */
  const [aiLabelAktif, setAiLabelAktif] = useState(() => {
    try { return localStorage.getItem('sesaat_ai_label_v1') !== '0'; } catch { return true; }
  });
  const tukarAiLabel = () => {
    const baru = !aiLabelAktif;
    setAiLabelAktif(baru);
    try { localStorage.setItem('sesaat_ai_label_v1', baru ? '1' : '0'); } catch { /* storan ditutup — abai */ }
  };
  /* ★ [2026-09-18] POST TANPA PRODUK — toggle "Pasang beg kuning" (TikTok) dan
     "Add product" (Facebook). Per-browser, LALAI HIDUP (= perangai lama). Sampai
     ke robot dengan cara webapp TIDAK menghantar link produk bila MATI — lihat
     utils/pautanProduk.js. Pengendali (`tukarBegKuning` / `tukarFbAddProduct`)
     hidup bersama peraturan suis Post Only / Showcase di bawah. */
  const [begKuningAktif, setBegKuningAktif] = useState(() => bacaSuisProduk(KUNCI_BEG_KUNING));
  const [fbAddProductAktif, setFbAddProductAktif] = useState(() => bacaSuisProduk(KUNCI_FB_ADD_PRODUCT));
  /* ★ [2026-09-26] Laluan post FB Reels (Content Library | Page) — per-browser, lalai
     Content Library. Sampai ke robot melalui `tetapan.laluan` — lihat utils/laluanFb.js. */
  const [fbLaluan, setFbLaluan] = useState(() => bacaLaluanFb());
  const [isPostOnly, setIsPostOnly] = useState(false);

  /* ★ [v3.9.3] CTA beg kuning — pilihan per-BROWSER (senarai template milik akaun).
     Dibaca sekali semasa mount daripada storan tempatan. ⚠️ Hook senarai digate
     pada `!!user`, BUKAN `true`: ia membaca uid di dalam effect, dan effect itu
     tidak berjalan semula bila auth tiba lewat — tanpa gate ini, dropdown kekal
     kosong sampai halaman dimuat semula. */
  const [ctaPilihan, setCtaPilihan] = useState(() => bacaCtaPilihan());
  const { senarai: ctaSenarai, sudahMuat: ctaSudahMuat } = useSenaraiCta(!!user);
  /* ⛔ Tulisan HANYA di sini (tindakan pengguna), tidak pernah dari useEffect —
     effect turut berjalan pada mount dan akan mengunci setiap browser pada nilai
     lalai. Perangkap sama: sesaat_products_paparan_v2 + kotak Isi jadual. */
  const ubahCtaPilihan = (id) => { setCtaPilihan(id); tulisCtaPilihan(id); };
  /* Teks sebenar diselesaikan daripada senarai HIDUP pada masa render, bukan
     disimpan. Template yang dipadam → '' → skrip automation jatuh ke lalai. */
  /* 🔒 [2026-09-26] CTA sendiri hanya bila claim percuma CTA aktif (hingga 30 Nov
     2026) — belum claim / tamat → '' → robot guna lalai "BELI KAT SINI".
     ⚠️ KEMBAR kunci dropdown PilihCtaV39 (PautanProdukV39.jsx). */
  const ciriPercumaApp = useCiriPercuma(!!user);
  const ctaTeksLarian = bolehGunaCiri(ciriPercumaApp.status(CIRI.CTA))
    ? ctaTeksUntukLarian(ctaSenarai, ctaPilihan)
    : '';
  const [isShowcaseOnly, setIsShowcaseOnly] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledStartTime, setScheduledStartTime] = useState(() => {
    const now = new Date();
    now.setMinutes(Math.ceil(now.getMinutes() / 15) * 15, 0, 0);
    const tzOffset = now.getTimezoneOffset() * 60000;
    const localISOTime = (new Date(now - tzOffset)).toISOString().slice(0, 16);
    return localISOTime;
  });
  const [scheduledInterval, setScheduledInterval] = useState('30');
  /* Direct Schedule — robot menekan butang "Schedule" TikTok sendiri, bukan
     sekadar menyimpan draf.

     ⭐ DEFAULT ON (arahan pemilik 2026-08-21) — DIBALIK daripada OFF. Aliran
        tab Schedule v3.9 berpusat pada menjadualkan video ke slot masa, jadi
        Direct Schedule (robot terus menjadualkan & menerbitkan ikut waktu slot)
        ialah kelakuan yang dijangka. Pengguna boleh mematikannya → label bertukar
        "Schedule (Save in Draft)" (simpan draf, terbit sendiri).
     ⚠️ Keadaan ini DIKONGSI dengan rupa lama: bila dideploy, pelanggan sedia
        ada yang guna Scheduled Post turut default auto-terbit, bukan draf.
        Robot hanya menekan Schedule apabila masa dan tarikh yang ditetapkan
        dibaca balik dan PADAN (lihat `result.schedule.verified` dalam
        tiktok-automation.js) — kalau tidak ia jatuh semula kepada draf dan
        melaporkan sebabnya. */
  const [directSchedule, setDirectSchedule] = useState(true);
  /* ★ [2026-09-16] CARA POST — satu pemilih tiga cara (Post sekarang · Jadual ·
     Simpan draf) untuk TikTok di tab Schedule DAN Facebook, menggantikan suis
     Direct Schedule pada rupa v3.9 (keputusan user 12 Sept). Per-browser; lalai
     KOSONG = pelanggan WAJIB memilih (pemilih berdenyut, butang Run terkunci).
     `directSchedule` di atas KEKAL untuk rupa lama + laluan "Scheduled Post"
     (masa mula + selang); ia dicermin daripada pilihan ini supaya dua rupa
     tidak bercanggah. Laluan larian membaca `caraPost` DAHULU (WorkspaceView).
     ⛔ Ditulis HANYA dalam pengendali `pilihCaraPost`, bukan effect — effect
        turut berjalan pada mount dan akan menyimpan nilai lalai. */
  const [caraPost, setCaraPost] = useState(() => {
    try {
      const v = localStorage.getItem('sesaat_cara_post_v1');
      return ['now', 'schedule', 'draft'].includes(v) ? v : '';
    } catch { return ''; }
  });
  const pilihCaraPost = (v) => {
    if (!['now', 'schedule', 'draft'].includes(v)) return;
    setCaraPost(v);
    setDirectSchedule(v === 'schedule');
    try { localStorage.setItem('sesaat_cara_post_v1', v); } catch { /* storan ditutup — abai */ }
  };

  /* ── Phone automation (v4, belakang suis generateV2) ──────────────────
     Toggle dalam Control Panel: OFF = panel sedia ada (robot extension),
     ON = mod telefon (post ke apps atas telefon, cth Shopee). `phoneMode`
     hanya BENAR bila generateV2 (v4) hidup — pelanggan belum nampak. Pilihan
     disimpan per-browser (sama corak caraPost) dan dibaca semula oleh
     halaman /phone-automation (connect + pilih video + run sebenar). ⛔ Enjin
     bulk hands-off BELUM siap; butang "Open phone posting" bawa ke page itu. */
  const [phoneModeOn, setPhoneModeOn] = useState(() => {
    try { return localStorage.getItem('sesaat_phone_mode_v1') === '1'; } catch { return false; }
  });
  const tukarPhoneMode = (v) => {
    setPhoneModeOn(v);
    try { localStorage.setItem('sesaat_phone_mode_v1', v ? '1' : '0'); } catch { /* storan ditutup — abai */ }
  };
  const [phoneApp, setPhoneApp] = useState(() => {
    try { return localStorage.getItem('sesaat_phone_app_v1') || 'shopee'; } catch { return 'shopee'; }
  });
  const pilihPhoneApp = (v) => { setPhoneApp(v); try { localStorage.setItem('sesaat_phone_app_v1', v); } catch { /* storan ditutup — abai */ } };
  const [phoneRoute, setPhoneRoute] = useState(() => {
    try { const v = localStorage.getItem('sesaat_phone_route_v1'); return ['now', 'schedule', 'draft'].includes(v) ? v : ''; } catch { return ''; }
  });
  const pilihPhoneRoute = (v) => { if (!['', 'now', 'schedule', 'draft'].includes(v)) return; setPhoneRoute(v); try { localStorage.setItem('sesaat_phone_route_v1', v); } catch { /* storan ditutup — abai */ } };
  /* BENAR hanya bila v4 hidup DAN toggle ON — satu pintu, jadi pelanggan
     (v4 mati) tidak sekali-kali masuk mod telefon walaupun storan '1'. */
  // Production: admin sahaja (suis v4). Localhost: semua akaun, supaya boleh diuji akaun user.
  const phoneBoleh = generateV2.aktif || import.meta.env.DEV;
  const phoneMode = phoneBoleh && phoneModeOn;

  // Ref to access WorkspaceView handlers
  const workspaceViewRef = useRef();

  // ═══ LICENSE SECURITY ═══
  const [licenseStatus, setLicenseStatus] = useState('loading'); // 'loading' | 'valid' | 'none' | 'expired'
  const [licenseChecked, setLicenseChecked] = useState(false);
  // BARU: multi-signal renewal eligibility — independent of license doc existence.
  // Detect via users/{uid} fields: paidAt | licenseKey | tier='paid' (any one true).
  // Robust against admin manually deleting licenses/{uid} doc.
  const [isRenewalEligible, setIsRenewalEligible] = useState(false);

  // ═══ TRIAL BANNER — show sticky banner when trial user runs out of coins ═══
  const [trialCoinsEmpty, setTrialCoinsEmpty] = useState(false);

  // ═══ Pangkah pada gate langganan (arahan user 2026-08-26) ═══
  //     Pelawat boleh menutup gate dan masuk melihat-lihat. Ini TIDAK membuka ciri
  //     berbayar — modul ikut allowedModules pada lesen, dan setiap generate masih
  //     melalui pagar kuota di server.
  //     Diingat per-SESI dan per-UID. Per-sesi: gate ini juga skrin renew, jadi ia
  //     mesti muncul semula pada lawatan berikutnya. Per-UID: kalau tidak, akaun kedua
  //     yang log masuk pada browser sama akan melepasi gate tanpa pernah melihatnya.
  //     ⛔⛔ KEDUDUKAN INI PENTING — hook mesti berada SEBELUM setiap `return` awal
  //        dalam komponen ini (halaman /flow, /hooks, skrin memuatkan lesen).
  //        Meletakkannya di sebelah blok LICENSE GATE di bawah mematikan SELURUH app
  //        dengan "Rendered more hooks than during the previous render" — ia betul-betul
  //        berlaku semasa kerja ini dibina, dan hanya kelihatan bila halaman dibuka.
  const gateSkipKey = user ? `sesaat_gate_skip_${user.uid}` : '';
  const [gateDismissed, setGateDismissed] = useState(false);
  useEffect(() => {
    if (!gateSkipKey) { setGateDismissed(false); return; }
    try { setGateDismissed(sessionStorage.getItem(gateSkipKey) === '1'); }
    catch { setGateDismissed(false); }
  }, [gateSkipKey]);
  const tutupGate = () => {
    try { if (gateSkipKey) sessionStorage.setItem(gateSkipKey, '1'); } catch { /* mod privasi — abaikan */ }
    setGateDismissed(true);
  };
  // ═══ USER TIER — 'trial' | 'paid' | 'free' | 'admin' | null ═══
  const [userTier, setUserTier] = useState(null);
  // Plan Billplz — 'monthly' (premium) | 'yearly' (fighter) | null (legacy/manual). Dari users.plan.
  const [userPlan, setUserPlan] = useState(null);
  const [pipelineCoins, setPipelineCoins] = useState(null);
  const [dailyPipelineUsed, setDailyPipelineUsed] = useState(0);
  const [dailyCloudVideoUsed, setDailyCloudVideoUsed] = useState(0); // ★ R2 (Fasa 3) — kaunter 300/hari
  const [dailyCloudVideoDate, setDailyCloudVideoDate] = useState(null);
  const [dailyPipelineDate, setDailyPipelineDate] = useState(null);
  // Coin percubaan FLOWNITY — one-shot 5 seumur hidup (users.flownityTrialUsed, server yang potong)
  const [flownityTrialUsed, setFlownityTrialUsed] = useState(0);

  // Storage Limit — 2GB per user
  const STORAGE_LIMIT_BYTES = 5 * 1024 * 1024 * 1024; // 5GB (naik dari 2GB — Fasa 3 R2)
  const [storageUsed, setStorageUsed] = useState(0);
  const [isCleaningStorage, setIsCleaningStorage] = useState(false);
  // cleanupProgress: null | { stage, message, current, total, reclaimedMB, orphanCount, potentialReclaimMB }
  // stage: 'scanning' | 'finding' | 'confirm' | 'deleting' | 'done' | 'error' | 'nothing'
  const [cleanupProgress, setCleanupProgress] = useState(null);
  const confirmCleanupResolveRef = useRef(null);

  // ★ Kiraan simetri (arahan user 2026-08-26): setiap tambah/padam fail dalam skop
  //   bar mengubah kaunter SERTA-MERTA; Optimize kekal jaring pembetul, bukan
  //   satu-satunya jalan nombor jadi betul. Salinan corak bumpStorageCounter
  //   WorkspaceView (dua fail tak kongsi komponen).
  const ubahKaunterStorage = (uid, deltaBytes) => {
    if (!uid || !deltaBytes) return;
    setDoc(doc(db, 'users', uid), { storageUsedBytes: increment(Math.round(deltaBytes)) }, { merge: true })
      .catch(() => { /* bukan kritikal — recount Optimize betulkan */ });
  };

  const calculateStorageUsed = useCallback(async () => {
    if (!user) return;
    const uid = user.uid;
    // ★ Parallel recursive scan. The old version fetched every file's size
    //   SEQUENTIALLY (one await at a time) + recursed folders one by one — for a
    //   heavy account (100s of products = 1000s of files) it took minutes and the
    //   bar stayed stuck at 0. This mirrors the (fast) Optimize scan: fan out
    //   getMetadata + subfolder recursion with Promise.all, return the subtotal.
    const scanFolder = async (folderRef) => {
      let result;
      try {
        result = await listAll(folderRef);
      } catch (e) {
        console.warn('[Storage] scanFolder error for', folderRef.fullPath, ':', e.message);
        return 0;
      }
      /* ⛔ POSTER (`_thumb.jpg`) DILANGKAU DARIPADA KIRAAN (keputusan user 2026-08-26).
         Path poster adalah TETAP dan ditimpa setiap kali video diganti, jadi laluan
         naik + padam sengaja tidak menyentuh kaunter. Kalau kiraan semula di sini
         MEMASUKKANNYA, meter pelanggan melompat naik setiap kali dia menekan Optimize
         dan turun semula selepas itu — nombor yang berkelip lebih memeningkan daripada
         nombor yang tersasar sedikit. Poster kecil (~30-60KB) dan tidak boleh membesar. */
      const bukanPoster = (r) => !/_thumb.jpg$/i.test(r.name || r.fullPath || '');
      const [itemSizes, subSizes] = await Promise.all([
        Promise.all(result.items.filter(bukanPoster).map(itemRef =>
          getMetadata(itemRef).then(md => md.size || 0).catch(() => 0)
        )),
        Promise.all(result.prefixes.map(scanFolder)),
      ]);
      return [...itemSizes, ...subSizes].reduce((a, b) => a + b, 0);
    };
    // ★ R2 (fix 2026-08-23): video BARU duduk di gudang R2, BUKAN Firebase — imbasan Firebase
    //   sahaja tidak nampak ia. Bahagian R2 dikira SERVER (senarai bucket dikunci dari client).
    //   Jalan selari dengan imbasan Firebase; kegagalannya DITANGKAP BERASINGAN sebab akibatnya
    //   berbeza: imbasan folder gagal → 0 (fail-soft, macam dulu), tetapi R2 gagal → recount
    //   DIBATALKAN — menulis jumlah Firebase-sahaja bermakna MEMADAM bahagian R2 dari kaunter,
    //   iaitu tepat pepijat yang fix ini tutup.
    const r2Promise = getR2UsedBytes().then(b => ({ ok: true, bytes: b }), e => ({ ok: false, err: e }));
    const [usersBytes, videosBytes, avatarBytes, avatarFolderBytes] = await Promise.all([
      scanFolder(ref(storage, `users/${uid}`)),
      scanFolder(ref(storage, `videos/${uid}`)),
      // ★ Dua bentuk avatar wujud SERENTAK (fix 2026-08-26): avatar Settings =
      //   SATU objek bernama `avatars/{uid}`, avatar workspace (tab Generate) =
      //   FOLDER `avatars/{uid}/…`. Dulu hanya objek tunggal dibaca → 1.1GB
      //   avatar workspace (1,085 fail, diukur 2026-08-26) halimunan terus dari
      //   kiraan. listAll pada nama itu memulangkan isi FOLDER sahaja, jadi
      //   kedua-dua bacaan perlu dan tidak bertindih.
      getMetadata(ref(storage, `avatars/${uid}`)).then(md => md.size || 0).catch(() => 0),
      scanFolder(ref(storage, `avatars/${uid}`)),
    ]);
    const r2Res = await r2Promise;
    if (!r2Res.ok) {
      console.warn('[Storage] Bacaan R2 gagal — recount DIBATALKAN (kaunter tidak disentuh):', r2Res.err?.message || r2Res.err);
      return;
    }
    const total = usersBytes + videosBytes + avatarBytes + avatarFolderBytes + r2Res.bytes;
    setStorageUsed(total);
    // ★ COST-FIX kaunter storage: scan ini kini jalan HANYA masa Optimize —
    //   tulis semula jumlah tepat ke user doc (self-heal drift kaunter increment).
    setDoc(doc(db, 'users', uid), { storageUsedBytes: total }, { merge: true })
      .catch(() => { /* bukan kritikal */ });
  }, [user]);

  // ★ STORAGE CLEANUP LOGIC
  const handleCleanupStorage = async () => {
    if (!user) return;
    try {
      setIsCleaningStorage(true);
      setCleanupProgress({ stage: 'scanning', message: 'Mengimbas produk aktif & arkib…', current: 0, total: 0 });
      const uid = user.uid;
      const validPaths = new Set();

      // Helper to traverse any object/array and collect Firebase Storage URLs safely
      const extractUrls = (obj) => {
        if (!obj) return;
        if (typeof obj === 'string') {
          if (obj.includes('firebasestorage.googleapis.com') && obj.includes(uid)) {
            try {
              const urlMatch = obj.match(/\/o\/(.+?)(\?|$)/);
              if (urlMatch) {
                validPaths.add(decodeURIComponent(urlMatch[1]));
              }
            } catch (e) { /* ignore parse error */ }
          }
        } else if (Array.isArray(obj)) {
          obj.forEach(extractUrls);
        } else if (typeof obj === 'object') {
          Object.values(obj).forEach(extractUrls);
        }
      };

      // 1. ★ SAFETY: Build the "keep" list from a FRESH Firestore read of the
      //    products subcollection — NOT the in-memory `products` React state.
      //    The React state can be empty or half-loaded right after page open;
      //    trusting it once nuked every referenced video for a user (Abang Kereta).
      //    A fresh getDocs is authoritative: it either returns the complete set
      //    or throws (caught below → 'error' stage, nothing deleted).
      const [productsSnap, archiveSnap, userDocSnap] = await Promise.all([
        getDocs(collection(db, 'users', uid, 'products')),
        getDocs(collection(db, 'users', uid, 'productArchive')),
        getDoc(doc(db, 'users', uid)),
      ]);

      // ★ ABORT GUARD: if the fresh product read comes back empty, refuse to run.
      //    Better to skip cleanup than risk deleting in-use media on a bad read.
      //    (A user with genuinely 0 products has no video storage to reclaim anyway.)
      if (productsSnap.empty) {
        setCleanupProgress({ stage: 'error', message: 'Senarai produk belum siap dimuat. Sila cuba lagi sekejap.' });
        return;
      }

      extractUrls(productsSnap.docs.map(d => d.data()));
      extractUrls(archiveSnap.docs.map(d => d.data()));
      if (userDocSnap.exists()) {
        extractUrls(userDocSnap.data());
      }

      // Also protect the avatar file specifically
      validPaths.add(`avatars/${uid}`);

      console.log(`[Storage Cleanup] Found ${validPaths.size} unique active paths to keep.`);

      setCleanupProgress({ stage: 'finding', message: 'Mencari fail terbiar di storan…', current: 0, total: 0 });

      // 3. Recursive scan — parallelize getMetadata per folder + recurse subfolders in parallel
      const orphans = [];
      let orphansSizeBytes = 0;

      const findOrphans = async (folderRef) => {
        // ★ NEVER touch My Products library images. The files live under
        //   users/{uid}/library/… but their references live in a separate
        //   Firestore collection (library/{folderId}/items) that this scan does
        //   NOT read — so they'd look like orphans and get wrongly deleted.
        //   Skip the whole subtree: these are always user-visible = always keep.
        if (folderRef.fullPath === `users/${uid}/library`
            || folderRef.fullPath.startsWith(`users/${uid}/library/`)) {
          return;
        }
        // ★ JANGAN sentuh background template (GOYANG dll) + fail tugasan Import
        //   (fix 2026-08-26). Rujukan kedua-duanya duduk dalam koleksi yang scan
        //   ini TIDAK baca (users/{uid}/templates/… dan task Import extension) —
        //   tanpa langkauan ini semuanya kelihatan "terbiar", dan background
        //   template pelanggan DIPADAM sebaik dia sahkan Optimize (template terus
        //   rosak; Import separuh jalan pula hilang failnya). Kelas bahaya sama
        //   dengan langkauan library/ di atas.
        {
          const relatif = folderRef.fullPath.startsWith(`users/${uid}/`)
            ? folderRef.fullPath.slice(`users/${uid}/`.length)
            : '';
          const akar = relatif.split('/')[0] || '';
          if (akar.endsWith('-template-bg') || akar === 'import-tasks') return;
        }

        let result;
        try {
          result = await listAll(folderRef);
        } catch {
          return; // folder doesn't exist
        }

        const toCheck = result.items.filter(item => !validPaths.has(item.fullPath));
        const metaResults = await Promise.all(
          toCheck.map(itemRef =>
            getMetadata(itemRef)
              .then(md => ({ ref: itemRef, size: md.size || 0 }))
              .catch(() => ({ ref: itemRef, size: 0 }))
          )
        );
        for (const m of metaResults) {
          orphans.push(m);
          orphansSizeBytes += m.size;
        }

        await Promise.all(result.prefixes.map(findOrphans));
      };

      await Promise.all([
        findOrphans(ref(storage, `users/${uid}`)),
        findOrphans(ref(storage, `videos/${uid}`)),
      ]);

      if (orphans.length === 0) {
        setCleanupProgress({ stage: 'nothing', message: 'Tiada fail terbiar dikesan. Storan anda dalam keadaan sangat optimum.' });
        return;
      }

      const potentialReclaimMB = (orphansSizeBytes / 1024 / 1024).toFixed(2);

      // 4. Request user confirmation via inline overlay (no double-dialog)
      setCleanupProgress({
        stage: 'confirm',
        orphanCount: orphans.length,
        potentialReclaimMB,
      });
      const confirmed = await new Promise((resolve) => {
        confirmCleanupResolveRef.current = resolve;
      });
      confirmCleanupResolveRef.current = null;

      if (!confirmed) {
        console.log('[Storage Cleanup] Canceled by user.');
        setCleanupProgress(null);
        return;
      }

      // 5. Delete orphans in chunks of 20 (parallel within chunk, sequential across chunks)
      setCleanupProgress({ stage: 'deleting', message: 'Memadam fail terbiar…', current: 0, total: orphans.length });

      const CHUNK_SIZE = 20;
      let deletedCount = 0;
      let totalBytesReclaimed = 0;

      for (let i = 0; i < orphans.length; i += CHUNK_SIZE) {
        const batch = orphans.slice(i, i + CHUNK_SIZE);
        const results = await Promise.all(
          batch.map(o =>
            deleteObject(o.ref)
              .then(() => ({ ok: true, size: o.size, path: o.ref.fullPath }))
              .catch(err => ({ ok: false, size: 0, path: o.ref.fullPath, err }))
          )
        );
        for (const r of results) {
          if (r.ok) {
            deletedCount++;
            totalBytesReclaimed += r.size;
          } else {
            console.warn(`Failed to delete orphan ${r.path}`, r.err);
          }
        }
        setCleanupProgress({ stage: 'deleting', message: 'Memadam fail terbiar…', current: deletedCount, total: orphans.length });
      }

      // 6. Refresh storage bar
      await calculateStorageUsed();

      const reclaimedMB = (totalBytesReclaimed / 1024 / 1024).toFixed(2);
      setCleanupProgress({ stage: 'done', message: `Sebanyak ${deletedCount} fail terbiar dipadam.`, reclaimedMB, current: deletedCount, total: orphans.length });

    } catch (err) {
      console.error('[Storage Cleanup] Error:', err);
      setCleanupProgress({ stage: 'error', message: err.message || 'Ralat tidak diketahui.' });
    } finally {
      setIsCleaningStorage(false);
    }
  };

  // Called by overlay: user clicked "Padam Sekarang" during confirm stage
  const handleConfirmCleanup = () => {
    if (confirmCleanupResolveRef.current) {
      confirmCleanupResolveRef.current(true);
    }
  };

  // Called by overlay: user clicked X or Tutup. If confirm is pending, resolve(false); else just close.
  const handleCloseCleanupOverlay = () => {
    if (confirmCleanupResolveRef.current) {
      confirmCleanupResolveRef.current(false);
    } else {
      setCleanupProgress(null);
    }
  };

  // ★ COST-FIX (kaunter storage, 2026-07): scan on-mount + listener
  //   SESAAT_STORAGE_CHANGED (rescan penuh listAll+getMetadata setiap fail,
  //   setiap login × setiap browser) DIBUANG — punca utama 126 JUTA Storage
  //   ops/bln (RM849). Bar kini baca `storageUsedBytes` dari user doc
  //   (listener TRIAL COINS WATCHER bawah). calculateStorageUsed hanya
  //   dipanggil oleh butang Optimize (recount tepat + tulis semula = self-heal).

  // Reload selectedSubModuleId from localStorage when active module changes
  useEffect(() => {
    const saved = localStorage.getItem(`${activeModuleId}_selectedSubModuleId`) || null;
    setSelectedSubModuleId(saved);
  }, [activeModuleId]);

  // ★ Sync isMultipleBrowserMode ke chrome.storage.local extension via content.ts bridge.
  // Non-admin: sentiasa paksa true (cloud sync wajib — quota 10MB local storage tak cukup).
  // Admin: sync ikut state sebenar supaya boleh test local mode.
  // Re-run tiap 30s untuk handle kes content script inject lewat.
  useEffect(() => {
    const syncValue = () => {
      const valueToSync = isAdmin ? isMultipleBrowserMode : true;
      window.postMessage({ type: 'SESAAT_SET_STORAGE', key: 'isMultipleBrowserMode', value: valueToSync }, '*');
    };
    syncValue();
    const interval = setInterval(syncValue, 30000);
    return () => clearInterval(interval);
  }, [isAdmin, isMultipleBrowserMode]);

  // Auth Listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setLoading(false);
      // ─── Gate "Lengkapkan Pendaftaran" — pra-semak SEGERA (elak flash app/StartGate) ───
      // Calon gate = akaun google-only (tiada provider password) + baru (≤30 hari, elak
      // tembok akaun lama hasil takeover/link) + bukan staff. Semakan whatsappNumber
      // yang async dibuat di bawah; sementara tu tahan di 'checking'.
      let _regGateCandidate = false;
      try {
        if (currentUser) {
          const _providers = (currentUser.providerData || []).map(p => p.providerId);
          const _googleOnly = _providers.includes('google.com') && !_providers.includes('password');
          const _creationMs = new Date(currentUser.metadata?.creationTime || 0).getTime();
          const _isFresh = _creationMs > 0 && (Date.now() - _creationMs) <= 30 * 24 * 60 * 60 * 1000;
          const _isStaff = canAccessAdmin(getUserRole(currentUser.email));
          _regGateCandidate = _googleOnly && _isFresh && !_isStaff;
        }
      } catch (_) {}
      setRegGate(_regGateCandidate ? 'checking' : 'ok');
      // ─── Keselamatan PIN admin: buang status verified bila LOGOUT atau TUKAR akaun ───
      // sessionStorage kekal merentas logout→login dalam tab sama. Tanpa ni, login
      // semula boleh pintas PIN selagi sesi 30-min lama belum tamat. Ikat pada uid juga
      // supaya akaun berbeza tak warisi status PIN akaun sebelum.
      try {
        const pinUid = sessionStorage.getItem('admin_pin_uid');
        if (!currentUser || (pinUid && pinUid !== currentUser.uid)) {
          sessionStorage.removeItem('admin_pin_verified');
          sessionStorage.removeItem('admin_pin_time');
          sessionStorage.removeItem('admin_pin_uid');
          setAdminPinVerified(false);
        }
      } catch (_) {}
      if (!currentUser) {
        setLicenseStatus('loading');
        setLicenseChecked(false);
        return;
      }
      // Always upsert email to users/{uid} doc — prevents missing-email rows in admin view
      try {
        if (currentUser.email) {
          const userRef = doc(db, 'users', currentUser.uid);
          const snap = await getDoc(userRef);
          if (!snap.exists() || !snap.data()?.email) {
            await setDoc(userRef, {
              email: currentUser.email,
              createdAt: snap.exists() ? (snap.data().createdAt || serverTimestamp()) : serverTimestamp()
            }, { merge: true });
          }
        }
      } catch (_) {}
      // ─── Gate "Lengkapkan Pendaftaran": semakan whatsappNumber (async) ───
      if (_regGateCandidate) {
        try {
          const gateSnap = await getDoc(doc(db, 'users', currentUser.uid));
          setRegGate(gateSnap.data()?.whatsappNumber ? 'ok' : 'incomplete');
        } catch (_) {
          setRegGate('ok'); // fail-open — jangan kunci pengguna kalau semakan gagal
        }
      }
      // Bind referredBy permanently on FIRST login only — reject if account is not a fresh signup.
      // Existing users who click an affiliate link should NOT be attributed as a referral.
      try {
        const cookieMatch = document.cookie.match(/(?:^|;\s*)sesaat_ref=([^;]*)/);
        const refCode = cookieMatch ? decodeURIComponent(cookieMatch[1]) : (localStorage.getItem('sesaat_ref') || null);
        if (refCode) {
          // Fresh signup = account created within the last 30 days (matches cookie lifetime)
          const FRESH_SIGNUP_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
          const creationMs = new Date(currentUser.metadata?.creationTime || 0).getTime();
          const isFreshSignup = creationMs > 0 && (Date.now() - creationMs) <= FRESH_SIGNUP_WINDOW_MS;

          if (isFreshSignup) {
            const userRef = doc(db, 'users', currentUser.uid);
            const snap = await getDoc(userRef);
            if (!snap.exists() || !snap.data()?.referredBy) {
              await setDoc(userRef, { referredBy: refCode }, { merge: true });
              // Create referral doc server-side (function re-validates freshness)
              httpsCallable(functions, 'bindReferral')({ refCode }).catch(() => {});
            }
          }
        }
      } catch (_) {}
    });
    return () => unsubscribe();
  }, []);

  // ═══ GLOBAL ?ref= READER — sole handler for affiliate attribution + click tracking.
  // Cookie set synchronously so it survives fast navigations; click fired-and-forgotten.
  // ⚠️ ?ref= SENGAJA DIKEKALKAN dalam alamat (dulu dipadam serta-merta) — cookie tak
  // ikut bila pelanggan bertukar browser (klik dalam WhatsApp → "Open in Safari" =
  // kotak cookie berlainan; punca lead iOS hilang ejen). Lihat utils/refLink.js.
  // Klik kini di-dedupe per sesi — dulu pemadaman ?ref= yang jaga benda tu.
  const routeLocation = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (!ref) return;

    const cookieMatch = document.cookie.match(/(?:^|;\s*)sesaat_ref=([^;]*)/);
    const existingCookie = cookieMatch ? decodeURIComponent(cookieMatch[1]) : null;
    let existingLS = null;
    try { existingLS = localStorage.getItem('sesaat_ref'); } catch (_) {}
    const existingRef = existingCookie || existingLS;

    // ① Cookie first — guaranteed even if user navigates away mid-request
    if (!existingRef) {
      setRefCookie(ref, 30);
      try { localStorage.setItem('sesaat_ref', ref); } catch (_) {}
    }

    // ② Dedupe klik — sekali per kod ejen per sesi (elak statistik ejen berganda
    //    sekarang ?ref= kekal dalam alamat merentas navigasi)
    let alreadyTracked = false;
    try {
      alreadyTracked = sessionStorage.getItem('sesaat_ref_tracked') === ref;
      sessionStorage.setItem('sesaat_ref_tracked', ref);
    } catch (_) { /* storage disekat — biar klik direkod, lebih baik dari hilang */ }
    if (alreadyTracked) return;

    // ③ Track click — fire-and-forget; server resolves customSlug → canonical refCode
    httpsCallable(functions, 'trackAffiliateClick')({ slug: ref })
      .then(result => {
        const code = result.data?.refCode;
        if (code && code !== ref && !existingRef) {
          setRefCookie(code, 30);
          try { localStorage.setItem('sesaat_ref', code); } catch (_) {}
        }
      })
      .catch(() => {});
  }, [routeLocation.pathname, routeLocation.search]);

  // ═══ LICENSE CHECK — uses Cloud Function for server-side validation ═══
  useEffect(() => {
    if (!user) return;

    const checkLicense = async () => {
      try {
        // FAST PATH: Admin/Moderator bypass (client-side for instant UX)
        const role = getUserRole(user.email);
        if (role === ROLES.ADMIN || role === ROLES.MODERATOR) {
          console.log('[License] Admin/Moderator bypass — access granted');
          setLicenseStatus('valid');
          setLicenseChecked(true);
          return;
        }

        // ★ SECURITY: Use Cloud Function for authoritative license validation
        const validateLicenseFn = httpsCallable(functions, 'validateLicense');
        const result = await validateLicenseFn({});
        const data = result.data;

        console.log('[License] Server validation result:', data.status);

        if (data.status === 'valid') {
          setLicenseStatus('valid');
        } else if (data.status === 'expired') {
          setLicenseStatus('expired');
        } else {
          setLicenseStatus('none');
        }
        setLicenseChecked(true);

        // BARU: kira renewal eligibility dari users/{uid} doc (independent of license doc)
        // Multi-signal: paidAt | licenseKey | tier='paid' — any one = pernah paid before
        try {
          const uSnap = await getDoc(doc(db, 'users', user.uid));
          if (uSnap.exists()) {
            const u = uSnap.data();
            const eligible = !!(u.paidAt || u.licenseKey || u.tier === 'paid');
            setIsRenewalEligible(eligible);
          } else {
            setIsRenewalEligible(false);
          }
        } catch (e) {
          console.warn('[License] Renewal eligibility check failed:', e);
          setIsRenewalEligible(false);
        }

      } catch (err) {
        console.error('[License] Server check failed:', err);

        // FALLBACK: If Cloud Function fails, try direct Firestore read
        // (allowed by rules for own user document)
        try {
          const licenseDocRef = doc(db, 'licenses', user.uid);
          const licenseSnap = await getDoc(licenseDocRef);

          if (!licenseSnap.exists()) {
            setLicenseStatus('none');
          } else {
            const d = licenseSnap.data();
            const key = d.licenseKey || '';
            if (!key || key.trim() === '') {
              setLicenseStatus('none');
            } else {
              const exp = d.expiresAt?.toDate ? d.expiresAt.toDate() : new Date(d.expiresAt);
              setLicenseStatus(new Date() > exp ? 'expired' : 'valid');
            }
          }
          // BARU: cek eligibility juga dalam fallback path
          try {
            const uSnap = await getDoc(doc(db, 'users', user.uid));
            if (uSnap.exists()) {
              const u = uSnap.data();
              setIsRenewalEligible(!!(u.paidAt || u.licenseKey || u.tier === 'paid'));
            }
          } catch (_) {}
        } catch (fallbackErr) {
          console.error('[License] Fallback check also failed:', fallbackErr);
          setLicenseStatus('none');
        }
        setLicenseChecked(true);
      }
    };

    checkLicense();
  }, [user]);

  // ═══ POST-TRIAL-CLAIM REDIRECT — StartGate sets sessionStorage flag before
  // calling onLicenseActivated; once license flips to 'valid' we hop to /r/support
  // (which itself silently bounces to / if no agent group link is set).
  useEffect(() => {
    if (licenseStatus !== 'valid') return;
    let pending = null;
    try { pending = sessionStorage.getItem('postTrialClaimRedirect'); } catch (_) {}
    if (pending === '1') {
      try { sessionStorage.removeItem('postTrialClaimRedirect'); } catch (_) {}
      navigate('/r/support', { replace: true });
    }
  }, [licenseStatus, navigate]);

  // ═══ TRIAL COINS WATCHER — real-time listener, only active for trial users ═══
  useEffect(() => {
    if (!user) {
      setTrialCoinsEmpty(false); setUserTier(null); setPipelineCoins(null);
      setUserPlan(null); setFlownityTrialUsed(0);
      upgradePopupShownOnceRef.current = false; // Reset on logout — next login shows popup again
      return;
    }
    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, (snap) => {
      if (!snap.exists()) return;
      const d = snap.data();
      setUserTier(d.tier ?? null);
      setStorageUsed(d.storageUsedBytes || 0); // ★ kaunter storage — bar baca dari doc, bukan scan
      setUserPlan(d.plan ?? null);
      setPipelineCoins(d.pipelineCoins ?? null);
      setDailyPipelineUsed(d.dailyPipelineUsed ?? 0);
      setDailyPipelineDate(d.dailyPipelineDate ?? null);
      setDailyCloudVideoUsed(d.dailyCloudVideoUsed ?? 0); // ★ R2 (Fasa 3)
      setDailyCloudVideoDate(d.dailyCloudVideoDate ?? null);
      setFlownityTrialUsed(d.flownityTrialUsed ?? 0);
      if (d.tier === 'trial' && d.trialClaimed === true) {
        const empty = (d.pipelineCoins ?? 0) === 0;
        setTrialCoinsEmpty(empty);
        // Auto-show popup once per login when coins go empty.
        // If user dismiss, ref stays true — don't re-open on subsequent snapshots.
        if (empty && !upgradePopupShownOnceRef.current) {
          setShowUpgradePopup(true);
          upgradePopupShownOnceRef.current = true;
        }
      } else {
        setTrialCoinsEmpty(false);
        setShowUpgradePopup(false);
      }
    });
    return () => unsubscribe();
  }, [user]);

  // Mode (Locked Set) State
  const [lockedSet, setLockedSet] = useState(() => {
    return localStorage.getItem('lockedSet') || 'set1';
  });

  const handleSetLockChange = (newSet) => {
    setLockedSet(newSet);
    localStorage.setItem('lockedSet', newSet);
  };
  // ═══ MOBILE RESPONSIVE ═══
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setIsMobileSidebarOpen(false);
      if (mobile) setIsLeftPanelVisible(false); // auto-hide left panel on mobile
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* Tutup laci menu sebaik route bertukar. Ini jaring utama: ia menangkap
     SEMUA cara navigasi (item menu, submenu, kad Extension, logo, baris
     profil, spaces) tanpa perlu menyentuh setiap pengendali klik dalam
     NewSidebar. Klik yang TIDAK menukar route — cth mengembang submenu
     "Admin" — sengaja tidak menutup laci. */
  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  /* ═══ "Hantar ke Workspace" dari My Products ═══
     LibraryView menulis produk terpilih ke sessionStorage lalu navigate ke
     /workspace. Di sini kita ambil, stage ke panel kiri (user pilih avatar/
     module → Add Product) dan bersihkan handoff supaya ia tidak berulang.
     ⚠️ Bentuk hantaran = doc item PENUH (sama seperti pemilih Add Product) —
        peraturan + sebabnya dalam utils/hantarKeWorkspace.js. */
  useEffect(() => {
    if (location.pathname !== '/workspace') return;
    let raw = null;
    try { raw = sessionStorage.getItem(KUNCI_HANTAR_WORKSPACE); } catch { return; }
    if (!raw) return;
    try { sessionStorage.removeItem(KUNCI_HANTAR_WORKSPACE); } catch { /* abaikan */ }
    try {
      const items = JSON.parse(raw);
      if (Array.isArray(items) && items.length > 0) handleLibrarySelect(items);
    } catch (err) { console.warn('[App] stage-to-workspace parse gagal:', err); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  /* Route yang MELUKIS butang menu (hamburger) sendiri dalam komponennya.
     Header kongsi di bawah mesti langkau route ini — kalau tidak pelanggan
     nampak DUA hamburger bertindih atas satu sama lain pada telefon.
     Tambah route baharu ke sini bila komponennya terima prop `onMenuClick`. */
  const ROUTES_WITH_OWN_MENU_BUTTON = [
    '/products',    // LibraryView (+ /system, /myproducts)
    '/notes',       // NotesView
    '/extension',   // ExtensionView
    '/feed1',       // Feed1View
    '/profile',     // ProfileView — setting mengambil alih skrin penuh & ada nav sendiri
  ];

  // Sidebar collapse state (persisted)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [workspaceTab, setWorkspaceTab] = useState('products');
  const [showHeaderGroupDropdown, setShowHeaderGroupDropdown] = useState(false);
  const [headerSelectedGroupId, setHeaderSelectedGroupId] = useState(() => localStorage.getItem('selectedWorkspaceGroup') || 'all');
  const [headerGroups, setHeaderGroups] = useState(['Workspace']);
  const [headerGroupCounts, setHeaderGroupCounts] = useState({});
  const [headerNewGroupInput, setHeaderNewGroupInput] = useState(false);
  const [headerNewGroupName, setHeaderNewGroupName] = useState('');
  const [headerEditingGroup, setHeaderEditingGroup] = useState(null);
  const [headerEditingGroupName, setHeaderEditingGroupName] = useState('');
  const [headerDeletingGroup, setHeaderDeletingGroup] = useState(null);
  const [headerDeleteDestination, setHeaderDeleteDestination] = useState('Workspace');
  const [isLeftPanelVisible, setIsLeftPanelVisible] = useState(() => window.innerWidth >= 768);
  /* Tab Workspace yang sedang dibuka, dihantar naik oleh WorkspaceView.
     Dalam v3.9 panel larian hanya wujud pada tab Flow dan Schedule
     (arahan user 2026-08-11, ikut prototaip) — jadi App perlu tahu tab
     semasa untuk memutuskan sama ada panel itu dirender. */
  const [wsTab, setWsTab] = useState('all');

  /* Senarai group dalam bentuk yang GroupSwitcherV39 fahami: {id, nama, kira}.
     Group utama disimpan sebagai "Workspace" dalam data tetapi dipilih dengan
     id "all" di header — dua nama untuk benda yang sama, warisan kod lama.
     Pemetaan itu dikurung DI SINI supaya komponen v3.9 tidak perlu tahu
     mengenainya; ia cuma nampak satu id tunggal (GROUP_UTAMA). */
  const v39Groups = useMemo(() => ([
    { id: GROUP_UTAMA, nama: 'Main Group', kira: headerGroupCounts['Workspace'] || 0 },
    ...headerGroups
      .filter((g) => g !== 'Workspace')
      .map((g) => ({ id: g, nama: g, kira: headerGroupCounts[g] || 0 })),
  ]), [headerGroups, headerGroupCounts]);

  // Hide TikTok panel state (persisted)
  const [hideTikTokPanel, setHideTikTokPanel] = useState(() => {
    return localStorage.getItem('hideTikTokPanel') === 'true';
  });

  // Hide Flow panel state (persisted)
  const [hideFlowPanel, setHideFlowPanel] = useState(() => {
    return localStorage.getItem('hideFlowPanel') === 'true';
  });

  const toggleHideTikTokPanel = () => {
    setHideTikTokPanel(prev => {
      // Don't allow hiding both panels
      if (!prev === true && hideFlowPanel) return prev;
      localStorage.setItem('hideTikTokPanel', String(!prev));
      return !prev;
    });
  };

  const toggleHideFlowPanel = () => {
    setHideFlowPanel(prev => {
      // Don't allow hiding both panels
      if (!prev === true && hideTikTokPanel) return prev;
      localStorage.setItem('hideFlowPanel', String(!prev));
      return !prev;
    });
  };

  const toggleSidebarCollapse = () => {
    setIsSidebarCollapsed(prev => {
      localStorage.setItem('sidebarCollapsed', String(!prev));
      return !prev;
    });
  };

  // ===== Spaces State — shared between Sidebar & WorkspaceView =====
  // System default logic config — reads from system_spaces/ai8saat document (centralized, admin-managed)
  const ADMIN_UID = 'MNKAEqqARVTSaXkhCvTapTkOlFI2';
  const [systemDefaultLogicId, setSystemDefaultLogicId] = useState('default'); // ★ Centralized from system_spaces
  const [systemDefaultLogicName, setSystemDefaultLogicName] = useState('TEKNIK AI8SAAT');

  const [spaces, setSpaces] = useState([
    { id: 'default', name: 'Workspace', icon: 'auto_awesome', color: 'indigo', isSystem: true }
  ]);
  const [selectedSpaceId, setSelectedSpaceId] = useState(() => localStorage.getItem('selectedSpaceId') || 'default');
  const [productLibraryHidden, setProductLibraryHidden] = useState(false);
  const [productLibraryCount, setProductLibraryCount] = useState(0);
  const [products, setProducts] = useState([]);
  const [isFlownitySelected, setIsFlownitySelected] = useState(false);
  // Mirrors WorkspaceView's own selected-count (per-browser UI flag, not in Firestore).
  // Source of truth lives in WorkspaceView.products; this is just so footer counter renders.
  const [headerSelectedCount, setHeaderSelectedCount] = useState(0);
  /* Langkah 3: ringkasan larian kalendar (mod Schedule). null = bukan mod
     Schedule → bar bawah papar kiraan kad bertick seperti biasa. */
  const [schedRunInfo, setSchedRunInfo] = useState(null);
  // ★ Track product IDs that are being deleted — prevents onSnapshot from re-injecting them
  const pendingDeletesRef = useRef(new Set());

  // Detect if on a space page
  const isOnSpacePage = location.pathname.startsWith('/space/') || location.pathname === '/workspace';
  // Kunci chrome workspace (panel kiri + toolbar) untuk admin/mod yang belum masuk PIN.
  // Customer biasa (bukan privileged) TAK terkunci — workspace mereka buka macam biasa.
  const workspaceLocked = isOnSpacePage
    && canAccessAdmin(getUserRole(user?.email))
    && !adminPinVerified;

  /* ── Control Panel: sorok / papar (v3.9) ────────────────────────────
     SATU tetapan untuk semua tab yang membawa panel — pengguna yang
     menyoroknya pada tab Flow tidak sepatutnya terpaksa menyorok semula
     sebaik dia beralih ke Schedule atau Agent AI.

     Kuncinya SAMA dengan prototaip (`CP_KEY`, satu takrifan diimport dari
     WorkspaceV39View) supaya tidak ada dua tetapan berlainan yang mengawal
     benda yang sama. Disimpan dalam browser, bukan Firestore: ini keutamaan
     ruang skrin, dan skrin berbeza pada setiap komputer yang dia guna.

     Baca dan tulis dibalut try/catch — localStorage boleh melontar ralat
     dalam pelayaran peribadi, dan panel yang tersorok bukan alasan untuk
     seluruh Workspace gagal render. */
  const [panelSorok, setPanelSorok] = useState(() => {
    try { return localStorage.getItem(CP_KEY) === 'sorok'; } catch { return false; }
  });
  const tetapkanPanelSorok = (sorok) => {
    setPanelSorok(sorok);
    try { localStorage.setItem(CP_KEY, sorok ? 'sorok' : 'papar'); } catch { /* abaikan */ }
  };

  /* Tab yang MEMANG membawa panel. Dipisahkan daripada `panelLarianNampak`
     kerana butang sorok/papar perlu tahu perkara ini sendiri: pada tab
     Generate butang itu tidak boleh muncul langsung — ia tidak akan mengubah
     apa-apa, dan kawalan yang tidak mengubah apa-apa ialah kawalan palsu. */
  const tabAdaPanel = wsTab === 'flow' || wsTab === 'tiktok' || wsTab === 'chatgpt';

  /* Bila panel larian Workspace kelihatan.
     v3.9 — ikut TAB, kemudian ikut pilihan sorok pengguna.
     Rupa lama — ikut butang penyembunyi seperti sebelum ini.
     ⚠️ Nilai tab datang daripada WorkspaceView melalui `onActiveTabChange`;
        `tiktok` ialah nilai simpanan bagi tab yang kini dipanggil Schedule.

     TELEFON turut mengikut pilihan sorok. Ia PERNAH dikecualikan semasa butang
     itu duduk di baris tajuk, kerana baris tajuk versi telefon hanya hamburger
     + pemilih group — jadi panel boleh hilang tanpa jalan memanggilnya balik.
     Butang kini duduk dalam jalur alat, yang MEMANG dirender pada telefon
     (jalur menatal mengufuk; lihat `.sesaat-ws-toolbar` dalam mobile.css), jadi
     perangkap itu tiada lagi. ⛔ Kalau butang itu dipindahkan semula ke tempat
     yang tidak wujud pada telefon, pengecualian tadi WAJIB dikembalikan —
     kalau tidak pengguna telefon kehilangan panel secara kekal. */
  const panelLarianNampak = uiV39
    ? (tabAdaPanel && !panelSorok)
    : isLeftPanelVisible;

  /* ── Control Panel tab Agent AI (v3.9) ──────────────────────────────
     Suis "Smart" (arahan user 2026-08-14): MATI = agent yang SESAAT
     sediakan (FLOWNITY buat masa ini), HIDUP = agent custom GPT
     pelanggan sendiri daripada PRO Studio.

     Ia disimpan per browser, sama seperti pilihan agent dan platform yang
     sedia ada — tetapan alat, bukan data produk. */
  const [smartMode, setSmartMode] = useState(() => {
    try { return localStorage.getItem('agent_smartMode') === '1'; } catch { return false; }
  });
  const tukarSmartMode = () => {
    setSmartMode(prev => {
      const next = !prev;
      try { localStorage.setItem('agent_smartMode', next ? '1' : '0'); } catch { /* abaikan */ }
      return next;
    });
  };
  /* Keadaan panel datang daripada WorkspaceView (senarai agent, agent
     dipilih, platform, kiraan kad, keadaan larian). Ia PAPARAN sahaja —
     sumber kebenaran kekal di sana; lihat `onAgentPanelChange`. */
  const [agentPanel, setAgentPanel] = useState(null);

  /* Nod lajur kanan di DALAM bingkai halaman — destinasi Control Panel
     dalam v3.9 (arahan user 2026-08-12: "control panel ni termasuk sekali
     dalam page workspace macam dalam prototaip").

     Sebelum ini panel ialah adik-beradik <main>, jadi ia duduk di LUAR
     bingkai: melekat pada tepi tetingkap, tinggi penuh, memotong bucu bulat
     halaman. Dalam prototaip ia lajur di dalam halaman.

     Disimpan sebagai state (bukan ref biasa) kerana portal hanya boleh
     dipasang selepas nod itu wujud — menyimpannya dalam state mencetuskan
     satu render susulan yang memasangnya. */
  const [nodPanelKanan, setNodPanelKanan] = useState(null);

  /* v3.9: TAB yang menentukan panel mana keluar — Flow atau TikTok.
     Dalam prototaip panel tab Flow dan tab Schedule memang dua benda
     berlainan; tiada suis dalam panel kerana tab sudah menjawabnya.

     Di sini suis segmen Flow|TikTok dalam panel DIKEKALKAN (arahan user
     2026-08-12: jangan buang apa-apa daripada panel — ia dikongsi dengan
     rupa lama, dan rupa lama tiada komposer untuk menggantikannya). Yang
     ditambah hanyalah penyelarasan: bertukar tab menetapkan suis itu.
     Menekan suis secara manual masih berfungsi seperti biasa sehingga tab
     ditukar semula.

     Rupa lama tidak terjejas — tanpa `uiV39` effect ini tidak berbuat apa. */
  useEffect(() => {
    if (!uiV39) return;
    if (wsTab === 'flow') setWorkspaceTab('products');
    else if (wsTab === 'tiktok') setWorkspaceTab('auto');
    /* Nilai KETIGA, v3.9 sahaja. Rupa lama tidak boleh sampai ke sini —
       di sana `workspaceTab` hanya ditetapkan oleh suis Flow|TikTok dalam
       panel, dan suis itu tiada butang Agent AI. */
    else if (wsTab === 'chatgpt') setWorkspaceTab('agent');
  }, [uiV39, wsTab]);

  /* ---------- Kepala halaman Workspace (v3.9 sahaja) -------------------
     Tajuk "Workspace" kini kepala halaman BIASA di dalam bingkai kongsi,
     sama seperti Extension, Marketplace dan Products.

     Kenapa ia ditukar (aduan user 2026-08-12, "kenapa jarak tak sama macam
     page lain"): dahulu ia blok TERAPUNG (`position: absolute`) yang duduk
     di luar bingkai, dijajarkan dengan tangan pada senarai produk dan bukan
     pada halaman lain — 51px dari kiri dan 37px dari atas, sedangkan setiap
     halaman v3.9 yang lain 28px dan 26px. Tiga nombor itu terpaksa diukur
     semula setiap kali padding senarai berubah; komen lama di tempatnya
     sendiri sudah memberi amaran tentang perangkap itu.

     Sekarang jaraknya datang daripada `.s-work__head` (26px 28px, nilai
     yang sama dipakai /formula) dan tajuknya daripada komponen `PageHeader`
     yang sama seperti halaman lain. Satu sumber — laraskan halaman lain,
     Workspace ikut sendiri.

     ⛔ Rupa lama TIDAK menggunakan blok ini. Kepalanya kekal blok terapung
        di dalam <main> seperti sebelum ini, tidak disentuh langsung.       */
  const pemilihGroupV39 = (
    <div style={{ zIndex: 2000 }}>
      <GroupSwitcherV39
        groups={v39Groups}
        aktif={headerSelectedGroupId === 'all' ? GROUP_UTAMA : headerSelectedGroupId}
        onPilih={(id) => {
          const next = id === GROUP_UTAMA ? 'all' : id;
          setHeaderSelectedGroupId(next);
          workspaceViewRef.current?.setSelectedGroupId(next);
          window.postMessage({ type: 'SESAAT_GROUP_SYNC', groupId: next }, '*');
        }}
        onCipta={(nama) => {
          workspaceViewRef.current?.createGroup(nama);
          setHeaderSelectedGroupId(nama);
          workspaceViewRef.current?.setSelectedGroupId(nama);
          window.postMessage({ type: 'SESAAT_GROUP_SYNC', groupId: nama }, '*');
        }}
        onNamaSemula={async (id, nama) => {
          const res = await workspaceViewRef.current?.renameGroup(id, nama);
          if (!res?.ok) {
            alert(res?.reason === 'duplicate' ? `Nama "${nama}" dah digunakan.`
              : res?.reason === 'reserved' ? `Nama "${nama}" tak dibenarkan.` : 'Rename gagal.');
            return;
          }
          if (headerSelectedGroupId === id) setHeaderSelectedGroupId(res.newName);
          window.postMessage({ type: 'SESAAT_GROUP_SYNC', groupId: headerSelectedGroupId === id ? res.newName : headerSelectedGroupId }, '*');
        }}
        onMintaPadam={(id) => setHeaderDeletingGroup(id)}
      />
    </div>
  );

  /* ── Pemilih set — SATU takrifan logik, dua kulit ─────────────────────
     Kulit 'v39'  : bulatan besar `s-pill` dalam seksyen "Set" badan kad.
     Kulit 'lama' : pil kecil dalam jalur kaki, seperti sebelum ini.

     ⛔ JANGAN salin logik ini ke tempat pemanggil. Cubaan pertama saya
        menulis salinan BAHARU untuk v3.9 dan menyorok yang lama — user
        menegur, dan teguran itu betul: hasilnya dua salinan peraturan yang
        sama dalam kod, dan salinan yang tidak dilihat sesiapa itulah yang
        akan terlepas bila peraturan berubah (contohnya bila FLOWNITY tidak
        lagi 5 scene). Yang berbeza antara dua rupa hanyalah rupa butang.

     FLOWNITY memakai 5 "scene" dengan state yang BERLAINAN
     (`automationTargetScenes`), jadi kedua-dua cabang itu tinggal di sini
     dan bukan diulang oleh setiap pemanggil. */
  const butangPilihanSet = (kulit) => {
    const senarai = isFlownitySelected
      ? ['set1', 'set2', 'set3', 'set4', 'set5']
      : ['set1', 'set2', 'set3'];

    return senarai.map((sid) => {
      const aktif = isFlownitySelected
        ? automationTargetScenes.includes(sid)
        : automationTargetSets.includes(sid);
      const tukar = () => {
        const simpan = isFlownitySelected ? setAutomationTargetScenes : setAutomationTargetSets;
        simpan((p) => (aktif ? p.filter((s) => s !== sid) : [...p, sid].sort()));
      };
      const teks = sid.toUpperCase().replace('SET', 'S');

      if (kulit === 'v39') {
        return (
          <button
            key={sid}
            type="button"
            aria-pressed={aktif}
            className={`s-pill${aktif ? ' s-pill--on' : ''}`}
            onClick={tukar}
          >{teks}</button>
        );
      }
      return (
        <button
          key={sid}
          onClick={tukar}
          style={{
            width: 28, height: 28, borderRadius: '50%', border: 'none',
            background: aktif ? '#6366f1' : 'transparent',
            color: aktif ? '#ffffff' : '#94a3b8', fontSize: 10, fontWeight: 800,
            cursor: 'pointer', transition: 'all 0.2s',
            boxShadow: aktif ? '0 2px 6px rgba(99,102,241,0.3)' : 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >{teks}</button>
      );
    });
  };

  /* Peraturan tiga suis Schedule — SATU takrifan, dipanggil oleh kedua-dua
     kulit. Dahulu peraturan saling-menolak ini hidup di dalam `onChange`
     setiap checkbox; memindahkannya ke sini bermakna kulit v3.9 tidak perlu
     menyalinnya semula (arahan user 2026-08-14: tukar kulit, jangan salin
     logik).

     Peraturannya kekal sama seperti sebelum ini:
       Post Only hidup   → Showcase dimatikan.
       Showcase hidup    → Post Only DAN Schedule dimatikan (tiada video
                           dimuat naik, jadi tiada apa untuk dijadualkan).
       Schedule hidup    → Showcase dimatikan.
       Schedule mati     → Direct Schedule dimatikan sekali (lihat
                           `tukarDirectSchedule`). */
  /* Pilih tempat post. Bukan suis bebas — dua baris itu SALING MENOLAK, jadi
     menekan satu mematikan satu lagi. Menekan baris yang sudah hidup tidak
     membuat apa-apa: "tiada tempat post" bukan keadaan yang sah. */
  const pilihPostTo = (tempat) => {
    if (tempat === postTo) return;
    setPostTo(tempat);
    try { localStorage.setItem('sesaat_post_to_v1', tempat); } catch { /* storan ditutup — abai */ }
  };
  /* ★ [2026-09-18] Beg kuning = INDUK Post Mode (keputusan user). MATI → Showcase
     Only dimatikan sekali: ia tersorok, dan kalau kekal hidup robot menamatkan
     larian selepas langkah Showcase yang kosong — TIADA video dipost, senyap.
     Post Only sengaja dibiar (tiada kesan bila link tidak dihantar) supaya
     pilihan pelanggan kembali bila beg kuning dihidupkan semula. */
  const tukarBegKuning = () => {
    const baru = !begKuningAktif;
    setBegKuningAktif(baru);
    tulisSuisProduk(KUNCI_BEG_KUNING, baru);
    if (!baru) setIsShowcaseOnly(false);
  };
  const pilihFbLaluan = (laluan) => {
    if (laluan === fbLaluan) return;
    setFbLaluan(laluan);
    tulisLaluanFb(laluan);
  };
  const tukarFbAddProduct = () => {
    const baru = !fbAddProductAktif;
    setFbAddProductAktif(baru);
    tulisSuisProduk(KUNCI_FB_ADD_PRODUCT, baru);
  };
  const tukarPostOnly = () => {
    const hidup = !isPostOnly;
    setIsPostOnly(hidup);
    if (hidup) setIsShowcaseOnly(false);
  };
  const tukarShowcase = () => {
    const hidup = !isShowcaseOnly;
    setIsShowcaseOnly(hidup);
    if (hidup) { setIsPostOnly(false); setIsScheduled(false); setDirectSchedule(false); }
  };
  const tukarSchedule = () => {
    const hidup = !isScheduled;
    setIsScheduled(hidup);
    if (hidup) setIsShowcaseOnly(false);
    /* Direct Schedule hanya bermakna apabila ada masa jadual. Membiarkannya
       hidup selepas Schedule dimatikan meninggalkan suis yang menyatakan
       "video akan tersiar sendiri" sedangkan tiada waktu langsung untuk
       disiarkan — jadi ia dimatikan bersama. */
    else setDirectSchedule(false);
  };
  const tukarDirectSchedule = () => {
    /* Tiada gunanya menghidupkannya tanpa Schedule; pagar ini menjadikan
       keadaan "Direct hidup, Schedule mati" mustahil dari sini, bukan
       sekadar tidak digalakkan. */
    if (!isScheduled) return;
    setDirectSchedule((sedia) => !sedia);
  };

  /* Had yang boleh ditaip dalam kotak Start. TikTok menolak jadual kurang
     15 minit dari sekarang dan lebih HAD_HARI_TIKTOK hari ke hadapan; had
     yang sama diperiksa semula semasa Run (lihat pagar julat dalam
     WorkspaceView) kerana tetingkap boleh dibiar terbuka berjam-jam
     sehingga nilai di sini menjadi basi.

     ⚠️ Zon waktu ditolak DAHULU, sama seperti nilai awal
        `scheduledStartTime` di atas. `toISOString()` mentah bercakap dalam
        UTC — di Malaysia ia akan menggeser had lapan jam dan menghalang
        pengguna daripada memilih waktu yang sebenarnya sah. */
  const kepadaMasaTempatan = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

  /* ⚠️⚠️ `min` ialah STEP BASE bagi <input type="datetime-local"> (spesifikasi
     HTML) — BUKAN sekadar had bawah. Kalau `min` mendarat pada 16:07, maka
     `step={300}` menjadikan nilai sah :07, :12, :17… iaitu minit yang roda
     TikTok LANGSUNG tidak ada (ia hanya 00,05,…,55).

     Kesannya bukan kosmetik: setiap video dalam larian itu akan gagal verify
     ("minit 07 tiada dalam senarai TikTok") dan jatuh menjadi draf. Satu masa
     mula yang tersasar meracuni seluruh barisan, kerana selang 15/30/60/120
     mengekalkan offset yang sama.

     Jadi bundarkan NAIK ke sempadan 5 minit berikutnya — naik, bukan turun,
     supaya had 15 minit TikTok tidak pernah dilanggar. */
  const bundarNaik5Minit = (d) => {
    const x = new Date(d.getTime());
    x.setSeconds(0, 0);
    const baki = x.getMinutes() % 5;
    if (baki) x.setMinutes(x.getMinutes() + (5 - baki));
    return x;
  };
  const jadualMasaMin = kepadaMasaTempatan(bundarNaik5Minit(new Date(Date.now() + 15 * 60 * 1000)));
  const jadualMasaMax = kepadaMasaTempatan(new Date(Date.now() + HAD_HARI_TIKTOK * 24 * 60 * 60 * 1000));

  /* Baris suis v3.9 — bentuk `BarisSuis` prototaip: penerangan di kiri,
     suis di kanan hujung, nota di bawah label.

     SATU takrifan untuk semua baris suis dalam panel (Continue Collection,
     Post To, Post Mode, Schedule). Ia KULIT sahaja — setiap pemanggil
     membawa state dan handler sedia ada masing-masing; tiada logik baharu
     ditulis dan tiada logik lama disalin.

     `lock` menukar suis kepada mangga, untuk ciri yang memang belum dibuka. */
  const barisSuisV39 = ({ kunci, label, nota, on, onChange, disabled, lock }) => (
    <div className="s-togrow" key={kunci}>
      <div className="s-togrow__txt">
        <span className="s-lbl" style={{ marginBottom: 0, opacity: lock ? 0.5 : 1 }}>{label}</span>
        {nota && <p className="s-hint">{nota}</p>}
      </div>
      {lock ? (
        <span
          className="material-symbols-outlined"
          style={{ fontSize: 16, color: 'var(--s-ink-3)', flex: 'none', marginTop: 3 }}
        >lock</span>
      ) : (
        <button
          type="button"
          role="switch"
          aria-checked={!!on}
          aria-label={label}
          disabled={disabled}
          onClick={onChange}
          className={`s-switch${on ? ' s-switch--on' : ''}`}
          style={{ marginTop: 2 }}
        >
          <span className="s-switch__dot" />
        </button>
      )}
    </div>
  );

  const kepalaWorkspaceV39 = (
    <div className="s-work__head">
      {/* Telefon: tajuk digugurkan dan hamburger menggantikannya — parity
          tepat dengan kelakuan sebelum ini (tajuk dahulunya dipagar
          `!isMobile`). Pemilih group ialah SATU takrifan yang dikongsi
          kedua-dua laluan; jangan salin ia menjadi dua. */}
      {isMobile ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
          <button
            onClick={() => setIsMobileSidebarOpen(true)}
            className="w-8 h-8 flex items-center justify-center rounded-lg"
            aria-label="Buka menu"
          >
            <span className="material-symbols-outlined" style={{ fontSize: 20, color: 'var(--s-ink-3)' }}>menu</span>
          </button>
          {pemilihGroupV39}
          {/* Pil bahasa — telefon SAHAJA perlu disebut di sini. Bentuk
              desktop di bawah memakai <PageHeader> yang sudah membawanya
              sendiri; bentuk telefon ini menggugurkan tajuk (dan dengan itu
              PageHeader), jadi tanpa baris ini Workspace menjadi satu-satunya
              halaman yang tidak boleh menukar bahasa pada telefon.
              `marginLeft:auto` menolaknya ke hujung kanan — kedudukan yang
              SAMA seperti pada setiap halaman lain. */}
          <span style={{ marginLeft: 'auto', flex: 'none' }}><SuisBahasa /></span>
        </div>
      ) : (
        /* ⛔ Butang sorok/papar Control Panel PERNAH duduk di sini (baris tajuk).
              Ia dipindahkan ke jalur tab dalam WorkspaceView, sebelah kanan ikon
              buka/tutup semua (arahan user 2026-08-15) — jalur itu MELEKAT
              semasa menatal (`position: sticky`), jadi butang tetap sentiasa
              kelihatan. Jangan pindahkan ia balik ke sini tanpa bertanya. */
        <PageHeader
          className="s-work__pagehead"
          title="Workspace"
          sisiTajuk={<><span className="s-chiprule" />{pemilihGroupV39}{generateV2.aktif && <span ref={setSlotTabV4} style={{ display: 'contents' }} />}</>}
          /* Toggle Classic ⇄ New (admin sahaja) — SEBELAH pil bahasa, HANYA pada tab Generate
             (arahan user 23 Okt: tab lain akan ada kawalan sendiri di baris ini). Suis itu sendiri
             untuk SELURUH Workspace V2 — `generateV2.aktif` berkuat kuasa di semua tab. */
          actions={generateV2.bolehTukar ? (
            <>
              {generateV2.aktif && <span ref={setSlotAlatV4} style={{ display: 'contents' }} />}
              {wsTab === 'all' && <GenerateV2Suis aktif={generateV2.aktif} onTukar={generateV2.tukar} />}
            </>
          ) : undefined}
        />
      )}

      {headerDeletingGroup && (
        <PadamGroupModalV39
          g={v39Groups.find((x) => x.id === headerDeletingGroup) || { id: headerDeletingGroup, nama: headerDeletingGroup, kira: 0 }}
          groups={v39Groups}
          onBatal={() => setHeaderDeletingGroup(null)}
          onSah={async (dest) => {
            const destSebenar = dest === GROUP_UTAMA ? 'Workspace' : dest;
            const res = await workspaceViewRef.current?.deleteGroup(headerDeletingGroup, destSebenar);
            if (!res?.ok) { alert('Delete gagal.'); return; }
            if (headerSelectedGroupId === headerDeletingGroup) {
              const nextId = destSebenar === 'Workspace' ? 'all' : destSebenar;
              setHeaderSelectedGroupId(nextId);
              workspaceViewRef.current?.setSelectedGroupId(nextId);
              window.postMessage({ type: 'SESAAT_GROUP_SYNC', groupId: nextId }, '*');
            }
            setHeaderDeletingGroup(null);
          }}
        />
      )}
    </div>
  );

  // Load AI Logic options — from shared system_spaces for system spaces
  useEffect(() => {
    if (!user) return;

    const currentSpace = spaces.find(s => s.id === selectedSpaceId);
    const isSystemSpace = currentSpace?.isSystem;

    const personasPath = isSystemSpace
      ? `system_spaces/ai8saat/personas`
      : `users/${user.uid}/personas`;

    const personasRef = collection(db, ...personasPath.split('/'));
    const unsubscribe = onSnapshot(personasRef, (snapshot) => {
      const options = snapshot.docs.map(d => ({ id: d.id, _isSystem: isSystemSpace || false, ...d.data() }));
      setHeaderLogicOptions(options);
      if (options.length > 0 && !headerSelectedLogicId) {
        setHeaderSelectedLogicId(options[0].id);
      }
    });
    return () => unsubscribe();
  }, [user, selectedSpaceId, spaces]);

  // ★ Subscribe to system_spaces/ai8saat for centralized default logic (real-time)
  // Admin sets defaultLogicId here → ALL users automatically follow it
  useEffect(() => {
    const systemSpaceRef = doc(db, 'system_spaces', 'ai8saat');
    const unsubscribe = onSnapshot(systemSpaceRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.defaultLogicId) {
          setSystemDefaultLogicId(data.defaultLogicId);
          console.log('[App] ★ System defaultLogicId from system_spaces:', data.defaultLogicId);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  // (userRole + isAdmin kini diisytihar di atas, dekat dengan useState user — lihat awal App())

  // Handle click outside for avatar dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (avatarDropdownRef.current && !avatarDropdownRef.current.contains(event.target)) {
        setIsAvatarDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ★ MODULE MASTER PROMPT: Load all modules from Firestore modules/ collection
  // KUKUH: (1) hanya langgan bila user dah log masuk (sesi Firestore sedia), (2) auto
  // cuba-semula bila gagal — elak senarai module kosong SENYAP masa first login (punca
  // tiles/button tak keluar → kena refresh; + generation jatuh ke laluan fallback = "Lain-lain").
  useEffect(() => {
    if (!user?.uid) return;               // modules perlu auth — tunggu sesi sedia dulu
    let unsub = null, retryTimer = null, cancelled = false, attempts = 0;
    const subscribe = () => {
      if (cancelled) return;
      const modulesRef = collection(db, 'modules');
      unsub = onSnapshot(modulesRef, (snapshot) => {
      attempts = 0;                       // berjaya → reset backoff
      const mods = snapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          name: data.name || d.id,
          description: data.description || '',
          icon: data.icon || 'extension',
          color: data.color || '#94a3b8',
          order: data.order || 99,
          locked: data.locked || false,
          jsonMode: data.jsonMode || false,
          outputFormat: data.outputFormat || '3sets',
          // Only expose masterPrompt to admin — non-admin gets empty string
          masterPrompt: isAdmin ? (data.masterPrompt || '') : '',
          mandatoryImageAppend: data.mandatoryImageAppend || '',
          mandatoryVideoAppend: data.mandatoryVideoAppend || '',
          mandatoryVideoAppend2: data.mandatoryVideoAppend2 || '',
          // Scene 2 prepend (admin-controlled per module)
          prependScene2Enabled: data.prependScene2Enabled || false,
          prependScene2Text: data.prependScene2Text || '',
          /* ★ RUPA KAD FORMULA (v3.9) — ditaip admin dalam modal tetapan module.
             ⛔ JANGAN tambah `|| ''` pada empat baris ini. `undefined` bermaksud
                "admin belum pernah menyimpan module ini" → kad guna ayat asal;
                `''` bermaksud "admin sengaja mengosongkannya" → kad memang
                kosong. Menukar undefined jadi '' memusnahkan beza itu dan
                setiap kad akan kosong pada hari ciri ini naik. */
          kadNotaMs: data.kadNotaMs,
          kadNotaEn: data.kadNotaEn,
          kadBadge: data.kadBadge,
          kadDurasi: data.kadDurasi,
          /* Generate V2 — sama peraturan: undefined = guna lalai kod (generateV2Matlamat.js). */
          kadGoal: data.kadGoal,
          kadFormat: data.kadFormat,
          kadPil: data.kadPil,
          kadScenes: data.kadScenes,
          // ★ Engine/gating fields — untuk module ciptaan UI (registerRuntimeModules).
          // Built-in modules (ai8saat dll) abaikan ni — registry in-code yang menang.
          marketingName: data.marketingName || data.name || d.id,
          badge: data.badge || null,
          scriptKey: data.scriptKey !== undefined ? data.scriptKey : null,
          promptSchema: data.promptSchema || 'single-scene',
          uploadVia: data.uploadVia || 'webapp-or-extension',
          adminOnly: data.adminOnly || false,
          isPremiumAddon: data.isPremiumAddon || false,
          releaseStatus: data.releaseStatus || 'released',
          sceneCount: data.sceneCount || null, // N-scene module — bilangan babak
          // ★ Borang tempahan enjin RESIPI. ⛔ Tanpa baris ini getModule(id).resipi sentiasa null → module "gambar berlainan"
          //   berjalan senyap sebagai "satu gambar".
          resipi: data.resipi || null,
          // ★ Flag server: module ni skip Stage 1+2 di dalam generateProductContent (module
          //   yang memang tak guna data scrape). Dibawa ke client supaya sekatan "wajib ada
          //   data" (WorkspaceView.generateForProduct) tahu module mana dikecualikan.
          skipStages12: data.skipStages12 === true,
          // Sub-module fields — non-admin dapat id+name+locked sahaja (masterPrompt admin-only;
          // server yang resolve prompt ikut subModuleId masa generate)
          hasSubModules: data.hasSubModules || false,
          subModuleLabel: data.subModuleLabel || 'Teknik',
          subModules: isAdmin
            ? (data.subModules || [])
            : (data.subModules || []).map(sm => ({ id: sm.id, name: sm.name, locked: sm.locked === true })),
        };
      });
      // Inject registry-only modules (cth Smart Custom — manual mode, tiada Firestore doc)
      const firestoreIds = new Set(mods.map(m => m.id));
      const registryOnly = listRegistryOnlyModules()
        .filter(m => !firestoreIds.has(m.moduleId))
        .map(m => ({
          id: m.moduleId,
          name: m.marketingName,
          description: '',
          icon: m.icon || 'extension',
          color: m.color || '#94a3b8',
          order: m.order || 99,
          locked: false,
          jsonMode: false,
          outputFormat: '3sets',
          masterPrompt: '',
          mandatoryImageAppend: '',
          mandatoryVideoAppend: '',
          mandatoryVideoAppend2: '',
          prependScene2Enabled: false,
          prependScene2Text: '',
        }));
      const allMods = [...mods, ...registryOnly];
      allMods.sort((a, b) => a.order - b.order);
      // ★ Daftar module ciptaan UI (bukan built-in) supaya getModule() pulangkan
      // engine/gating betul. Dipanggil sebelum setModules supaya render guna data terkini.
      registerRuntimeModules(allMods);
      setModules(allMods);
      console.log('[App] ★ Modules loaded:', allMods.map(m => `${m.id} (${m.locked ? 'locked' : 'active'}${registryOnly.find(r => r.id === m.id) ? ' — registry-only' : ''})`));
      }, (err) => {
        // Gagal (cth auth belum siap masa first login) — JANGAN diam, cuba semula automatik.
        console.warn('[App] ⚠ Modules listener error:', err?.code || err?.message, '→ cuba semula...');
        if (unsub) { try { unsub(); } catch (_) {} unsub = null; }
        if (cancelled) return;
        attempts += 1;
        retryTimer = setTimeout(subscribe, Math.min(1000 * attempts, 5000)); // backoff, cap 5s
      });
    };
    subscribe();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (unsub) { try { unsub(); } catch (_) {} }
    };
  }, [user?.uid, isAdmin]);

  // ★ CREATE MODULE: cipta module baharu (admin) → tulis Firestore modules/{id}.
  // Engine "pinjam" dari yang sedia ada; "custom" = shell locked sehingga dev bina script extension.
  const handleCreateModule = async () => {
    setCreateModuleError('');
    const id = (newModule.id || '').trim().toLowerCase();
    const name = (newModule.name || '').trim();
    if (!/^[a-z0-9][a-z0-9-]{1,40}$/.test(id)) {
      setCreateModuleError('Module ID mesti huruf kecil / nombor / "-" sahaja (cth: ai-beauty), 2–41 aksara.');
      return;
    }
    if (!name) { setCreateModuleError('Nama paparan wajib diisi.'); return; }
    if (MODULES[id] || modules.some(m => m.id === id)) {
      setCreateModuleError(`Module ID "${id}" sudah wujud. Pilih ID lain.`);
      return;
    }
    // ⚠️ KEMBAR: ENGINE_PRESETS + bentuk doc module dalam functions/partnerStudio.js (adminLulus —
    //    approve module Partner jadi module rasmi). Tambah/ubah engine = ubah dua-dua.
    const ENGINE_PRESETS = {
      flow1:  { scriptKey: 'ai8saat',  promptSchema: 'single-scene', uploadVia: 'webapp-or-extension' },
      flow2:  { scriptKey: 'ai16saat', promptSchema: 'two-scene',    uploadVia: 'extension-only' },
      // Flow N-Scene — robot flow automation BARU (dev bina + daftar scriptKey 'nscene' di extension).
      // Bukan guna robot AI16SAAT. AI tulis N babak dah berfungsi; robot pending.
      nscene: { scriptKey: 'nscene',   promptSchema: 'n-scene',      uploadVia: 'extension-only' },
      goyang: { scriptKey: 'goyang',   promptSchema: 'goyang-style', uploadVia: 'extension-only' },
      manual: { scriptKey: null,       promptSchema: 'manual',       uploadVia: 'extension-only' },
      // ★ [2026-10-17] Enjin Flow RESIPI — "satu gambar AI untuk semua scene", 1–5 scene (3+ = 1 set per card)
      //   (PLAN_FLOW_RESIPI.md). Robot: flow-automation-resipi.js (kunci 'resipi' dalam peta module robot).
      //   promptSchema 'resipi' = bentuk card tersendiri (Image Prompt + Video Prompt setiap scene) — BUKAN
      //   'n-scene' FLOWNITY (di sana promptVideo{n} = prompt gambar). Generate untuk bentuk ini BELUM dibina.
      resipi: { scriptKey: 'resipi',   promptSchema: 'resipi',       uploadVia: 'extension-only' },
    };
    const isResipi = newModule.engine === 'resipi';
    const resipiBerlainan = isResipi && newModule.resipiGambar === 'berlainan';   // gambar berlainan setiap scene = 2–5 scene
    const resipiGambar = resipiBerlainan ? 'berlainan' : (['tiada', 'sahaja', 'frame'].includes(newModule.resipiGambar) ? newModule.resipiGambar : 'sama');
    // N-scene module — bilangan babak (clamp 2-6). Resipi — 1-5 (4 + 5 scene diukur hidup 3 + 8 Okt). null untuk engine lain.
    const sceneCount = newModule.engine === 'nscene'
      ? Math.min(6, Math.max(2, parseInt(newModule.nscene, 10) || 3))
      : isResipi
        ? (resipiGambar === 'frame' ? 1 : Math.min(resipiGambar === 'sahaja' ? 4 : 5, Math.max(resipiBerlainan ? 2 : 1, parseInt(newModule.nscene, 10) || 3)))
        : null;
    const ACCESS_PRESETS = {
      admin:   { adminOnly: true,  isPremiumAddon: false },
      premium: { adminOnly: false, isPremiumAddon: true  },
      allpaid: { adminOnly: false, isPremiumAddon: false },
    };
    const eng = ENGINE_PRESETS[newModule.engine] || ENGINE_PRESETS.flow1;
    const acc = ACCESS_PRESETS[newModule.access] || ACCESS_PRESETS.admin;
    const isNScene = newModule.engine === 'nscene';
    try {
      setCreatingModule(true);
      const { setDoc, doc: fsDoc, serverTimestamp } = await import('firebase/firestore');
      const maxOrder = modules.reduce((mx, m) => Math.max(mx, m.order || 0), 0);
      await setDoc(fsDoc(db, 'modules', id), {
        name,
        marketingName: name,
        description: '',
        icon: (newModule.icon || 'extension').trim() || 'extension',
        color: newModule.color || '#6366f1',
        order: maxOrder + 1,
        badge: newModule.badge || null,
        scriptKey: eng.scriptKey,
        promptSchema: eng.promptSchema,
        uploadVia: eng.uploadVia,
        ...(sceneCount ? { sceneCount } : {}),
        // ★ Borang tempahan enjin RESIPI — dibaca config/modules.js registerRuntimeModules → payload product.resipi.
        ...(isResipi ? { resipi: { gambar: resipiGambar } } : {}),
        adminOnly: acc.adminOnly,
        isPremiumAddon: acc.isPremiumAddon,
        // Tak lock tile — admin (access default "Admin sahaja") kena boleh pilih untuk test jana skrip.
        // Robot pending ditanda via releaseStatus 'in-development'.
        locked: false,
        // Resipi: robot siap kod tetapi belum lulus larian hidup → kekal 'in-development' sehingga user lulus ujian.
        releaseStatus: (isNScene || isResipi) ? 'in-development' : 'released',
        // ★ [2026-10-29] Module resipi LAHIR dengan jsonMode ON — Generate guna laluan JSON
        //   berbilang-scene (1 Image Prompt + N Video Prompt). Dibuktikan hidup: resipi 1/3/4/5
        //   scene menjana card penuh bila jsonMode ON + ada master prompt (peti besi private/prompt).
        //   Engine lain kekal OFF (SMART = manual MEMANG perlu OFF; AI8SAAT dll sedia ada tak terjejas).
        jsonMode: isResipi,
        outputFormat: eng.promptSchema === 'two-scene' ? '3sets-2scenes' : '3sets',
        masterPrompt: { mode1_noAvatarNoGender: '', mode2_noAvatarWithGender: '', mode3_withAvatar: '' },
        mandatoryImageAppend: '',
        mandatoryVideoAppend: '',
        mandatoryVideoAppend2: '',
        prependScene2Enabled: false,
        prependScene2Text: '',
        createdBy: user?.uid || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      console.log('[CreateModule] ★ Module baharu dicipta:', id, eng);
      setCreateModuleModal(false);
      setNewModule({ id: '', name: '', engine: 'flow1', nscene: '3', resipiGambar: 'sama', icon: 'auto_awesome', color: '#6366f1', badge: '', access: 'admin' });
      // onSnapshot modules/ akan auto-refresh tile + registerRuntimeModules.
    } catch (err) {
      console.error('[CreateModule] Save error:', err);
      setCreateModuleError('Gagal cipta module: ' + (err?.message || err));
    } finally {
      setCreatingModule(false);
    }
  };

  // ★ MODULE MASTER PROMPT: Load user's allowed modules
  // Policy:
  //   - Admin: all modules (driven by licenses doc, fall back to all)
  //   - Paid (non-trial): baca licenses/{uid}.allowedModules (admin grant per-license);
  //                       fallback ke baseline ai8saat + ai16saat-half
  //   - Trial: ai8saat + ai16saat-half + homeniche + goyang + flownity (Smart Custom admin-only → nampak tapi locked)
  // Settings gear remains admin-only regardless of tier.
  useEffect(() => {
    if (!user) return;
    const PAID_BASELINE = ['ai8saat', 'ai16saat-half'];
    if (isAdmin) {
      const licenseRef = doc(db, 'licenses', user.uid);
      const unsubscribe = onSnapshot(licenseRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserAllowedModules(data.allowedModules || modules.map(m => m.id));
        } else {
          setUserAllowedModules(modules.map(m => m.id));
        }
      });
      return () => unsubscribe();
    } else if (userTier === 'trial') {
      // Trial user — boleh cuba module (kecuali Smart Custom yang admin-only → nampak tapi locked).
      // Coin quota (5) shared across modules via checkAndDeductPipelineCoin
      // (1 generation call = 1 coin, regardless of module).
      // flownity kongsi pool 5 coin yang SAMA, tapi potong via deductFlownityTrialCoin masa
      // tekan Generate (scrape Agent AI) — flownity tak lalu pipeline Stage 1-3.
      setUserAllowedModules(['ai8saat', 'ai16saat-half', 'homeniche', 'goyang', 'flownity']);
    } else {
      // Paid user — baca allowedModules dari license; fallback baseline kalau legacy/kosong.
      const licenseRef = doc(db, 'licenses', user.uid);
      const unsubscribe = onSnapshot(licenseRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const allowed = Array.isArray(data.allowedModules) && data.allowedModules.length > 0
            ? data.allowedModules
            : PAID_BASELINE;
          setUserAllowedModules(allowed);
        } else {
          setUserAllowedModules(PAID_BASELINE);
        }
      });
      return () => unsubscribe();
    }
  }, [user, isAdmin, userTier]);

  // Fetch system default logic name from shared system_spaces
  useEffect(() => {
    const fetchSystemPersonaName = async () => {
      try {
        // Use systemDefaultLogicId (which comes from system_spaces/ai8saat) to get the name
        const personaDoc = await getDoc(doc(db, 'system_spaces', 'ai8saat', 'personas', systemDefaultLogicId));
        if (personaDoc.exists()) {
          setSystemDefaultLogicName(personaDoc.data().name || 'TEKNIK AI8SAAT');
        }
      } catch (e) {
        console.warn('Could not fetch system persona name:', e);
      }
    };
    fetchSystemPersonaName();
  }, [systemDefaultLogicId]);

  // Load saved avatars from Firestore
  useEffect(() => {
    if (!user) return;
    const avatarsRef = collection(db, 'users', user.uid, 'avatars');
    const unsubscribe = onSnapshot(avatarsRef, (snapshot) => {
      const avatars = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      avatars.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
      setSavedAvatars(avatars);
    });
    return () => unsubscribe();
  }, [user]);

  // Load avatar max slots from license (trial users hard-capped to 1)
  useEffect(() => {
    if (!user) return;
    const fetchLimit = async () => {
      try {
        if (userTier === 'trial') {
          setAvatarMaxSlots(1);
          return;
        }
        const licenseDoc = await getDoc(doc(db, 'licenses', user.uid));
        if (licenseDoc.exists()) {
          setAvatarMaxSlots(licenseDoc.data().maxProfiles || 5);
        }
      } catch (e) { console.warn('Could not fetch license for avatar limit:', e); }
    };
    fetchLimit();
  }, [user, userTier]);

  // Check if AI Logic dropdown should be locked (AI8SAAT default space + non-admin)
  const isDefaultSpace = selectedSpaceId === 'default';
  const isLogicLocked = isDefaultSpace && !isAdmin;

  // ★ Effective default logic: system spaces use centralized admin config, custom spaces use per-user
  const effectiveDefaultLogicId = useMemo(() => {
    const currentSpace = spaces.find(s => s.id === selectedSpaceId);
    return currentSpace?.isSystem ? systemDefaultLogicId : (currentSpace?.settings?.defaultLogicId || null);
  }, [spaces, selectedSpaceId, systemDefaultLogicId]);

  // Sync header AI logic dropdown with current space's default logic
  useEffect(() => {
    if (effectiveDefaultLogicId && headerLogicOptions.find(o => o.id === effectiveDefaultLogicId)) {
      // ★ Use centralized default for system spaces, per-user for custom spaces
      setHeaderSelectedLogicId(effectiveDefaultLogicId);
    } else if (headerLogicOptions.length > 0) {
      // Fallback: use first available logic option
      setHeaderSelectedLogicId(headerLogicOptions[0].id);
    } else if (selectedSpaceId === 'default') {
      // Final fallback for default AI8SAAT space
      setHeaderSelectedLogicId(systemDefaultLogicId);
    }
  }, [selectedSpaceId, effectiveDefaultLogicId, headerLogicOptions, systemDefaultLogicId]);

  // Add product from header bar
  const handleHeaderAddProduct = async () => {
    const input = headerProductInput.trim();
    const hasStaged = stagedLibraryProducts.length > 0;
    if ((!input && !hasStaged) || !user) return;

    // Get selected avatar data (if any)
    // Support "No Avatar" with gender: __male__ / __female__ set gender without avatar image
    const isGenderOnly = selectedAvatarId === '__male__' || selectedAvatarId === '__female__';
    const selectedAvatar = isGenderOnly
      ? { gender: selectedAvatarId === '__male__' ? 'male' : 'female', id: null, imageUrl: null }
      : (savedAvatars.find(a => a.id === selectedAvatarId) || null);

    // ★ Build products from EITHER staged library products OR text input
    const effectiveLogicId = (headerSelectedLogicId && headerSelectedLogicId !== '__data_only__') ? headerSelectedLogicId : (headerSelectedLogicId === '__data_only__' ? null : (effectiveDefaultLogicId || null));

    let newProducts = [];

    if (hasStaged) {
      // Schema v2: intelligenceJson dah pindah ke library/product/items/{id}/payload/data subdoc.
      // Lazy-fetch payload untuk setiap staged item (parallel, max 20 items).
      // Backward-compat: kalau lp.intelligenceJson masih ada (item belum migrate),
      // guna terus tanpa fetch — jimat round-trip.
      const LIBRARY_FOLDER_ID = 'product'; // ProductLibraryModal hardcoded ke folder ni
      const hydrated = await Promise.all(stagedLibraryProducts.map(async (lp) => {
        if (lp.intelligenceJson) return lp; // legacy v1 item — guna terus
        if (!lp.hasIntelligence) return lp; // tiada intel — skip fetch
        try {
          const payload = await loadLibraryPayload(LIBRARY_FOLDER_ID, lp.id);
          return { ...lp, intelligenceJson: payload?.intelligenceJson ?? null };
        } catch (err) {
          console.warn('[App] Library payload fetch failed for', lp.id, err);
          return lp;
        }
      }));

      // Build from staged library products (user picked from library modal)
      newProducts = hydrated.map((lp, i) => ({
        id: `prod_${Date.now()}_${i}_${Math.random().toString(36).substring(2, 9)}`,
        originalIndex: lp.number || products.length + i + 1,
        name: lp.name,
        link: lp.link || '',
        shopeeLink: lp.shopeeLink || '',   // ★ v4 FB Reels — bawa link Shopee sekali (macam link TikTok)
        /* ★ 2026-10-06 — halaman produk Shopee item import Shopee (utils/shopeeImport.js). Item itu
           SENGAJA tiada `link` TikTok, jadi tanpa medan ini kad langsung tiada rujukan produk: borang
           Edit data tidak dapat mencari itemnya, syarat READY gagal bila tiada link affiliate, dan chip
           kad tiada halaman untuk dibuka (audit Shopee S3). Jambatan ini menyalin SATU PERSATU — medan
           yang tidak disebut tidak sampai. Nilai disemak: hanya URL produk Shopee yang ada ID. */
        shopeeProductUrl: kunciShopee(lp.shopeeProductUrl) ? String(lp.shopeeProductUrl).trim() : '',
        // Harga dari library item (produk import/scrape ada; produk system mungkin kosong).
        // Perlu supaya baris Price/Original Price/Discount dalam PRODUCT DATA block Stage 3
        // terisi nombor sebenar — bukan "Not specified".
        price: lp.price || '',
        originalPrice: lp.originalPrice || '',
        discount: lp.discount || '',
        imageBase64: lp.imageBase64 || null,
        imageUrl: lp.imageUrl || null,
        localImagePath: null,
        /* ★ Gambar produk TAMBAHAN (2026-09-11) ikut item My Products masuk ke kad. Jambatan ini menyalin
           medan SATU PERSATU — medan yang tidak disebut di sini TIDAK sampai ke kad dan gagalnya SENYAP
           (pelanggan isi gambar di My Products, kad Workspace kekal satu gambar). Had 4 tambahan = 5 termasuk utama. */
        extraImageUrls: Array.isArray(lp.extraImageUrls) ? lp.extraImageUrls.filter(u => typeof u === 'string' && u).slice(0, 4) : [],
        intelligenceData: lp.intelligenceJson || null,
        prompts: { set1: { promptImage: '', promptVideo: '', captionTiktok: '' }, set2: { promptImage: '', promptVideo: '', captionTiktok: '' }, set3: { promptImage: '', promptVideo: '', captionTiktok: '' } },
        selectedSets: ['set1', 'set2', 'set3'],
        logs: [],
        selected: true,
        // P6.3 — Smart Custom library pull: tiada prompts → start dari 'chatgpt' tab
        tab: (activeModuleId === 'smartcustom' || activeModuleId === 'flownity') ? 'chatgpt' : 'flow',
        spaceId: selectedSpaceId === 'default' ? null : selectedSpaceId,
        workspaceGroup: headerSelectedGroupId === 'all' ? 'Workspace' : headerSelectedGroupId,
        logicId: effectiveLogicId,
        moduleId: activeModuleId || 'ai8saat',
        gender: selectedAvatar?.gender || null,
        avatarId: selectedAvatar?.id || null,
        avatarImageUrl: selectedAvatar?.imageUrl || null
      }));
      setStagedLibraryProducts([]);
    }

    if (input) {
      // Build from text input (manual paste)
      const lines = input.split('\n').filter(l => l.trim());
      const textProducts = lines.map((line, i) => {
        const parts = line.split('*|*').map(s => s.trim().replace(/^['"]+|['"]+$/g, ''));

        let name = parts[0];
        let link = '';
        let imageSrc = '';
        let parsedIndex;

        if (!/^\d+$/.test(parts[0])) {
          const match = parts[0].match(/^(\d+)[.)]\s+(.*)/);
          if (match) {
            parsedIndex = parseInt(match[1]);
            name = match[2];
          }
        }

        const isIndexFirst = /^\d+$/.test(parts[0]);

        if (isIndexFirst && parts.length >= 2) {
          parsedIndex = parseInt(parts[0]);
          name = parts[1];
          imageSrc = parts[2] || '';
          link = parts[3] || '';
        } else if (!isIndexFirst && !parsedIndex) {
          link = parts[1] || '';
          imageSrc = parts[2] || '';
        } else if (parsedIndex && !isIndexFirst) {
          link = parts[1] || '';
          imageSrc = parts[2] || '';
        }

        if (parsedIndex === undefined) {
          const idMatch = name.match(/(\d+)/);
          if (idMatch) {
            parsedIndex = parseInt(idMatch[1], 10);
          }
        }

        if (!imageSrc && link) {
          const lowerLink = link.toLowerCase();
          const isPathLike = lowerLink.match(/^[a-z]:\\/i) || lowerLink.startsWith('/') || lowerLink.match(/\.(png|jpg|jpeg|webp|gif|bmp)$/i);
          const isUrl = lowerLink.startsWith('http://') || lowerLink.startsWith('https://');
          if (isPathLike && !isUrl) {
            imageSrc = link;
            link = '';
          }
        }

        let imageBase64 = null;
        let imageUrl = null;
        if (imageSrc && imageSrc.startsWith('http')) {
          imageUrl = imageSrc;
        } else if (imageSrc && imageSrc.startsWith('data:')) {
          imageBase64 = imageSrc;
        }

        return {
          id: `prod_${Date.now()}_${newProducts.length + i}_${Math.random().toString(36).substring(2, 9)}`,
          originalIndex: parsedIndex || products.length + newProducts.length + i + 1,
          name,
          link,
          imageBase64,
          imageUrl,
          localImagePath: imageSrc || null,
          prompts: { set1: { promptImage: '', promptVideo: '', captionTiktok: '' }, set2: { promptImage: '', promptVideo: '', captionTiktok: '' }, set3: { promptImage: '', promptVideo: '', captionTiktok: '' } },
          selectedSets: ['set1', 'set2', 'set3'],
          logs: [],
          selected: true,
          // P6.3 — Smart Custom text paste tiada prompts → start dari 'chatgpt' tab
          tab: (activeModuleId === 'smartcustom' || activeModuleId === 'flownity') ? 'chatgpt' : 'flow',
          spaceId: selectedSpaceId === 'default' ? null : selectedSpaceId,
          workspaceGroup: headerSelectedGroupId === 'all' ? 'Workspace' : headerSelectedGroupId,
          logicId: effectiveLogicId,
          moduleId: activeModuleId || 'ai8saat',
          gender: selectedAvatar?.gender || null,
          avatarId: selectedAvatar?.id || null,
          avatarImageUrl: selectedAvatar?.imageUrl || null
        };
      });
      newProducts = [...newProducts, ...textProducts];
    }

    const isDataOnly = headerSelectedLogicId === '__data_only__';
    const updated = [...products, ...newProducts];
    setProducts(updated);
    setHeaderProductInput('');

    // ★ CRITICAL FIX: Dispatch AI generation event IMMEDIATELY, BEFORE any async operations.
    // Previously this was at the END of the try block, AFTER saveProducts + image upload + runTransaction.
    // If any of those failed (network error, timeout), the event never fired → pipeline never started.
    // Image uploads alone can take 5-30 seconds, causing massive delays.
    if (!isDataOnly) {
      window.dispatchEvent(new CustomEvent('SESAAT_NEW_PRODUCTS', {
        detail: { newProducts }
      }));
    }

    try {
      // ★ Save new products to subcollection (individually)
      await saveProducts(user.uid, newProducts);

      // ⛔ [2026-08-26] TULISAN ke medan warisan `users.productLibrary` DIBUANG.
      // Medan itu = 87% saiz koleksi users (45.9MB) tetapi TIADA pembaca UI langsung
      // (halaman paparannya dipadam 2026-07-28). Ia dihantar PENUH ke setiap listener
      // pada SETIAP tulisan doc users (kaunter kuota/storage) — diukur ~35GB/hari
      // egress ≈ RM555/bulan, dan menolak 3 akaun ke ambang had keras 1MB Firestore.
      // My Products SEBENAR hidup dalam koleksi library/product/items — tidak terjejas.
      // ⛔ JANGAN pulangkan tulisan ini. Lihat nota penuh dalam CLAUDE.md.
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(null), 2500);
    } catch (err) {
      console.error('Header add product error:', err);
    }
  };

  // Handle manual product entries from ManualProductModal
  const handleManualProductsSubmit = async (entries) => {
    if (!user || entries.length === 0) return;
    // Get selected avatar data (if any) — same logic as handleHeaderAddProduct
    const isGenderOnly = selectedAvatarId === '__male__' || selectedAvatarId === '__female__';
    const selectedAvatar = isGenderOnly
      ? { gender: selectedAvatarId === '__male__' ? 'male' : 'female', id: null, imageUrl: null }
      : (savedAvatars.find(a => a.id === selectedAvatarId) || null);

    const newProducts = entries.map((entry, i) => ({
      id: `prod_${Date.now()}_${i}_${Math.random().toString(36).substring(2, 9)}`,
      originalIndex: entry.number || products.length + i + 1,
      name: entry.name,
      link: entry.link || '',
      shopeeLink: entry.shopeeLink || '',   // ★ v4 FB Reels — bawa link Shopee sekali
      imageBase64: entry.imageBase64 || null,
      imageUrl: entry.imageUrl || null,
      localImagePath: null,
      prompts: { set1: { promptImage: '', promptVideo: '', captionTiktok: '' }, set2: { promptImage: '', promptVideo: '', captionTiktok: '' }, set3: { promptImage: '', promptVideo: '', captionTiktok: '' } },
      selectedSets: ['set1', 'set2', 'set3'],
      logs: [],
      selected: true,
      spaceId: selectedSpaceId === 'default' ? null : selectedSpaceId,
      workspaceGroup: headerSelectedGroupId === 'all' ? 'Workspace' : headerSelectedGroupId,
      logicId: (entry.logicId && entry.logicId !== '__data_only__') ? entry.logicId : (entry.logicId === '__data_only__' ? null : (headerSelectedLogicId || effectiveDefaultLogicId || null)),
      moduleId: activeModuleId || 'ai8saat',  // ★ MODULE MASTER PROMPT
      gender: selectedAvatar?.gender || null,
      avatarId: selectedAvatar?.id || null,
      avatarImageUrl: selectedAvatar?.imageUrl || null,
      _dataOnly: entry.logicId === '__data_only__',
    }));

    const updated = [...products, ...newProducts];
    setProducts(updated);

    // ★ CRITICAL FIX: Dispatch AI generation event IMMEDIATELY, BEFORE async Firestore operations
    const productsForAI = newProducts.filter(p => !p._dataOnly);
    if (productsForAI.length > 0) {
      window.dispatchEvent(new CustomEvent('SESAAT_NEW_PRODUCTS', { detail: { newProducts: productsForAI } }));
    }

    try {
      // ★ Save new products to subcollection
      await saveProducts(user.uid, newProducts);

      // ⛔ [2026-08-26] Tulisan `users.productLibrary` DIBUANG — lihat nota penulis#1 di atas.
    } catch (err) {
      console.error('Manual add product error:', err);
    }
  };

  // Handle adding products from library picker — STAGE ONLY, no pipeline yet
  const handleLibrarySelect = (libraryProducts) => {
    if (libraryProducts.length === 0) return;

    const MAX_STAGED = 20;
    const currentCount = stagedLibraryProducts.length;
    const availableSlots = MAX_STAGED - currentCount;

    if (availableSlots <= 0) {
      alert(`Maksimum ${MAX_STAGED} produk dalam satu masa.\n\nAnda dah ada ${currentCount} produk dipilih. Sila proses dulu (klik "Add Product") atau buang sebahagian, lepas tu baru boleh tambah lagi.`);
      return;
    }

    if (libraryProducts.length > availableSlots) {
      const accepted = libraryProducts.slice(0, availableSlots);
      const rejected = libraryProducts.length - availableSlots;
      alert(`Maksimum ${MAX_STAGED} produk dalam satu masa.\n\nAnda dah ada ${currentCount} dipilih. Hanya ${availableSlots} yang ditambah, ${rejected} lagi tak diterima.\n\nHabiskan batch ni dulu, lepas tu boleh tambah lagi.`);
      setStagedLibraryProducts(prev => [...prev, ...accepted]);
      return;
    }

    // Only stage products visually in left panel — user still needs to pick avatar/module then click "Add Product"
    setStagedLibraryProducts(prev => [...prev, ...libraryProducts]);
  };

  // Listen to Firestore for spaces + selectedSpaceId (user doc — no products here anymore)
  useEffect(() => {
    if (!user) return;
    const docRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const spacesData = data.spaces || data.groups;
        if (spacesData && Array.isArray(spacesData)) {
          // Strip productCount — it's computed dynamically from products, not stored
          // Filter out deprecated 'ai16saat' space — module lives inside Workspace now
          let userSpaces = spacesData
            .map(({ productCount, ...rest }) => rest)
            .filter(s => s.id !== 'ai16saat');

          // Rename legacy 'AI8SAAT' default space → 'Workspace'
          userSpaces = userSpaces.map(s => s.id === 'default' && s.name === 'AI8SAAT' ? { ...s, name: 'Workspace' } : s);

          setSpaces(userSpaces);
        } else {
           // Fallback default state
           setSpaces([
             { id: 'default', name: 'Workspace', icon: 'auto_awesome', color: 'indigo', isSystem: true }
           ]);
        }
        // Per-browser isolation: selectedSpaceId & selectedWorkspaceGroup kept in
        // localStorage instead of syncing via Firestore — so opening the same
        // account in multiple Chrome profiles no longer cross-syncs UI state.
        setProductLibraryHidden(!!data.productLibraryHidden);

        const libMap = data.productLibrary || {};
        const libTotal = Object.values(libMap).reduce(
          (sum, items) => sum + (Array.isArray(items) ? items.length : 0),
          0
        );
        setProductLibraryCount(libTotal);
      }
    });
    return () => unsubscribe();
  }, [user]);

  // Persist selectedSpaceId per-browser (localStorage). Extension dlm browser
  // yg sama follow via window.postMessage, bukan Firestore.
  useEffect(() => {
    if (!selectedSpaceId) return;
    localStorage.setItem('selectedSpaceId', selectedSpaceId);
  }, [selectedSpaceId]);

  // Persist selectedWorkspaceGroup per-browser (localStorage). Cover semua
  // perubahan termasuk rename/delete group yg auto-update state.
  useEffect(() => {
    if (!headerSelectedGroupId) return;
    localStorage.setItem('selectedWorkspaceGroup', headerSelectedGroupId);
  }, [headerSelectedGroupId]);

  // ★ Listen to products SUBCOLLECTION (users/{uid}/products/{productId})
  useEffect(() => {
    if (!user) return;
    const unsubscribe = subscribeToProducts(user.uid, (prods) => {
      setProducts(prev => {
        // ★ FIX: Preserve isGenerating/generationFailed from local state
        // These are UI-only flags set during pipeline execution.
        // Without this merge, onSnapshot fires from saveToCloud and
        // STRIPS the isGenerating flag, killing the pipeline mid-run.
        const prevMap = new Map(prev.map(p => [p.id, p]));

        // ★ CRITICAL FIX: Filter out products that are being deleted
        // Without this, onSnapshot fires mid-deletion and re-injects not-yet-deleted docs.
        const pendingDeletes = pendingDeletesRef.current;
        const filteredProds = pendingDeletes.size > 0
          ? prods.filter(p => !pendingDeletes.has(p.id))
          : prods;

        const firestoreIds = new Set(filteredProds.map(p => p.id));

        // Merge Firestore data with local UI flags
        // Per-browser isolation: preserve LOCAL state for fields that are
        // stripped from Firestore (selected, pipeline flags) so each Chrome
        // profile keeps its own checkbox + run-flow state.
        const merged = filteredProds.map(fp => {
          const local = prevMap.get(fp.id);
          if (local) {
            return {
              ...fp,
              selected: local.selected ?? false,
              isGenerating: local.isGenerating,
              generatingStatus: local.generatingStatus,
              generationFailed: local.generationFailed,
            };
          }
          // New product from another browser — start unselected, no UI flags
          return { ...fp, selected: false };
        });

        // ★ Keep locally-added products that haven't synced to Firestore yet
        // (they exist in local state with isGenerating but Firestore snapshot
        // hasn't received them due to write latency)
        const localOnly = prev.filter(p =>
          !firestoreIds.has(p.id) && !pendingDeletes.has(p.id) && (p.isGenerating || p.generationFailed)
        );

        if (localOnly.length > 0) {
          console.log(`[App] Preserving ${localOnly.length} local-only products during Firestore sync`);
        }

        return [...merged, ...localOnly];
      });
    }, (err) => {
      console.error('[App] Products subscription error:', err);
    });
    return () => unsubscribe();
  }, [user]);

  // Compute product counts per space for sidebar display
  const spacesWithCounts = spaces.map(s => ({
    ...s,
    productCount: s.id === 'default'
      ? products.filter(p => !(p.spaceId || p.groupId) || p.spaceId === 'default' || p.groupId === 'default').length
      : products.filter(p => p.spaceId === s.id || p.groupId === s.id).length
  }));

  // Load saved rewrites from localStorage
  const [rewrites, setRewrites] = useState(() => {
    const saved = localStorage.getItem('tiktok_rewrites');
    return saved ? JSON.parse(saved) : [];
  });

  const saveToLocalStorage = (newList) => {
    setRewrites(newList);
    localStorage.setItem('tiktok_rewrites', JSON.stringify(newList));
  };

  const handleTranscribe = async (payload) => {
    try {
      let result;
      if (payload.type === 'url') {
        result = await aiService.transcribeTikTok(payload.value);
      } else {
        const data = await aiService.transcribeWithGroq(payload.value);
        result = {
          name: payload.value.name,
          transcription: data.text,
          timestampedText: data.timestampedText,
          date: tarikhPapar(new Date()),
          engine: "Groq (Whisper-v3-Turbo)"
        };
      }

      setTranscriptionData({
        text: result.text || result.transcription,
        timestampedText: result.timestampedText,
        videoUrl: result.videoUrl
      });
      setCurrentVideo(result);
      setEditingId(null);
      setIsModalOpen(false);
    } catch (error) {
      console.error(error);
      alert(`Transcription failed: ${error.message}`);
    }
  };

  const handleEdit = (id) => {
    const item = rewrites.find(r => r.id === id);
    if (item) {
      setTranscriptionData({
        text: item.originalText || item.originalTranscription || "",
        timestampedText: item.timestampedOriginal || "",
        videoUrl: item.videoUrl
      });
      setCurrentVideo({
        name: item.name,
        link: item.link
      });
      setEditingId(id);
    }
  };

  const handleSave = (finalData) => {
    if (editingId) {
      const updated = rewrites.map(r => {
        if (r.id === editingId) {
          return {
            ...r,
            ...finalData,
            originalText: transcriptionData.text,
            timestampedOriginal: transcriptionData.timestampedText,
            videoUrl: transcriptionData.videoUrl || r.videoUrl
          };
        }
        return r;
      });
      saveToLocalStorage(updated);
    } else {
      const newEntry = {
        id: Date.now(),
        name: currentVideo?.name || "Untitled",
        link: currentVideo?.link || "Uploaded File",
        date: new Date().toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }),
        scenes: 1,
        originalText: transcriptionData.text,
        timestampedOriginal: transcriptionData.timestampedText,
        videoUrl: transcriptionData.videoUrl,
        ...finalData
      };
      const updated = [newEntry, ...rewrites];
      saveToLocalStorage(updated);
      setEditingId(newEntry.id); // Switch to editing the new entry
    }
    // No setView here, stay in rewrite mode
  };

  const handleDelete = (id) => {
    const updated = rewrites.filter(r => r.id !== id);
    saveToLocalStorage(updated);
    if (editingId === id) {
      setEditingId(null);
      setTranscriptionData(null);
    }
  };

  // ═══ MAIN DOMAIN — sesaat.ai = domain MARKETING SAHAJA; app TAK render di domain ni.
  //     Hanya route marketing dalam blok ni wujud di sesaat.ai — path lain balik ke homepage.
  //     Nak tambah page marketing baru (cth /workspace versi promo)? Tambah <Route> DALAM blok ni.
  //     Login/app sentiasa di app.sesaat.ai (auth Firebase tak dikongsi antara domain). ═══
  const isMainDomain = window.location.hostname === 'sesaat.ai' || window.location.hostname === 'www.sesaat.ai';
  if (isMainDomain) {
    // Kecuali: user balik dari Billplz (bayaran dibuat semasa masih login di sesaat.ai lama) —
    // hantar ke app supaya status order tetap terpapar, jangan telan ke homepage.
    if (location.pathname === '/payment/return') {
      window.location.replace(`https://app.sesaat.ai/payment/return${location.search}`);
      return null;
    }
    // sesaat.ai/flow → app.sesaat.ai/flow (begitu juga /promo). Link pendek ni yang
    // akan dikongsi dalam WhatsApp, jadi kedua-dua domain mesti sampai ke tempat yang sama.
    if (laluanBayarLuarGate(location.pathname)) {
      window.location.replace(`https://app.sesaat.ai${location.pathname}${location.search}`);
      return null;
    }
    // withRef: cookie TAK merentas domain dgn pasti (dan langsung tak merentas browser) —
    // bawa kod ejen dalam alamat supaya attribution selamat. Lihat utils/refLink.js.
    const goAppLogin = () => { window.location.href = withRef('https://app.sesaat.ai/login'); };
    return (
      <Routes>
        <Route path="/" element={<SalesPageView onNavigateToLogin={goAppLogin} onNavigateToPricing={() => navigate('/pricing')} />} />
        <Route path="/sesaat-1" element={<SalesPageView onNavigateToLogin={goAppLogin} onNavigateToPricing={() => navigate('/pricing')} />} />
        <Route path="/sesaat-2" element={<Navigate to="/pricing" replace />} />
        <Route path="/pricing" element={<PricingBaruView />} />
        <Route path="/pricing-baru" element={<PricingBaruView />} />
        <Route path="/pricing-lama" element={<Navigate to="/pricing" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Prototaip pricing v4 — localhost sahaja; butang belum disambung ke bayaran.
  if (import.meta.env.DEV && location.pathname === '/pricing-v4') return <PricingV4View />;
  if (import.meta.env.DEV && location.pathname === '/landing-v4') return <LandingV4Lab />;

  // ═══ PRICING — salespage penuh (design baru): public, tiada app shell/sidebar, login tak perlu ═══
  // /pricing (utama) + /pricing-baru (alias) → design baru. /pricing-lama DIBUANG 2026-07-03 (jual tawaran lama RM299 Flow Ultra) — redirect ke /pricing.
  // viewer: early-return ni jalan SEBELUM auth resolve — loggedIn boleh false seketika (PricingSection
  // fallback anon, akan re-render bila state hydrate).
  if (location.pathname === '/pricing' || location.pathname === '/pricing-baru') {
    // authLoading: sesi Firebase belum selesai dipulihkan. Tanpa penanda ini,
    // PricingSection menganggap pelawat berulang sebagai orang baharu dan memapar
    // tawaran yang kemudian ditarik balik bila sesi masuk.
    return <PricingBaruView viewer={{ loggedIn: !!user, authLoading: loading, tier: userTier, plan: userPlan, licenseStatus, allowedModules: userAllowedModules }} />;
  }


  // ═══ PUBLIC ROUTES — accessible without login ═══
  // Pricing page & affiliate redirect must be accessible to everyone (/ is now salespage at sesaat.ai only)
  // ⚠️ /sesaat-2 KEKAL dalam senarai ni walaupun halamannya DIBUANG 2026-08-13 — tanpa
  //    entri ini, pelawat belum log masuk yang datang dari pautan lama (iklan/WhatsApp)
  //    akan mendarat di skrin log masuk, bukan lencongan ke /pricing.
  const isPublicRoute = location.pathname === '/sesaat-1' || location.pathname === '/sesaat-2' || location.pathname === '/pricing' || location.pathname === '/r/support'; // /r/:refCode dibuang 8 Okt 2026 — hanya /r/support yang tinggal

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="w-8 h-8 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user && isPublicRoute) {
    const isV2 = location.pathname === '/' || location.pathname === '/sesaat-1';
    // Public route — render salespage/pricing/affiliate without login
    return (
      <div className="min-h-screen" style={isV2 ? {} : { background: '#F5F4F1' }}>
        {/* ─── Sticky Public Header (disorok pada / dan /sesaat-1 — salespage ada nav sendiri) ─── */}
        {!isV2 && (
        <header style={{
          position: 'sticky', top: 0, zIndex: 50, width: '100%',
          background: 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          padding: '0 24px',
        }}>
          <div style={{ maxWidth: 960, margin: '0 auto', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => navigate('/')}>
              <div style={{ width: 32, height: 32, background: 'linear-gradient(135deg, #7c3aed, #8b5cf6)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(124, 58, 237, 0.35)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                  <path d="M13 2L4.5 13.5H11L10 22L20.5 10H14L13 2Z" />
                </svg>
              </div>
              <span style={{ fontWeight: 800, fontSize: 15, letterSpacing: '0.08em', color: '#1a1a2e' }}>SESAAT</span>
            </div>
            <button
              onClick={() => navigate('/login')}
              style={{ background: 'linear-gradient(135deg, #7c3aed, #6d28d9)', color: '#fff', border: 'none', borderRadius: 10, padding: '9px 20px', fontFamily: 'inherit', fontWeight: 600, fontSize: 14, cursor: 'pointer', boxShadow: '0 2px 10px rgba(124, 58, 237, 0.3)' }}
            >
              Log Masuk
            </button>
          </div>
        </header>
        )}
        <Routes>
          {/* ⛔ viewer WAJIB dihantar. Tanpa ia halaman jualan melayan SEMUA orang
              sebagai pembeli baharu — pelanggan berbayar pun nampak kotak add-on
              dan harga RM299, kemudian semuanya hilang bila dia sampai /pricing. */}
          <Route path="/" element={<SalesPageView onNavigateToLogin={() => navigate('/login')} onNavigateToPricing={() => navigate('/pricing')} viewer={{ loggedIn: !!user, authLoading: loading, tier: userTier, plan: userPlan, licenseStatus, allowedModules: userAllowedModules }} />} />
          <Route path="/sesaat-1" element={
            <SalesPageView
              onNavigateToLogin={() => navigate('/login')}
              onNavigateToPricing={() => navigate('/pricing')}
            />
          } />
          <Route path="/sesaat-2" element={<Navigate to="/pricing" replace />} />
          <Route path="/pricing-lama" element={<Navigate to="/pricing" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    );
  }

  // ═══ PROMO — pelawat BELUM login nampak tawaran dahulu, bukan skrin login kosong ═══
  // Rupa awam: tawaran + "Tahniah" + butang "Login untuk bayar" (email/WhatsApp disorok).
  // Butang itu sendiri yang menyimpan `sesaat_flow_next`, jadi selepas login pelanggan
  // kembali ke /promo dengan borang sudah terisi. `key` beza dengan rupa selepas login
  // supaya komponen dibina semula, bukan mewarisi keadaan pelawat.
  // /promo/berjaya kekal di bawah (terus ke login) — ia halaman selepas bayar.
  if (!user && location.pathname === '/promo') return <PromoAiProView key="awam" user={null} />;

  if (!user) {
    // Ingat destinasi — HANYA untuk /flow dan /promo (skop sempit, sifar kesan pada route lain).
    // Tanpa ini, pembeli yang klik pautan /flow akan hilang destinasinya semasa
    // dilencongkan ke /login, dan selepas mendaftar dia mendarat di StartGate
    // "Langgan RM199" — bukan halaman bayar yang dia datang untuk beli.
    // (Nama kunci kekal `sesaat_flow_next` — ia memegang kedua-dua laluan.)
    if (laluanBayarLuarGate(location.pathname)) {
      try { sessionStorage.setItem('sesaat_flow_next', location.pathname + location.search); } catch { /* storage disabled — abaikan */ }
    }
    // Non-public routes for non-logged in users → redirect to login
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // ═══ GATE LENGKAPKAN PENDAFTARAN — pengguna BARU via Google: wajib ref ejen + isi WhatsApp ═══
  // Mesti SEBELUM license gate/StartGate supaya pendaftaran lengkap dulu sebelum apa-apa.
  if (regGate === 'checking') {
    return (
      <div className="h-screen flex items-center justify-center" style={{ background: '#0a0e1a' }}>
        <div className="w-8 h-8 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin"></div>
      </div>
    );
  }
  if (regGate === 'incomplete') {
    return <CompleteRegistration user={user} onCompleted={() => setRegGate('ok')} />;
  }

  // ═══ Payment return — bypass license gate ═══
  // User baru bayar; status license client mungkin belum refresh, jadi gate akan
  // tersilap tunjuk StartGate. Halaman return baca status dari order doc sendiri.
  if (location.pathname === '/payment/return') {
    return <BillplzPaymentReturnView />;
  }

  // ═══ FLOW ULTRA — beli / sambung akaun Google (RM100 · 30 hari) ═══
  // Kedudukan sengaja SAMA seperti /payment/return: SELEPAS log masuk, tetapi
  // SEBELUM dinding lesen.
  //   · perlu log masuk  → emel diambil dari akaun, uid direkod, rekod kekal
  //                        dalam sistem (bukan pembelian tanpa nama)
  //   · sebelum gate     → pelanggan Flow yang belum/tidak melanggan SESAAT
  //                        tetap boleh bayar; kalau diletak selepas gate mereka
  //                        hanya nampak skrin "Langgan Sekarang".
  if (location.pathname === '/flow') return <FlowPayView user={user} />;
  if (location.pathname === '/flow/berjaya') return <FlowPaySuccessView user={user} />;

  // ═══ PROMO KEMBALI PREMIUM + GOOGLE AI PRO (RM99) ═══
  // Kedudukan sama seperti /flow — pelanggan sasaran memang lesennya sudah tamat,
  // jadi diletak selepas dinding lesen mereka hanya nampak skrin "Langgan Sekarang".
  if (location.pathname === '/promo') return <PromoAiProView key={user.uid} user={user} />;
  if (location.pathname === '/promo/berjaya') return <PromoAiProSuccessView user={user} />;

  // Pulihkan destinasi /flow atau /promo selepas log masuk / daftar selesai.
  // Diperiksa hanya bila kita BUKAN di laluan itu, supaya ia tidak berpusing.
  if (!laluanBayarLuarGate(location.pathname)) {
    let flowNext = null;
    try { flowNext = sessionStorage.getItem('sesaat_flow_next'); } catch { flowNext = null; }
    if (flowNext) {
      try { sessionStorage.removeItem('sesaat_flow_next'); } catch { /* abaikan */ }
      return <Navigate to={flowNext} replace />;
    }
  }

  // ═══ LICENSE GATE — Block access if no valid license ═══
  if (!licenseChecked || licenseStatus === 'loading') {
    return (
      <div className="h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0a0e1a, #111827)' }}>
        <div className="w-8 h-8 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (licenseStatus !== 'valid' && !gateDismissed) {
    return (
      <StartGate
        user={user}
        licenseStatus={licenseStatus}
        isRenewalEligible={isRenewalEligible}
        onClose={tutupGate}
        viewer={{ loggedIn: !!user, tier: userTier, plan: userPlan, licenseStatus, allowedModules: userAllowedModules }}
        onLicenseActivated={() => {
          setLicenseStatus('valid');
          setLicenseChecked(true);
        }}
      />
    );
  }

  // /r/support renders fullscreen (no sidebar) — same UX class as StartGate.
  if (routeLocation.pathname === '/r/support') {
    return <SupportGroupView />;
  }

  /* ═══════════════════════════════════════════════════════════════
     KOMPOSER TAB GENERATE (v3.9) — data + pengendali sahaja
     ═══════════════════════════════════════════════════════════════
     Kotak janaan (avatar · Add Products · formula · Generate) berpindah
     daripada panel ke tab Generate, melebar, mengikut prototaip
     /workspace-v3.9 (arahan user 2026-08-12).

     ⛔ Yang berpindah ialah TEMPAT, bukan logik. Setiap nilai di bawah
        menunjuk kepada state dan fungsi yang SAMA seperti sebelum ini —
        `stagedLibraryProducts`, `selectedAvatarId`, `activeModuleId`,
        `handleHeaderAddProduct`. Tiada satu pun ditulis semula, jadi
        laluan Generate sebenar tidak berubah langsung.

     ⛔ JANGAN pindahkan blok ini ke dalam WorkspaceView atau ke dalam
        komposer. State janaan tinggal di App.jsx; menyalinnya ke fail
        lain bermakna dua salinan yang akan hanyut berasingan. */

  /* Media formula = fail yang SAMA dengan prototaip (salinan kecil dalam
     public/salespage/thumbs/, bukan fail salespage saiz penuh — lihat
     nota dalam PromptComposerV39). Module yang tiada dalam peta ini
     (cth module ciptaan admin) memapar petak kosong bernama, bukan
     imej rosak.

     ⚠️ `kunciNota` ialah KUNCI kamus, bukan ayat siap. Dahulu ayat English
        ditaip terus di sini ("1 scene · 3 sets"), jadi ia kekal English
        walaupun pelanggan memilih Melayu (dilaporkan user 2026-08-18).
        Terjemahan berlaku di dalam komposer v3.9 — fail ini dikongsi dengan
        rupa LAMA, jadi ia tidak boleh memanggil kamus sendiri. */
  const FORMULA_MEDIA = {
    ai8saat: { poster: '/salespage/thumbs/ai8saat.avif', durasi: '0:08', kunciNota: 'workspace.composer.nota.ai8saat' },
    ai16saat: { poster: '/salespage/thumbs/ai16saat.avif', video: '/salespage/thumbs/ai16saat-360.mp4', durasi: '0:16', kunciNota: 'workspace.composer.nota.ai16saat' },
    /* ⚠️ AI16SAAT dikenali dengan DUA id. Kad itu keluar kosong tanpa nota
       (dilaporkan user 2026-08-12) kerana hanya `ai16saat` dipetakan,
       sedangkan rekod sebenar memakai `ai16saat-half`. Dua-dua dikekalkan;
       menghapuskan salah satu akan mengosongkan kad itu semula. */
    'ai16saat-half': { poster: '/salespage/thumbs/ai16saat.avif', video: '/salespage/thumbs/ai16saat-360.mp4', durasi: '0:16', kunciNota: 'workspace.composer.nota.ai16saat' },
    homeniche: { poster: '/salespage/thumbs/homeniche.avif', durasi: '0:16', kunciNota: 'workspace.composer.nota.homeniche' },
    goyang: { poster: '/salespage/thumbs/goyang.avif', durasi: '0:08', kunciNota: 'workspace.composer.nota.goyang' },
    /* Nama fail `smartcustom`, BUKAN `smart` — itu punca kad SMART memapar
       ikon gambar pecah. Nama fail disahkan terus daripada folder thumbs. */
    smartcustom: { poster: '/salespage/thumbs/smartcustom.avif', video: '/salespage/thumbs/smartcustom-360.mp4', durasi: '0:08', kunciNota: 'workspace.composer.nota.smart' },
    flownity: { poster: '/salespage/thumbs/flownity.avif', durasi: '0:40', kunciNota: 'workspace.composer.nota.flownity' },
  };

  /* ── Akses module SMART untuk PRO Studio ──────────────────────────
     Seksyen "Agent Saya" dalam PRO Studio hanya berguna kepada pelanggan
     yang lesennya mengandungi SMART. Ia dikira DI SINI dan dihantar ke
     bawah, memakai penjaga lesen yang SAMA seperti Rak Formula.

     ⛔ Jangan kira semula di dalam PRO Studio. Peraturan lesen yang
        disalin ke tempat kedua akan terlupa dikemas kini pada satu hari,
        dan module berbayar terdedah percuma.

     ⚠️ Ambil `mod` daripada senarai `modules` yang hidup supaya bendera
        `locked` daripada Firestore turut dihormati; kalau module itu
        belum ada dalam senarai, guna id sahaja. */
  const aksesSmart = computeFormulaAccess({
    mod: modules.find((m) => m.id === 'smartcustom') || { id: 'smartcustom' },
    isAdmin,
    userAllowedModules,
    userTier,
    flownityTrialUsed,
    maintenanceLockedModules: MAINTENANCE_LOCKED_MODULES,
  });

  /* ★ FB REELS (v4) — kebenaran untuk menghantar video ke Facebook Reels.
     Ia BUKAN formula, jadi ia tidak melalui `computeFormulaAccess`: tiada doc
     Firestore, tiada entri registry, dan tiada kad dalam komposer. Yang wujud
     hanyalah string 'fbreels' dalam licenses/{uid}.allowedModules, ditanda oleh
     admin melalui kotak tanda dalam tab User → Aktifkan.

     ⛔ Wildcard '*' SENGAJA tidak diterima di sini. Wildcard bermakna "semua
        module tanpa isPremiumAddon", dan menerimanya bermakna setiap pemegang
        wildcard mendapat FB Reels percuma. Semak id EKSPLISIT sahaja.

     🔓 DIBUKA KEPADA PELANGGAN PREMIUM (arahan user 2026-09-05, release v3.9.3):
        "buka lock facebook reels utk semua user premium yg dh renew atau masih
        aktif langganan". Tiga pintu, mana-mana satu cukup:
          1. admin;
          2. kebenaran eksplisit `fbreels` yang admin tanda dalam tab User (kekal —
             ia jalan untuk memberi akses kepada seorang pelanggan di luar pakej);
          3. ⭐ PAKEJ: lesen AKTIF (`licenseStatus === 'valid'`) DAN bukan trial DAN
             bukan pakej SMART. Ini merangkumi Premium (users.plan 'monthly'),
             Fighter ('yearly' — Premium tahunan) dan akaun lama yang `plan`/`tier`
             belum ter-set tetapi lesennya sah.
        ⚠️ `licenseStatus === 'valid'` SAHAJA tidak memadai — server memulangkan
           'valid' untuk akaun TRIAL juga (supaya mereka boleh masuk app), jadi
           `userTier !== 'trial'` WAJIB ada. Pakej dikenal daripada `users.plan`,
           BUKAN senarai module (peraturan 2026-08-31: senarai module bukan
           penanda identiti pakej). Lesen tamat ('expired') → gembok balik sendiri,
           dan penjaga `postToBerkesan` di bawah menarik pilihan 'facebook' yang
           tersimpan supaya pelanggan tidak tersangkut. */
  const aksesFbReels = isAdmin
    || userAllowedModules.includes('fbreels')
    || (licenseStatus === 'valid' && userTier !== 'trial' && userPlan !== 'smart');

  /* Tempat post yang BERKESAN. Pilihan tersimpan dalam browser boleh menjadi
     'facebook' sedangkan aksesnya sudah ditarik (lesen tamat, admin nyahtanda).
     Tanpa penjaga ini pelanggan tersangkut pada destinasi yang dia langsung tak
     boleh guna, dan barisnya bergembok — jadi tiada jalan menukarnya balik.
     ⛔ Sengaja DIKIRA, bukan menulis semula state: menulis pada masa render
        mengubah tetapan tersimpan pelanggan tanpa dia menyentuh apa-apa. */
  const postToBerkesan = aksesFbReels ? postTo : 'tiktok';

  /* ★ [2026-09-16] Run terkunci sehingga cara post dipilih — hanya bila pemilih
     itu memang dipapar (Facebook pada mana-mana tab, atau TikTok di tab Schedule)
     dan bukan Showcase Only (tiada video dipost, pemilih dimatikan). Rupa lama
     tiada pemilih → tidak pernah terkunci. */
  /* ★ [2026-09-18] Showcase Only hanya BERKESAN bila beg kuning hidup — bila beg
     kuning mati ia tersorok dan robot tidak menjalankannya (utils/pautanProduk.js).
     Dikira, bukan ditulis semula (nilai tersimpan pelanggan tidak disentuh). */
  const showcaseOnlyBerkesan = begKuningAktif && isShowcaseOnly;
  const perluCaraPost = uiV39 && (postToBerkesan === 'facebook' || wsTab === 'tiktok')
    && !(postToBerkesan !== 'facebook' && showcaseOnlyBerkesan);
  const runTerkunciCara = perluCaraPost && !caraPost;
  /* ★ [2026-09-29] Run MALAP selagi tiada slot kalendar ditick (arahan user —
     dahulu butang menyala walaupun bar tulis "No slots selected", dan pelanggan
     menyangka kad yang ditick akan dipost). `schedRunInfo` bukan-null HANYA di
     tab Schedule v3.9, di mana Run TikTok DAN Facebook membaca slot kalendar
     sahaja (`handleRun*Kalendar` menolak kunci kosong). Tab lain / rupa lama →
     null → tidak pernah terkunci. Label butang TIDAK berubah (hanya kunci
     cara post menukar label). */
  const runTerkunciSlot = !!schedRunInfo && schedRunInfo.bil === 0;
  const runTerkunci = runTerkunciCara || runTerkunciSlot;
  /* Telefon: How to post wajib dipilih (tiada lalai, Post now boleh terbit tanpa sengaja) dan
     mesti ada slot ATAU card ditick (telefon baca kedua-duanya, lihat handleRunPhone). */
  const phoneTiadaPilihan = schedRunInfo ? (schedRunInfo.bil === 0 && headerSelectedCount === 0) : headerSelectedCount === 0;
  const phoneTerkunci = !phoneRoute || phoneTiadaPilihan;

  /* ★ PRO Studio — siapa boleh MENGGUNAKANNYA (arahan user 2026-08-30:
     "utk yg ada akses bole guna. utk yg tak ada akses, biar terkunci").

     Dahulu ia `!isAdmin`, bermakna SETIAP pelanggan bukan-admin mendapat skrin
     baca-sahaja — termasuk pelanggan yang MEMBAYAR untuk SMART. Mereka nampak
     halaman itu tetapi setiap butang mati, iaitu tepat corak "kawalan palsu"
     yang kita elak di tempat lain.

     Kini ia mengikut lesen: pemegang SMART boleh guna penuh, yang tiada SMART
     kekal terkunci (skrin jualan `AgentTerkunci` dipandu oleh prop `aksesSmart`
     yang berasingan, jadi mereka masih nampak apa yang ditawarkan). */
  const proStudioReadOnly = !isAdmin && aksesSmart.isLocked;

  /* ★ CTA BEG KUNING — siapa boleh MENCIPTA teks butang sendiri
     (arahan user 2026-09-08: "semua user yg aktif paid — pakej SMART,
     Premium dan Fighter. free trial mmg tak dapat dan user yg expired pun
     tak dapat").

     ⛔ SENGAJA BERASINGAN daripada `proStudioReadOnly` di atas. Kunci itu
        ialah kunci module SMART; CTA beg kuning pula ciri TIKTOK dan tiada
        kena mengena dengan SMART. Sebelum ini CTA terikut sekali dengan
        kunci itu semata-mata kerana ia duduk dalam halaman yang sama, jadi
        pelanggan Premium/Fighter yang tidak membeli SMART boleh membuka
        halaman CTA tetapi setiap kotak mati — tepat corak "kawalan palsu"
        yang kita elak di tempat lain.

     Corak sama `aksesFbReels` di atas, cuma TANPA pengecualian 'smart':
     pemegang SMART memang patut dapat. `userTier !== 'trial'` (bukan
     `=== 'paid'`) kerana sebahagian pelanggan lama tidak ter-set
     `users.tier` langsung — lesen yang sah sudah memadai. Lesen tamat →
     `licenseStatus` bukan 'valid' → tertutup, seperti yang dikehendaki. */
  const aksesCta = isAdmin || (licenseStatus === 'valid' && userTier !== 'trial');

  /* Kunci module dikira dengan formula yang SAMA seperti grid module dalam
     panel — disalin sebaris demi sebaris, bukan diringkaskan. Kunci ada
     tiga lapis (locked global · adminOnly · grant pelanggan) dan
     meringkaskannya bermakna membuka module yang pelanggan belum bayar. */
  const komposerFormula = modules.map((mod) => {
    const regModule = getModule(mod.id);
    if (regModule.adminOnly && !isAdmin) return null;
    const hasAccess = isAdmin
      || (!MAINTENANCE_LOCKED_MODULES.includes(mod.id)
          && (userAllowedModules.includes(mod.id)
              || (userAllowedModules.includes('*') && !regModule.isPremiumAddon)));
    const isFlownityTrialCandidate = mod.id === 'flownity' && !isAdmin && userTier === 'paid' && !hasAccess;
    const flownityTrialActive = isFlownityTrialCandidate && flownityTrialUsed < 5;
    const media = FORMULA_MEDIA[mod.id] || {};
    return {
      id: mod.id,
      nama: mod.name || mod.id,
      kunciNota: media.kunciNota || '',
      /* Ayat tulisan admin (satu setiap bahasa). `undefined` = belum pernah
         disimpan → komposer jatuh balik kepada `kunciNota`. */
      notaMs: mod.kadNotaMs,
      notaEn: mod.kadNotaEn,
      /* Lencana + tempoh: nilai admin menang bila medan itu WUJUD, walaupun
         ia kosong (kosong = admin mahu ia hilang dari kad). */
      tag: mod.kadBadge !== undefined ? (mod.kadBadge || undefined) : (regModule.badge || undefined),
      poster: media.poster,
      video: media.video,
      durasi: mod.kadDurasi !== undefined ? (mod.kadDurasi || undefined) : media.durasi,
      /* Pil sudut kad — sama maklumat seperti tile module lama:
           TRIAL n/5 → percubaan FLOWNITY yang masih berbaki
           UPGRADE   → terkunci kerana pakej, bukan kerana penyelenggaraan
         Ia berasingan daripada lencana BETA/NEW: yang itu memberitahu
         umur formula, yang ini memberitahu HAK pelanggan ke atasnya. */
      pil: flownityTrialActive ? `TRIAL ${Math.max(0, 5 - flownityTrialUsed)}/5`
        : (!hasAccess && !mod.locked) ? 'UPGRADE' : undefined,
      terkunci: mod.locked || (!hasAccess && !flownityTrialActive),
      /* Sebab kad berkunci — kunci kamus juga. Ayat Melayu yang ditaip
         terus di sini dahulu kekal Melayu pada skrin English. */
      kunciTip: mod.locked ? 'workspace.composer.formulaLockedMaintenance'
        : (!hasAccess && !flownityTrialActive) ? 'workspace.composer.formulaLockedPlan' : undefined,
      /* Generate V2 sahaja (composer v3.9 mengabaikannya): tab Sell/Grow/Earn, tapisan
         format, pill HOLD/DEMO/ZOOM. */
      ...(() => { const m = matlamatModule(mod); return { goal: m.goal, format: m.format, pilMedia: m.pil }; })(),
      /* Scene yang pelanggan boleh pilih dalam Prompt Input v4 (tetapan admin, lalai = scene module). */
      scenes: sceneModule(mod, getSceneCount({ id: mod.id })),
    };
  }).filter(Boolean);

  /* ⭐ Module yang dilihat oleh KOMPOSER v3.9 sahaja. `null` bermaksud
     "pelanggan belum memilih apa-apa" — galeri tidak menandakan sebarang
     kad, jalur tetapan (template GOYANG · sub-module · Suara Watak) tidak
     muncul, dan butang Generate tersekat.

     ⛔ Ia BUKAN pengganti `activeModuleId`. Nilai sebenar itu kekal
        'ai8saat' sepanjang masa supaya rupa LAMA dan setiap laluan lain
        yang membacanya tidak berubah langsung. Yang berbeza hanyalah apa
        yang komposer v3.9 nampak. */
  const modulKomposer = modulDipilih ? activeModuleId : null;

  /* Kuota: teks yang SAMA seperti lencana pada butang Generate lama
     (admin tanpa had · berbayar 100/hari · percubaan coin · percubaan
     FLOWNITY). Dikira di sini supaya komposer tidak perlu tahu apa-apa
     tentang tier. */
  const komposerKuota = (() => {
    const hariIni = new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const guna = dailyPipelineDate === hariIni ? dailyPipelineUsed : 0;
    if (modulKomposer === 'flownity' && !isAdmin && userTier === 'paid' && !userAllowedModules.includes('flownity')) {
      return `TRIAL ${Math.max(0, 5 - flownityTrialUsed)}/5`;
    }
    if (userTier === 'trial') return `${pipelineCoins ?? 0} coins`;
    if (isAdmin) return `${guna}/∞`;
    if (userTier === 'paid' || licenseStatus === 'valid') return `${guna}/100`;
    return null;
  })();

  /* ---- Jalur tetapan formula (pilihan B, arahan user 2026-08-12) --------
     Template GOYANG · sub-module · Suara Watak dipapar sebagai JALUR di
     bawah galeri formula, muncul hanya bila formula berkenaan dipilih.

     Kenapa jalur, bukan chip: dua daripada tiga tetapan ini WAJIB (tanpa
     template, butang Generate memang tersekat). Tetapan wajib yang
     tersembunyi di belakang klik bermakna pengguna baru tersangkut pada
     butang yang berkata "Pilih template dulu" tanpa tahu di mana hendak
     memilihnya.

     ⛔ Logik SETIAP satu kekal sama seperti dalam panel — pengendali,
        kunci localStorage dan nama medan tidak disentuh. Yang berubah
        hanyalah bekas dan warnanya. */
  const komposerSubModule = (() => {
    const activeMod = modules.find((m) => m.id === modulKomposer);
    if (!activeMod?.hasSubModules || !activeMod?.subModules?.length) return null;
    const label = activeMod.subModuleLabel || 'Teknik';
    const perluPilih = !selectedSubModuleId;
    return (
      <JalurTetapan tajuk={label} wajib={perluPilih}>
        <select
          value={selectedSubModuleId || ''}
          onChange={(e) => {
            const id = e.target.value;
            setSelectedSubModuleId(id || null);
            if (id) localStorage.setItem(`${activeModuleId}_selectedSubModuleId`, id);
            else localStorage.removeItem(`${activeModuleId}_selectedSubModuleId`);
          }}
          style={{
            width: '100%', padding: '8px 10px', fontSize: 12.5, fontWeight: 500,
            background: 'var(--s-card)', border: '1px solid var(--s-line-strong)',
            borderRadius: 8, color: 'var(--s-ink)', outline: 'none',
            cursor: 'pointer', boxSizing: 'border-box', fontFamily: 'inherit',
          }}
        >
          <option value="">— Pilih {label.toLowerCase()} —</option>
          {activeMod.subModules.map((sm) => {
            const smLocked = sm.locked === true && !isAdmin;
            return (
              <option key={sm.id} value={sm.id} disabled={smLocked}>
                {smLocked ? `🔒 ${sm.name}` : sm.name}
              </option>
            );
          })}
        </select>
      </JalurTetapan>
    );
  })();

  const komposerTambahan = (
    <>
      {modulKomposer === 'goyang' && (
        <JalurTetapan tajuk="Template GOYANG" wajib={!selectedTemplate}>
          <TemplateSelector
            v39
            moduleId="goyang"
            selectedTemplateId={selectedTemplateId}
            refreshKey={templatesRefreshKey}
            onSelectTemplate={(t) => {
              setSelectedTemplate(t);
              const id = t?.id || '';
              setSelectedTemplateId(id);
              if (id) localStorage.setItem('goyang_selectedTemplateId', id);
              else localStorage.removeItem('goyang_selectedTemplateId');
            }}
          />
        </JalurTetapan>
      )}

      {komposerSubModule}

      {modulKomposer === 'flownity' && (
        <JalurTetapan tajuk="Suara Watak">
          <div style={{ display: 'flex', gap: 6 }}>
            {[{ v: 'female', label: 'Perempuan' }, { v: 'male', label: 'Lelaki' }].map(({ v, label }) => (
              <button
                key={v}
                type="button"
                onClick={() => { setFlownityVoice(v); localStorage.setItem('flownity_voice', v); }}
                style={{
                  /* Lebar tetap, bukan `flex: 1` — dua pilihan sahaja, jadi
                     meregangkannya cuma menjadikan sasaran klik sebesar
                     separuh skrin tanpa memberitahu apa-apa yang baharu. */
                  flex: '0 0 140px', padding: '7px 8px', borderRadius: 8, cursor: 'pointer',
                  fontSize: 12, fontWeight: 500, textAlign: 'center', fontFamily: 'inherit',
                  background: flownityVoice === v ? 'var(--s-card)' : 'transparent',
                  border: `1px solid ${flownityVoice === v ? 'var(--s-select)' : 'var(--s-line-strong)'}`,
                  color: flownityVoice === v ? 'var(--s-ink)' : 'var(--s-ink-3)',
                  transition: 'all 0.15s',
                }}
              >{label}</button>
            ))}
          </div>
        </JalurTetapan>
      )}
    </>
  );

  /* ★ PINTU TUNGGAL — buka modal tetapan module (nama · master prompt 3 mode ·
     Wajib Append · prepend Scene 2 · preset GOYANG · sub-module).

     Modal itu memang SATU sahaja; yang dulu bercabang ialah langkah MEMBUKA-nya.
     Gear pada tile module lama memuatkan semuanya, tetapi butang tetapan dalam
     komposer v3.9 hanya menetapkan nama + membuka modal — jadi setiap kotak
     terpapar KOSONG walaupun datanya ada dalam Firestore, dan menekan
     "Simpan Semua" ketika itu akan MEMADAM master prompt + Wajib Append +
     senarai sub-module module tersebut (butang Simpan menulis apa yang ada
     dalam kotak). Sebab itu setiap pintu WAJIB lalu fungsi ini — jangan sekali
     lagi menaip semula langkah muat ini di tempat lain. */
  const bukaTetapanModule = async (modId) => {
    const fullMod = modules.find(m => m.id === modId);
    if (!fullMod) return;
    // Pre-load master prompt dari PETI BESI (modules/{id}/private/prompt),
    // fallback ke field lama kalau module belum di-migrate.
    let vaultMp = null;
    let vaultSub = {};
    setTetapanMuatGagal(null);
    setTetapanMeta(null);
    try {
      const { getDoc, doc: fsDoc } = await import('firebase/firestore');
      const vSnap = await getDoc(fsDoc(db, 'modules', modId, 'private', 'prompt'));
      if (vSnap.exists()) {
        const vd = vSnap.data() || {};
        if (vd.masterPrompt != null) vaultMp = vd.masterPrompt;
        if (vd.subModulePrompts && typeof vd.subModulePrompts === 'object') vaultSub = vd.subModulePrompts;
        setTetapanMeta({
          updatedAt: vd.updatedAt?.toDate ? vd.updatedAt.toDate() : (vd.updatedAt || null),
          updatedBy: vd.updatedBy || ''
        });
      }
    } catch (err) {
      console.warn('[ModuleSettings] Gagal baca peti besi prompt:', err);
      /* Kegagalan BACAAN — bukan "module ini memang kosong". Kunci Simpan supaya
         kotak kosong akibat ralat ini tidak boleh menimpa resepi yang masih elok
         dalam database. */
      setTetapanMuatGagal(err?.message || String(err));
    }
    const mp = vaultMp != null ? vaultMp : fullMod?.masterPrompt;
    if (typeof mp === 'string') {
      // Legacy string format — seed mode1 with it, leave mode2/3 empty
      setEditingModulePrompts({
        mode1_noAvatarNoGender: mp,
        mode2_noAvatarWithGender: '',
        mode3_withAvatar: ''
      });
    } else {
      setEditingModulePrompts({
        mode1_noAvatarNoGender: mp?.mode1_noAvatarNoGender || '',
        mode2_noAvatarWithGender: mp?.mode2_noAvatarWithGender || '',
        mode3_withAvatar: mp?.mode3_withAvatar || ''
      });
    }
    setActivePromptTab(1);
    /* ★ Rupa kad formula — kotak diisi dengan APA YANG PELANGGAN NAMPAK
       SEKARANG, bukan dibiar kosong. Admin membuka modal, melihat ayat
       semasa, dan mengubah yang perlu sahaja.

       Selagi medan itu belum pernah disimpan (`undefined`), nilai asal
       daripada kod/kamus yang dipaparkan — dan menekan Simpan menulisnya
       masuk tanpa apa-apa berubah pada skrin. Selepas itu barulah kotak
       kosong bermakna "kad tanpa ayat". */
    setEditingKadNotaMs(fullMod?.kadNotaMs !== undefined
      ? fullMod.kadNotaMs
      : (FORMULA_MEDIA[modId]?.kunciNota ? cari('ms', FORMULA_MEDIA[modId].kunciNota) : ''));
    setEditingKadNotaEn(fullMod?.kadNotaEn !== undefined
      ? fullMod.kadNotaEn
      : (FORMULA_MEDIA[modId]?.kunciNota ? cari('en', FORMULA_MEDIA[modId].kunciNota) : ''));
    setEditingKadBadge(fullMod?.kadBadge !== undefined
      ? fullMod.kadBadge
      : (getModule(modId).badge || ''));
    setEditingKadDurasi(fullMod?.kadDurasi !== undefined
      ? fullMod.kadDurasi
      : (FORMULA_MEDIA[modId]?.durasi || ''));
    {
      const m = matlamatModule(fullMod || { id: modId });
      setEditingKadGoal(m.goal);
      setEditingKadFormat(m.format);
      setEditingKadPil(m.pil);
      setEditingKadScenes(sceneModule(fullMod || { id: modId }, getSceneCount({ id: modId })));
    }
    setEditingMandatoryImageAppend(fullMod?.mandatoryImageAppend || '');
    setEditingMandatoryVideoAppend(fullMod?.mandatoryVideoAppend || '');
    setEditingMandatoryVideoAppend2(fullMod?.mandatoryVideoAppend2 || '');
    setEditingPrependScene2Enabled(fullMod?.prependScene2Enabled || false);
    setEditingPrependScene2Text(fullMod?.prependScene2Text || '');
    // GOYANG-specific presets — load array as multi-line string
    setEditingHookPresets((fullMod?.hookPresets || []).join('\n'));
    setEditingBgPresets((fullMod?.bgPresets || []).join('\n'));
    // Sub-module settings
    setEditingHasSubModules(fullMod?.hasSubModules || false);
    setEditingSubModuleLabel(fullMod?.subModuleLabel || 'Teknik');
    setEditingSubModules((fullMod?.subModules || []).map(sm => ({
      ...sm,
      masterPrompt: (vaultSub[sm.id] != null ? vaultSub[sm.id] : sm.masterPrompt) || ''
    })));
    setSubModuleExpandedIdx(null);
    // GOYANG admin — load system templates + fragments
    if (modId === 'goyang') {
      listSystemTemplates('goyang').then(setAdminSystemTemplates).catch(err => {
        console.warn('[ModuleSettings] Failed load system templates:', err);
        setAdminSystemTemplates([]);
      });
      listFragments('goyang', 'hooks').then(setAdminHookFragments).catch(err => {
        console.warn('[ModuleSettings] Failed load hook fragments:', err);
        setAdminHookFragments([]);
      });
      listFragments('goyang', 'backgrounds').then(setAdminBgFragments).catch(err => {
        console.warn('[ModuleSettings] Failed load bg fragments:', err);
        setAdminBgFragments([]);
      });
    } else {
      setAdminSystemTemplates([]);
      setAdminHookFragments([]);
      setAdminBgFragments([]);
    }
    setEditingModuleName(fullMod.name || '');
    // Sejarah dimuat secara malas — kosongkan supaya modal tidak papar senarai module sebelumnya.
    setSejarahTetapan(null);
    setSejarahMemuat(false);
    setSejarahRalat(null);
    setModuleSettingsModal({ open: true, moduleId: fullMod.id, moduleLabel: fullMod.name });
  };

  /* Berapa banyak versi lama disimpan. Had ialah BILANGAN SIMPAN, bukan masa —
     dan tempoh perlindungan memastikan satu petang penuh tuning tidak menolak
     keluar versi elok semalam. Kedua-duanya digabung: versi dibuang hanya bila
     ia melebihi 20 TERBARU **dan** sudah lebih 30 hari. */
  const SEJARAH_SIMPAN_VERSI = 20;
  const SEJARAH_LINDUNG_HARI = 30;

  const msDaripadaIdSejarah = (id) => {
    const n = Number(String(id).replace('history_', ''));
    return Number.isFinite(n) ? n : 0;
  };

  const muatSejarahTetapan = async (modId) => {
    setSejarahMemuat(true);
    setSejarahRalat(null);
    try {
      const { collection, getDocs } = await import('firebase/firestore');
      const snap = await getDocs(collection(db, 'modules', modId, 'private'));
      const rows = snap.docs
        .filter(d => d.id.startsWith('history_'))
        .map(d => ({ id: d.id, ms: msDaripadaIdSejarah(d.id), ...d.data() }))
        .sort((a, b) => b.ms - a.ms);
      setSejarahTetapan(rows);
    } catch (err) {
      console.warn('[ModuleSettings] Gagal muat sejarah:', err);
      setSejarahRalat(err?.message || String(err));
      setSejarahTetapan([]);
    } finally {
      setSejarahMemuat(false);
    }
  };

  /* Pulih = ISI KOTAK sahaja, TIDAK menulis ke database. Admin masih perlu
     menekan "Simpan Semua" sendiri — jadi satu klik silap tidak boleh menukar
     resepi yang sedang hidup, dan versi semasa turut diarkibkan masa Simpan. */
  const pulihVersiTetapan = (versi) => {
    const mp = versi?.masterPrompt;
    setEditingModulePrompts({
      mode1_noAvatarNoGender: (typeof mp === 'string' ? mp : mp?.mode1_noAvatarNoGender) || '',
      mode2_noAvatarWithGender: (typeof mp === 'string' ? '' : mp?.mode2_noAvatarWithGender) || '',
      mode3_withAvatar: (typeof mp === 'string' ? '' : mp?.mode3_withAvatar) || ''
    });
    const m = versi?.meta || {};
    setEditingMandatoryImageAppend(m.mandatoryImageAppend || '');
    setEditingMandatoryVideoAppend(m.mandatoryVideoAppend || '');
    setEditingMandatoryVideoAppend2(m.mandatoryVideoAppend2 || '');
    setEditingPrependScene2Enabled(!!m.prependScene2Enabled);
    setEditingPrependScene2Text(m.prependScene2Text || '');
    setEditingHookPresets((m.hookPresets || []).join('\n'));
    setEditingBgPresets((m.bgPresets || []).join('\n'));
    setEditingHasSubModules(!!m.hasSubModules);
    setEditingSubModuleLabel(m.subModuleLabel || 'Teknik');
    const subLama = versi?.subModulePrompts || {};
    setEditingSubModules((m.subModules || []).map(sm => ({
      ...sm,
      masterPrompt: subLama[sm.id] || ''
    })));
    setSubModuleExpandedIdx(null);
    setActivePromptTab(1);
  };

  const goyangPerluTemplate = modulKomposer === 'goyang' && !selectedTemplate;
  /* Pro Mode (PLAN_PROSTUDIO_BLOCKS.md). `proBlokPerlu` = suis hidup tetapi
     tiada blocks tersimpan → Generate tersekat DENGAN SEBAB (corak sama
     goyangPerluTemplate), bukan senyap jatuh ke default. `proModeAktif`
     dihantar ke WorkspaceView → payload Generate → server. GOYANG sahaja
     (Fasa 1) — module lain belum ada Blocks. */
  const proModeGoyangAktif = isAdmin && modulKomposer === 'goyang' && proModeGoyang.hidup;
  const proBlokPerlu = proModeGoyangAktif && proModeGoyang.sudahMuat && !proModeGoyang.ada;
  const proModeAktif = proModeGoyangAktif && proModeGoyang.ada;
  const proModeNotaTeks = !proModeGoyangAktif ? ''
    : !proModeGoyang.sudahMuat ? '…'
      : proModeGoyang.ada ? `GOYANG · ${proModeGoyang.bilangan} blocks`
        : tCP('workspace.composer.proModeNoBlocks', 'GOYANG · belum ada blocks — buka PRO Studio');
  const trialHabis = userTier === 'trial' && (pipelineCoins ?? 0) <= 0;
  /* ⛔ Generate WAJIB tersekat selagi tiada formula dipilih. Tanpa pagar ini
     `handleHeaderAddProduct` akan jalan juga dan setiap kad lahir dengan
     `moduleId: activeModuleId || 'ai8saat'` — iaitu pelanggan menjana guna
     AI8SAAT tanpa dia pernah memilihnya, dan kuotanya tetap ditolak. */
  const perluPilihFormula = !modulKomposer;

  const komposerNyata = {
    lampiran: stagedLibraryProducts.map((p) => ({
      id: p.id,
      nama: p.name,
      img: p.imageBase64 || p.imageUrl || null,
    })),
    onBuang: (id) => setStagedLibraryProducts((prev) => prev.filter((p) => p.id !== id)),
    onTambah: () => setShowLibraryModal(true),

    formula: komposerFormula,
    formulaPilihan: modulKomposer,
    /* Module terkunci: tekanan TIDAK menukar pilihan — ia menghantar
       pelanggan ke halaman harga, sama seperti tile module dalam panel.
       Ia juga TIDAK menandakan "sudah pilih": pelanggan itu masih belum
       memiliki formula tersebut. */
    onPilihFormula: (id, f) => {
      if (f?.terkunci) { navigate('/pricing'); return; }
      setActiveModuleId(id);
      setModulDipilih(true);
    },
    /* Menekan di luar baris formula membatalkan pilihan (arahan user
       2026-08-18). Ia menurunkan PENANDA sahaja — `activeModuleId` sengaja
       tidak disentuh kerana ia dikongsi dengan rupa LAMA; menetapkannya
       null akan mengosongkan module aktif pelanggan yang masih pada rupa
       itu. Lihat nota pada `modulDipilih`. */
    onBatalFormula: () => setModulDipilih(false),
    onCiptaFormula: isAdmin ? () => navigate('/formula') : undefined,

    /* Pintu masuk tetapan module (nama · master prompt · sub-module) untuk
       ADMIN. Ia memanggil `bukaTetapanModule` — fungsi yang SAMA digunakan oleh
       gear pada tile module lama, jadi modal terisi serupa dari mana-mana pintu.
       ⛔ Jangan ganti dengan `setModuleSettingsModal(...)` terus: modal akan
       terbuka kosong dan "Simpan Semua" akan memadam master prompt module itu.
       Bukan admin → `undefined`, jadi butangnya langsung tidak dirender. */
    onTetapanFormula: isAdmin ? (id) => bukaTetapanModule(id) : undefined,
    /* ★ [2026-10-17] Pintu "New module" untuk ADMIN dalam rupa v3.9 (arahan user). Dahulu satu-satunya
       pintu ialah butang "+" di sebelah "Modules" dalam panel rupa LAMA — disorok sepenuhnya dalam v3.9.
       Membuka modal cipta module yang SAMA (`createModuleModal`, dirender di akar App untuk kedua-dua rupa).
       Bukan admin → `undefined`, butang tidak dirender. */
    onCiptaModule: isAdmin ? () => { setCreateModuleError(''); setCreateModuleModal(true); } : undefined,

    avatarTetap: [
      { id: '', nama: tCP('workspace.composer.avatarNone', 'No avatar'), kosong: true },
      { id: '__male__', nama: tCP('workspace.composer.avatarDefaultM', 'Default (M)'), inisial: 'L' },
      { id: '__female__', nama: tCP('workspace.composer.avatarDefaultF', 'Default (F)'), inisial: 'P' },
    ],
    avatarSimpanan: savedAvatars.map((av) => ({
      id: av.id,
      nama: av.name || 'Avatar',
      img: av.imageUrl || null,
      inisial: (av.name || 'A').charAt(0).toUpperCase(),
      gender: av.gender === 'male' ? 'Male' : av.gender === 'female' ? 'Female' : undefined,
    })),
    avatarPilihan: selectedAvatarId,
    onPilihAvatar: (id) => setSelectedAvatarId(id),
    slotAvatar: 5,
    /* Tambah/sunting avatar membuka modal avatar yang SEDIA ADA. Tanpa dua
       baris ini, "New avatar" dan pensel dalam menu itu ditekan tetapi
       tiada apa berlaku — dan pelanggan langsung kehilangan cara menambah
       avatar sebaik panel lama dibuang. */
    onAvatarBaharu: () => { setEditingAvatar(null); setShowAvatarModal(true); },
    onSuntingAvatar: (id) => {
      const av = savedAvatars.find((x) => x.id === id);
      if (!av) return;
      setEditingAvatar(av);
      setShowAvatarModal(true);
    },

    /* GOYANG ialah mod TANGAN sahaja — tiada watak dalam videonya. Panel
       lama menggantikan pemilih avatar dengan bar makluman bila GOYANG
       aktif; komposer buat perkara yang sama supaya pelanggan tidak
       memilih avatar yang memang takkan digunakan. */
    avatarMati: modulKomposer === 'goyang',
    avatarMatiNota: tCP('workspace.composer.avatarGoyangNote', 'GOYANG guna tangan sahaja — tiada avatar'),

    /* PRO MODE (PLAN_PROSTUDIO_BLOCKS.md bahagian 2) — Fasa 1: admin sahaja
       + GOYANG sahaja. Module lain belum ada Blocks, jadi suis di situ akan
       jadi kawalan yang tidak membuat apa-apa. `null` = chip tidak dirender. */
    /* Suis, bukan menu dua pilihan (arahan user 2026-08-12). Menu memaksa
       dua klik untuk sesuatu yang cuma ada dua keadaan, dan "Auto" pada
       chip terbaca seperti nama tetapan, bukan keadaan hidup/mati. */
    proMode: isAdmin && modulKomposer === 'goyang' ? proModeGoyang.hidup : null,
    onProMode: proModeGoyang.setHidup,
    proModeNota: proModeNotaTeks,
    onProModeNota: proBlokPerlu ? () => navigate('/prostudio/blok') : null,

    /* Bulk Import — syarat kemunculan KEKAL sama seperti dalam panel lama:
       admin sahaja, dan hanya bila Smart Custom aktif. Ia dihidupkan semula
       di sini (arahan user 2026-08-12) selepas blok panel ditutup; tanpa ini
       ia hanya boleh dicapai dengan mematikan suis v3.9. */
    onBulkImport: (isAdmin && modulKomposer === 'smartcustom')
      ? () => setShowBulkImportModal(true)
      : null,

    tambahan: komposerTambahan,

    /* Popup ajakan Upgrade menuding kepada butang ini. Dahulu sasarannya
       ialah butang Upgrade dalam panel; sebaik panel itu ditutup pada tab
       Generate, popup tersebut menuding ke ruang kosong. Ref yang SAMA
       diserahkan ke sini — tiada popup kedua dibina. */
    janaRef: upgradeButtonRef,
    janaDenyut: showUpgradePopup,

    kuota: komposerKuota,
    bolehJana: stagedLibraryProducts.length > 0 && !perluPilihFormula && !goyangPerluTemplate && !proBlokPerlu && !trialHabis,
    labelJana: trialHabis ? tCP('workspace.composer.genUpgrade', 'Upgrade')
      : perluPilihFormula ? tCP('workspace.composer.genPickFormula', 'Pilih formula dulu')
        : goyangPerluTemplate ? tCP('workspace.composer.genPickTemplate', 'Pilih template dulu')
          : proBlokPerlu ? tCP('workspace.composer.genPickBlocks', 'Pilih blocks dulu') : tCP('workspace.composer.generate', 'Generate'),
    tipJana: trialHabis ? tCP('workspace.composer.genTipTrialEnded', 'Coin percubaan sudah habis — upgrade ke lesen berbayar')
      : perluPilihFormula ? tCP('workspace.composer.genTipPickFormula', 'Pilih satu formula dahulu — setiap formula menghasilkan video style yang berbeza')
        : goyangPerluTemplate ? tCP('workspace.composer.genTipPickTemplate', 'Pilih template GOYANG dahulu sebelum generate')
          : proBlokPerlu ? tCP('workspace.composer.genTipPickBlocks', 'Pro Mode hidup tetapi GOYANG belum ada blocks tersimpan — simpan di PRO Studio → Blocks, atau matikan Pro Mode')
            : stagedLibraryProducts.length === 0 ? tCP('workspace.composer.generateTipNeedProduct', 'Lampirkan product dahulu')
              : tCP('workspace.composer.generateTip', 'Setiap product memakan satu kuota; reset 12:00 tengah malam.'),
    onGenerate: () => {
      if (trialHabis) { navigate('/pricing'); return; }
      if (perluPilihFormula || goyangPerluTemplate || proBlokPerlu) return;
      handleHeaderAddProduct();
    },

    /* ★ [2026-11-02] BLANK CARD (keputusan user 1-2 Nov) — cipta kad KOSONG tanpa AI, tanpa
       kuota. Produk OPSYEN: ada produk staged → satu kad kosong setiap produk; tiada → SATU
       kad kosong. Kad mendarat di My Agent (tab 'chatgpt') + view terus bertukar ke sana supaya
       kad kelihatan (bukan senyap — prinsip "kegagalan/hasil mesti bising"). Ia SATU-SATUNYA kad
       yang bebas pindah ke semua tab (moduleId 'blank' didaftar dalam MODUL_TAB_AGENT —
       WorkspaceView). ⛔ TIADA SESAAT_NEW_PRODUCTS → tiada pipeline AI (sama corak laluan
       '__data_only__' dalam handleHeaderAddProduct). Bentuk doc kad = SAMA seperti kad biasa
       (jejak medan satu-persatu); nama/link/gambar diedit kemudian melalui chip "Edit data"
       (kes TIADA_LINK — editDataProduk.js). */
    onCiptaKadKosong: async () => {
      if (!user) return;
      const isGenderOnly = selectedAvatarId === '__male__' || selectedAvatarId === '__female__';
      const selectedAvatar = isGenderOnly
        ? { gender: selectedAvatarId === '__male__' ? 'male' : 'female', id: null, imageUrl: null }
        : (savedAvatars.find(a => a.id === selectedAvatarId) || null);
      const promptsKosong = () => ({ set1: { promptImage: '', promptVideo: '', captionTiktok: '' }, set2: { promptImage: '', promptVideo: '', captionTiktok: '' }, set3: { promptImage: '', promptVideo: '', captionTiktok: '' } });
      // Produk opsyen: staged → satu kad/produk; tiada → satu kad kosong.
      const asas = stagedLibraryProducts.length > 0 ? stagedLibraryProducts : [null];
      const newProducts = asas.map((lp, i) => ({
        id: `prod_${Date.now()}_${i}_${Math.random().toString(36).substring(2, 9)}`,
        originalIndex: products.length + i + 1,
        name: lp?.name || 'Blank card',
        link: lp?.link || '',
        shopeeLink: lp?.shopeeLink || '',
        shopeeProductUrl: kunciShopee(lp?.shopeeProductUrl) ? String(lp.shopeeProductUrl).trim() : '',
        price: lp?.price || '',
        originalPrice: lp?.originalPrice || '',
        discount: lp?.discount || '',
        imageBase64: lp?.imageBase64 || null,
        imageUrl: lp?.imageUrl || null,
        localImagePath: null,
        extraImageUrls: Array.isArray(lp?.extraImageUrls) ? lp.extraImageUrls.filter(u => typeof u === 'string' && u).slice(0, 4) : [],
        intelligenceData: null,
        prompts: promptsKosong(),
        selectedSets: ['set1', 'set2', 'set3'],
        logs: [],
        selected: true,
        kosong: true,              // penanda kad kosong (label "Blank card" + gating tab)
        moduleId: 'blank',         // getModule() fallback selamat ke ai8saat (config/modules.js)
        tab: 'chatgpt',            // mendarat My Agent
        spaceId: selectedSpaceId === 'default' ? null : selectedSpaceId,
        workspaceGroup: headerSelectedGroupId === 'all' ? 'Workspace' : headerSelectedGroupId,
        logicId: null,
        gender: selectedAvatar?.gender || null,
        avatarId: selectedAvatar?.id || null,
        avatarImageUrl: selectedAvatar?.imageUrl || null,
      }));
      setStagedLibraryProducts([]);
      setProducts((prev) => [...prev, ...newProducts]);
      setWsTab('chatgpt');         // bawa user ke My Agent — kad terus kelihatan
      try {
        await saveProducts(user.uid, newProducts);
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus(null), 2500);
      } catch (err) {
        console.error('[BlankCard] gagal simpan kad kosong:', err);
        alert('Kad kosong gagal disimpan. Cuba sekali lagi.');   // gagal mesti bising
      }
    },
  };

  return (
    /* `s-root` membawa masuk token --s-* (design/tokens.css). Kelas itu HANYA
       mentakrif pemboleh ubah warna — ia tidak menetapkan `color` atau
       `background` pada apa-apa, jadi halaman lama di dalamnya kekal seperti
       sedia ada. Latar gelap ditetapkan di sini pula WAJIB: rel v3.9 sendiri
       telus, jadi tanpa latar ini teksnya jatuh atas putih dan hilang. */
    <div
      className={`app-container relative flex h-screen overflow-hidden ${uiV39 ? 's-root' : 'bg-slate-50 dark:bg-slate-950'}`}
      style={uiV39 ? { background: 'var(--s-ground)' } : undefined}
    >

      {/* ═══ Trial Coins Empty — Auto-show popup anchored to Upgrade button ═══ */}
      {showUpgradePopup && trialCoinsEmpty && (
        <>
          <div
            className="fixed inset-0 z-[199] bg-black/40 backdrop-blur-[2px] animate-[fadeIn_0.2s_ease-out]"
            onClick={() => setShowUpgradePopup(false)}
          />
          <div
            ref={(el) => {
              if (el && upgradeButtonRef.current && !upgradePopupAnchor) {
                const rect = upgradeButtonRef.current.getBoundingClientRect();
                setUpgradePopupAnchor({ top: rect.top, left: rect.right, height: rect.height });
              }
            }}
            className="fixed z-[200] animate-[fadeIn_0.2s_ease-out]"
            style={{
              top: upgradePopupAnchor ? Math.max(16, upgradePopupAnchor.top - 20) : '50%',
              left: upgradePopupAnchor ? upgradePopupAnchor.left + 20 : '50%',
              transform: upgradePopupAnchor ? 'none' : 'translate(-50%, -50%)',
              maxWidth: 360
            }}
          >
            <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-5 border border-orange-200 dark:border-orange-800">
              {upgradePopupAnchor && (
                <div className="absolute top-6 -left-2 w-4 h-4 bg-white dark:bg-slate-900 border-l border-b border-orange-200 dark:border-orange-800 rotate-45"></div>
              )}

              <div className="flex items-start gap-3 mb-3">
                <div className="shrink-0 w-9 h-9 rounded-full bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center">
                  <span className="material-symbols-outlined text-orange-600 dark:text-orange-400 text-[20px]">warning</span>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1">
                    Coin Percubaan Habis
                  </h3>
                  <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                    Coin free trial anda telah habis. Langgan <strong>Premium</strong> untuk teruskan menggunakan pipeline AI tanpa had.
                  </p>
                </div>
              </div>

              {upgradePopupAnchor && (
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-2.5 mb-3 flex items-center gap-2">
                  <span className="material-symbols-outlined text-orange-500 text-[16px]">arrow_back</span>
                  <p className="text-[11px] text-orange-700 dark:text-orange-300 font-medium">
                    Klik butang <strong>Upgrade ke Premium</strong> (bergetar) untuk ke halaman langganan.
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => setShowUpgradePopup(false)}
                  className="flex-1 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  Nanti Dulu
                </button>
                <button
                  onClick={() => {
                    setShowUpgradePopup(false);
                    navigate('/pricing');
                  }}
                  className="flex-1 px-3 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-md transition-all"
                >
                  Langgan Sekarang
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══ Mobile Sidebar Overlay ═══ */}
      {isMobile && isMobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-[3990] backdrop-blur-sm"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      <div
        className={isMobile ? 'mobile-sidebar-drawer' : ''}
        /* Auto-tutup laci selepas pelanggan pilih menu — mereka tidak perlu
           ketik kali kedua untuk menyorokkannya.
           Kenapa disaring `a[href]` dan bukan sebarang klik: tajuk submenu
           (cth "Admin ▸") ialah <button> yang cuma mengembang senarai — kalau
           setiap klik menutup laci, submenu itu mustahil dibuka. Item yang
           terkunci guna href="#" (tiada navigasi) jadi ia turut dikecualikan.
           Laluan yang menavigasi tanpa <a> (logo, baris profil) ditangkap oleh
           useEffect pathname di atas. */
        onClick={isMobile ? (e) => {
          const link = e.target.closest?.('a[href]');
          if (link && !link.getAttribute('href').endsWith('#')) setIsMobileSidebarOpen(false);
        } : undefined}
        /* ⛔ LAPISAN 4000 — JANGAN turunkan tanpa membaca ini.
           Laci menu MESTI berada di hadapan segala-galanya pada halaman,
           kerana bila ia terbuka ia satu-satunya benda yang pelanggan
           sepatutnya nampak. Nombor 4000 dipilih daripada tangga lapisan
           sebenar app ini:
             · hiasan halaman + menu jatuh (cth cip "Main Group" pada kepala
               Workspace, yang duduk pada 2000)  → SEMUANYA di bawah 4000
             · modal + rel arkib (9998 ke atas)                → di atas 4000
           Jadi laci menang ke atas kandungan halaman, tetapi tidak pernah
           menutupi kotak amaran/modal.
           Ia PERNAH 100, dan pada nombor itu cip group memanjat ke hadapan
           menu — pelanggan nampak cip terapung di atas menu yang baru
           dibuka (aduan user 2026-08-23). */
        style={isMobile ? {
          position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 4000,
          transform: isMobileSidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
          width: 280,
        } : undefined}
      >
        {/* ★ v3.9 — rel menu baharu menggantikan sidebar lama apabila suis
            dihidupkan. Ia diletak DALAM laci yang sama, jadi kelakuan mobile
            (buka/tutup, auto-tutup selepas pilih menu) kekal berfungsi tanpa
            kod tambahan. Tiada halaman atau enjin yang disentuh di sini —
            hanya menu di tepi yang bertukar.
            ⚠️ NewSidebar menerima laluan TANPA garis miring di depan (ia
            menambahnya sendiri); StudioRail pula memberi laluan penuh. Sebab
            itu dua pengendali berbeza — jangan satukan. */}
        {uiV39 ? (
          <StudioRail
            modSebenar
            /* Rel ini duduk DALAM laci telefon apabila skrin sempit — ia
               perlu tahu, kerana keadaan "kuncup" (jalur ikon 60px) yang
               disimpan dari komputer tidak masuk akal di dalam laci 280px. */
            mobil={isMobile}
            active={idAktifDariLaluan(location.pathname)}
            onNavigate={(path) => { navigate(path); if (isMobile) setIsMobileSidebarOpen(false); }}
            /* Prop di bawah ialah prop yang SAMA yang diterima NewSidebar —
               rel baharu perlu semuanya untuk memapar kad Extension, bar
               storan, seksyen SPACE dan baris profil tanpa kehilangan apa-apa.
               `lockedSet`, `onSetLockChange` dan `hideProductLibrary` sengaja
               TIDAK dihantar: ketiga-tiganya diterima NewSidebar tetapi tidak
               pernah digunakan olehnya (kod mati). */
            user={user}
            isAdmin={isAdmin}
            isPrivileged={canAccessAdmin(userRole)}
            bukaBoost={boostBuka === true}
            userTier={userTier}
            userPlan={userPlan}
            spaces={spacesWithCounts}
            onSelectSpace={(id) => { setSelectedSpaceId(id); if (isMobile) setIsMobileSidebarOpen(false); }}
            storageUsed={storageUsed}
            storageLimitBytes={STORAGE_LIMIT_BYTES}
            isCleaningStorage={isCleaningStorage}
            onCleanupStorage={handleCleanupStorage}
          />
        ) : (
        <NewSidebar
          onNavigate={(path) => { navigate(`/${path}`); if (isMobile) setIsMobileSidebarOpen(false); }}
          lockedSet={lockedSet}
          onSetLockChange={handleSetLockChange}
          spaces={spacesWithCounts}
          selectedSpaceId={selectedSpaceId}
          onSelectSpace={(id) => {
            const space = spacesWithCounts.find(s => s.id === id);
            const spaceName = space?.name || id;
            setSelectedSpaceId(id);
            navigate(spaceName === 'Workspace' ? '/workspace' : `/space/${encodeURIComponent(spaceName)}`);
            if (isMobile) setIsMobileSidebarOpen(false);
          }}
          isCollapsed={isMobile ? false : isSidebarCollapsed}
          onToggleCollapse={isMobile ? () => setIsMobileSidebarOpen(false) : toggleSidebarCollapse}
          user={user}
          storageUsed={storageUsed}
          storageLimitBytes={STORAGE_LIMIT_BYTES}
          isCleaningStorage={isCleaningStorage}
          onCleanupStorage={handleCleanupStorage}
          hideProductLibrary={productLibraryHidden || productLibraryCount === 0}
          userTier={userTier}
          userPlan={userPlan}
        />
        )}
      </div>

      {/* ═══════════════════════════════════════════════════
          Fasa 2: WORKSPACE LEFT PANEL (w-360) 
          ═══════════════════════════════════════════════════ */}
      {isOnSpacePage && !workspaceLocked && (
        /* ⚠️ v3.9 (arahan user 2026-08-11): panel ini BERPINDAH KE KANAN dan
           hanya wujud pada tab Flow dan Schedule — mengikut prototaip. Yang
           berubah hanyalah KEDUDUKAN dan BILA ia muncul; isi panel tidak
           disentuh langsung.

           `order: 2` yang memindahkannya, bukan menyusun semula JSX —
           bekasnya flex, jadi satu nilai order sudah memadai dan kod di
           dalam panel kekal di tempat asalnya.

           Dalam v3.9 tiada butang buka/tutup panel (dibuang atas arahan
           user), jadi tab itulah satu-satunya yang menentukan. Pada rupa
           lama semuanya kekal seperti dahulu: di kiri, dengan butang.

           ⭐ v3.9 (2026-08-12): panel kini dipindahkan KE DALAM bingkai
              halaman melalui `Portal` — ia lajur di dalam halaman, sama
              seperti prototaip, bukan lagi rel yang melekat pada tepi
              tetingkap dan memotong bucu bulat halaman. Kod di bawah tidak
              berpindah; hanya DOM-nya yang mendarat di slot lajur kanan
              (lihat `BingkaiIsi`). Rupa lama: `ke` kosong, jadi ia kekal di
              sini seperti sebelum ini. */
        <Portal ke={uiV39 ? nodPanelKanan : null}>
        <aside style={{
          width: isMobile ? (panelLarianNampak ? '100%' : 0) : (panelLarianNampak ? 360 : 0),
          opacity: panelLarianNampak ? 1 : 0,
          /* ── v3.9: panel ini ialah KAD, bukan rel ────────────────────
             Bentuk `.s-panelbox` prototaip: latar kad, satu garis nipis
             mengelilinginya, bucu bulat. Tingginya IKUT ISI (bukan penuh
             lajur) supaya ia kelihatan sebagai kad yang terletak di dalam
             halaman — lajur di sekelilingnya yang membawa jurang dan
             menatal (lihat `lajurKanan` dalam BingkaiIsi).

             ⚠️ `overflow: hidden` WAJIB dikekalkan — itu yang membuatkan
                bucu bulat benar-benar memotong isi di penjuru. */
          ...(uiV39
            ? {
                height: 'auto',
                background: 'var(--s-card)',
                border: '1px solid var(--s-card-edge)',
                borderRadius: 'var(--s-r-lg)',
                order: 2,
              }
            : {
                background: '#ffffff',
                borderRight: isMobile ? 'none' : '1px solid #f3f4f6',
              }),
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.2s ease-in-out, opacity 0.2s ease-in-out',
          overflow: 'hidden',
          flexShrink: 0,
          ...(isMobile && panelLarianNampak ? { position: 'absolute', top: 0, left: 0, bottom: 0, zIndex: 60 } : {}),
        }}>
          {/* ★ Mobile: Close button header */}
          {isMobile && isLeftPanelVisible && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 16px', borderBottom: '1px solid #f1f5f9', flexShrink: 0,
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>Add Product</span>
              <button
                onClick={() => setIsLeftPanelVisible(false)}
                style={{
                  width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 8, border: 'none', background: '#f1f5f9', cursor: 'pointer',
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#64748b' }}>close</span>
              </button>
            </div>
          )}

          {/* ── Kepala panel v3.9 ──────────────────────────────────────
              Tajuk "Control Panel" sahaja.

              ⛔ Perihal satu baris di bawah tajuk DIBUANG (arahan user
                 2026-08-21 — ia "menyemak"). Ayat itu dulu berubah ikut
                 tab, tetapi ia hanya mengulang jawapan yang bar tab di
                 atas sudah beri: satu baris tambahan tanpa maklumat
                 baharu. Empat kunci kamus `workspace.panel.desc*`
                 dipadam sekali — jangan pasang balik tanpa tanya user.
                 Lakaran /workspace-v3.9 turut diselaraskan.

              Ia duduk di LUAR kawasan tatal supaya tajuk kekal kelihatan
              semasa kawalan digolek — panel ini panjang.

              ⛔ Isi panel TIDAK disentuh (arahan user 2026-08-12). Yang
                 ditambah hanyalah kepala ini.

              ⛔ TIADA garis di bawah kepala — keputusan reka bentuk yang
                 sudah didokumen pada `.s-cardhead` dalam design/ui.css: Cal
                 meletakkan tajuk sebagai label di dalam badan kad, dan
                 satu-satunya garis dalam kad ialah di atas jalur kaki.
                 Menambah garis di sini memecahkan kad jadi dua blok
                 bertindan. (Saya sempat menambahnya; dibuang semula.) */}
          {uiV39 && (
            <div className="s-cardhead" style={{ flexShrink: 0, paddingBottom: 14 }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                {/* `Hide` duduk di KIRI tajuk, di dalam panel yang ia sorokkan
                    (arahan user 2026-08-15). Pasangannya — "Show Control Panel"
                    berteks penuh — muncul dalam jalur tab HANYA semasa panel
                    tersorok (lihat WorkspaceView.jsx), jadi pada bila-bila masa
                    hanya SATU daripada dua kawalan itu wujud.

                    Kenapa satu perkataan cukup di sini: "Control Panel" berada
                    betul-betul di sebelahnya, jadi baris ini terbaca sebagai
                    satu ayat — "Hide Control Panel".

                    ⛔ Butang ialah ADIK-BERADIK tajuk, bukan di dalam
                       `<p class="s-cardhead__t">` — di dalam tajuk ia mewarisi
                       saiz dan tebal tajuk dan keluar berlainan daripada setiap
                       butang lain di halaman ini. */}
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {/* Workspace v4: Hide berpindah ke kepala halaman (butang Show ⇄ Hide, WorkspaceView) —
                      SEMUA tab termasuk Schedule (arahan user 23 Okt). */}
                  {!generateV2.aktif && (
                  <button
                    onClick={() => tetapkanPanelSorok(true)}
                    title={tCP('workspace.panel.hideTip', 'Sembunyikan Control Panel')}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      height: 24, padding: '0 8px', borderRadius: 6,
                      border: '1px solid var(--s-line-strong)',
                      background: 'transparent', cursor: 'pointer',
                      color: 'var(--s-ink-3)', fontSize: 12, fontWeight: 500,
                      whiteSpace: 'nowrap', flex: 'none',
                    }}
                  >
                    <PanelRightClose size={13} strokeWidth={2} />
                    {tCP('workspace.page.hide', 'Hide')}
                  </button>
                  )}
                  <p className="s-cardhead__t">{tCP('workspace.page.controlPanel', 'Control Panel')}</p>
                </span>
              </span>
              {/* ⭐ Suis "Smart" — hanya pada tab Agent AI (arahan user
                  2026-08-14). Ia duduk di kepala, bukan dalam badan panel,
                  kerana ia bukan satu lagi tetapan: ia menentukan panel apa
                  yang dilihat di bawahnya, sama peranan dengan tab.
                  Ia dikunci semasa larian berjalan — menukar enjin di
                  tengah larian bermakna butang Run dan kerja yang sedang
                  berjalan bercakap tentang dua benda berbeza. */}
              {wsTab === 'chatgpt' && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
                  <span className="s-lbl" style={{ marginBottom: 0 }}>Smart</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={smartMode}
                    aria-label="Smart"
                    disabled={!!agentPanel?.running}
                    onClick={tukarSmartMode}
                    className={`s-switch${smartMode ? ' s-switch--on' : ''}`}
                  >
                    <span className="s-switch__dot" />
                  </button>
                </span>
              )}
            </div>
          )}

          {/* ★ v3.0.0: Entire sidebar scrollable as one unit */}
          {/* ⚠️ v3.9: `flex: 1` DIGUGURKAN. Hampir semua isi bekas ini
              (avatar · kotak teks · Add Products · grid module · Generate)
              sudah berpindah ke komposer tab Generate dan ditutup di sini,
              jadi dalam v3.9 bekas ini selalunya KOSONG — tetapi `flex: 1`
              tetap membuatkannya menuntut semua baki ruang, meninggalkan
              jurang gelap ~330px antara tajuk "Control Panel" dan kawalan
              di bawahnya.

              `0 1 auto` bermakna: kosong → tinggi sifar; ada isi → setinggi
              isinya, dan mengecut (serta menatal) bila panel penuh. Rupa
              lama kekal `flex: 1` — di sana bekas ini memang penuh. */}
          <div style={{ flex: uiV39 ? '0 1 auto' : 1, overflowY: 'auto', minHeight: 0 }}>

          {/* ★ R2 (Fasa 3): kaunter video cloud harian — di Control Panel kerana di sinilah
              pelanggan mula proses yang simpan video ke cloud (arahan user 2026-08-20). */}
          {(() => {
            const _klT = new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10);
            const _ct = dailyCloudVideoDate === _klT ? dailyCloudVideoUsed : 0;
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 12px', padding: '7px 11px', borderRadius: 9, background: 'rgba(16,185,129,0.10)', fontSize: 12, color: '#059669', fontWeight: 600 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>cloud_done</span>
                <span>{_ct} / 300 video cloud hari ni</span>
              </div>
            );
          })()}

          {/* 1. Add Products Section */}
          {/* ⛔ v3.9: SELURUH blok janaan di bawah ini (avatar · kotak teks ·
              Add Products · grid module · butang Generate) sudah berpindah ke
              kad komposer pada tab Generate. Ia ditutup di sini supaya tiada
              DUA tempat hidup yang melakukan kerja yang sama.

              Ia ditutup, BUKAN dipadam, kerana rupa lama masih memakai blok
              ini di tempat asalnya. Tutup suis v3.9 → panel kembali lengkap,
              tanpa deploy. Inilah juga jalan patah balik kalau komposer
              bermasalah di production.

              ⚠️ Butang "Bulk Import" (admin + Smart Custom) duduk DALAM blok
                 ini, jadi ia turut hilang dalam v3.9. Ia belum diberi tempat
                 baharu atas arahan user (hold). */}
          {!uiV39 && (
          <div style={{ padding: '12px 16px 0 16px' }}>
              <div>
                
                {/* Unified Input Container — Avatar Picker + Textarea combined */}
                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, marginBottom: 18 }}>
                  
                  {/* GOYANG info bar — replaces avatar selector (hand-only mode) */}
                  {activeModuleId === 'goyang' && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14,
                      padding: '10px 14px', borderRadius: 12,
                      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                      border: '1px solid #fde68a'
                    }}>
                      <span style={{ fontSize: 18 }}>🤚</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#92400e' }}>GOYANG = Hand-Only Mode</div>
                        <div style={{ fontSize: 9, color: '#b45309', marginTop: 2, lineHeight: 1.4 }}>Gender tangan pilih dalam <b>Template editor</b> (bawah Custom BG section).</div>
                      </div>
                    </div>
                  )}
                  {/* Integrated Avatar Selector — hidden for GOYANG (hand-only mode) */}
                  <div style={{
                    display: activeModuleId === 'goyang' ? 'none' : 'flex', gap: 14, marginBottom: 14,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                    padding: 14, borderRadius: 14, border: '1px solid #f1f5f9',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
                    position: 'relative'
                  }}>
                    {/* IMAGE preview box — shows selected avatar */}
                    <div style={{ 
                      width: 64, height: 64, background: '#ffffff', borderRadius: 8, border: '1px solid #e2e8f0',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, overflow: 'hidden', position: 'relative'
                    }}>
                      {(() => {
                        const av = savedAvatars.find(a => a.id === selectedAvatarId);
                        if (av?.imageUrl) {
                          return <img src={av.imageUrl} alt={av.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
                        }
                        if (selectedAvatarId === '__male__') {
                          return (
                            <>
                              <span style={{ fontSize: 24 }}>👨</span>
                              <span style={{ fontSize: 7, fontWeight: 800, color: '#6366f1', textTransform: 'uppercase' }}>MALE</span>
                            </>
                          );
                        }
                        if (selectedAvatarId === '__female__') {
                          return (
                            <>
                              <span style={{ fontSize: 24 }}>👩</span>
                              <span style={{ fontSize: 7, fontWeight: 800, color: '#ec4899', textTransform: 'uppercase' }}>FEMALE</span>
                            </>
                          );
                        }
                        return (
                          <>
                            <span className="material-symbols-outlined" style={{ fontSize: 22, color: '#cbd5e1' }}>person</span>
                            <span style={{ fontSize: 7, fontWeight: 800, color: '#cbd5e1', textTransform: 'uppercase' }}>AVATAR</span>
                          </>
                        );
                      })()}
                    </div>

                    {/* Right side: label + Custom Dropdown */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: 3 }}>
                        <div style={{ fontSize: 10, fontWeight: 800, color: '#334155', letterSpacing: '0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>AI AVATAR</div>
                        <div style={{
                          fontSize: 9, color: savedAvatars.length >= avatarMaxSlots ? '#ef4444' : '#6366f1',
                          fontWeight: 800, background: savedAvatars.length >= avatarMaxSlots ? '#fef2f2' : '#f5f3ff',
                          padding: '1px 6px', borderRadius: 4, border: `1px solid ${savedAvatars.length >= avatarMaxSlots ? '#fee2e2' : '#e0e7ff'}`,
                          flexShrink: 0, whiteSpace: 'nowrap'
                        }}>
                          {savedAvatars.length}/{avatarMaxSlots}
                        </div>
                      </div>
                      <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 600, marginBottom: 8, lineHeight: 1.1 }}>Influencer personality for AI content</div>
                      
                      {/* Premium Custom Dropdown */}
                      <div style={{ position: 'relative' }} ref={avatarDropdownRef}>
                        <div
                          onClick={() => setIsAvatarDropdownOpen(!isAvatarDropdownOpen)}
                          style={{
                            width: '100%', height: 32, padding: '0 10px', boxSizing: 'border-box',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            fontSize: 11, fontWeight: 700,
                            color: (selectedAvatarId && selectedAvatarId !== '') ? '#1e293b' : '#94a3b8',
                            background: '#ffffff', border: `1px solid ${isAvatarDropdownOpen ? '#6366f1' : '#e2e8f0'}`,
                            borderRadius: 8, cursor: 'pointer', transition: 'all 0.2s',
                            boxShadow: isAvatarDropdownOpen ? '0 0 0 3px rgba(99,102,241,0.1)' : '0 1px 2px rgba(0,0,0,0.03)',
                            overflow: 'hidden'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', flex: 1, minWidth: 0 }}>
                            {(() => {
                              const av = savedAvatars.find(a => a.id === selectedAvatarId);
                              if (av) return <><span style={{ fontSize: 12, flexShrink: 0 }}>{av.gender === 'male' ? '👨' : '👩'}</span> <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }} title={av.name}>{av.name}</span></>;
                              if (selectedAvatarId === '__male__') return <><span style={{ fontSize: 12, flexShrink: 0 }}>👨</span> Default (M)</>;
                              if (selectedAvatarId === '__female__') return <><span style={{ fontSize: 12, flexShrink: 0 }}>👩</span> Default (F)</>;
                              return <span>No Avatar Selection</span>;
                            })()}
                          </div>
                          <span className="material-symbols-outlined" style={{
                            fontSize: 16, color: '#94a3b8', transition: 'transform 0.2s', flexShrink: 0,
                            transform: isAvatarDropdownOpen ? 'rotate(180deg)' : 'rotate(0)'
                          }}>expand_more</span>
                        </div>

                        {/* Dropdown Menu */}
                        {isAvatarDropdownOpen && (
                          <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 6,
                            background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10,
                            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
                            zIndex: 100, overflow: 'hidden', padding: 4
                          }}>
                            {/* Scrollable list */}
                            <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                              {/* Option: No Avatar */}
                              <div 
                                onClick={() => { setSelectedAvatarId(''); setIsAvatarDropdownOpen(false); }}
                                style={{
                                  padding: '8px 10px', fontSize: 11, fontWeight: 600, color: selectedAvatarId === '' ? '#6366f1' : '#64748b',
                                  borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                                  background: selectedAvatarId === '' ? '#f5f3ff' : 'transparent',
                                  transition: 'background 0.15s'
                                }}
                                onMouseEnter={(e) => { if(selectedAvatarId !== '') e.currentTarget.style.background = '#f8fafc'; }}
                                onMouseLeave={(e) => { if(selectedAvatarId !== '') e.currentTarget.style.background = 'transparent'; }}
                              >
                                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>person_off</span> No Avatar Selection
                              </div>

                              {/* Defaults */}
                              <div 
                                onClick={() => { setSelectedAvatarId('__male__'); setIsAvatarDropdownOpen(false); }}
                                style={{
                                  padding: '8px 10px', fontSize: 11, fontWeight: 600, color: selectedAvatarId === '__male__' ? '#6366f1' : '#64748b',
                                  borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                                  background: selectedAvatarId === '__male__' ? '#f5f3ff' : 'transparent',
                                  transition: 'background 0.15s'
                                }}
                                onMouseEnter={(e) => { if(selectedAvatarId !== '__male__') e.currentTarget.style.background = '#f8fafc'; }}
                                onMouseLeave={(e) => { if(selectedAvatarId !== '__male__') e.currentTarget.style.background = 'transparent'; }}
                              >
                                <span style={{ fontSize: 14 }}>👨</span> Default (Male)
                              </div>
                              <div 
                                onClick={() => { setSelectedAvatarId('__female__'); setIsAvatarDropdownOpen(false); }}
                                style={{
                                  padding: '8px 10px', fontSize: 11, fontWeight: 600, color: selectedAvatarId === '__female__' ? '#6366f1' : '#64748b',
                                  borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                                  background: selectedAvatarId === '__female__' ? '#f5f3ff' : 'transparent',
                                  transition: 'background 0.15s'
                                }}
                                onMouseEnter={(e) => { if(selectedAvatarId !== '__female__') e.currentTarget.style.background = '#f8fafc'; }}
                                onMouseLeave={(e) => { if(selectedAvatarId !== '__female__') e.currentTarget.style.background = 'transparent'; }}
                              >
                                <span style={{ fontSize: 14 }}>👩</span> Default (Female)
                              </div>

                              {/* Divider */}
                              {savedAvatars.length > 0 && <div style={{ height: 1.5, background: '#f1f5f9', margin: '4px 8px' }} />}

                              {/* Saved Avatars */}
                              {savedAvatars.map(av => (
                                <div 
                                  key={av.id}
                                  onClick={() => { setSelectedAvatarId(av.id); setIsAvatarDropdownOpen(false); }}
                                  style={{
                                    padding: '6px 8px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                                    background: selectedAvatarId === av.id ? '#f5f3ff' : 'transparent',
                                    transition: 'background 0.15s', marginBottom: 2
                                  }}
                                  onMouseEnter={(e) => { if(selectedAvatarId !== av.id) e.currentTarget.style.background = '#f8fafc'; }}
                                  onMouseLeave={(e) => { if(selectedAvatarId !== av.id) e.currentTarget.style.background = 'transparent'; }}
                                >
                                  {/* Mini Thumb */}
                                  <div style={{ width: 28, height: 28, borderRadius: 6, overflow: 'hidden', background: '#f1f5f9', flexShrink: 0, border: `1px solid ${selectedAvatarId === av.id ? '#c7d2fe' : '#e2e8f0'}` }}>
                                    {av.imageUrl ? (
                                      <img src={av.imageUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" />
                                    ) : (
                                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                                        {av.gender === 'male' ? '👨' : '👩'}
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: selectedAvatarId === av.id ? '#4f46e5' : '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{av.name}</div>
                                    <div style={{ fontSize: 8, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>{av.gender} • {av.age}</div>
                                  </div>
                                  {selectedAvatarId === av.id && <span className="material-symbols-outlined" style={{ fontSize: 14, color: '#6366f1' }}>check_circle</span>}
                                </div>
                              ))}
                            </div>
                            
                            {/* Footer: Create New */}
                            <div 
                              onClick={() => { setShowAvatarModal(true); setIsAvatarDropdownOpen(false); }}
                              style={{
                                padding: '10px 10px', marginTop: 4, borderTop: '1px solid #f1f5f9',
                                fontSize: 11, fontWeight: 800, color: '#6366f1',
                                display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                                background: '#fcfaff'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#f5f3ff'}
                              onMouseLeave={(e) => e.currentTarget.style.background = '#fcfaff'}
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add_circle</span> Create New Avatar
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Edit/Delete buttons — refined styling */}
                      {selectedAvatarId && !selectedAvatarId.startsWith('__') && (
                        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                          <button
                            onClick={() => {
                              const av = savedAvatars.find(a => a.id === selectedAvatarId);
                              if (av) { setEditingAvatar(av); setShowAvatarModal(true); }
                            }}
                            style={{ 
                              flex: 1, padding: '3px 0', background: '#f8fafc', border: '1px solid #e2e8f0', 
                              borderRadius: 5, fontSize: 9, fontWeight: 700, color: '#6366f1', 
                              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: 12 }}>edit</span>Edit
                          </button>
                          <button
                            onClick={async () => {
                              if (!confirm('Delete this avatar?')) return;
                              const av = savedAvatars.find(a => a.id === selectedAvatarId);
                              if (!av || !user) return;
                              try {
                                await deleteDoc(doc(db, 'users', user.uid, 'avatars', av.id));
                                if (av.imageUrl) {
                                  /* Padam avatar = tolak kiraan storan (kiraan simetri,
                                     arahan user 2026-08-26). Saiz dibaca SEBELUM padam;
                                     gagal baca → 0 = fail terpadam tanpa tolakan, recount
                                     Optimize betulkan kemudian. */
                                  try {
                                    const rujAv = ref(storage, `avatars/${user.uid}/${av.id}`);
                                    let saizAv = 0;
                                    try { saizAv = (await getMetadata(rujAv)).size || 0; } catch { /* abaikan */ }
                                    await deleteObject(rujAv);
                                    if (saizAv > 0) ubahKaunterStorage(user.uid, -saizAv);
                                  } catch { /* fail mungkin sudah tiada */ }
                                }
                                setSelectedAvatarId('');
                              } catch(e) { console.error('Delete avatar error:', e); }
                            }}
                            style={{ 
                              flex: 1, padding: '3px 0', background: '#fff1f2', border: '1px solid #fecaca', 
                              borderRadius: 5, fontSize: 9, fontWeight: 700, color: '#ef4444', 
                              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.borderColor = '#fca5a5'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = '#fff1f2'; e.currentTarget.style.borderColor = '#fecaca'; }}
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: 12 }}>delete</span>Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ position: 'relative', width: '100%' }}>
                    {stagedLibraryProducts.length > 0 ? (
                      /* ★ STAGED LIBRARY PRODUCTS — card view (scrollable) */
                      <div style={{ width: '100%' }}>
                        {/* Header bar */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#6366f1', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>checklist</span>
                            {stagedLibraryProducts.length} produk dipilih
                          </span>
                          <button
                            onClick={() => setStagedLibraryProducts([])}
                            style={{ padding: '2px 8px', background: 'none', border: '1px solid #fecaca', borderRadius: 6, fontSize: 9, fontWeight: 700, color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: 12 }}>close</span>
                            Clear
                          </button>
                        </div>
                        {/* Scrollable product cards */}
                        <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, paddingRight: 4, paddingBottom: 36 }}>
                          {stagedLibraryProducts.map((product) => (
                            <div
                              key={product.id}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                                background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
                                transition: 'all 0.15s'
                              }}
                            >
                              {/* Thumbnail */}
                              <div style={{ width: 36, height: 36, borderRadius: 8, overflow: 'hidden', background: '#e2e8f0', flexShrink: 0 }}>
                                {(product.imageBase64 || product.imageUrl) ? (
                                  <img alt="" src={product.imageBase64 || product.imageUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#cbd5e1' }}>image</span>
                                  </div>
                                )}
                              </div>
                              {/* Info */}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                {/* ID produk TikTok — baris sendiri atas nama (ID panjang, kalau
                                    inline ia sepit nama produk dalam panel sempit ni). */}
                                <div title="ID produk TikTok" style={{ fontSize: 10, fontWeight: 800, color: '#6366f1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>#{productDisplayNo(product)}</div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: '#334155', textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</div>
                                {product.link && (
                                  <p style={{ fontSize: 8, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>{product.link}</p>
                                )}
                              </div>
                              {/* Remove button */}
                              <button
                                onClick={() => setStagedLibraryProducts(prev => prev.filter(p => p.id !== product.id))}
                                style={{ width: 22, height: 22, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', color: '#cbd5e1', cursor: 'pointer', flexShrink: 0, transition: 'color 0.15s' }}
                                onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                                onMouseLeave={(e) => e.currentTarget.style.color = '#cbd5e1'}
                                title="Buang produk ini"
                              >
                                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>close</span>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      /* Default textarea */
                      <textarea
                        placeholder="Or add new product data or script here..."
                        value={headerProductInput}
                        onChange={(e) => setHeaderProductInput(e.target.value)}
                        style={{
                          width: '100%', minHeight: 120, padding: 0, paddingBottom: 36, border: 'none',
                          fontSize: 13, outline: 'none', resize: 'none', background: 'transparent', color: '#1f2937',
                          lineHeight: 1.5, fontFamily: 'inherit'
                        }}
                      />
                    )}
                    <button
                      onClick={() => setShowLibraryModal(true)}
                      style={{
                        position: 'absolute', bottom: 4, left: 0,
                        padding: '5px 10px',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: 6,
                        fontSize: 10, fontWeight: 700, color: '#6366f1', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 14 }}>inventory_2</span>
                      Add Products
                    </button>
                    {/* ★ Bulk Import button — Smart Custom + admin only */}
                    {activeModuleId === 'smartcustom' && isAdmin && (
                      <button
                        onClick={() => setShowBulkImportModal(true)}
                        style={{
                          position: 'absolute', bottom: 4, left: 90,
                          padding: '5px 10px', background: '#ecfeff',
                          border: '1px solid #67e8f9', borderRadius: 6,
                          fontSize: 10, fontWeight: 700, color: '#0891b2', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}
                        title="Bulk import dari Google Sheet / Excel"
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>cloud_upload</span>
                        Bulk Import
                      </button>
                    )}
                    {autoSaveStatus === 'saved' && (
                      <div style={{
                        position: 'absolute', bottom: 6, right: 0,
                        display: 'flex', alignItems: 'center', gap: 4,
                        fontSize: 10, fontWeight: 600, color: '#10b981'
                      }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 12 }}>check_circle</span>
                        Saved to My Products
                      </div>
                    )}
                  </div>

                  {/* ⛔ v3.9: TIGA kotak di bawah ini (template GOYANG, sub-module,
                      Suara Watak) DAN panel MODE PRO sudah berpindah ke dalam kad
                      komposer pada tab Generate — sebagai jalur di bawah galeri
                      formula (arahan user 2026-08-12, pilihan B).

                      Ia disorok di sini, BUKAN dipadam, kerana rupa lama masih
                      memakainya di tempat yang sama. Tutup suis v3.9 dan panel ini
                      kembali lengkap tanpa deploy.

                      ⛔ JANGAN biarkan kedua-duanya hidup serentak: dua pemilih
                         template untuk satu nilai bermakna pengguna boleh menekan
                         yang satu lagi dan tertanya kenapa pilihannya berubah. */}
                  {/* ★ TEMPLATE SELECTOR (GOYANG) — only render when GOYANG is the active module */}
                  {!uiV39 && activeModuleId === 'goyang' && (
                    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px dashed #f1f5f9' }}>
                      <TemplateSelector
                        moduleId="goyang"
                        selectedTemplateId={selectedTemplateId}
                        refreshKey={templatesRefreshKey}
                        onSelectTemplate={(t) => {
                          setSelectedTemplate(t);
                          const id = t?.id || '';
                          setSelectedTemplateId(id);
                          if (id) localStorage.setItem('goyang_selectedTemplateId', id);
                          else localStorage.removeItem('goyang_selectedTemplateId');
                        }}
                      />
                    </div>
                  )}

                  {/* ★ SUB-MODULE PICKER — tunjuk bila module aktif ada hasSubModules: true */}
                  {(() => {
                    if (uiV39) return null;   // sudah jadi jalur dalam komposer
                    const activeMod = modules.find(m => m.id === activeModuleId);
                    if (!activeMod?.hasSubModules || !activeMod?.subModules?.length) return null;
                    const label = activeMod.subModuleLabel || 'Teknik';
                    const needsPick = !selectedSubModuleId;
                    return (
                      <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px dashed #f1f5f9' }}>
                        <div style={{
                          background: 'linear-gradient(135deg,#fff7ed 0%,#ffedd5 100%)',
                          border: needsPick ? '1px solid #fca5a5' : '1px solid #fed7aa',
                          borderRadius: 12, padding: 12
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#f97316' }}>category</span>
                            <div style={{ fontSize: 11, fontWeight: 800, color: '#ea580c', letterSpacing: '0.04em', textTransform: 'uppercase' }}>{label}</div>
                            {needsPick && (
                              <span style={{ marginLeft: 'auto', fontSize: 9, fontWeight: 800, background: '#fee2e2', color: '#dc2626', padding: '2px 6px', borderRadius: 4, letterSpacing: '0.04em' }}>WAJIB</span>
                            )}
                          </div>
                          <select
                            value={selectedSubModuleId || ''}
                            onChange={(e) => {
                              const id = e.target.value;
                              setSelectedSubModuleId(id || null);
                              if (id) localStorage.setItem(`${activeModuleId}_selectedSubModuleId`, id);
                              else localStorage.removeItem(`${activeModuleId}_selectedSubModuleId`);
                            }}
                            style={{
                              width: '100%', padding: '8px 10px', fontSize: 12, fontWeight: 600,
                              background: '#ffffff',
                              border: needsPick ? '1px solid #ef4444' : '1px solid #fdba74',
                              borderRadius: 8, color: '#1e293b', outline: 'none',
                              cursor: 'pointer', boxSizing: 'border-box'
                            }}
                          >
                            <option value="">— Pilih {label.toLowerCase()} —</option>
                            {activeMod.subModules.map(sm => {
                              // Jenis locked (sm.locked di Firestore) — non-admin nampak 🔒 + tak boleh pilih.
                              const smLocked = sm.locked === true && !isAdmin;
                              return (
                                <option key={sm.id} value={sm.id} disabled={smLocked}>
                                  {smLocked ? `🔒 ${sm.name}` : sm.name}
                                </option>
                              );
                            })}
                          </select>
                          {needsPick && (
                            <div style={{ fontSize: 10, fontWeight: 600, color: '#dc2626', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span className="material-symbols-outlined" style={{ fontSize: 12 }}>info</span>
                              Pilih {label.toLowerCase()} sebelum Generate.
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* ★ SUARA WATAK — hanya muncul bila FLOWNITY aktif */}
                  {!uiV39 && activeModuleId === 'flownity' && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #f1f5f9' }}>
                      <div style={{
                        background: 'linear-gradient(135deg,#fdf4ff 0%,#fae8ff 100%)',
                        border: '1px solid #e9d5ff', borderRadius: 12, padding: 12
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#9333ea' }}>record_voice_over</span>
                          <div style={{ fontSize: 11, fontWeight: 800, color: '#7e22ce', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Suara Watak</div>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {[{ v: 'female', label: '👩 Perempuan' }, { v: 'male', label: '👨 Lelaki' }].map(({ v, label }) => (
                            <button
                              key={v}
                              type="button"
                              onClick={() => { setFlownityVoice(v); localStorage.setItem('flownity_voice', v); }}
                              style={{
                                flex: 1, padding: '7px 8px', borderRadius: 8, cursor: 'pointer',
                                fontSize: 11, fontWeight: 700, textAlign: 'center',
                                background: flownityVoice === v ? '#f3e8ff' : '#ffffff',
                                border: `1.5px solid ${flownityVoice === v ? '#9333ea' : '#e2e8f0'}`,
                                color: flownityVoice === v ? '#7e22ce' : '#64748b',
                                transition: 'all 0.15s'
                              }}
                            >{label}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ★ MOCKUP UI — MODE PRO (SEMUA module; hook kongsi, gaya AI8SAAT dulu) — admin-only, UI sahaja TIADA logic (selamat walaupun terdeploy) */}
                  {!uiV39 && isAdmin && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #f1f5f9' }}>
                      <PilihanGayaMockPanel moduleId={activeModuleId} />
                    </div>
                  )}

                  {/* Integrated Modules Section — ★ NOW READS FROM FIRESTORE modules/ COLLECTION */}
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px dashed #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#94a3b8' }}>apps</span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Modules</span>
                      {/* ★ Admin: Cipta module baharu */}
                      {isAdmin && (
                        <button
                          onClick={() => { setCreateModuleError(''); setCreateModuleModal(true); }}
                          title="Cipta module baharu"
                          style={{
                            marginLeft: 'auto', width: 22, height: 22, borderRadius: 6,
                            border: '1px solid #e0e7ff', background: '#eef2ff', color: '#6366f1',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', padding: 0, transition: 'all 0.15s'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#e0e7ff'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = '#eef2ff'; }}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span>
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {modules.map(mod => {
                        // ★ adminOnly modules hide entirely dari non-admin (cth Smart Custom dalam beta)
                        const regModule = getModule(mod.id);
                        if (regModule.adminOnly && !isAdmin) return null;

                        const isActive = activeModuleId === mod.id;
                        // Gate by license:
                        // - Admin sees all
                        // - Explicit module ID in allowedModules
                        // - Wildcard '*' gives all EXCEPT premium addons (require explicit grant)
                        // ★ Kunci penyelenggaraan: module dalam MAINTENANCE_LOCKED_MODULES
                        //   dipaksa terkunci utk SEMUA non-admin — walaupun ada grant eksplisit
                        //   atau wildcard '*'. Admin tetap lulus utk test.
                        const hasAccess = isAdmin
                          || (!MAINTENANCE_LOCKED_MODULES.includes(mod.id)
                              && (userAllowedModules.includes(mod.id)
                                  || (userAllowedModules.includes('*') && !regModule.isPremiumAddon)));
                        // ★ FLOWNITY TRIAL — paid user TANPA grant flownity dapat 5 percubaan one-shot
                        //   (users.flownityTrialUsed, server yang potong masa scrape). mod.locked global
                        //   & adminOnly kekal supreme. Module lain TAK terjejas.
                        const isFlownityTrialCandidate = mod.id === 'flownity' && !isAdmin && userTier === 'paid' && !hasAccess;
                        const flownityTrialActive = isFlownityTrialCandidate && flownityTrialUsed < 5;
                        const isLocked = mod.locked || (!hasAccess && !flownityTrialActive);

                        return (
                          <button
                            key={mod.id}
                            onClick={() => {
                              if (isLocked) {
                                // ★ FLOWNITY trial habis → bawa ke pricing (dapatkan Add-on).
                                //   Module locked LAIN kekal silent no-op — jangan ubah.
                                if (isFlownityTrialCandidate && !mod.locked && flownityTrialUsed >= 5) navigate('/pricing');
                                return;
                              }
                              setActiveModuleId(mod.id);
                              // Rupa LAMA tidak membaca penanda ini (tile di sini sentiasa
                              // menandakan module aktif seperti dahulu) — ia ditulis semata
                              // supaya galeri v3.9 bersetuju kalau pilihan dibuat di sini.
                              setModulDipilih(true);
                              // V3: Module selection stays in current space — no navigation
                              // Module only affects which flow-automation script runs
                            }}
                            style={{
                              padding: '10px 8px', background: isActive ? '#f5f3ff' : '#ffffff',
                              border: `1px solid ${isActive ? '#6366f1' : '#f1f5f9'}`,
                              borderRadius: 8, cursor: isLocked ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
                              display: 'flex', alignItems: 'center', gap: 8,
                              boxShadow: isActive ? '0 2px 6px rgba(99,102,241,0.08)' : 'none',
                              opacity: isLocked ? 0.6 : 1, position: 'relative'
                            }}
                          >
                            <div style={{ 
                              width: 24, height: 24, borderRadius: 6, background: isActive ? mod.color : '#f8fafc',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', color: isActive ? '#fff' : '#cbd5e1'
                            }}>
                              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
                                {isLocked ? 'lock' : mod.icon}
                              </span>
                            </div>
                            <div style={{ fontSize: 9, fontWeight: 700, color: isActive ? '#4f46e5' : '#64748b', whiteSpace: 'pre-line', lineHeight: 1.1, textAlign: 'left', flex: 1 }}>{mod.name}</div>
                            {/* 🔒 Locked badge for non-accessible modules — hide if registry badge ada (lock icon dah cukup) */}
                            {isLocked && !mod.locked && !regModule.badge && (
                              <div style={{ fontSize: 7, color: '#94a3b8', fontWeight: 600, position: 'absolute', bottom: 3, right: 6 }}>UPGRADE</div>
                            )}
                            {/* ★ FLOWNITY TRIAL pill — baki coin percubaan (posisi sama dgn badge UPGRADE) */}
                            {flownityTrialActive && !isLocked && (
                              <div style={{
                                fontSize: 7, color: '#ffffff', background: 'linear-gradient(90deg,#f59e0b,#d97706)',
                                fontWeight: 700, position: 'absolute', bottom: 3, right: 6,
                                padding: '1px 4px', borderRadius: 3, letterSpacing: '0.05em'
                              }}>TRIAL {5 - flownityTrialUsed}/5</div>
                            )}
                            {/* ★ Generic badge render (BETA, NEW, etc) — sourced from registry, sentiasa render
                                (kecuali masa TRIAL pill flownity aktif — elak bertindih di corner sama) */}
                            {regModule.badge && !(flownityTrialActive && !isLocked) && (
                              <div style={{
                                fontSize: 7, color: '#ffffff',
                                background: regModule.badge === 'NEW'
                                  ? 'linear-gradient(90deg,#06b6d4,#3b82f6)'
                                  : 'linear-gradient(90deg,#f59e0b,#ef4444)',
                                fontWeight: 700, position: 'absolute', bottom: 3, right: 6,
                                padding: '1px 4px', borderRadius: 3, letterSpacing: '0.05em'
                              }}>{regModule.badge}</div>
                            )}
                            {/* Admin Settings Gear */}
                            {isAdmin && (
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Muat + buka melalui pintu tunggal `bukaTetapanModule`
                                  // (definisinya di atas) — dikongsi dengan butang
                                  // tetapan dalam komposer v3.9.
                                  bukaTetapanModule(mod.id);
                                }}
                                style={{
                                  position: 'absolute', top: 4, right: 4,
                                  width: 18, height: 18, borderRadius: 4,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  background: 'rgba(99,102,241,0.08)', cursor: 'pointer',
                                  transition: 'all 0.2s', opacity: 0.5
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = 'rgba(99,102,241,0.15)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.5'; e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; }}
                                title={`Configure module: ${mod.name}`}
                              >
                                <span className="material-symbols-outlined" style={{ fontSize: 12, color: '#6366f1' }}>settings</span>
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {(() => {
                    const isTrial = userTier === 'trial';
                    const coins = pipelineCoins ?? 0;
                    const trialDepleted = isTrial && coins <= 0;
                    if (trialDepleted) {
                      return (
                        <button
                          ref={upgradeButtonRef}
                          onClick={() => { navigate('/pricing'); if (isMobile) setIsLeftPanelVisible(false); }}
                          title="Coin percuma dah habis — upgrade ke lesen berbayar"
                          className={showUpgradePopup ? 'animate-pulse' : ''}
                          style={{
                            width: '100%', height: 44,
                            background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
                            color: '#ffffff', borderRadius: 10,
                            border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                            boxShadow: showUpgradePopup
                              ? '0 0 0 4px rgba(249,115,22,0.4), 0 0 24px rgba(249,115,22,0.6)'
                              : '0 4px 14px rgba(245,158,11,0.35)',
                            marginTop: 16,
                            position: 'relative',
                            zIndex: showUpgradePopup ? 210 : 'auto'
                          }}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 18, fontVariationSettings: "'FILL' 1" }}>bolt</span>
                          Upgrade ke Premium — Langgan Sekarang
                        </button>
                      );
                    }
                    // GOYANG WAJIB ada template dipilih — block Generate kalau takda.
                    // Tooltip & visual dim supaya user faham kenapa button tak berfungsi.
                    const goyangNeedsTemplate = activeModuleId === 'goyang' && !selectedTemplate;
                    return (
                      <button
                        disabled={goyangNeedsTemplate}
                        onClick={() => { if (goyangNeedsTemplate) return; handleHeaderAddProduct(); if (isMobile) setIsLeftPanelVisible(false); }}
                        title={goyangNeedsTemplate ? 'Pilih template GOYANG dulu sebelum generate' : ''}
                        style={{
                          width: '100%', height: 44, background: goyangNeedsTemplate ? '#94a3b8' : '#6366f1',
                          color: '#ffffff', borderRadius: 10,
                          border: 'none', fontWeight: 700, fontSize: 13, cursor: goyangNeedsTemplate ? 'not-allowed' : 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                          boxShadow: goyangNeedsTemplate ? 'none' : '0 4px 12px rgba(99,102,241,0.2)',
                          opacity: goyangNeedsTemplate ? 0.75 : 1,
                          marginTop: 16
                        }}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{goyangNeedsTemplate ? 'lock' : 'add'}</span>
                        {goyangNeedsTemplate ? 'Pilih template dulu' : 'Generate'}
                        {(() => {
                          // Admin: show used/∞ (purple-ish on solid bg already)
                          // Paid: show used/100 — sentiasa nampak, tiada isyarat warna
                          // Trial: existing coins display
                          // ★ FLOWNITY TRIAL — paid user tanpa grant flownity: tunjuk baki coin trial
                          //   (sama sumber dengan badge "TRIAL X/5" atas tile module). Wildcard '*'
                          //   TAK grant flownity (isPremiumAddon), jadi check explicit sahaja.
                          if (activeModuleId === 'flownity' && !isAdmin && userTier === 'paid'
                              && !userAllowedModules.includes('flownity')) {
                            return (
                              <span style={{
                                fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 999,
                                background: 'rgba(255,255,255,0.22)', color: '#ffffff',
                                display: 'inline-flex', alignItems: 'center', gap: 3
                              }}>
                                <span className="material-symbols-outlined" style={{ fontSize: 11, fontVariationSettings: "'FILL' 1" }}>bolt</span>
                                TRIAL {Math.max(0, 5 - flownityTrialUsed)}/5
                              </span>
                            );
                          }
                          if (isTrial) {
                            return (
                              <span style={{
                                fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 999,
                                background: 'rgba(255,255,255,0.22)', color: '#ffffff',
                                display: 'inline-flex', alignItems: 'center', gap: 3
                              }}>
                                <span className="material-symbols-outlined" style={{ fontSize: 11, fontVariationSettings: "'FILL' 1" }}>bolt</span>
                                {coins} coins
                              </span>
                            );
                          }
                          // Compute today's used (auto-zero if date stale)
                          const today = new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);
                          const usedToday = dailyPipelineDate === today ? dailyPipelineUsed : 0;
                          if (isAdmin) {
                            return (
                              <span title={`Admin — unlimited generation (${usedToday} today)`} style={{
                                fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 999,
                                background: 'rgba(255,255,255,0.22)', color: '#ffffff'
                              }}>
                                {usedToday}/Unlimited
                              </span>
                            );
                          }
                          // Paid — sentiasa papar kaunter (dulu tersorok sampai 150/200: siling
                          // server 100, jadi badge TAK PERNAH keluar & user kena dinding tanpa amaran).
                          // Sandaran licenseStatus: pelanggan lama yang users.tier tak ter-set 'paid'.
                          if (userTier === 'paid' || licenseStatus === 'valid') {
                            const cap = 100; // WAJIB padan DAILY_PAID_PIPELINE_CAP dlm functions/index.js
                            return (
                              <span title={`Kuota harian: ${usedToday}/${cap} generation. Reset 12:00 AM (Malaysia). 1 generation = 3 set.`} style={{
                                fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 999,
                                background: 'rgba(255,255,255,0.22)', color: '#ffffff',
                                display: 'inline-flex', alignItems: 'center', gap: 3
                              }}>
                                <span className="material-symbols-outlined" style={{ fontSize: 11, fontVariationSettings: "'FILL' 1" }}>bolt</span>
                                {usedToday}/{cap}
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </button>
                    );
                  })()}
                </div>
              </div>
          </div>
          )}
          </div>{/* close scroll wrapper */}

          {/* ════════ Automation Controls ════════ */}
          {/* Garis atas: dalam v3.9 ia mesti ikut token tema. Nilai lama
              (#e2e8f0) direka untuk latar terang — atas kad gelap ia keluar
              sebagai jalur kelabu cerah yang memotong kad. */}
          <div style={{ flexShrink: 0, padding: '16px 20px', background: '#f1f5f9', borderTop: uiV39 ? '1px solid var(--s-line)' : '1px solid #e2e8f0' }}>
            
            {/* Tabs Navigation — Integrated into Footer */}
            {/* ⛔ v3.9: suis Flow|TikTok ini DISOROK (arahan user 2026-08-12:
                "user buka tab flow, hanya ada control panel utk flow sahaja;
                buka tab schedule, hanya control panel schedule/tiktok").

                Sebabnya dua pemilih untuk satu keputusan. Tab workspace sudah
                menentukan Flow atau Schedule; suis ini boleh menetapkan yang
                BERLAWANAN, jadi pengguna boleh berada pada tab Flow sambil
                melihat kawalan TikTok dengan butang "Run TikTok". Menyorok
                suis meninggalkan satu jawapan sahaja — tab.

                Kawalan di bawahnya TIDAK disentuh; ia masih dipilih oleh
                `workspaceTab`, cuma kini tab yang menetapkannya (lihat effect
                penyelarasan berhampiran `panelLarianNampak`).

                DISOROK, bukan dipadam — rupa lama masih memerlukannya, kerana
                di sana tiada tab Flow/Schedule untuk menggantikannya. */}
            {!uiV39 && (
            <div style={{ display: 'flex', background: '#dde3ed', borderRadius: 12, padding: 4, marginBottom: 14, gap: 2 }}>
              <button
                onClick={() => setWorkspaceTab('products')}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '6px 0', fontSize: 12, fontWeight: 700,
                  border: 'none', borderRadius: 9, cursor: 'pointer', transition: 'all 0.2s',
                  background: workspaceTab === 'products' ? '#ffffff' : 'transparent',
                  color: workspaceTab === 'products' ? '#6366f1' : '#64748b',
                  boxShadow: workspaceTab === 'products' ? '0 2px 10px rgba(99,102,241,0.18)' : 'none',
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 17 }}>merge</span>
                Flow
              </button>
              <button
                onClick={() => setWorkspaceTab('auto')}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '6px 0', fontSize: 12, fontWeight: 700,
                  border: 'none', borderRadius: 9, cursor: 'pointer', transition: 'all 0.2s',
                  background: workspaceTab === 'auto' ? '#ffffff' : 'transparent',
                  color: workspaceTab === 'auto' ? '#f43f5e' : '#64748b',
                  boxShadow: workspaceTab === 'auto' ? '0 2px 10px rgba(244,63,94,0.18)' : 'none',
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 17 }}>music_note</span>
                TikTok
              </button>
            </div>
            )}

            {/* ⭐ Tab Agent AI (v3.9) — panel KETIGA. Ia menggantikan dua
                bar mendatar di atas senarai kad; lihat AgentPanelV39.jsx
                untuk sebab dan peraturan suis Smart. */}
            {workspaceTab === 'agent' ? (
              <AgentPanelBodyV39
                data={agentPanel}
                smart={smartMode}
                onPilihAgent={(id) => workspaceViewRef.current?.setSmartAgent(id)}
                onPilihPlatform={(v) => workspaceViewRef.current?.setFlownityPlatform(v)}
                /* Dengan id agent → terus buka editor agent itu (Langkah 1).
                   Tanpa id (keadaan kosong, tiada agent dipilih) → hub PRO Studio. */
                onBukaProStudio={(agentId) => navigate(agentId ? `/prostudio/agent/${agentId}` : '/prostudio')}
              />
            ) : workspaceTab === 'products' ? (
              <>
                {/* ─── FLOW AUTOMATION CONTROLS (Slate Style) ─── */}
                {/* Selected Count — RUPA LAMA sahaja.
                    Dalam v3.9 kiraan ini berpindah ke jalur kaki kad, di
                    sebelah butang Run (arahan user 2026-08-14), mengikut
                    prototaip: "No product selected · [Run]". Di situ ia
                    berada betul-betul di sebelah butang yang akan bertindak
                    ke atas kiraan itu, bukan di hujung lain panel. */}
                {!uiV39 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, background: '#6366f1',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 8px rgba(99,102,241,0.25)', flexShrink: 0
                  }}>
                    <span style={{ color: '#fff', fontSize: 13, fontWeight: 800 }}>{headerSelectedCount}</span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>Selected</div>
                    <div style={{ fontSize: 9, fontWeight: 600, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Flow Queue</div>
                  </div>
                </div>
                )}

                {/* Toggles Grid — 2 columns */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  {/* Multi Browser — kini SATU-SATUNYA kotak pada baris ini (lihat nota
                      "Advanced" di bawah). Ia melebar penuh dengan sendirinya (`flex: 1`).
                      Admin: boleh OFF untuk menguji simpanan local (badge ADMIN).
                      Non-admin: kelihatan tetapi terkunci ON. */}
                  {isAdmin ? (
                    <label style={{
                      flex: 1, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                      padding: '8px 10px', borderRadius: 10,
                      background: '#ffffff',
                      border: `1px solid ${isMultipleBrowserMode ? '#1e293b' : '#cbd5e1'}`,
                      transition: 'all 0.2s',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                      position: 'relative'
                    }}>
                      <div style={{
                        position: 'absolute', top: -6, right: 6,
                        background: '#6366f1', color: '#fff',
                        fontSize: 7, fontWeight: 800, letterSpacing: '0.05em',
                        padding: '1px 5px', borderRadius: 4,
                        boxShadow: '0 1px 2px rgba(99,102,241,0.3)'
                      }}>ADMIN</div>
                      <div style={{
                        width: 32, height: 18, borderRadius: 9,
                        background: isMultipleBrowserMode ? '#1e293b' : '#cbd5e1',
                        position: 'relative', transition: 'all 0.2s', flexShrink: 0
                      }}>
                        <div style={{
                          width: 14, height: 14, borderRadius: '50%', background: '#fff',
                          position: 'absolute', top: 2, left: isMultipleBrowserMode ? 16 : 2,
                          transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                        }} />
                      </div>
                      <input type="checkbox" style={{ display: 'none' }}
                        checked={isMultipleBrowserMode}
                        onChange={(e) => setIsMultipleBrowserMode(e.target.checked)}
                      />
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#334155', lineHeight: 1.3 }}>Multi Browser</div>
                        <div style={{ fontSize: 8, fontWeight: 600, color: isMultipleBrowserMode ? '#1e293b' : '#94a3b8' }}>
                          {isMultipleBrowserMode ? 'Cloud Sync' : 'Local only (test)'}
                        </div>
                      </div>
                    </label>
                  ) : (
                    <div style={{
                      flex: 1, display: 'flex', alignItems: 'center', gap: 8, cursor: 'not-allowed',
                      padding: '8px 10px', borderRadius: 10,
                      background: '#ffffff',
                      border: '1px solid #1e293b',
                      transition: 'all 0.2s',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                    }}
                    title={'Video disimpan ke cloud secara automatik untuk SEMUA module dan formula — supaya ia boleh di-post dari mana-mana browser anda.\n\n'
                      + 'Had harian: 300 video cloud sehari · setiap video bawah 50MB · simpanan 5GB.\n\n'
                      + 'Bila had cukup, cloud TIDAK dimatikan. Video itu sahaja kekal dalam browser ini dan masih boleh di-post ke TikTok dari browser yang sama. Kiraan berpatah balik tengah malam.'}>
                      <div style={{
                        width: 32, height: 18, borderRadius: 9,
                        background: '#1e293b',
                        position: 'relative', transition: 'all 0.2s', flexShrink: 0
                      }}>
                        <div style={{
                          width: 14, height: 14, borderRadius: '50%', background: '#fff',
                          position: 'absolute', top: 2, left: 16,
                          transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                        }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#334155', lineHeight: 1.3 }}>Multi Browser</div>
                        <div style={{ fontSize: 8, fontWeight: 600, color: '#1e293b' }}>
                          Cloud Sync
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ⛔ Kotak berkunci "Advanced · Pro only" DIBUANG (arahan user 2026-08-21).
                      Ia hiasan tulen — tiada onClick, tiada tetapan di sebaliknya, dan tiada
                      pakej "Pro" yang membukanya. Pelanggan membacanya sebagai ciri yang dia
                      belum bayar untuk dapat, sedangkan tiada apa-apa di situ.
                      ⛔ Jangan pasang balik sebagai hiasan — bina fungsinya dahulu. */}
                </div>

                {/* ⭐ WORKFLOW — ADMIN SAHAJA sehingga laluan card disahkan hidup (2026-09-15).
                    Didahulukan sebelum Model kerana pilihan ini menentukan medan
                    Model mana yang terpakai. Lihat WorkflowV39.jsx. */}
                {/* ★ [3 Nov] Mod New (v4, admin) = dropdown Workflow ikut page /workflow + Formula kad ditanda
                    ("Cara A"). Rupa lama TIDAK disentuh. Nilai larian sama: setFlowWorkflow. */}
                {workflowAktif && generateV2.aktif && (
                  <WorkflowDropdownV4 ringkasan={ringkasanKadWf} flowWorkflow={kadTakSokong ? 'generate' : flowWorkflow} onFlowWorkflow={setFlowWorkflow} tunjukDraft={isAdmin} />
                )}
                {workflowAktif && !generateV2.aktif && (
                  <WorkflowV39 nilai={kadTakSokong ? 'generate' : flowWorkflow} onPilih={setFlowWorkflow} halangKad={kadTakSokong} cubaKad={flowWorkflow === 'kad'} />
                )}

                {/* ★ HOSTED FLOW BETA — pemilih mode untuk pelanggan LAYAK (self-gate: render null kalau tak layak).
                    Flag BERASINGAN dari v4: hanya `hostedBetaBolehAktif()` (env, default OFF) — bukan generateV2.
                    Submit → Central Worker Queue. Lihat HostedBetaSuisV39.jsx. */}
                {hostedBetaBolehAktif() && (
                  <HostedBetaSuisV39 nilai={hostedBeta} onUbah={ubahHostedBeta} onEligible={setHostedEligible} uid={user?.uid} />
                )}
                {hostedBetaBolehAktif() && hostedEligible && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <HostedNotifBell onNavigate={(path) => navigate(`/${path}`)} />
                  </div>
                )}
                {hostedBetaAktif && <HostedStatusKad />}

                {/* ★ [2026-10-17] SCENE OUTPUT — hanya bila module aktif menuntut enjin RESIPI dengan 2+ scene (1 scene tiada apa untuk digabung).
                    Rupa: HasilSceneV39.jsx · nilai: `hasilScene` / `pilihHasilScene` di atas. */}
                {workflowAktif && babakResipiAktif >= 2 && gambarResipiAktif !== 'sahaja' && (
                  <HasilSceneV39 nilai={hasilScene} onPilih={pilihHasilScene} bilBabak={babakResipiAktif} />
                )}
                {workflowAktif && ((babakResipiAktif >= 2 && gambarResipiAktif === 'berlainan') || (babakResipiAktif >= 1 && ['sahaja', 'frame'].includes(gambarResipiAktif))) && (
                  <RujukanProdukV39 nilai={rujukanProduk} onPilih={pilihRujukanProduk} />
                )}
                {workflowAktif && babakResipiAktif >= 1 && gambarResipiAktif === 'sahaja' && (
                  <BilOutputV39 nilai={bilOutput} onPilih={pilihBilOutput} />
                )}
                {workflowAktif && babakResipiAktif >= 1 && gambarResipiAktif === 'sahaja' && (
                  <SimpanGambarV39 nilai={simpanGambar} onPilih={pilihSimpanGambar} />
                )}

                {/* ── Model (v3.9) ────────────────────────────────────────
                    Susunan prototaip: tajuk seksyen, kemudian setiap medan
                    dengan label DI ATAS kawalan dan nota di bawahnya —
                    bukan label sempit di kiri seperti rupa lama, yang
                    memaksa nama dipendekkan ("Video Mode") dan dropdown
                    dihimpit ke separuh lebar.

                    ⭐ IMAGE didahulukan, kemudian VIDEO (arahan user
                       2026-08-12). Ia juga urutan kerja sebenar: Flow
                       menjana gambar dahulu, barulah gambar itu dianimasi
                       menjadi video. */}
                {uiV39 ? (
                <div className="s-sect" style={{ padding: '0 0 16px', borderBottom: '1px solid var(--s-line)', marginBottom: 16 }}>
                  <p className="s-sect__title">{tCP('workspace.page.sect.model', 'Model')}</p>

                  <div>
                    {/* ⭐ Workflow (admin): "Guna gambar pada card" tiada generate
                        gambar → model Image DIKUNCI + ikon lock + nota (arahan user 2026-09-15:
                        "jangan sorok, tapi lock"). Pudar + kursor dilarang datang daripada
                        .s-pick:disabled — JANGAN balut pemilih dengan opacity lagi (pudar
                        berganda). Paparan sahaja, nilai flowImageModel tidak disentuh. */}
                    <span className="s-lbl">
                      <span style={wfKad ? { opacity: 0.5 } : undefined}>{tCP('workspace.page.run.imageLabel', 'Image')}</span>
                      {wfKad && (
                        <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 14, color: 'var(--s-ink-3)' }}>lock</span>
                      )}
                    </span>
                    <PilihV39
                      ariaLabel={tCP('workspace.page.run.imageModelAria', 'Image model')}
                      nilai={flowImageModel}
                      onPilih={setFlowImageModel}
                      disabled={wfKad}
                      pilihan={PILIHAN_MODEL_IMEJ.map((m) => ({ id: m, label: m }))}
                    />
                    {wfKad && (
                      <p className="s-hint">{tCP('workspace.workflow.imageUnused', 'Not used — this workflow skips image generation.')}</p>
                    )}
                  </div>

                  <div>
                    <span className="s-lbl">{tCP('workspace.page.run.videoLabel', 'Video')}</span>
                    <PilihV39
                      ariaLabel={tCP('workspace.page.run.videoModelAria', 'Video model')}
                      nilai={flowUltraMode}
                      onPilih={(m) => {
                        setFlowUltraMode(m);
                        // Veo tiada 10s — kalau model bertukar dari Omni, pangkas 10s → 8s
                        if (m !== 'omni_flash' && flowVideoDuration === '10s') setFlowVideoDuration('8s');
                      }}
                      pilihan={PILIHAN_MODEL_VIDEO.map((m) => ({ id: m.v, label: m.t }))}
                    />
                    <p className="s-hint">
                      {tCP('workspace.run.videoNote', 'Kos credit mengikut pakej Google anda. Flow memaparkan jumlah sebenar sebelum generate.')}
                    </p>
                    {/* ⭐ Workflow card + Veo: Flow hanya menerima 3 gambar (diukur 2026-09-15). */}
                    {wfKad && flowUltraMode !== 'omni_flash' && (
                      <p className="s-hint">{tCP('workspace.workflow.veoImageLimit', 'Veo uses up to 3 card images (main image, avatar, then image 2). Omni Flash uses all of them.')}</p>
                    )}
                  </div>

                  {/* ⭐ Tempoh video — pilihan berubah ikut model (10s Omni sahaja).
                      Workflow card + Veo: DIKUNCI 8s (Flow matikan 4s/6s bila ada gambar Ingredients) —
                      paparan sahaja, nilai flowVideoDuration tidak diubah (lihat `durasiKunci8`). */}
                  <div style={{ marginTop: 4 }}>
                    <span className="s-lbl">
                      <span style={durasiKunci8 ? { opacity: 0.5 } : undefined}>{tCP('workspace.page.run.durationLabel', 'Tempoh video')}</span>
                      {durasiKunci8 && (
                        <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 14, color: 'var(--s-ink-3)' }}>lock</span>
                      )}
                    </span>
                    <PilihV39
                      ariaLabel={tCP('workspace.page.run.durationAria', 'Tempoh video')}
                      nilai={durasiKunci8 ? '8s' : flowVideoDuration}
                      onPilih={setFlowVideoDuration}
                      disabled={durasiKunci8}
                      pilihan={DURASI_UNTUK_MODEL(flowUltraMode).map((d) => ({ id: d, label: d }))}
                    />
                    {durasiKunci8 ? (
                      <p className="s-hint">{tCP('workspace.workflow.durationLocked', 'Veo only makes 8-second videos from card images.')}</p>
                    ) : flowUltraMode !== 'omni_flash' && (
                      <p className="s-hint">{tCP('workspace.run.durationNote', '10 saat hanya tersedia untuk model Omni Flash.')}</p>
                    )}
                  </div>

                  {/* ⭐ Resolusi video — model Omni sahaja (Veo tiada pilihan kualiti video) */}
                  {flowUltraMode === 'omni_flash' && (
                    <div style={{ marginTop: 12 }}>
                      <span className="s-lbl">{tCP('workspace.page.run.resolutionLabel', 'Resolusi video')}</span>
                      <PilihV39
                        ariaLabel={tCP('workspace.page.run.resolutionAria', 'Resolusi video')}
                        nilai={flowVideoResolution}
                        onPilih={setFlowVideoResolution}
                        pilihan={PILIHAN_RESOLUSI_VIDEO.map((r) => ({ id: r, label: r }))}
                      />
                      <p className="s-hint">{tCP('workspace.run.resolutionNote', 'Hanya model Omni ada pilihan kualiti video. Flow memaparkan kos credit sebenar sebelum generate.')}</p>
                    </div>
                  )}

                  {/* ⭐ 2026-09-16 — Saiz fail video (menu Download Flow). SEMUA model:
                      Omni DAN Veo Lite kedua-duanya ada "Original size" + "Upscaled"
                      (diukur hidup) — jadi TIDAK dipagar ikut model, berbeza dgn Resolusi. */}
                  <div style={{ marginTop: 12 }}>
                    <span className="s-lbl">
                      <span style={saizKunci ? { opacity: 0.5 } : undefined}>{tCP('workspace.page.run.dlSizeLabel', 'Saiz fail video')}</span>
                      {saizKunci && (
                        <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 14, color: 'var(--s-ink-3)' }}>lock</span>
                      )}
                    </span>
                    <PilihV39
                      ariaLabel={tCP('workspace.page.run.dlSizeAria', 'Saiz fail video')}
                      nilai={flowDownloadSize}
                      onPilih={setFlowDownloadSize}
                      disabled={saizKunci}
                      pilihan={PILIHAN_SAIZ_VIDEO.map((v) => ({
                        id: v,
                        /* Label = teks Flow sendiri (nombor + "Original size"/"Upscaled"),
                           dikira dari model + resolusi semasa. Sengaja TIDAK diterjemah. */
                        label: labelSaizVideo(flowUltraMode, flowVideoResolution)[v],
                      }))}
                    />
                    {saizKunci ? (
                      <p className="s-hint">{tCP('workspace.run.dlSizeLocked', 'Modul ini menggabungkan babak menjadi satu video dan memuat turunnya terus dari Flow — Flow tidak menawarkan pilihan saiz untuk video gabungan.')}</p>
                    ) : (
                      <p className="s-hint">{tCP('workspace.run.dlSizeNote', 'Ikut menu Download dalam Flow. Pilihan berubah ikut resolusi video anda — 360p memberi 360p/720p; 720p (dan semua model Veo) memberi 720p/1080p. Upscaled tambah kira-kira 30 saat setiap video. 270p GIF dan 4K (50 kredit) sengaja tidak ditawarkan. Untuk kad 2 babak, Flow memuat turun video gabungan — pilihan ini tidak terpakai.')}</p>
                    )}
                  </div>

                  {/* ⭐ Ingredients — hidup: video rujuk gambar produk (mod Ingredients); mati: mod Frames */}
                  <div style={{ marginTop: 12 }}>
                    {/* ⭐ Workflow (admin): "Guna gambar pada card" = Ingredients sentiasa hidup.
                        Suis dipapar hidup + dikunci — nilai tersimpan flowIngredientsMode TIDAK
                        ditukar; payload laluan card menghantar Ingredients hidup (WorkspaceView). */}
                    {wfKad
                      ? barisSuisV39({
                          label: tCP('workspace.page.run.ingredientsLabel', 'Ingredients'),
                          nota: tCP('workspace.workflow.ingredientsLocked', 'Always on — Frames mode only takes a start and end frame.'),
                          on: true,
                          disabled: true,
                          onChange: () => {},
                        })
                      : barisSuisV39({
                          label: tCP('workspace.page.run.ingredientsLabel', 'Ingredients'),
                          nota: tCP('workspace.run.ingredientsNote', 'Hidup: video merujuk gambar produk anda. Mati: mod Frames biasa.'),
                          on: flowIngredientsMode,
                          onChange: () => setFlowIngredientsMode((v) => !v),
                        })}
                  </div>
                </div>
                ) : (
                <>
                {/* Video Mode — label left, dropdown right */}
                <div style={{ display: 'grid', gridTemplateColumns: '62px 1fr', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                    Video Mode
                  </div>
                  <select
                    value={flowUltraMode}
                    onChange={(e) => setFlowUltraMode(e.target.value)}
                    style={{
                      width: '100%', padding: '7px 8px', borderRadius: 8,
                      border: '1px solid #cbd5e1', fontSize: 9, fontWeight: 600,
                      color: '#334155',
                      background: '#ffffff', outline: 'none', cursor: 'pointer',
                      appearance: 'auto', boxSizing: 'border-box'
                    }}
                  >
                    {PILIHAN_MODEL_VIDEO.map((m) => <option key={m.v} value={m.v}>{m.t}</option>)}
                  </select>
                  <div style={{ gridColumn: '2 / 3', fontSize: 8, fontWeight: 500, color: '#94a3b8', lineHeight: 1.4, marginTop: 4 }}>
                    Kos credit ikut pakej Google anda — Flow papar jumlah sebenar sebelum generate.
                  </div>
                </div>

                {/* Model gambar — nilai MESTI sama persis dgn nama dlm dropdown Google Flow
                    (skrip padan nama; "Nano Banana 2" vs "Nano Banana 2 Lite" dibezakan di flow-common.js) */}
                <div style={{ display: 'grid', gridTemplateColumns: '62px 1fr', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                    Image Mode
                  </div>
                  <select
                    value={flowImageModel}
                    onChange={(e) => setFlowImageModel(e.target.value)}
                    style={{
                      width: '100%', padding: '7px 8px', borderRadius: 8,
                      border: '1px solid #cbd5e1', fontSize: 9, fontWeight: 600,
                      color: '#334155',
                      background: '#ffffff', outline: 'none', cursor: 'pointer',
                      appearance: 'auto', boxSizing: 'border-box'
                    }}
                  >
                    {PILIHAN_MODEL_IMEJ.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                </>
                )}

                {/* In Project / Collection — user isi SATU sahaja (kedua-dua tulis ke flowProjectUrl yg sama;
                    collection URL start dgn prefix project yg sama, jadi downstream navigate + skip "New Project" sama je) */}
                {(() => {
                  const isColl = isAdmin && flowProjectUrl.includes('/collection/'); // non-admin: kotak Collection tersorok → anggap URL sbg project je
                  const projectOwns = !!flowProjectUrl && !isColl;   // ada nilai + bentuk project
                  const collOwns = !!flowProjectUrl && isColl;        // ada nilai + bentuk collection
                  // Kotak mana yg papar nilai: ikut focus dulu (supaya paste tak "loncat"), lepas tu ikut bentuk URL
                  const activeField = flowUrlFocusField || (collOwns ? 'collection' : (projectOwns ? 'project' : null));
                  /* v3.9 — kulit prototaip: label DI ATAS medan, medan
                     selebar penuh, nota di bawah. Rupa lama kekal: label
                     sempit di kiri, medan di kanan.
                     ⚠️ KULIT sahaja — `activeField`, `projectOwns`,
                        `collOwns`, `handleBlur` dan semua handler di bawah
                        tidak disentuh langsung. */
                  const labelStyle = uiV39
                    ? {
                        display: 'flex', alignItems: 'center', gap: 6,
                        fontSize: 13, fontWeight: 500, color: 'var(--s-ink)', marginBottom: 6,
                      }
                    : {
                        fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase',
                        letterSpacing: '0.05em', paddingTop: 7, whiteSpace: 'nowrap'
                      };
                  const inputStyle = (active, disabled) => (uiV39
                    ? {
                        width: '100%', height: 36, padding: '0 30px 0 11px', borderRadius: 8,
                        background: 'var(--s-ground)',
                        border: `1px solid ${active ? 'var(--s-ink-3)' : 'var(--s-line-strong)'}`,
                        color: 'var(--s-ink)', fontSize: 13.5, fontFamily: 'inherit',
                        outline: 'none', boxSizing: 'border-box',
                        opacity: disabled ? 0.45 : 1,
                        cursor: disabled ? 'not-allowed' : 'text',
                      }
                    : {
                        width: '100%', padding: '7px 26px 7px 8px', borderRadius: 8,
                        border: `1px solid ${active ? '#6366f1' : '#cbd5e1'}`,
                        fontSize: 9, fontWeight: 500,
                        color: disabled ? '#94a3b8' : '#334155',
                        background: disabled ? '#f1f5f9' : (active ? '#f0f0ff' : '#ffffff'),
                        outline: 'none', transition: 'all 0.2s', boxSizing: 'border-box',
                        cursor: disabled ? 'not-allowed' : 'text'
                      });
                  /* Susun atur baris: bertindan dalam v3.9, dua lajur dalam rupa lama. */
                  const barisGaya = uiV39
                    ? { marginBottom: 15 }
                    : { display: 'grid', gridTemplateColumns: '62px 1fr', gap: 8, alignItems: 'start', marginBottom: 8 };
                  /* [2026-09-09] KOTAK "IN PROJECT" TERIMA APA SAHAJA YANG DITAMPAL —
                     JANGAN tendang, JANGAN tukar bentuk (arahan user).
                     Sebab: bila satu browser ada beberapa akaun Google, link Flow
                     membawa nombor akaun (flow.google.com/u/1/project/...). Penapis
                     lama hanya kenal bentuk tanpa nombor itu → link dikosongkan
                     senyap pada blur, dan pelanggan sangka "tak boleh letak project".
                     Menyeragamkan ke bentuk labs.google juga SALAH untuk kes ini:
                     ia membuang nombor akaun → Google buka projek di akaun pertama →
                     "Something went wrong". Jadi link disimpan SEPERTI DITAMPAL.
                     Extension sendiri yang menilai (terima labs.google/.../project/
                     atau flow.google.com yang ada /project/; selain itu ia buka
                     projek baharu seperti biasa). */
                  const handleBlur = (e) => {
                    setFlowUrlFocusField(null);
                    setFlowProjectUrl(e.target.value.trim());
                  };
                  const clearBtn = (
                    <button
                      onClick={() => { setFlowProjectUrl(''); setFlowUrlFocusField(null); }}
                      style={{
                        position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                        background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 12, color: '#94a3b8' }}>close</span>
                    </button>
                  );
                  const okHint = (text) => (uiV39
                    ? <p className="s-hint">{text}</p>
                    : (
                      <div style={{ fontSize: 7, fontWeight: 600, color: '#6366f1', marginTop: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 9 }}>check_circle</span>
                        {text}
                      </div>
                    ));
                  const lockHint = (text) => (uiV39
                    ? <p className="s-hint">{text}</p>
                    : (
                      <div style={{ fontSize: 7, fontWeight: 600, color: '#94a3b8', marginTop: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 9 }}>lock</span>
                        {text}
                      </div>
                    ));
                  /* Nota lalai bila tiada keadaan khas — prototaip sentiasa
                     memberitahu medan itu untuk apa, bukan hanya bila salah. */
                  const notaBiasa = (text) => (uiV39 ? <p className="s-hint">{text}</p> : null);
                  return (
                    <div style={uiV39
                      ? { paddingTop: 16, borderTop: '1px solid var(--s-line)', marginTop: 16 }
                      : { marginBottom: 14 }}>
                      {uiV39 && <p className="s-sect__title" style={{ marginBottom: 15 }}>{tCP('workspace.page.sect.destination', 'Destination')}</p>}

                      {/* Row: In Project */}
                      <div style={barisGaya}>
                        <div style={labelStyle}>{tCP('workspace.page.run.inProject', uiV39 ? 'In project' : 'In Project')}</div>
                        <div>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="text"
                              disabled={collOwns}
                              value={activeField === 'project' ? flowProjectUrl : ''}
                              onFocus={() => setFlowUrlFocusField('project')}
                              onChange={(e) => setFlowProjectUrl(e.target.value.trim())}
                              onBlur={handleBlur}
                              placeholder={tCP('workspace.run.projectPlaceholder', uiV39 ? 'Tampal link project…' : 'Paste project URL...')}
                              style={inputStyle(activeField === 'project', collOwns)}
                            />
                            {activeField === 'project' && flowProjectUrl && clearBtn}
                          </div>
                          {projectOwns && okHint(tCP('workspace.run.projectUsed', 'Guna project ini — langkah "New Project" dilangkau.'))}
                          {collOwns && lockHint(tCP('workspace.run.projectBlocked', 'Kosongkan "Collection" dahulu.'))}
                          {!projectOwns && !collOwns && notaBiasa(tCP('workspace.run.projectEmpty', 'Biarkan kosong untuk mencipta project baharu setiap kali.'))}
                        </div>
                      </div>

                      {/* Row: Collection — ADMIN-ONLY (logik rerun FLOWNITY masih WIP; customer biasa tak nampak) */}
                      {isAdmin && (
                      <div style={uiV39 ? { marginBottom: 15 } : { display: 'grid', gridTemplateColumns: '62px 1fr', gap: 8, alignItems: 'start' }}>
                        <div style={labelStyle}>{tCP('workspace.page.run.collection', 'Collection')}</div>
                        <div>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="text"
                              disabled={projectOwns}
                              value={activeField === 'collection' ? flowProjectUrl : ''}
                              onFocus={() => setFlowUrlFocusField('collection')}
                              onChange={(e) => setFlowProjectUrl(e.target.value.trim())}
                              onBlur={handleBlur}
                              placeholder={tCP('workspace.run.collectionPlaceholder', uiV39 ? 'Tampal link collection…' : 'Paste collection URL...')}
                              style={inputStyle(activeField === 'collection', projectOwns)}
                            />
                            {activeField === 'collection' && flowProjectUrl && clearBtn}
                          </div>
                          {collOwns && okHint(tCP('workspace.run.collectionUsed', 'Guna collection ini — langkah "New Project" dilangkau.'))}
                          {projectOwns && lockHint(tCP('workspace.run.collectionBlocked', 'Kosongkan "In project" dahulu.'))}
                          {!projectOwns && !collOwns && notaBiasa(tCP('workspace.run.collectionEmpty', 'Isi satu sahaja: In project ATAU Collection.'))}
                        </div>
                      </div>
                      )}
                    </div>
                  );
                })()}

                {/* ★ FASA 2: Continue Collection — guna collection tersimpan pada produk, reconcile yg hilang (AI16SAAT/Home Niche)
                    ADMIN-ONLY sementara (feature belum ready; buang `isAdmin &&` bila nak release — lihat nota CLAUDE.md) */}
                {isAdmin && (uiV39 ? (
                  /* Kulit v3.9 — baris suis prototaip. State dan handler
                     SAMA (`continueCollectionMode`); hanya rupanya bertukar
                     daripada kad bergaris kepada baris suis. */
                  barisSuisV39({
                    label: tCP('workspace.page.run.continueCollection', 'Continue Collection'),
                    nota: tCP('workspace.run.continueCollectionNote', 'Isi gambar atau video yang hilang pada collection tersimpan, dan langkau set yang sudah siap. Pilih satu produk sahaja.'),
                    on: continueCollectionMode,
                    onChange: () => setContinueCollectionMode((v) => !v),
                  })
                ) : (
                <div
                  onClick={() => setContinueCollectionMode(v => !v)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, padding: '8px 10px',
                    borderRadius: 8, cursor: 'pointer',
                    background: continueCollectionMode ? '#f0f0ff' : '#f8fafc',
                    border: `1px solid ${continueCollectionMode ? '#6366f1' : '#e2e8f0'}`, transition: 'all 0.2s'
                  }}
                >
                  <div style={{
                    width: 34, height: 20, borderRadius: 20, flexShrink: 0, position: 'relative',
                    background: continueCollectionMode ? '#6366f1' : '#cbd5e1', transition: 'all 0.2s'
                  }}>
                    <div style={{
                      width: 16, height: 16, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2,
                      left: continueCollectionMode ? 16 : 2, transition: 'all 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                    }} />
                  </div>
                  <div style={{ lineHeight: 1.3 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#334155' }}>Continue Collection</div>
                    <div style={{ fontSize: 8, color: '#94a3b8' }}>Sambung collection tersimpan — isi gambar/video hilang, langkau set siap (pilih 1 produk)</div>
                  </div>
                </div>
                ))}

                {/* ── Set (v3.9) ──────────────────────────────────────────
                    Bentuk prototaip: tajuk seksyen, bulatan besar S1·S2·S3,
                    nota di bawah. Dalam rupa lama pemilih ini duduk dalam
                    jalur kaki bersebelahan butang Run — muat, tetapi tiada
                    ruang untuk tajuk mahupun nota, jadi pengguna baharu tidak
                    pernah diberitahu bahawa satu set = satu video.

                    Pemilih dalam jalur kaki DISOROK dalam v3.9 (lihat nota di
                    situ) supaya hanya ADA SATU tempat memilih set. Dua tempat
                    yang menulis state yang sama akan bercanggah di mata
                    pengguna sebaik salah satunya terlepas dikemas kini.

                    FLOWNITY memakai 5 "scene", bukan 3 set — sama seperti
                    jalur kaki, kerana ia state yang sama. */}
                {uiV39 && (
                <div className="s-sect" style={{ padding: '16px 0 0', borderTop: '1px solid var(--s-line)', marginTop: 16 }}>
                  <p className="s-sect__title">{isFlownitySelected ? tCP('workspace.page.sect.scene', 'Scene') : tCP('workspace.page.sect.set', 'Set')}</p>
                  <div style={{ display: 'flex', gap: 8 }} role="group" aria-label={isFlownitySelected ? tCP('workspace.page.sect.scene', 'Scene') : tCP('workspace.page.sect.set', 'Set')}>
                    {butangPilihanSet('v39')}
                  </div>
                  <p className="s-hint" style={{ marginTop: 0 }}>
                    {isFlownitySelected
                      ? tCP('workspace.run.sceneNote', 'Satu scene menghasilkan satu bahagian video. Tandakan scene yang hendak dijalankan sahaja.')
                      : tCP('workspace.run.setNote', 'Satu set menghasilkan satu video. Tandakan set yang hendak dijalankan sahaja.')}
                  </p>
                </div>
                )}

              </>
            ) : (
              <>
                {/* ─── TIKTOK AUTOMATION CONTROLS (Rose Style) ───
                    Kiraan terpilih: RUPA LAMA sahaja — dalam v3.9 ia di jalur
                    kaki, sama seperti panel Flow. */}
                {!uiV39 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, background: '#f43f5e',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 8px rgba(244,63,94,0.25)', flexShrink: 0
                  }}>
                    <span style={{ color: '#fff', fontSize: 13, fontWeight: 800 }}>{headerSelectedCount}</span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>Selected</div>
                    <div style={{ fontSize: 9, fontWeight: 600, color: '#f43f5e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>TikTok Queue</div>
                  </div>
                </div>
                )}

                {/* ── Panel Schedule v3.9 ─────────────────────────────────
                    Tiga seksyen prototaip. KULIT sahaja: setiap suis memanggil
                    `tukarPostOnly` / `tukarShowcase` / `tukarSchedule` yang
                    SAMA seperti rupa lama, jadi peraturan saling-menolak
                    antara Post Only, Showcase dan Schedule hidup di satu
                    tempat sahaja.

                    ⚠️ "Post To" ialah seksyen BAHARU (arahan user 2026-08-14)
                       — prototaip ada, app tiada. TikTok dipapar HIDUP dan
                       TIDAK BOLEH DITEKAN, bukan suis yang boleh ditekan
                       tetapi tidak berbuat apa-apa: TikTok memang satu-satunya
                       channel yang wujud, jadi baris itu menyatakan keadaan
                       sebenar. Threads dan Facebook bergembok, dengan nota
                       yang menyatakan sebabnya. Tiada kawalan palsu.
                       ⛔ Bila channel lain benar-benar dibina, barulah suis
                          TikTok boleh dijadikan boleh-tekan — dan pada masa
                          itu ia perlu state sebenar, bukan `true` tetap. */}
                {uiV39 ? (
                <>
                  {/* Phone automation (v4) — TOGGLE dalam Control Panel (arahan user 23 Okt).
                      Sentiasa papar baris toggle bila v4 hidup; ON = mod telefon (seksyen
                      extension di bawah DISOROK, lihat `phoneMode ? null :` di hujung blok).
                      Dipagar suis Workspace v4 SAHAJA — suis itu admin sahaja. */}
                  {phoneBoleh && (
                    <PhoneControlPanelV4
                      on={phoneModeOn}
                      onToggle={tukarPhoneMode}
                      app={phoneApp}
                      onApp={pilihPhoneApp}
                      route={phoneRoute}
                      onRoute={pilihPhoneRoute}
                      onSetup={() => navigate('/phone-automation')}
                    />
                  )}
                  {phoneMode ? null : (<>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 15, paddingBottom: 16, borderBottom: '1px solid var(--s-line)', marginBottom: 16 }}>
                    <p className="s-sect__title">{tCP('workspace.page.sect.postTo', 'Post To')}</p>
                    {/* ★ [2026-09-29] Tiga KOTAK "pilih satu" menggantikan tiga baris
                        suis (rupa diluluskan user): TikTok · Facebook Reels · Threads.
                        Nilai + simpanan TIDAK berubah — kotak memanggil `pilihPostTo`
                        yang sama. Tiada akses FB Reels → kotak Facebook berkunci tetapi
                        kekal dipapar (keputusan user 2026-08-30). Ayat nota bawah
                        ikon/kotak (tiktokNote · fbNote · pilihSatu · lockedNote)
                        sengaja tidak dipapar lagi (arahan user); kunci kamus kekal. */}
                    <PostToV39 t={tCP} postTo={postToBerkesan} aksesFb={aksesFbReels} onPilih={pilihPostTo} />
                    {/* ★ v3.9.4 — TIADA lagi medan URL page (keputusan user 2026-09-11).
                        Robot upload melalui Content Library, yang sentiasa mengikut page
                        AKTIF dalam browser itu — satu browser = satu page. Nama page aktif
                        dibaca robot dan dipapar dalam kemajuan larian (TIADA pagar semak
                        page — user menolaknya). Muncul hanya bila Facebook dipilih.
                        ★ [2026-09-29] Ayat "page AKTIF" (fbPageActive) tidak dipapar lagi
                        — arahan user: tiada ayat penerangan di bawah kotak Post To. */}
                    {aksesFbReels && postToBerkesan === 'facebook' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                        <LaluanFbV39 t={tCP} laluan={fbLaluan} onPilih={pilihFbLaluan} />
                        {/* ★ [2026-09-29] DIBUANG (arahan user, lakaran diluluskan): pemilih
                            "Set untuk jadi reel" (S1/S2/S3) + kotak kuning arahan run
                            (fbRunHint). Panel TikTok/FB v3.9 hanya muncul di tab Schedule,
                            dan di situ Run Facebook SENTIASA ikut kalendar (`schedMode` →
                            `handleRunFacebookKalendar`, yang tidak membaca `tiktokTargetSets`).
                            Pemilih itu hanya dibaca cabang tick kad `handleRunFacebook`, yang
                            tidak boleh dicapai dari UI v3.9 — jadi ia kawalan palsu; kotak
                            kuning pula mendakwa "Run Facebook (mana-mana tab)", tidak benar.
                            Mod tick kad KEKAL dalam kod (rupa lama + WorkspaceView).
                            ⛔ Jangan pasang semula tanpa tanya user. Kunci kamus fbSetLabel ·
                            fbSetHint · fbRunHint kekal. */}
                      </div>
                    )}
                  </div>

                  {/* ★ v4 — Post Mode (Showcase) + CTA beg kuning = seksyen TikTok, disorok
                      bila Facebook dipilih (arahan user 2026-09-04). State kekal — cuma
                      tak dirender; balik ke TikTok, ia muncul semula.
                      ★ v3.9.4 — seksyen Schedule TIDAK lagi disorok untuk Facebook: ia kini
                      membawa suis Direct Schedule untuk FB juga (lihat bawah).
                      ★ [2026-09-18] Post Mode + CTA kini dalam SATU kotak "Beg kuning":
                      toggle → CTA → Post Mode (rupa diluluskan user). CTA + Post Mode
                      hanya dipapar bila beg kuning hidup. Facebook dapat kotak "Link
                      produk" (toggle Add product) di tempat yang sama. RUPA dalam
                      components/PautanProdukV39.jsx; state + peraturan kekal di sini.
                      ⛔ CTA beg kuning = pilihan per-BROWSER (v3.9.3). Jangan pindahkan ke
                         PRO Studio — pelanggan guna 3-5 browser serentak, dan pilihan
                         sepunya akan menukar CTA larian browser lain yang sedang berjalan.
                      ★ [2026-09-23] CTA kini dipapar dalam KEDUA-DUA kotak (soalan user
                         "kenapa tak ada pilihan CTA untuk FB?"). Ia memang sudah dihantar ke
                         robot FB sejak v4 → "Link name" pautan produk reel; yang hilang cuma
                         UI-nya. SATU pilihan dikongsi TikTok + FB (keputusan user) — jadi
                         `ctaPilihan`/`ubahCtaPilihan` yang sama diberi kepada dua kotak. */}
                  {postToBerkesan !== 'facebook' ? (
                    <KotakBegKuningV39
                      t={tCP}
                      barisSuis={barisSuisV39}
                      aktif={begKuningAktif}
                      onTukar={tukarBegKuning}
                      cta={{ pilihan: ctaPilihan, senarai: ctaSenarai, sudahMuat: ctaSudahMuat, onUbah: ubahCtaPilihan }}
                      postMode={{ postOnly: isPostOnly, onPostOnly: tukarPostOnly, showcaseOnly: isShowcaseOnly, onShowcaseOnly: tukarShowcase }}
                    />
                  ) : (
                    <KotakAddProductFbV39
                      t={tCP}
                      barisSuis={barisSuisV39}
                      aktif={fbAddProductAktif}
                      onTukar={tukarFbAddProduct}
                      cta={{ pilihan: ctaPilihan, senarai: ctaSenarai, sudahMuat: ctaSudahMuat, onUbah: ubahCtaPilihan }}
                    />
                  )}

                  {/* ★ v3.9.4 — AI LABEL (TikTok + Facebook). Satu suis untuk kedua-dua
                      tempat (keputusan user 2026-09-11), lalai HIDUP, disimpan per-browser.
                      Sampai ke robot melalui `tetapan.aiLabel`. */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 15, paddingBottom: 16, borderBottom: '1px solid var(--s-line)', marginBottom: 16 }}>
                    <p className="s-sect__title">{tCP('workspace.page.sect.aiLabel', 'AI Label')}</p>
                    {barisSuisV39({
                      kunci: 'ailabel', label: tCP('workspace.post.aiLabel', 'Add AI label'),
                      nota: aiLabelAktif
                        ? tCP('workspace.post.aiLabelOn', 'Robot tandakan setiap video sebagai kandungan AI (TikTok: AI-generated content · Facebook: Add AI label).')
                        : tCP('workspace.post.aiLabelOff', 'Robot tidak menandakan video sebagai kandungan AI. Platform boleh mengambil tindakan pada kandungan AI yang tidak dilabel.'),
                      on: aiLabelAktif, onChange: tukarAiLabel,
                    })}
                  </div>

                  {/* ★ [2026-10-13] TIKTOK CHECKS — Music copyright check + Content check lite
                      (TikTok sahaja; lalai HIDUP). Rupa + simpanan dalam komponen sendiri;
                      sampai ke robot melalui `tetapan` (utils/semakanTiktok.js). */}
                  {postToBerkesan !== 'facebook' && (
                    <SemakanTiktokV39 t={tCP} barisSuis={barisSuisV39} />
                  )}

                  {/* ★ [2026-11-04] PHOTO POST TIKTOK (PLAN_TIKTOK_POST_GAMBAR.md) — WORKSPACE v4 MOD NEW SAHAJA
                      (arahan user: "pastikan ia suis v4 mod new"); `generateV2.aktif` sendiri = admin sahaja.
                      ⚠️ KEMBAR pagar: WorkspaceView hanya menghormati pilihan ini bila `slotTabV4` wujud (= v4
                      hidup) — suis tersorok TIDAK boleh menukar larian secara senyap. Buka kepada pelanggan =
                      ZIP extension yang faham `jenisPost` + gate versi + ubah pagar di DUA tempat itu. */}
                  {generateV2.aktif && postToBerkesan !== 'facebook' && (
                    <JenisPostTiktokV39 t={tCP} barisSuis={barisSuisV39} />
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 15, marginBottom: 16 }}>
                    <p className="s-sect__title">{(postToBerkesan === 'facebook' || (uiV39 && wsTab === 'tiktok')) ? tCP('workspace.cara.title', 'Cara post') : tCP('workspace.page.sect.schedule', 'Schedule')}</p>
                    {(postToBerkesan === 'facebook' || (uiV39 && wsTab === 'tiktok')) ? (
                      /* ★ [2026-09-16] SATU pemilih tiga cara untuk Facebook (mana-mana tab)
                         dan TikTok di tab Schedule — menggantikan suis Direct Schedule
                         (keputusan user 12 Sept). Facebook di tab lain = tick kad, jadi
                         "Jadual" berkunci di situ (tiada waktu untuk dijadualkan). Showcase
                         Only tidak memuat naik video → pemilih dimatikan, Run tidak terkunci.
                         Nilai + simpanan: `caraPost` / `pilihCaraPost` di atas. */
                      <CaraPostV39
                        nilai={caraPost}
                        onPilih={pilihCaraPost}
                        t={tCP}
                        platform={postToBerkesan}
                        kalendar={uiV39 && wsTab === 'tiktok'}
                        dimatikan={postToBerkesan !== 'facebook' && showcaseOnlyBerkesan}
                      />
                    ) : (
                      barisSuisV39({
                        kunci: 'scheduled', label: tCP('workspace.page.post.scheduledPost', 'Scheduled Post'),
                        /* ⚠️ Nota ini WAJIB mengikut keadaan Direct Schedule. Tanpa
                           syarat itu ia menjanjikan TikTok akan menyiarkan video,
                           sedangkan pada tetapan lalai (Direct Schedule OFF) video
                           hanya disimpan sebagai draf dan tidak tersiar langsung —
                           dua ayat bercanggah pada satu skrin yang sama. */
                        nota: directSchedule
                          ? tCP('workspace.post.scheduledNote', 'Setiap video mendapat tarikh dan jamnya sendiri. TikTok yang menyiarkannya kemudian — browser tidak perlu dibuka pada waktu itu.')
                          : tCP('workspace.post.scheduledNoteOff', 'Setiap video mendapat tarikh dan jamnya sendiri. Hidupkan Direct Schedule di bawah jika anda mahu TikTok menyiarkannya sendiri.'),
                        on: isScheduled, disabled: showcaseOnlyBerkesan, onChange: tukarSchedule,
                      })
                    )}
                  </div>
                  {/* ⬆ hujung seksyen extension yang disorok bila mod telefon ON */}
                  </>)}
                </>
                ) : (
                <>
                {/* Toggles Grid (3 Columns) */}
                <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                  {/* Post Only Toggle */}
                  <label style={{ 
                    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer',
                    padding: '8px 4px', borderRadius: 10,
                    background: isPostOnly ? '#fff1f2' : '#ffffff',
                    border: `1px solid ${isPostOnly ? '#f43f5e' : '#cbd5e1'}`,
                    transition: 'all 0.2s',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                  }}>
                    <div style={{ 
                      width: 28, height: 16, borderRadius: 8, 
                      background: isPostOnly ? '#f43f5e' : '#cbd5e1',
                      position: 'relative', transition: 'all 0.2s', flexShrink: 0
                    }}>
                      <div style={{ 
                        width: 12, height: 12, borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: 2, left: isPostOnly ? 14 : 2,
                        transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                      }} />
                    </div>
                    <input type="checkbox" style={{ display: 'none' }}
                      checked={isPostOnly}
                      onChange={tukarPostOnly}
                    />
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#334155', lineHeight: 1.1 }}>Post Only</div>
                      <div style={{ fontSize: 8, fontWeight: 600, color: isPostOnly ? '#f43f5e' : '#94a3b8' }}>
                        {isPostOnly ? 'Skip' : 'Full'}
                      </div>
                    </div>
                  </label>

                  {/* Showcase Toggle */}
                  <label style={{ 
                    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer',
                    padding: '8px 4px', borderRadius: 10,
                    background: isShowcaseOnly ? '#fffbeb' : '#ffffff',
                    border: `1px solid ${isShowcaseOnly ? '#f59e0b' : '#cbd5e1'}`,
                    transition: 'all 0.2s',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                  }}>
                    <div style={{ 
                      width: 28, height: 16, borderRadius: 8, 
                      background: isShowcaseOnly ? '#f59e0b' : '#cbd5e1',
                      position: 'relative', transition: 'all 0.2s', flexShrink: 0
                    }}>
                      <div style={{ 
                        width: 12, height: 12, borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: 2, left: isShowcaseOnly ? 14 : 2,
                        transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                      }} />
                    </div>
                    <input type="checkbox" style={{ display: 'none' }}
                      checked={isShowcaseOnly}
                      onChange={tukarShowcase}
                    />
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#334155', lineHeight: 1.1 }}>Showcase</div>
                      <div style={{ fontSize: 8, fontWeight: 600, color: isShowcaseOnly ? '#f59e0b' : '#94a3b8' }}>
                        {isShowcaseOnly ? 'Sahaja' : 'Full'}
                      </div>
                    </div>
                  </label>

                  {/* Schedule Toggle */}
                  <label style={{
                    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer',
                    padding: '8px 4px', borderRadius: 10,
                    background: isScheduled ? '#fff1f2' : '#ffffff',
                    border: `1px solid ${isScheduled ? '#f43f5e' : '#cbd5e1'}`,
                    transition: 'all 0.2s',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                  }}>
                    <div style={{
                      width: 28, height: 16, borderRadius: 8,
                      background: isScheduled ? '#f43f5e' : '#cbd5e1',
                      position: 'relative', transition: 'all 0.2s', flexShrink: 0
                    }}>
                      <div style={{
                        width: 12, height: 12, borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: 2, left: isScheduled ? 14 : 2,
                        transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                      }} />
                    </div>
                    <input type="checkbox" style={{ display: 'none' }}
                      checked={isScheduled}
                      onChange={tukarSchedule}
                    />
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#334155', lineHeight: 1.1 }}>Schedule</div>
                      <div style={{ fontSize: 8, fontWeight: 600, color: isScheduled ? '#f43f5e' : '#94a3b8' }}>
                        {isScheduled ? 'Active' : 'Off'}
                      </div>
                    </div>
                  </label>
                </div>
                </>
                )}

                {/* Conditional Scheduling Panel — 2-column grid.
                    v3.9: bentuk yang sama (dua lajur, Start dan Interval)
                    tetapi memakai medan v3.9. Rupa lama membawa kotak putih
                    bergaris rose dengan teks 8px — pada tema gelap jaring
                    keselamatan ui.css menukar latarnya sahaja, jadi yang
                    tinggal ialah bingkai merah jambu dan tulisan sekecil
                    setengah saiz label jirannya. */}
                {/* Start + Interval = mod LAMA (masa mula + selang). Dalam mod
                    kalendar (tab Schedule) waktu datang dari slot, jadi blok ini
                    disorok — Direct Schedule sudah dipapar di atas. */}
                {!phoneMode && isScheduled && uiV39 && wsTab !== 'tiktok' && (
                  <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                    <div>
                      <span className="s-lbl">Start</span>
                      <input
                        className="s-input"
                        type="datetime-local"
                        value={scheduledStartTime}
                        onChange={(e) => setScheduledStartTime(e.target.value)}
                        step={300}
                        min={jadualMasaMin}
                        max={jadualMasaMax}
                      />
                    </div>
                    <div>
                      <span className="s-lbl">Interval</span>
                      <PilihV39
                        ariaLabel="Interval"
                        nilai={String(scheduledInterval)}
                        onPilih={setScheduledInterval}
                        pilihan={[
                          { id: '15', label: '15 min' },
                          { id: '30', label: '30 min' },
                          { id: '60', label: '1 hour' },
                          { id: '120', label: '2 hours' },
                        ]}
                      />
                    </div>
                  </div>

                  {/* Direct Schedule — lihat `tukarDirectSchedule`. Ia duduk
                      DI DALAM panel jadual, bukan di sebelah suis Schedule,
                      supaya ia hanya wujud pada skrin apabila memang ada masa
                      untuk dijadualkan. */}
                  <div style={{ marginBottom: 16 }}>
                    {barisSuisV39({
                      kunci: 'directschedule', label: 'Direct Schedule',
                      nota: directSchedule
                        ? 'Video akan dijadualkan terus di TikTok dan tersiar sendiri pada masa dipilih.'
                        : 'Robot akan menetapkan masa jadual tetapi menyimpan sebagai draf sahaja.',
                      on: directSchedule, onChange: tukarDirectSchedule,
                    })}
                  </div>
                  </>
                )}
                {isScheduled && !uiV39 && (
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6,
                    padding: 8, background: '#ffffff', borderRadius: 10, border: '1px solid #f43f5e',
                    marginBottom: 14, boxShadow: '0 2px 8px rgba(244,63,94,0.1)'
                  }}>
                    <div>
                      <div style={{ fontSize: 8, fontWeight: 800, color: '#f43f5e', textTransform: 'uppercase', marginBottom: 3 }}>Start</div>
                      <input
                        type="datetime-local"
                        value={scheduledStartTime}
                        onChange={(e) => setScheduledStartTime(e.target.value)}
                        step={300}
                        min={jadualMasaMin}
                        max={jadualMasaMax}
                        style={{
                          width: '100%', padding: '5px 6px', borderRadius: 6, border: '1px solid #fecdd3',
                          fontSize: 10, color: '#1e293b', background: '#fff5f5',
                          fontFamily: 'inherit'
                        }}
                      />
                    </div>
                    <div>
                      <div style={{ fontSize: 8, fontWeight: 800, color: '#f43f5e', textTransform: 'uppercase', marginBottom: 3 }}>Interval</div>
                      <select
                        value={scheduledInterval}
                        onChange={(e) => setScheduledInterval(e.target.value)}
                        style={{
                          width: '100%', padding: '5px 6px', borderRadius: 6, border: '1px solid #fecdd3',
                          fontSize: 10, color: '#1e293b', background: '#fff5f5',
                          fontFamily: 'inherit', cursor: 'pointer'
                        }}
                      >
                        <option value="15">15 min</option>
                        <option value="30">30 min</option>
                        <option value="60">1 hour</option>
                        <option value="120">2 hours</option>
                      </select>
                    </div>

                    {/* Direct Schedule — lihat `tukarDirectSchedule`. Ia duduk
                        DI DALAM panel jadual, bukan di sebelah suis Schedule,
                        supaya ia hanya wujud pada skrin apabila memang ada
                        masa untuk dijadualkan. */}
                    <label style={{
                      gridColumn: '1 / -1', display: 'flex', alignItems: 'flex-start', gap: 8,
                      cursor: 'pointer', marginTop: 2, paddingTop: 8, borderTop: '1px solid #fee2e2'
                    }}>
                      <div style={{
                        width: 28, height: 16, borderRadius: 8, marginTop: 1,
                        background: directSchedule ? '#f43f5e' : '#cbd5e1',
                        position: 'relative', transition: 'all 0.2s', flexShrink: 0
                      }}>
                        <div style={{
                          width: 12, height: 12, borderRadius: '50%', background: '#fff',
                          position: 'absolute', top: 2, left: directSchedule ? 14 : 2,
                          transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                        }} />
                      </div>
                      <input type="checkbox" style={{ display: 'none' }}
                        checked={directSchedule}
                        onChange={tukarDirectSchedule}
                      />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 9, fontWeight: 800, color: '#f43f5e', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Direct Schedule</div>
                        <div style={{ fontSize: 9, fontWeight: 600, color: '#64748b', lineHeight: 1.35, marginTop: 2 }}>
                          {directSchedule
                            ? 'Video akan dijadualkan terus di TikTok dan tersiar sendiri pada masa dipilih.'
                            : 'Robot akan menetapkan masa jadual tetapi menyimpan sebagai draf sahaja.'}
                        </div>
                      </div>
                    </label>
                  </div>
                )}

                {/* Sets + Run TikTok moved to sticky footer below */}
                <div style={{ display: 'none' }}>
                  {/* Pill-style Set Selector Container */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 2,
                    background: '#ffffff', padding: '2px 8px 2px 10px', borderRadius: 20,
                    border: '1px solid #fecdd3', boxShadow: '0 1px 2px rgba(244,63,94,0.05)'
                  }}>
                    <span style={{ fontSize: 9, fontWeight: 800, color: '#f43f5e', textTransform: 'uppercase', letterSpacing: '0.04em', marginRight: 6 }}>Sets</span>
                    {['set1', 'set2', 'set3'].map(sid => {
                      const isActive = tiktokTargetSets.includes(sid);
                      const label = sid.toUpperCase().replace('SET', 'S');
                      return (
                        <button
                          key={sid}
                          onClick={() => {
                            if (isActive) setTiktokTargetSets(prev => prev.filter(s => s !== sid));
                            else setTiktokTargetSets(prev => [...prev, sid].sort());
                          }}
                          style={{
                            width: 28, height: 28, borderRadius: '50%', border: 'none',
                            background: isActive ? '#f43f5e' : 'transparent',
                            color: isActive ? '#ffffff' : '#94a3b8', fontSize: 10, fontWeight: 800,
                            cursor: 'pointer', transition: 'all 0.2s',
                            boxShadow: isActive ? '0 2px 6px rgba(244,63,94,0.3)' : 'none',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => (postToBerkesan === 'facebook'
                      ? workspaceViewRef.current?.handleRunFacebook()
                      : workspaceViewRef.current?.handleRunTikTok())}
                    style={{
                      flex: 1, height: 40, background: '#f43f5e',
                      color: '#ffffff', borderRadius: 10, border: 'none', fontWeight: 800, fontSize: 12,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      boxShadow: '0 4px 12px rgba(244,63,94,0.3)', transition: 'all 0.2s',
                      textTransform: 'uppercase', letterSpacing: '0.02em'
                    }}
                    onMouseDown={e => e.currentTarget.style.transform = 'scale(0.98)'}
                    onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>music_note</span>
                    Run TikTok
                  </button>
                </div>
              </>
            )}
          </div>

          {/* ★ v3.0.0: Sticky footer — Sets + Run button always visible */}
          {/* Jalur kaki kad — sama sebabnya dengan garis di atas: #d1d9e6
              ialah warna untuk latar terang. */}
          <div style={{ padding: '10px 16px', borderTop: uiV39 ? '1px solid var(--s-line)' : '1px solid #d1d9e6', background: '#f1f5f9', flexShrink: 0 }}>
            {workspaceTab === 'products' && !isAdmin && RUN_FLOW_MAINTENANCE_MODULES.includes(activeModuleId) && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                background: 'linear-gradient(90deg, #fef3c7 0%, #fde68a 100%)',
                border: '1px solid #fcd34d', borderRadius: 8,
                padding: '6px 12px', marginBottom: 8,
                fontSize: 11, fontWeight: 700, color: '#92400e',
                letterSpacing: '0.01em'
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>build</span>
                {tCP('workspace.run.maintenanceBar', 'Run Flow sedang dalam penyelenggaraan')}
              </div>
            )}
            {(phoneMode && workspaceTab === 'auto') ? (
              /* Mod telefon (v4): butang RUN sebenar — bulk ke app telefon via
                 WebUSB/ADB (handleRunPhone di WorkspaceView). Hantar video semua
                 card ditick ke telefon + buka app. Connect telefon = reconnect
                 (grant dari page Set up/Test). BUKAN kawalan palsu.
                 ⛔ Dipagar `workspaceTab === 'auto'` (tab posting TikTok/FB/
                 Schedule) SAHAJA — arahan user 29 Okt: suis Phone automation
                 hanya tukar butang tab posting. Tab Flow ('products') kekal
                 "Run Flow", tab Agent kekal butang Agent. Toggle + Control
                 Panel telefon memang sudah duduk dalam badan tab 'auto'; yang
                 bocor ke tab lain dahulu cuma footer kongsi ini. */
              <button
                type="button"
                title={!phoneRoute ? 'Choose how to post first' : phoneTiadaPilihan ? 'Select a calendar slot or tick a card first' : undefined}
                onClick={() => { if (!phoneTerkunci) workspaceViewRef.current?.handleRunPhone(); }}
                style={{
                  width: '100%', height: 40, background: 'var(--s-primary)', color: 'var(--s-primary-ink)',
                  border: '1px solid var(--s-primary)', borderRadius: 10, fontWeight: 800, fontSize: 12,
                  opacity: phoneTerkunci ? 0.45 : 1,
                  cursor: phoneTerkunci ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  letterSpacing: '0.02em',
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>smartphone</span>
                {phoneRoute ? 'Post to phone' : 'Choose how to post'}
              </button>
            ) : workspaceTab === 'agent' ? (
              <AgentPanelFootV39
                data={agentPanel}
                smart={smartMode}
                onRun={() => workspaceViewRef.current?.runAgentPanel(smartMode)}
              />
            ) : workspaceTab === 'products' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* Pemilih set jalur kaki — RUPA LAMA sahaja.
                    Dalam v3.9 ia BERPINDAH ke seksyen "Set" dalam badan kad
                    (bentuk prototaip: bulatan besar + nota), jadi ia tidak
                    dirender di sini langsung — bukan sekadar disorok.
                    Logiknya dikongsi: `butangPilihanSet`. */}
                {!uiV39 && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 2,
                  background: '#ffffff', padding: '2px 8px 2px 10px', borderRadius: 20,
                  border: '1px solid #c7d2fe', boxShadow: '0 1px 2px rgba(99,102,241,0.05)'
                }}>
                  <span style={{ fontSize: 9, fontWeight: 800, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.04em', marginRight: 6 }}>{isFlownitySelected ? 'Scenes' : 'Sets'}</span>
                  {butangPilihanSet('lama')}
                </div>
                )}
                {/* Kiraan terpilih — v3.9, di sebelah butang Run seperti
                    prototaip ("No product selected · [Run Flow]"). */}
                {uiV39 && (
                  <span className="s-dim" style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>
                    {headerSelectedCount === 0 ? tCP('workspace.page.run.noSelection', 'No product selected') : tCP('workspace.page.run.selectedCount', `${headerSelectedCount} selected`, { n: headerSelectedCount })}
                  </span>
                )}
                <button
                  onClick={() => {
                    // ★ Maintenance check dialihkan ke handleRunFlow (product-level).
                    //   Semakan Workflow "Guna gambar card" (module disokong, had gambar Veo) juga di sana.
                    // App.jsx tak ada akses ke productsToRun — enforcement di WorkspaceView.
                    workspaceViewRef.current?.handleRunFlow();
                  }}
                  style={{
                    flex: 1, height: 40, background: '#6366f1',
                    color: '#ffffff', borderRadius: 10, border: 'none', fontWeight: 800, fontSize: 12,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    boxShadow: '0 4px 12px rgba(99,102,241,0.3)', transition: 'all 0.2s',
                    textTransform: 'uppercase', letterSpacing: '0.02em'
                  }}
                  onMouseDown={e => e.currentTarget.style.transform = 'scale(0.98)'}
                  onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>play_arrow</span>
                  {hostedBetaAktif
                    ? tCP('hosted.submit', 'Submit')   /* ★ Hosted Flow Beta — job ke PC ofis (bukan extension) */
                    : wfKad
                    ? tCP('workspace.workflow.runLabel', 'Run Flow · Card images')
                    : tCP('workspace.page.run.runFlow', 'Run Flow')}
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* ⛔ Pemilih set tab Schedule DISOROK dalam v3.9 (arahan user
                    2026-08-14) — bukan dibuang, kerana Run TikTok masih
                    membacanya dan belum ada cara lain menetapkan set untuk
                    penjadualan. Ia kembali sebaik `!uiV39`, dan tinggal satu
                    baris untuk dihidupkan semula bila kaedah jadual dipilih. */}
                {!uiV39 && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 2,
                  background: '#ffffff', padding: '2px 8px 2px 10px', borderRadius: 20,
                  border: '1px solid #fecdd3', boxShadow: '0 1px 2px rgba(244,63,94,0.05)'
                }}>
                  <span style={{ fontSize: 9, fontWeight: 800, color: '#f43f5e', textTransform: 'uppercase', letterSpacing: '0.04em', marginRight: 6 }}>Sets</span>
                  {['set1', 'set2', 'set3'].map(sid => {
                    const isActive = tiktokTargetSets.includes(sid);
                    const label = sid.toUpperCase().replace('SET', 'S');
                    return (
                      <button
                        key={sid}
                        onClick={() => {
                          if (isActive) setTiktokTargetSets(prev => prev.filter(s => s !== sid));
                          else setTiktokTargetSets(prev => [...prev, sid].sort());
                        }}
                        style={{
                          width: 28, height: 28, borderRadius: '50%', border: 'none',
                          background: isActive ? '#f43f5e' : 'transparent',
                          color: isActive ? '#ffffff' : '#94a3b8', fontSize: 10, fontWeight: 800,
                          cursor: 'pointer', transition: 'all 0.2s',
                          boxShadow: isActive ? '0 2px 6px rgba(244,63,94,0.3)' : 'none',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                      >{label}</button>
                    );
                  })}
                </div>
                )}
                {uiV39 && (
                  <span className="s-dim" style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>
                    {/* Langkah 3: di mod Schedule, bar bawah papar sumber KALENDAR
                        (bukan kad bertick) supaya pengguna nampak apa yang akan
                        jalan sebelum tekan Run.
                        ★ v4: Facebook TIADA jadual — ia baca kad bertick, jadi bar
                        papar kiraan kad (bukan slot kalendar) walaupun di tab Schedule. */}
                    {/* ★ [2026-09-16] Facebook turut membaca kalendar di tab Schedule
                        (Bahagian 4) — kiraan slot dipakai untuk KEDUA-DUA platform. */}
                    {schedRunInfo
                      ? (schedRunInfo.bil === 0
                          ? tCP('ws.sched.run.barKosong', 'No slots selected')
                          : tCP('ws.sched.run.bar', `${schedRunInfo.bil} videos · ${schedRunInfo.julat}`, { n: schedRunInfo.bil, julat: schedRunInfo.julat }))
                      : (headerSelectedCount === 0 ? tCP('workspace.page.run.noSelection', 'No product selected') : tCP('workspace.page.run.selectedCount', `${headerSelectedCount} selected`, { n: headerSelectedCount }))}
                  </span>
                )}
                <button
                  disabled={runTerkunci}
                  aria-disabled={runTerkunci}
                  title={runTerkunciCara ? tCP('workspace.cara.pilihDulu', 'Pilih satu cara dahulu — butang Run terkunci sehingga dipilih.')
                    : runTerkunciSlot ? tCP('ws.sched.run.noPilih', 'Sila pilih sekurang-kurangnya satu slot sebelum Run — tick kepala hari untuk pilih semua slot hari itu, atau klik slot satu-satu.')
                    : undefined}
                  onClick={() => { if (runTerkunci) return; (postToBerkesan === 'facebook'
                    ? workspaceViewRef.current?.handleRunFacebook()
                    : workspaceViewRef.current?.handleRunTikTok()); }}
                  style={{
                    flex: 1, height: 40, background: '#f43f5e', opacity: runTerkunci ? 0.45 : 1,
                    color: '#ffffff', borderRadius: 10, border: 'none', fontWeight: 800, fontSize: 12,
                    cursor: runTerkunci ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    boxShadow: '0 4px 12px rgba(244,63,94,0.3)', transition: 'all 0.2s',
                    textTransform: 'uppercase', letterSpacing: '0.02em'
                  }}
                  onMouseDown={e => e.currentTarget.style.transform = 'scale(0.98)'}
                  onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{postToBerkesan === 'facebook' ? 'movie' : 'music_note'}</span>
                  {runTerkunciCara
                    ? tCP('workspace.cara.runKunci', 'Pilih cara post')
                    : postToBerkesan === 'facebook'
                    ? tCP('workspace.page.run.runFacebook', 'Run Facebook')
                    : tCP('workspace.page.run.runTiktok', 'Run TikTok')}
                </button>
              </div>
            )}
          </div>
        </aside>
        </Portal>
      )}

      {/* Main Content Column (Header + Workspace) */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        {/* Sticky Header — constrained to workspace width */}
        {/* Header kongsi: render HANYA bila ada kandungan sebenar — hamburger (mobile) atau coin counter (/rewrite).
            Page lain (dashboard, affiliate, profile, admin, ai-instructions) ada layout/header sendiri,
            jadi header ni kosong di desktop → sorok. */}
        {(isMobile || location.pathname === '/rewrite')
          && !isOnSpacePage
          && !location.pathname.startsWith('/pricing')
          && !ROUTES_WITH_OWN_MENU_BUTTON.some(p => location.pathname.startsWith(p)) && (
        <header className={`sticky top-0 z-40 w-full shrink-0 ${isOnSpacePage ? 'glass-header' : 'bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800'}`}>
        <div className="w-full px-3 md:px-6 py-3 md:py-4 flex items-center justify-between gap-3 md:gap-6">
          {/* Left: Hamburger on mobile, spacer on desktop */}
          <div className="shrink-0">
            {isMobile ? (
              <button
                onClick={() => setIsMobileSidebarOpen(true)}
                className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <span className="material-symbols-outlined" style={{ fontSize: 24, color: '#475569' }}>menu</span>
              </button>
            ) : (
              <div className="w-10" />
            )}
          </div>

          {/* Center: Search & Add — only on space pages */}
          {isOnSpacePage && (
            <div className="flex-1 max-w-3xl flex items-center gap-3">
              <div className="relative flex-1 group">
                <div className="flex items-center w-full min-h-[40px] px-2 py-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 group-focus-within:border-blue-500/50 group-focus-within:ring-4 group-focus-within:ring-blue-500/5">
                  {/* Library Tab Button */}
                  <div className="shrink-0 flex items-center gap-1 mr-2">
                    <button
                      onClick={() => setShowLibraryModal(true)}
                      className="px-3 py-1 rounded-lg text-[10px] font-bold bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 text-violet-600 dark:text-violet-400 border border-violet-200/60 dark:border-violet-800/50 hover:from-violet-100 hover:to-purple-100 dark:hover:from-violet-900/30 dark:hover:to-purple-900/30 active:scale-[0.97] transition-all"
                    >
                      Library
                    </button>
                  </div>
                  {/* Textarea */}
                  <textarea
                    value={headerProductInput}
                    onChange={(e) => setHeaderProductInput(e.target.value)}
                    className="flex-1 bg-transparent border-none focus:ring-0 focus:outline-none outline-none text-[11px] text-slate-700 dark:text-slate-200 placeholder:text-slate-400 font-bold py-0 resize-none overflow-hidden whitespace-nowrap leading-6"
                    placeholder="Add new product data here.."
                    rows={1}
                    style={{ height: '24px' }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleHeaderAddProduct();
                      }
                    }}
                  />
                  {/* AI Logic Dropdown */}
                  <div className="shrink-0 flex items-center ml-2 pl-2">
                    {isLogicLocked ? (
                      /* CLIENT on AI8SAAT */
                      <div className="relative flex items-center gap-1.5 bg-gradient-to-r from-violet-50 via-purple-50 to-indigo-50 dark:from-violet-900/30 dark:via-purple-900/25 dark:to-indigo-900/30 border border-violet-200/70 dark:border-violet-700/40 rounded-full px-2.5 py-1 hover:shadow-md hover:shadow-violet-200/40 dark:hover:shadow-violet-900/20 hover:border-violet-300 dark:hover:border-violet-600/50 transition-all duration-300 cursor-pointer group">
                        <span className="material-symbols-outlined text-[13px] text-violet-500 dark:text-violet-400 group-hover:text-violet-600 transition-colors" style={{ fontVariationSettings: "'FILL' 1" }}>neurology</span>
                        <select
                          value={headerSelectedLogicId}
                          onChange={(e) => setHeaderSelectedLogicId(e.target.value)}
                          className="bg-transparent border-none text-[10px] font-bold focus:ring-0 focus:outline-none appearance-none cursor-pointer text-violet-700 dark:text-violet-300 pr-4 max-w-[110px] truncate"
                        >
                          {headerSelectedLogicId && headerSelectedLogicId !== '__data_only__' && (
                            <option value={headerSelectedLogicId}>
                              {headerLogicOptions.find(opt => opt.id === headerSelectedLogicId)?.name || systemDefaultLogicName}
                            </option>
                          )}
                          <option value="__data_only__">Data Sahaja</option>
                        </select>
                        <span className="material-symbols-outlined absolute right-2 top-1/2 -translate-y-1/2 text-[12px] text-violet-400 dark:text-violet-500 pointer-events-none">expand_more</span>
                      </div>
                    ) : (
                      /* UNLOCKED: Admin or non-default space */
                      <div className="relative flex items-center gap-1.5 bg-gradient-to-r from-violet-50 via-purple-50 to-indigo-50 dark:from-violet-900/30 dark:via-purple-900/25 dark:to-indigo-900/30 border border-violet-200/70 dark:border-violet-700/40 rounded-full px-2.5 py-1 hover:shadow-md hover:shadow-violet-200/40 dark:hover:shadow-violet-900/20 hover:border-violet-300 dark:hover:border-violet-600/50 transition-all duration-300 cursor-pointer group">
                        <span className="material-symbols-outlined text-[13px] text-violet-500 dark:text-violet-400 group-hover:text-violet-600 transition-colors" style={{ fontVariationSettings: "'FILL' 1" }}>neurology</span>
                        <select
                          value={headerSelectedLogicId}
                          onChange={(e) => setHeaderSelectedLogicId(e.target.value)}
                          className="bg-transparent border-none text-[10px] font-bold focus:ring-0 focus:outline-none appearance-none cursor-pointer text-violet-700 dark:text-violet-300 pr-4 max-w-[110px] truncate"
                        >
                          {headerLogicOptions.length > 0 ? (
                            <>
                              <option value="__data_only__">Data Sahaja</option>
                              {headerLogicOptions.map(opt => (
                                <option key={opt.id} value={opt.id}>{opt.name}</option>
                              ))}
                            </>
                          ) : (
                            <option value="">No Logic</option>
                          )}
                        </select>
                        <span className="material-symbols-outlined absolute right-2 top-1/2 -translate-y-1/2 text-[12px] text-violet-400 dark:text-violet-500 pointer-events-none">expand_more</span>
                      </div>
                    )}
                  </div>
                  {/* Add Button — GOYANG: block kalau takda template */}
                  {(() => {
                    const goyangNeedsTemplate = activeModuleId === 'goyang' && !selectedTemplate;
                    return (
                      <button
                        disabled={goyangNeedsTemplate}
                        onClick={() => { if (goyangNeedsTemplate) return; handleHeaderAddProduct(); }}
                        title={goyangNeedsTemplate ? 'Pilih template GOYANG dulu sebelum generate' : ''}
                        className={`shrink-0 flex items-center gap-1 ml-2 h-7 px-3 rounded-lg text-white text-[11px] font-bold shadow-sm transition-all ${goyangNeedsTemplate
                          ? 'bg-slate-400 cursor-not-allowed opacity-75'
                          : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 active:scale-[0.98]'}`}
                      >
                        <span className="material-symbols-outlined text-[15px] font-bold">{goyangNeedsTemplate ? 'lock' : 'add'}</span>
                        {goyangNeedsTemplate ? 'Pilih template' : 'Generate'}
                      </button>
                    );
                  })()}
                </div>
              </div>

              {/* Panel Visibility Toggle — Segmented Pill */}
              <div className="flex items-center h-7 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm overflow-hidden shrink-0">
                <button
                  onClick={toggleHideFlowPanel}
                  className={`flex items-center gap-1 h-full px-2.5 text-[11px] font-bold transition-all duration-200 whitespace-nowrap border-r border-slate-200 dark:border-slate-700 ${!hideFlowPanel
                    ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                    : 'text-slate-400 dark:text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-600'
                    }`}
                  title={hideFlowPanel ? 'Show Flow Panel' : 'Hide Flow Panel'}
                >
                  <span className="material-symbols-outlined text-[13px]" style={{ fontVariationSettings: !hideFlowPanel ? "'FILL' 1" : undefined }}>
                    {!hideFlowPanel ? 'visibility' : 'visibility_off'}
                  </span>
                  Flow
                </button>
                <button
                  onClick={toggleHideTikTokPanel}
                  className={`flex items-center gap-1 h-full px-2.5 text-[11px] font-bold transition-all duration-200 whitespace-nowrap ${!hideTikTokPanel
                    ? 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400'
                    : 'text-slate-400 dark:text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-600'
                    }`}
                  title={hideTikTokPanel ? 'Show TikTok Panel' : 'Hide TikTok Panel'}
                >
                  <span className="material-symbols-outlined text-[13px]" style={{ fontVariationSettings: !hideTikTokPanel ? "'FILL' 1" : undefined }}>
                    {!hideTikTokPanel ? 'visibility' : 'visibility_off'}
                  </span>
                  TikTok
                </button>
              </div>
            </div>
          )}

          {/* Right: Coin only if /rewrite */}
          <div className="flex items-center gap-4 shrink-0">
            {location.pathname === '/rewrite' && <CoinDisplay compact />}
          </div>
        </div>
      </header>
      )}

      {/* ═══ Upgrade Banner — trial users sahaja (admin/moderator/paid bypass) ═══ */}
      {user && userTier === 'trial' && isOnSpacePage && (
        <div style={{
          background: 'linear-gradient(90deg, #f59e0b 0%, #f97316 50%, #ea580c 100%)',
          color: '#ffffff',
          padding: '12px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12,
          flexShrink: 0,
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 2px 8px rgba(245,158,11,0.25)',
          zIndex: 30,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 24, fontVariationSettings: "'FILL' 1", flexShrink: 0 }}>workspace_premium</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.25, letterSpacing: '-0.01em' }}>
                Upgrade ke Pakej Premium SESAAT
              </div>
              <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.95, marginTop: 2, lineHeight: 1.3 }}>
                Akses penuh 3 Teknik + Tools Automation tanpa had penggunaan
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate('/pricing')}
            style={{
              background: '#ffffff',
              color: '#ea580c',
              border: 'none',
              borderRadius: 10,
              padding: '9px 16px',
              fontSize: 13,
              fontWeight: 800,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
              boxShadow: '0 3px 10px rgba(0,0,0,0.18)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 5px 14px rgba(0,0,0,0.22)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 3px 10px rgba(0,0,0,0.18)'; }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16, fontVariationSettings: "'FILL' 1" }}>bolt</span>
            Langgan Sekarang
          </button>
        </div>
      )}

      {/* Main Workspace Area */}
      {/* Corak bintik `dotted-canvas` dilukis dengan kelabu cerah (#cbd5e1) —
          di atas latar gelap v3.9 ia bertukar menjadi bintik-bintik pucat yang
          menonjol, dan prototaip memang tiada corak langsung. Jadi dalam v3.9
          kanvas dibiar rata; latar gelap datang daripada `s-root` di atas. */}
      <main className={`flex-1 flex flex-col min-w-0 overflow-hidden relative ${isOnSpacePage ? (uiV39 ? '' : 'dotted-canvas') : 'bg-slate-50 dark:bg-slate-950/50'}`}>
          {/* ── Kepala Workspace, RUPA LAMA sahaja ──────────────────────
              Blok terapung di atas kanvas, kekal seperti sebelum ini.

              ⛔ v3.9 TIDAK lagi melalui sini. Kepalanya ialah
                 `kepalaWorkspaceV39` yang dipasang DI DALAM bingkai kongsi
                 melalui `BingkaiIsi` — di situlah tempat untuk mengubah
                 tajuk, pemilih group atau jarak tepi bagi rupa baharu.

              Sejarah, supaya ia tidak dipasang balik: dahulu blok ini
              melayan KEDUA-DUA rupa, dengan v3.9 diletak pada `top: 37` dan
              `left: 51` — angka yang dijajarkan dengan tangan pada senarai
              produk, bukan pada halaman v3.9 yang lain (yang semuanya 26px
              dan 28px). Itulah punca jarak Workspace tidak pernah sepadan
              dengan Extension atau Marketplace, dan punca padding senarai
              tidak boleh disentuh tanpa mengukur semula tajuk.

              Rujukan `uiV39` yang masih tinggal DI DALAM blok ini sentiasa
              palsu sekarang; ia dibiar supaya diff kekal kecil, bukan kerana
              ia bermakna. */}
          {isOnSpacePage && !workspaceLocked && !uiV39 && (
            <div style={{
              position: 'absolute', right: 16, top: 16, left: 16,
              display: 'flex', alignItems: 'center', zIndex: 2000, pointerEvents: 'none',
            }}>
              {/* Jalur kepala — DUA kad berasingan: butang panel, dan pemilih
                  group. Dahulunya kedua-duanya berkongsi satu kad, jadi ia
                  kelihatan seperti satu kawalan bercantum.

                  ⛔ DIPISAHKAN atas permintaan user 2026-08-10 KERANA butang
                     "sembunyi panel" akan DIBUANG. Dengan pemisahan ini,
                     membuangnya nanti bermakna memadam satu blok bersih —
                     pemilih group langsung tidak perlu disentuh, jadi tiada
                     risiko tersilap merosakkannya semasa membuang.

                  v3.9: latar kad bergaris nipis, bukan petak putih pejal yang
                  terapung atas kanvas gelap. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, pointerEvents: 'auto' }}>

              {/* ── Kad 1: butang panel ──
                  ⛔ DIBUANG dalam v3.9 (arahan user 2026-08-11). Di sana panel
                     larian muncul mengikut TAB (Flow · Schedule) dan hilang
                     pada tab lain, jadi butang buka/tutup tidak lagi bermakna:
                     ia hanya boleh melawan keputusan yang tab sudah buat.
                     Rupa lama KEKAL ada butang ini.
                  Hamburger mobile dikekalkan pada kedua-dua rupa — ia membuka
                  menu tepi, bukan panel larian. */}
              {(!uiV39 || isMobile) && (
              <div style={{
                display: 'flex', alignItems: 'center',
                background: uiV39 ? 'var(--s-card)' : '#ffffff',
                border: uiV39 ? '1px solid var(--s-line)' : undefined,
                padding: '4px 8px', borderRadius: 12, gap: 4,
              }}>
                {/* Mobile hamburger for space pages */}
                {isMobile && (
                  <button
                    onClick={() => setIsMobileSidebarOpen(true)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#475569' }}>menu</span>
                  </button>
                )}
                {isMobile && uiV39 ? null : isMobile ? (
                  <button
                    onClick={() => setIsLeftPanelVisible(!isLeftPanelVisible)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors bg-indigo-50 text-indigo-600"
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                      {isLeftPanelVisible ? 'close' : 'add_circle'}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 700 }}>
                      {isLeftPanelVisible ? 'Tutup' : 'Generate'}
                    </span>
                  </button>
                ) : (
                  <button
                    onClick={() => setIsLeftPanelVisible(!isLeftPanelVisible)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-50 transition-colors text-indigo-600"
                    title={isLeftPanelVisible ? "Sembunyikan Panel" : "Tunjukkan Panel"}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
                      {isLeftPanelVisible ? 'vertical_split' : 'chrome_reader_mode'}
                    </span>
                  </button>
                )}
              </div>
              )}
              {/* ── hujung Kad 1 ── */}

              {/* ── Kad 2: pemilih group (KEKAL) ──
                  v3.9: TIADA kad pembalut. Butang group ialah `s-chip` yang
                  sudah membawa bingkai dan bucunya sendiri — membungkusnya
                  dalam kad lagi menghasilkan DUA lapis bingkai bersarang,
                  dan itu tidak berlaku dalam prototaip (di sana chip berdiri
                  terus di atas latar, bersebelahan tajuk "Workspace").
                  Rupa lama kekal berkad kerana butangnya memang tiada bingkai
                  sendiri — tanpa kad ia terapung tanpa bentuk. */}
              <div style={{
                display: 'flex', alignItems: 'center',
                ...(uiV39 ? { gap: 12 } : { background: '#ffffff', padding: '4px 8px', borderRadius: 12 }),
              }}>

                {/* Tajuk "Workspace" DIPINDAHKAN ke `kepalaWorkspaceV39`.
                    Rupa lama memang tidak pernah memaparkannya di sini — ia
                    dipagar `uiV39 && !isMobile` — jadi tiada apa yang hilang
                    daripada rupa lama.

                    Salinan tangan `<h1>` itu (20px/600, letter-spacing .5px,
                    Cal Sans opsz 46) turut dibuang: ia meniru `.s-title`
                    dengan nilai yang ditaip semula, jadi setiap pelarasan
                    pada tajuk halaman lain terlepas pandang Workspace. Kini
                    ia memakai komponen `PageHeader` yang sama. */}

                {/* ★ v3.0.0 Fasa 6: Group Switcher — dropdown RUPA LAMA.
                    Versi v3.9 (`GroupSwitcherV39`) sudah berpindah ke
                    `kepalaWorkspaceV39`; ia SATU takrifan, tiada salinan
                    kedua di sini.

                    Nota lama "⛔ kedudukan butang kekal di sini; prototaip
                    meletakkannya di dalam PageHeader" tidak lagi terpakai
                    kepada v3.9 — bar terapung yang dimaksudkan nota itu
                    memang sudah tiada pada rupa baharu. Kedudukannya di
                    SKRIN tidak berubah: ia tetap bersebelahan tajuk, di
                    penjuru kiri atas. `sisiTajuk` pada PageHeader memang
                    dibina untuk kes ini (lihat komennya dalam design/ui.jsx).

                    Rupa lama DIKEKALKAN sepenuhnya di bawah — itu yang
                    menjadikan patah balik percuma (tutup suis, tiada deploy). */}
                {showHeaderGroupDropdown && <div className="fixed inset-0 z-[1990]" onClick={() => { setShowHeaderGroupDropdown(false); setHeaderNewGroupInput(false); setHeaderEditingGroup(null); setHeaderEditingGroupName(''); setHeaderDeletingGroup(null); setHeaderDeleteDestination('Workspace'); }} />}
                <div className="relative" style={{ zIndex: 2000 }}>
                  <button
                    onClick={() => setShowHeaderGroupDropdown(prev => !prev)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ml-1 transition-colors min-w-[240px] ${uiV39 ? '' : 'bg-slate-50 hover:bg-slate-100'}`}
                    style={uiV39 ? { background: 'var(--s-fill)' } : undefined}
                  >
                    <span className={`material-symbols-outlined text-[18px] ${uiV39 ? '' : 'text-slate-500'}`} style={uiV39 ? { color: 'var(--s-ink-3)' } : undefined}>folder</span>
                    <span className={`text-[13px] flex-1 text-left truncate ${uiV39 ? '' : 'font-bold text-slate-700'}`} style={uiV39 ? { fontWeight: 500, color: 'var(--s-ink)' } : undefined}>
                      {headerSelectedGroupId === 'all' ? 'Main Group' : headerSelectedGroupId}
                    </span>
                    <span className={`text-[10px] font-bold ${uiV39 ? '' : 'text-slate-400'}`} style={uiV39 ? { color: 'var(--s-ink-3)' } : undefined}>
                      {headerSelectedGroupId === 'all'
                        ? headerGroupCounts['Workspace'] || 0
                        : headerGroupCounts[headerSelectedGroupId] || 0}
                    </span>
                    <span className="material-symbols-outlined text-slate-400 text-[14px]">expand_more</span>
                  </button>
                  {/* Had tinggi + scroll: shell webapp h-screen/overflow-hidden, jadi
                      dropdown yang lebih panjang dari skrin TERPOTONG (tiada scroll page).
                      Dulu "New Group" di hujung senarai — user dgn ~20 group ke atas tak
                      boleh capai butang tu langsung. Kini sticky di ATAS + senarai scroll. */}
                  {showHeaderGroupDropdown && (
                    <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-2xl border border-slate-200 pb-1 z-[2000] min-w-[240px] max-h-[60vh] overflow-y-auto">
                      <div className="sticky top-0 z-10 bg-white pt-1 pb-1 border-b border-slate-100">
                        {headerNewGroupInput ? (
                          <div className="px-2 py-1 flex gap-1">
                            <input
                              autoFocus
                              value={headerNewGroupName}
                              onChange={e => setHeaderNewGroupName(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter' && headerNewGroupName.trim()) {
                                  const name = headerNewGroupName.trim();
                                  workspaceViewRef.current?.createGroup(name);
                                  setHeaderSelectedGroupId(name);
                                  workspaceViewRef.current?.setSelectedGroupId(name);
                                  setShowHeaderGroupDropdown(false);
                                  setHeaderNewGroupInput(false);
                                  setHeaderNewGroupName('');
                                  window.postMessage({ type: 'SESAAT_GROUP_SYNC', groupId: name }, '*');
                                }
                                if (e.key === 'Escape') { setHeaderNewGroupInput(false); setHeaderNewGroupName(''); }
                              }}
                              placeholder="Group name"
                              className="flex-1 text-[11px] px-2 py-1 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                            <button
                              onClick={() => {
                                const name = headerNewGroupName.trim();
                                if (!name) return;
                                workspaceViewRef.current?.createGroup(name);
                                setHeaderSelectedGroupId(name);
                                workspaceViewRef.current?.setSelectedGroupId(name);
                                setShowHeaderGroupDropdown(false);
                                setHeaderNewGroupInput(false);
                                setHeaderNewGroupName('');
                                window.postMessage({ type: 'SESAAT_GROUP_SYNC', groupId: name }, '*');
                              }}
                              className="px-2 py-1 text-[10px] font-bold text-white bg-indigo-500 rounded-lg"
                            >OK</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setHeaderNewGroupInput(true)}
                            className="w-full px-3 py-2 text-left text-[12px] font-bold text-indigo-600 hover:bg-indigo-50 flex items-center gap-2"
                          >
                            <span className="material-symbols-outlined text-[16px]">add</span>
                            New Group
                          </button>
                        )}
                      </div>
                      <button
                        onClick={() => { setHeaderSelectedGroupId('all'); workspaceViewRef.current?.setSelectedGroupId('all'); setShowHeaderGroupDropdown(false); window.postMessage({ type: 'SESAAT_GROUP_SYNC', groupId: 'all' }, '*'); }}
                        className={`w-full px-3 py-2 text-left text-[12px] font-medium flex items-center gap-2 ${headerSelectedGroupId === 'all' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}
                      >
                        <span className="material-symbols-outlined text-[16px]">grid_view</span>
                        <span className="flex-1">Main Group</span>
                        <span className="text-[10px] font-bold text-slate-400">{headerGroupCounts['Workspace'] || 0}</span>
                      </button>
                      {headerGroups.filter(g => g !== 'Workspace').map(g => {
                        if (headerEditingGroup === g) {
                          const commitRename = async () => {
                            const next = headerEditingGroupName.trim();
                            if (!next || next === g) { setHeaderEditingGroup(null); setHeaderEditingGroupName(''); return; }
                            const res = await workspaceViewRef.current?.renameGroup(g, next);
                            if (!res?.ok) {
                              alert(res?.reason === 'duplicate' ? `Nama "${next}" dah digunakan.` : res?.reason === 'reserved' ? `Nama "${next}" tak dibenarkan.` : 'Rename gagal.');
                              return;
                            }
                            if (headerSelectedGroupId === g) setHeaderSelectedGroupId(res.newName);
                            window.postMessage({ type: 'SESAAT_GROUP_SYNC', groupId: headerSelectedGroupId === g ? res.newName : headerSelectedGroupId }, '*');
                            setHeaderEditingGroup(null);
                            setHeaderEditingGroupName('');
                          };
                          return (
                            <div key={g} className="px-2 py-1 flex gap-1 items-center">
                              <input
                                autoFocus
                                value={headerEditingGroupName}
                                onChange={e => setHeaderEditingGroupName(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') commitRename();
                                  if (e.key === 'Escape') { setHeaderEditingGroup(null); setHeaderEditingGroupName(''); }
                                }}
                                placeholder="Group name"
                                className="flex-1 text-[11px] px-2 py-1 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              />
                              <button onClick={commitRename} className="px-2 py-1 text-[10px] font-bold text-white bg-indigo-500 rounded-lg">OK</button>
                            </div>
                          );
                        }
                        if (headerDeletingGroup === g) {
                          const destOptions = [{ value: 'Workspace', label: 'Main Group' }, ...headerGroups.filter(x => x !== 'Workspace' && x !== g).map(x => ({ value: x, label: x }))];
                          const count = workspaceViewRef.current?.getGroupProductCount(g) || 0;
                          const destLabel = headerDeleteDestination === 'Workspace' ? 'Main Group' : headerDeleteDestination;
                          const commitDelete = async () => {
                            if (!confirm(`Delete group "${g}"? ${count} produk akan dipindah ke "${destLabel}".`)) return;
                            if (!confirm(`Reconfirm: padam group "${g}"? Tindakan ini tidak boleh dibatalkan.`)) return;
                            const res = await workspaceViewRef.current?.deleteGroup(g, headerDeleteDestination);
                            if (!res?.ok) { alert('Delete gagal.'); return; }
                            if (headerSelectedGroupId === g) {
                              const nextId = headerDeleteDestination === 'Workspace' ? 'all' : headerDeleteDestination;
                              setHeaderSelectedGroupId(nextId);
                              window.postMessage({ type: 'SESAAT_GROUP_SYNC', groupId: nextId }, '*');
                            }
                            setHeaderDeletingGroup(null);
                            setHeaderDeleteDestination('Workspace');
                          };
                          return (
                            <div key={g} className="px-2 py-1 flex flex-col gap-1 bg-rose-50 rounded-lg">
                              <div className="text-[10px] font-bold text-rose-700 px-1">Padam "{g}" ({count} produk)</div>
                              <div className="flex items-center gap-1">
                                <span className="text-[10px] text-slate-500 px-1">Pindah ke:</span>
                                <select
                                  value={headerDeleteDestination}
                                  onChange={e => setHeaderDeleteDestination(e.target.value)}
                                  className="flex-1 text-[11px] px-1 py-1 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-rose-400 bg-white"
                                >
                                  {destOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                              </div>
                              <div className="flex gap-1">
                                <button onClick={commitDelete} className="flex-1 px-2 py-1 text-[10px] font-bold text-white bg-rose-500 hover:bg-rose-600 rounded-lg">Delete</button>
                                <button onClick={() => { setHeaderDeletingGroup(null); setHeaderDeleteDestination('Workspace'); }} className="px-2 py-1 text-[10px] font-bold text-slate-500 hover:bg-slate-100 rounded-lg">Cancel</button>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div
                            key={g}
                            className={`w-full px-3 py-2 text-left text-[12px] font-medium flex items-center gap-2 cursor-pointer ${headerSelectedGroupId === g ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}
                            onClick={() => { setHeaderSelectedGroupId(g); workspaceViewRef.current?.setSelectedGroupId(g); setShowHeaderGroupDropdown(false); window.postMessage({ type: 'SESAAT_GROUP_SYNC', groupId: g }, '*'); }}
                          >
                            <span className="material-symbols-outlined text-[16px] text-slate-400">folder</span>
                            <span className="flex-1">{g}</span>
                            <span className="text-[10px] font-bold text-slate-400">{headerGroupCounts[g] || 0}</span>
                            <button
                              onClick={(e) => { e.stopPropagation(); setHeaderEditingGroup(g); setHeaderEditingGroupName(g); }}
                              title="Rename"
                              className="opacity-40 hover:opacity-100 transition-opacity"
                            >
                              <span className="material-symbols-outlined text-[14px] text-slate-500 hover:text-indigo-600">edit</span>
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setHeaderDeletingGroup(g); setHeaderDeleteDestination('Workspace'); }}
                              title="Delete"
                              className="opacity-40 hover:opacity-100 transition-opacity"
                            >
                              <span className="material-symbols-outlined text-[14px] text-slate-500 hover:text-rose-600">delete</span>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Modal padam versi v3.9 turut berpindah ke
                    `kepalaWorkspaceV39`. Rupa lama tidak pernah memakainya —
                    ia mengesahkan padam dengan dua `confirm()` browser
                    berturut-turut, dan itu kekal seperti sebelum ini. */}
              </div>
              {/* ── hujung Kad 2 ── */}

              </div>
            </div>
          )}
          <BingkaiHalaman aktif={uiV39 && !tiadaBingkai(location.pathname)}>
          <BingkaiIsi
            berkepala={uiV39 && isOnSpacePage && !workspaceLocked}
            kepala={kepalaWorkspaceV39}
            refKanan={setNodPanelKanan}
            /* ⚠️ `kananAktif` mengikut TAB, bukan `panelLarianNampak`. Bezanya
               penting untuk animasi: semasa pengguna menyorok panel, lajur
               mesti KEKAL dirender supaya lebarnya boleh bergerak ke sifar.
               Kalau ia di-`display: none` di sini, panel lenyap mengejut dan
               peralihan pada `<aside>` tidak pernah kelihatan.
               Tab yang memang tiada panel (Generate) kekal `display: none`
               seperti dahulu — di sana tiada apa untuk dianimasikan. */
            kananAktif={uiV39 ? tabAdaPanel : panelLarianNampak}
            kananSorok={uiV39 && panelSorok}
            tanpaGaris={generateV2.aktif}
          >
          <Routes>
            <Route path="/login" element={<Navigate to="/dashboard" replace />} />
            {/* Flow Google (pelanggan) — dahulu /my-flow, ditukar 2026-09-29 (arahan user).
                /my-flow KEKAL sebagai lencongan: alamat itu LIVE sejak 28 Sept (bookmark,
                pautan yang sudah dikongsi) + butang "Manage" dalam FlowAccessCard extension.
                ⚠️ '/flow-google' turut ditangkap `laluanBayarLuarGate` (startsWith '/flow'):
                pelawat belum log masuk dikembalikan ke sini selepas log masuk, dan
                sesaat.ai/flow-google dilencong ke app.sesaat.ai. Kedua-duanya tidak
                memudaratkan — tetapi ubah fungsi itu = semak halaman ini juga. */}
            {/* Route per TAB (deep-link, cth butang "Full details" My Plan → tab Billing).
                Corak sama /profile (7167+). Semua /flow-google/* kekal dalam `laluanBayarLuarGate`
                (startsWith '/flow') — tiada perubahan gate. */}
            {[
              ['/flow-google', null],
              ['/flow-google/overview', 'overview'],
              ['/flow-google/billing', 'billing'],
              ['/flow-google/settings', 'settings'],
            ].map(([p, flowTab]) => (
              <Route key={p} path={p} element={<FlowCustomerView onMenuClick={() => setIsMobileSidebarOpen(true)} initialTab={flowTab} />} />
            ))}
            <Route path="/my-flow" element={<Navigate to="/flow-google" replace />} />
            {/* /dashboard = route khas Dashboard — elak clash dgn salespage yang duduk di sesaat.ai/ */}
            {/* ★ v3.9 — rupa baharu muncul hanya bila suis hidup. Rupa lama
                (GettingStartedView) TIDAK dipadam: itu yang menjadikan patah
                balik percuma (tutup suis, tiada deploy).
                ⚠️ `modSebenar` WAJIB — tanpanya halaman memapar nama "Zuheir"
                   dan produk rekaan yang ditaip dalam fail, bukan data pelanggan.
                Kedua-dua rupa berkongsi satu otak: src/hooks/useDashboard.js. */}
            <Route path="/dashboard" element={uiV39
              ? <DashboardV39View
                  modSebenar
                  user={user}
                  isPaid={userTier === 'paid' || (!userTier && licenseStatus === 'valid')}
                  onNavigate={(p) => navigate(p)}
                />
              : <GettingStartedView
                spaces={spacesWithCounts}
                user={user}
                products={products}
                /* Group WhatsApp ikut tier. Tier diutamakan supaya trial TAK
                   tersasar ke group berbayar; licenseStatus cuma sandaran bila
                   users.tier langsung tiada (pelanggan lama). */
                isPaid={userTier === 'paid' || (!userTier && licenseStatus === 'valid')}
                onSelectSpace={(space) => {
                  setSelectedSpaceId(space.id);
                  navigate(space.name === 'Workspace' ? '/workspace' : `/space/${encodeURIComponent(space.name)}`);
                }}
                onGoToWorkspace={() => navigate('/workspace')}
              />
            } />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/workspace" element={
              <SpaceRoute spaces={spacesWithCounts} onResolveSpace={(id) => setSelectedSpaceId(id)} defaultSpaceName="Workspace">
                <AdminPinGate allowNonPrivileged onVerifiedChange={setAdminPinVerified}>
                <ErrorBoundary>
                  <WorkspaceView
                    ref={workspaceViewRef}
                    /* Suis v3.9 dihantar dari sini, BUKAN dibaca semula dalam
                       WorkspaceView: `useUiV39` membuka pendengar Firestore,
                       jadi memanggilnya kali kedua bermakna satu lagi bacaan
                       doc yang sama pada setiap sesi setiap browser. */
                    uiV39={uiV39}
                    hostedBetaAktif={hostedBetaAktif}
                    onNavigate={(path) => navigate(`/${path}`)}
                    lockedSet={lockedSet}
                    onSetLockChange={handleSetLockChange}
                    selectedSpaceId={selectedSpaceId}
                    onSelectSpace={setSelectedSpaceId}
                    hideTikTokPanel={hideTikTokPanel}
                    hideFlowPanel={hideFlowPanel}
                    pendingDeletesRef={pendingDeletesRef}
                    automationTargetSets={automationTargetSets}
                    automationTargetScenes={automationTargetScenes}
                    setAutomationTargetSets={setAutomationTargetSets}
                    isMultipleBrowserMode={isMultipleBrowserMode}
                    setIsMultipleBrowserMode={setIsMultipleBrowserMode}
                    flowProjectUrl={flowProjectUrl}
                    setFlowProjectUrl={setFlowProjectUrl}
                    continueCollectionMode={continueCollectionMode}
                    flowUltraMode={flowUltraMode}
                    setFlowUltraMode={setFlowUltraMode}
                    flowVideoDuration={flowVideoDuration}
                    setFlowVideoDuration={setFlowVideoDuration}
                    flowIngredientsMode={flowIngredientsMode}
                    flowVideoResolution={flowVideoResolution}
                    setFlowVideoResolution={setFlowVideoResolution}
                    flowDownloadSize={flowDownloadSize}
                    setFlowDownloadSize={setFlowDownloadSize}
                    flowWorkflow={flowWorkflow}
                    setFlowWorkflow={setFlowWorkflow}
                    workflowAktif={workflowAktif}
                    onSelectedScriptChange={setKadTakSokong}
                    onKadDitandaWfChange={setRingkasanKadWf}
                    setFlowIngredientsMode={setFlowIngredientsMode}
                    flowImageModel={flowImageModel}
                    setFlowImageModel={setFlowImageModel}
                    tiktokTargetSets={tiktokTargetSets}
                    setTiktokTargetSets={setTiktokTargetSets}
                    isPostOnly={isPostOnly}
                    ctaTeksLarian={ctaTeksLarian}
                    setIsPostOnly={setIsPostOnly}
                    isShowcaseOnly={isShowcaseOnly}
                    setIsShowcaseOnly={setIsShowcaseOnly}
                    modules={modules}
                    activeModuleId={activeModuleId}
                    savedAvatars={savedAvatars}
                    selectedTemplate={selectedTemplate}
                    proModeAktif={proModeAktif} aiLabelAktif={aiLabelAktif}
                    begKuningAktif={begKuningAktif} fbAddProductAktif={fbAddProductAktif} fbLaluan={fbLaluan}
                    selectedSubModuleId={selectedSubModuleId}
                    flownityVoice={activeModuleId === 'flownity' ? flownityVoice : null}
                    isScheduled={isScheduled}
                    scheduledStartTime={scheduledStartTime}
                    scheduledInterval={scheduledInterval}
                    directSchedule={directSchedule}
                    caraPost={caraPost}
                    hasilScene={hasilScene}
                    rujukanProduk={rujukanProduk}
                    simpanGambar={simpanGambar}
                    bilOutput={bilOutput}
                    onGroupsChange={setHeaderGroups}
                    onGroupCountsChange={setHeaderGroupCounts}
                    onSelectedGroupChange={setHeaderSelectedGroupId}
                    onActiveTabChange={setWsTab}
                    onAgentPanelChange={setAgentPanel}
                    /* Sorok/papar Control Panel. Keadaannya hidup DI SINI kerana
                       App yang memiliki lajur kanan (lihat `BingkaiIsi`), tetapi
                       BUTANGNYA dirender oleh WorkspaceView — ia duduk dalam
                       jalur tab, sebelah ikon buka/tutup semua (arahan user
                       2026-08-15). Jadi keadaan turun sebagai prop, dan klik
                       naik semula sebagai panggilan. */
                    panelSorok={panelSorok}
                    onTogglePanelSorok={() => tetapkanPanelSorok(!panelSorok)}
                    /* Kad komposer dihantar SIAP — WorkspaceView cuma
                       meletakkannya pada tab Generate (v3.9 sahaja). */
                    slotTabV4={generateV2.aktif ? slotTabV4 : null}
                    slotAlatV4={generateV2.aktif ? slotAlatV4 : null}
                    kandunganTabGenerate={uiV39 ? (
                      <>
                        {/* Toggle Classic ⇄ New di kepala Workspace (sebelah pil bahasa). */}
                        {generateV2.aktif
                          ? <GenerateV2Nyata nyata={komposerNyata} />
                          : <PromptComposerV39 nyata={komposerNyata} />}
                      </>
                    ) : null}
                    onSelectedCountChange={setHeaderSelectedCount}
                    onSchedRunInfo={setSchedRunInfo}
                    onSelectedFlownityChange={setIsFlownitySelected}
                    userTier={userTier}
                  />
                </ErrorBoundary>
                </AdminPinGate>
              </SpaceRoute>
            } />
            <Route
              path="/space/:spaceName"
              element={
                <SpaceRoute
                  spaces={spacesWithCounts}
                  onResolveSpace={(id) => setSelectedSpaceId(id)}
                >
                  <AdminPinGate allowNonPrivileged onVerifiedChange={setAdminPinVerified}>
                  <ErrorBoundary>
                    <WorkspaceView
                      ref={workspaceViewRef}
                      uiV39={uiV39}
                      hostedBetaAktif={hostedBetaAktif}
                      onNavigate={(path) => navigate(`/${path}`)}
                      lockedSet={lockedSet}
                      onSetLockChange={handleSetLockChange}
                      selectedSpaceId={selectedSpaceId}
                      onSelectSpace={setSelectedSpaceId}
                      hideTikTokPanel={hideTikTokPanel}
                      hideFlowPanel={hideFlowPanel}
                      pendingDeletesRef={pendingDeletesRef}
                      automationTargetSets={automationTargetSets}
                      automationTargetScenes={automationTargetScenes}
                      setAutomationTargetSets={setAutomationTargetSets}
                      isMultipleBrowserMode={isMultipleBrowserMode}
                      setIsMultipleBrowserMode={setIsMultipleBrowserMode}
                      flowProjectUrl={flowProjectUrl}
                      setFlowProjectUrl={setFlowProjectUrl}
                      flowUltraMode={flowUltraMode}
                      setFlowUltraMode={setFlowUltraMode}
                      flowVideoDuration={flowVideoDuration}
                      setFlowVideoDuration={setFlowVideoDuration}
                      flowIngredientsMode={flowIngredientsMode}
                    flowVideoResolution={flowVideoResolution}
                    setFlowVideoResolution={setFlowVideoResolution}
                    flowDownloadSize={flowDownloadSize}
                    setFlowDownloadSize={setFlowDownloadSize}
                      flowWorkflow={flowWorkflow}
                      setFlowWorkflow={setFlowWorkflow}
                      workflowAktif={workflowAktif}
                    onSelectedScriptChange={setKadTakSokong}
                    onKadDitandaWfChange={setRingkasanKadWf}
                      setFlowIngredientsMode={setFlowIngredientsMode}
                      flowImageModel={flowImageModel}
                      setFlowImageModel={setFlowImageModel}
                      tiktokTargetSets={tiktokTargetSets}
                      setTiktokTargetSets={setTiktokTargetSets}
                      isPostOnly={isPostOnly}
                    ctaTeksLarian={ctaTeksLarian}
                      setIsPostOnly={setIsPostOnly}
                      isShowcaseOnly={isShowcaseOnly}
                      setIsShowcaseOnly={setIsShowcaseOnly}
                      modules={modules}
                      activeModuleId={activeModuleId}
                      savedAvatars={savedAvatars}
                      selectedTemplate={selectedTemplate}
                      proModeAktif={proModeAktif} aiLabelAktif={aiLabelAktif}
                    begKuningAktif={begKuningAktif} fbAddProductAktif={fbAddProductAktif} fbLaluan={fbLaluan}
                      isScheduled={isScheduled}
                      scheduledStartTime={scheduledStartTime}
                      scheduledInterval={scheduledInterval}
                      directSchedule={directSchedule}
                    caraPost={caraPost}
                    hasilScene={hasilScene}
                    rujukanProduk={rujukanProduk}
                    simpanGambar={simpanGambar}
                    bilOutput={bilOutput}
                      onGroupsChange={setHeaderGroups}
                      onGroupCountsChange={setHeaderGroupCounts}
                      onSelectedGroupChange={setHeaderSelectedGroupId}
                      onActiveTabChange={setWsTab}
                      onAgentPanelChange={setAgentPanel}
                      panelSorok={panelSorok}
                      onTogglePanelSorok={() => tetapkanPanelSorok(!panelSorok)}
                      onSelectedCountChange={setHeaderSelectedCount}
                    onSchedRunInfo={setSchedRunInfo}
                      onSelectedFlownityChange={setIsFlownitySelected}
                      userTier={userTier}
                    />
                  </ErrorBoundary>
                  </AdminPinGate>
                </SpaceRoute>
              }
            />
            <Route
              path="/ai-instructions"
              element={
                <AIInstructionsView
                  onNavigate={(path) => navigate(`/${path}`)}
                  isAdmin={isAdmin}
                  systemDefaultLogicName={systemDefaultLogicName}
                  systemDefaultLogicId={systemDefaultLogicId}
                  adminUid={ADMIN_UID}
                  selectedSpaceId={selectedSpaceId}
                  spaces={spaces}
                />
              }
            />
            <Route
              path="/rewrite"
              element={
                canAccessAdmin(getUserRole(user?.email)) ? (
                  <RewriteView
                    rewrites={rewrites}
                    activeId={editingId}
                    originalData={transcriptionData}
                    videoName={currentVideo?.name}
                    initialData={editingId ? rewrites.find(r => r.id === editingId) : null}
                    onSave={handleSave}
                    onEdit={handleEdit}
                    onNewClick={() => setIsModalOpen(true)}
                    onDelete={handleDelete}
                    onNavigate={(path) => navigate(`/${path}`)}
                  />
                ) : (
                  <LockedPage featureName="Rewrite" />
                )
              }
            />
            <Route path="/sesaat-1" element={<SalesPageView onNavigateToLogin={() => navigate('/login')} onNavigateToPricing={() => navigate('/pricing')} viewer={{ loggedIn: !!user, authLoading: loading, tier: userTier, plan: userPlan, licenseStatus, allowedModules: userAllowedModules }} />} />
            <Route path="/sesaat-2" element={<Navigate to="/pricing" replace />} />
            {/* ══ ADMIN — SATU ALAMAT BAGI SETIAP TAB ══════════════════════
                Dulu keempat-empat halaman admin menyimpan tab yang dibuka
                dalam keadaan DALAMAN sahaja, jadi muat semula (atau pautan
                yang dikongsi) sentiasa mendarat balik pada tab pertama.
                Kini setiap tab ada alamatnya sendiri — boleh di-bookmark,
                boleh buka tab browser baharu, dan butang Back berfungsi.
                ⚠️ Pagar PIN kekal membalut SETIAP alamat. Jangan keluarkan
                   mana-mana satu daripada AdminPinGate; pagar itulah yang
                   menghalang capaian sebelum PIN disahkan.
                ⚠️ Senarai tab di sini WAJIB selari dengan senarai dalam
                   komponen masing-masing — tab yang tertinggal di sini jadi
                   alamat 404, dan tab yang tertinggal di sana jadi skrin
                   kosong. Kedua-duanya gagal SENYAP. */}
            <Route path="/admin" element={<AdminRedirect ke="/admin/system/dashboard" />} />
            <Route path="/admin/system" element={<AdminRedirect ke="/admin/system/dashboard" />} />
            {/* ★ [2026-09-24] 'fbhealth' + 'robot' DITAMBAH — kedua-duanya ada dalam senarai tab
                AdminView tetapi TIADA di sini, jadi alamatnya memulangkan skrin putih kosong
                (gagal senyap, betul-betul seperti amaran di atas). FB Reels health sebab itu
                tidak pernah boleh dibuka sejak dibina 20 Sept; Robot settings baharu 24 Sept. */}
            {['dashboard', 'users', 'risk', 'pending-premium', 'sales', 'email', 'flow', 'fbhealth', 'robot', 'kempen', 'flow-session-pilot'].map((tab) => (
              <Route key={tab} path={`/admin/system/${tab}`} element={<AdminPinGate onVerifiedChange={setAdminPinVerified}><AdminView initialTab={tab} /></AdminPinGate>} />
            ))}
            <Route path="/admin/affiliate" element={<AdminRedirect ke="/admin/affiliate/overview" />} />
            {['overview', 'affiliates', 'referrals', 'commissions', 'payouts', 'groups', 'settings', 'logs'].map((tab) => (
              <Route key={tab} path={`/admin/affiliate/${tab}`} element={<AdminPinGate onVerifiedChange={setAdminPinVerified}><AdminAffiliateView initialTab={tab} /></AdminPinGate>} />
            ))}
            {/* ★ v3.9 — kedua-dua rupa marketplace berkongsi alamat yang sama. */}
            <Route path="/admin/marketplace" element={<AdminRedirect ke="/admin/marketplace/products" />} />
            {['products', 'orders'].map((tab) => (
              <Route key={tab} path={`/admin/marketplace/${tab}`} element={<AdminPinGate onVerifiedChange={setAdminPinVerified}>{uiV39
                ? <AdminMarketplaceV39View initialTab={tab} />
                : <AdminMarketplaceView initialTab={tab} />}</AdminPinGate>} />
            ))}
            {/* Automation — tab bukan senarai tetap: admin boleh cipta tab
                sendiri (disimpan dalam Firestore), jadi alamat menerima
                sebarang id dan komponen yang memutuskan sah atau tidak. */}
            <Route path="/admin/automation" element={<AdminRedirect ke="/admin/automation/ai16saat" />} />
            <Route path="/admin/automation/:mapId" element={<AdminPinGate onVerifiedChange={setAdminPinVerified}><AutomationView /></AdminPinGate>} />
            {/* Rekod versi skrip automation — admin + PIN gate. Data live dari
                config/script_versions (ditulis bila versi skrip dibump). */}
            <Route path="/admin/script-versions" element={<AdminPinGate onVerifiedChange={setAdminPinVerified}><ScriptVersionsView /></AdminPinGate>} />
            <Route path="/admin/announce" element={<AdminPinGate onVerifiedChange={setAdminPinVerified}><AnnounceAdminView /></AdminPinGate>} />
            {/* Partner — admin tambah / gantung / reset PIN. Server menyemak ADMIN_EMAILS sendiri. */}
            {isAdmin && <Route path="/admin/partners" element={<AdminPinGate onVerifiedChange={setAdminPinVerified}><AdminPartnerView /></AdminPinGate>} />}
            {/* BOOST admin — urus MCN/TAP + rekod pemohon. RUPA SAHAJA buat masa ini. */}
            <Route path="/admin/boost" element={<AdminPinGate onVerifiedChange={setAdminPinVerified}><BoostAdminView /></AdminPinGate>} />
            {/* Makmal ujian — admin + PIN gate (sama macam page admin lain) */}
            {isAdmin && <Route path="/pipeline-test" element={<AdminPinGate onVerifiedChange={setAdminPinVerified}><PipelineTestView /></AdminPinGate>} />}
            {/* Android Auto Publisher — POC ujian WebUSB/ADB (admin + PIN gate, Fasa 1) */}
            {isAdmin && <Route path="/android-test" element={<AdminPinGate onVerifiedChange={setAdminPinVerified}><AndroidBuildView /></AdminPinGate>} />}
            {/* Shopee Video — skrin PELANGGAN (post ke Shopee dari telefon). Dibuka dari entry Control Panel (gated suis v4). */}
            {/* ⛔ [2026-11-04] Dipagar admin (localhost: semua akaun, sama `phoneBoleh`) — dahulu route terbuka:
                pelanggan yang menaip alamat ini nampak skrin v4 walaupun entry Control Panel tersorok.
                Arahan user "pastikan tak ada features suis v4 terpapar ke user". Hari buka v4: buang pagar ini
                SERENTAK dengan `phoneBoleh`. */}
            {(isAdmin || import.meta.env.DEV) && <Route path="/phone-automation" element={<PhoneAutomationView />} />}
            {/* Partner Studio — terbuka kepada semua akaun log masuk; halaman sendiri bertanya server
                (Partner aktif? PIN?). Bukan Partner → mesej sahaja. Tiada menu untuk pelanggan biasa.
                ⛔ Jangan pagar dengan lesen: Partner yang tak renew masih boleh sunting (keputusan user). */}
            <Route path="/partner" element={<PartnerStudioView />} />
            <Route path="/pricing-lama" element={<Navigate to="/pricing" replace />} />
            {/* /r/support handled at top-level (fullscreen, no sidebar) — see early return above */}
            {/* Setting — satu route per halaman supaya alamat boleh di-refresh/share/Back.
                `/profile` (alamat lama) di-redirect dalam ProfileView ke halaman sebenar.
                `/profile/notifications` = bookmark lama; ProfileView memetakannya ke
                `/profile/appearance` (halaman itu kini tema sahaja — 3 toggle notifikasi
                palsu telah dibuang 2026-08-10). */}
            {/* Rupa v3.9 bila suis hidup, rupa lama bila mati — corak sama seperti
                /extension dan /marketplace. Halaman lama TIDAK dipadam; itu yang
                menjadikan patah balik percuma: matikan suis dan ia kembali
                serta-merta, tanpa deploy.
                ⚠️ `modSebenar` WAJIB — tanpanya halaman itu memapar data contoh
                   ("Nama Pelanggan", "1.24 GB") dan bukan akaun sebenar. */}
            {[
              ['/profile', null],
              ['/profile/account', 'account'],
              ['/profile/appearance', 'appearance'],
              ['/profile/security', 'security'],
              ['/profile/billing', 'billing'],
              ['/profile/storage', 'storage'],
              ['/profile/extension', 'extension'],
              ['/profile/referral', 'referral'],
              ['/profile/notifications', 'notifications'],
            ].map(([p, tab]) => (
              <Route key={p} path={p} element={uiV39
                ? <SettingsV39View modSebenar initialTab={tab} />
                : <ProfileView initialTab={tab} />} />
            ))}
            <Route path="/space-settings/:spaceId" element={<SpaceSettingsView />} />
            {/* `uiV39` dihantar dari sini, BUKAN dibaca semula dalam
                LibraryView — corak sama seperti WorkspaceView: `useUiV39`
                membuka pendengar Firestore, jadi memanggilnya kali kedua
                bermakna satu lagi bacaan doc yang sama setiap sesi. */}
            <Route path="/products" element={<LibraryView uiV39={uiV39} onMenuClick={() => setIsMobileSidebarOpen(true)} />} />
            <Route path="/products/system" element={<LibraryView uiV39={uiV39} initialTab="system" onMenuClick={() => setIsMobileSidebarOpen(true)} />} />
            <Route path="/products/myproducts" element={<LibraryView uiV39={uiV39} initialTab="own" onMenuClick={() => setIsMobileSidebarOpen(true)} />} />
            {/* Prototaip — admin-only sementara (sorok dari public; buang `isAdmin &&` bila nak release) */}
            {isAdmin && <>
              <Route path="/workspace-v3.8" element={<WorkspaceV38View />} />
            </>}
            {/* Marketplace — DIBUKA kepada SEMUA pengguna (arahan user 2026-08-21).
                Dulu admin-only sementara; pagar `isAdmin` dibuang supaya menu +
                halaman marketplace boleh dicapai semua pengguna. Admin pengurusan
                (`/admin/marketplace`) KEKAL dipagar berasingan (AdminPinGate). */}
            <>
              {/* ★ v3.9 — KETIGA-TIGA halaman marketplace bertukar serentak.
                  Sengaja bersama: kalau hanya senarai bertukar, pelanggan klik
                  satu kad dan terjun daripada rupa gelap ke halaman terang.
                  Setiap pasangan membaca data yang SAMA; yang bertukar hanya
                  rupa. Halaman lama TIDAK dipadam — matikan suis dan semuanya
                  kembali serta-merta, tanpa deploy.
                  ⚠️ `modSebenar` WAJIB pada senarai — tanpanya ia memapar 6
                     produk contoh yang ditaip dalam fail, bukan produk sebenar.
                     Dua halaman lagi tiada mod pelan, jadi tiada prop itu. */}
              <Route path="/marketplace" element={uiV39
                ? <MarketplaceV39View modSebenar onNavigate={(p) => navigate(p)} />
                : <MarketplaceView onMenuClick={() => setIsMobileSidebarOpen(true)} />} />
              <Route path="/marketplace/p/:productId" element={uiV39
                ? <MarketplaceProductV39View />
                : <MarketplaceProductView />} />
              <Route path="/marketplace/pembelian" element={uiV39
                ? <MyPurchasesV39View />
                : <MyPurchasesView />} />
              {/* Troli — rupa v3.9 SAHAJA. Rupa LAMA sengaja tidak diberi troli:
                  ia laluan patah balik (matikan suis dan semuanya kembali), bukan
                  tempat ciri baharu ditambah. Pelanggan pada rupa lama tetap boleh
                  membeli satu-satu seperti sebelum ini, jadi tiada apa yang hilang. */}
              <Route path="/marketplace/troli" element={uiV39
                ? <TroliV39View />
                : <Navigate to="/marketplace" replace />} />
            </>
            {/* ★ v3.9 — rupa baharu muncul hanya bila suis hidup. Rupa lama
                TIDAK dipadam: itu yang menjadikan patah balik percuma (tutup
                suis, tiada deploy). Lihat .agents/workflows/ui-v39-suis.md.
                ⚠️ `modSebenar` WAJIB — tanpanya halaman memapar 4 nota contoh
                   yang ditaip dalam fail, bukan nota sebenar pelanggan. */}
            <Route path="/notes" element={uiV39
              ? <NotesV39View modSebenar onMenuClick={() => setIsMobileSidebarOpen(true)} />
              : <NotesView onMenuClick={() => setIsMobileSidebarOpen(true)} />} />
            {/* Changelog "What's New" DIBUANG 2026-10-18 (arahan user). Senarai route log
                masuk TIADA route "*", jadi link lama (bookmark, mesej WhatsApp) dihala
                ke Dashboard secara jelas — tanpa ini ia mendarat di page kosong. */}
            <Route path="/changelog" element={<Navigate to="/dashboard" replace />} />
            <Route path="/changelog-v3.9" element={<Navigate to="/dashboard" replace />} />
            {/* Extension — muat turun + cara pasang. Perlu login (keputusan user 2026-07-28:
                menu sahaja, tiada route awam untuk share link tanpa login) */}
            {/* Extension — rupa v3.9 bila suis hidup, rupa lama bila mati.
                Halaman lama TIDAK dipadam; itu yang menjadikan patah balik
                percuma: matikan suis dan ia kembali serta-merta, tanpa deploy.
                ⚠️ `modSebenar` WAJIB — tanpanya tajuk masih berlabel "Pelan".
                `isPaid` hanya untuk rupa LAMA (ia yang memilih group sokongan
                ikut tier). Rupa v3.9 tidak lagi memerlukannya sejak kad group
                dibuang 2026-08-16 — halaman itu kini muat turun + satu pautan
                ke Tutorial sahaja. */}
            <Route path="/extension" element={uiV39
              ? <ExtensionV39View modSebenar onMenuClick={() => setIsMobileSidebarOpen(true)} />
              : <ExtensionView onMenuClick={() => setIsMobileSidebarOpen(true)} isPaid={userTier === 'paid' || (!userTier && licenseStatus === 'valid')} />} />
            {/* Mockup UI feed komuniti — admin-only sementara (sorok dari non-admin; buang `isAdmin &&` bila nak release) */}
            {isAdmin && <Route path="/feed1" element={<Feed1View onMenuClick={() => setIsMobileSidebarOpen(true)} />} />}
            {/* BOOST — halaman pelanggan (PLAN_BOOST.md §🔄 2026-11-04). Admin sentiasa; pelanggan HANYA
                bila suis `config/boost.open` hidup (useBoostBuka, fail-closed) — suis mati = route TIDAK
                wujud untuk bukan-admin. BERPASANGAN dengan menu `suis: 'boost'` dalam design/StudioShell.jsx.
                Kelayakan pakej (Premium/Fighter/SMART) disemak di SERVER (callable `boost`), bukan di sini. */}
            {(isAdmin || boostBuka !== false) && <Route path="/boost" element={<BoostV39View onMenuClick={() => setIsMobileSidebarOpen(true)} adminSahaja={isAdmin && boostBuka !== true} />} />}
            {/* BOOST — halaman staff MCN (Gorilla Cat dll.). Terbuka kepada semua akaun log masuk; SERVER
                menyemak email dalam boost_mcn.partnerEmails dan hanya memulangkan pemohon MCN itu.
                Bukan partner → mesej sahaja. Tiada menu (link terus app.sesaat.ai/mcn). */}
            <Route path="/mcn" element={<BoostPartnerView />} />
            {/* HOOK LIBRARY — lead magnet (PROTOTAIP, rupa sahaja: setiap butang
                mati, data dummy dalam komponen sendiri).
                ⛔ ADMIN SAHAJA — route ini LANGSUNG tidak wujud untuk bukan-admin,
                jadi tiada apa boleh dicapai walaupun alamatnya ditaip sendiri.
                Pagar ini BERPASANGAN dengan `admin: true` pada item menu 'hooks'
                dalam design/StudioShell.jsx — buka KEDUA-DUA serentak bila sedia.
                ⚠️ Ia route SEBENAR (bukan senarai prototaip '-v3.9' di bawah):
                halaman ini mesti duduk dalam shell app yang SAMA seperti halaman
                hidup yang lain, kalau tidak menu di tepinya membawa pelanggan
                masuk ke dunia prototaip.
                ⚠️ Bila dibuka kepada AWAM nanti: '/hooks' WAJIB ditambah ke dalam
                `isPublicRoute` — tanpa itu pelawat yang klik link ejen
                (sesaat.ai/hooks?ref=…) mendarat di skrin log masuk, iaitu tepat
                kebalikan tujuan lead magnet. Ingat juga script cookie `?ref=`
                (peraturan "Affiliate Ref Capture" dalam CLAUDE.md). */}
            {isAdmin && <Route path="/hooks" element={<HooksV39View />} />}
            {/* WORKFLOW — galeri workflow Flow. ⛔ ADMIN SAHAJA (route tiada untuk bukan-admin); berpasangan
                dengan `admin: true` item menu 'workflow' dalam design/StudioShell.jsx — buka KEDUA-DUA serentak. */}
            {isAdmin && <Route path="/workflow" element={<WorkflowV39View />} />}
            {/* ADMIN → FORMULAS (prototaip design, data contoh). ⛔ isAdmin; berpasangan dengan item menu
                'adm-formulas' dalam design/StudioShell.jsx. ⚠️ Bungkus AdminPinGate SEBELUM disambung ke data sebenar. */}
            {isAdmin && <Route path="/admin/formulas" element={<FormulaAdminView />} />}
            {isAdmin && <Route path="/admin/formulas/:formulaId" element={<FormulaAdminEditView />} />}
            {/* Pratonton sistem reka bentuk SESAAT Studio (token warna + komponen kongsi + 2 skrin Fasa 1).
                Admin-only. UI sahaja — tiada Firestore, tiada kesan pada Generate atau Run Flow.
                Buang `isAdmin &&` hanya bila reka bentuk ini betul-betul jadi rupa rasmi app. */}
            {/* FASA 1 — Rak Formula. Admin-only buat masa ini supaya deploy tak sengaja
                tidak mendedahkan aliran yang belum diluluskan. Buang `isAdmin &&` pada
                KEDUA-DUA route di bawah bila sedia dibuka kepada pelanggan.
                ⛔ Gating lesen dikira dalam computeFormulaAccess() — jangan pintas. */}
            {isAdmin && (
              <Route
                path="/formula"
                element={
                  <StudioShell
                    active="formula"
                    onNavigate={(path) => navigate(path)}
                    userName={user?.displayName || user?.email || 'Pengguna'}
                    userPlan={isAdmin ? 'Admin' : (userTier === 'paid' ? 'Premium' : userTier === 'trial' ? 'Trial' : '')}
                  >
                    <FormulaShelfView
                      modules={modules}
                      activeModuleId={activeModuleId}
                      isAdmin={isAdmin}
                      userAllowedModules={userAllowedModules}
                      userTier={userTier}
                      flownityTrialUsed={flownityTrialUsed}
                      maintenanceLockedModules={MAINTENANCE_LOCKED_MODULES}
                      onOpenFormula={(id) => navigate(`/formula/${id}`)}
                      onNavigate={(path) => navigate(path)}
                    />
                  </StudioShell>
                }
              />
            )}
            {/* Tutorial — pusat belajar dalam-app.
                ⭐ [2026-08-16] TIADA StudioShell (arahan user). Tutorial
                mengambil alih skrin penuh dan membawa relnya SENDIRI: rel
                kurikulum MENGGANTIKAN menu app, bukan berdiri di sebelahnya.
                Corak yang SAMA dengan halaman Setting — lihat TutorialShell.
                Sebabnya: dua rel bersebelahan menyesakkan skrin dan memakan
                lebar yang sepatutnya milik video.
                ⭐ [2026-08-16] DIBUKA kepada semua pelanggan (arahan user):
                pagar `isAdmin &&` di sini, pada route lesson di bawah, dan
                `admin: true` pada item 'tutorial' dalam design/StudioShell.jsx
                dibuang serentak. Halaman Extension kini menghantar pelanggan
                ke /tutorial/v_install untuk langkah pasang — kalau mana-mana
                pagar itu dipasang semula, pautan tersebut mati.
                ⚠️ Rule bacaan `config/tutorial` WAJIB di-deploy bersama
                   deploy hosting seterusnya; selagi belum, pelanggan
                   bukan-admin jatuh ke SEED dalam src/data/tutorial.js dan
                   tidak nampak edit sebenar admin. */}
            <Route path="/tutorial" element={<TutorialView />} />
            {/* Halaman lesson — video dibuka di sini, bukan dalam modal.
                Terbuka kepada semua pelanggan, serentak dengan /tutorial di
                atas. ⚠️ Baris pautan pada halaman Extension menuju terus ke
                /tutorial/v_install — route ini yang menyambutnya. */}
            <Route path="/tutorial/:videoId" element={<TutorialLessonView />} />
            {/* ── Halaman Studio v3.9 ──────────────────────────────────────
                Semua dibalut StudioShell yang SAMA macam /formula, jadi
                menu di tepi kekal dan pengguna tidak terkeluar dari shell
                bila bertukar halaman. `active` mesti padan dengan `id`
                dalam GROUPS/FOOT (design/StudioShell.jsx) — kalau tidak
                menu itu tidak akan bertanda aktif.

                Admin-only atas sebab sama seperti /formula: deploy tidak
                sengaja tidak boleh mendedahkan aliran yang belum diluluskan.

                ⚠️ PROTOTAIP — data contoh sahaja. Halaman sebenar
                   (/workspace, /products, /extension, /affiliate,
                   /changelog, /profile) TIDAK disentuh dan masih hidup. */}
            {isAdmin && [
              { path: '/formula/editor', active: 'formula', El: FormulaEditorV39View },
              { path: '/dashboard-v3.9', active: 'dashboard', El: DashboardV39View },
              { path: '/marketplace-v3.9', active: 'marketplace', El: MarketplaceV39View },
              { path: '/notes-v3.9', active: 'notes', El: NotesV39View },
              { path: '/workspace-v3.9', active: 'workspace', El: WorkspaceV39View },
              /* Generate V2 pilot — data SEBENAR (produk library + callable promptArchV2),
                 tetapi admin sahaja dan tidak mencipta kad. Lihat WorkspaceV4PilotView.jsx. */
              { path: '/workspace-v4', active: 'workspace', El: WorkspaceV4PilotView },
              { path: '/products-v3.9', active: 'products', El: ProductsV39View },
              { path: '/extension-v3.9', active: 'extension', El: ExtensionV39View },
              { path: '/affiliate-v3.9', active: 'affiliate', El: AffiliateV39View },
              { path: '/sales-v3.9', active: 'sales', El: SalesV39View },
              { path: '/help-v3.9', active: 'help', El: HelpV39View },
              { path: '/settings-v3.9', active: 'settings', El: SettingsV39View },
              { path: '/prostudio-v3.9', active: 'prostudio', El: ProStudioV39View },
              { path: '/prostudio-v3.9/:seksyen', active: 'prostudio', El: ProStudioV39View },
              { path: '/prostudio-v3.9/:seksyen/:sub', active: 'prostudio', El: ProStudioV39View },
            ].map(({ path, active, El }) => (
              <Route
                key={path}
                path={path}
                element={
                  <StudioShell
                    active={active}
                    onNavigate={(p) => navigate(p)}
                    userName={user?.displayName || user?.email || 'Pengguna'}
                    userPlan={isAdmin ? 'Admin' : (userTier === 'paid' ? 'Premium' : userTier === 'trial' ? 'Trial' : '')}
                  >
                    <El onNavigate={(p) => navigate(p)} />
                  </StudioShell>
                }
              />
            ))}
            {isAdmin && (
              <Route
                path="/formula/:formulaId"
                element={
                  <FormulaOpenRoute
                    modules={modules}
                    isAdmin={isAdmin}
                    userAllowedModules={userAllowedModules}
                    userTier={userTier}
                    flownityTrialUsed={flownityTrialUsed}
                    maintenanceLockedModules={MAINTENANCE_LOCKED_MODULES}
                    onSetActiveModule={setActiveModuleId}
                  />
                }
              />
            )}
            {/* PRO Studio — PRATONTON terbuka kepada SEMUA user (sengaja: selamat walaupun ter-deploy).
                Semua tab bertanda SOON; rangka boleh diterokai; non-admin BACA-SAHAJA (readOnly → fieldset disabled).
                Admin kekal edit penuh. UI sahaja — tiada Firestore, tiada kesan pada Generate. */}
            {/* Setiap seksyen PRO Studio ada alamatnya sendiri
                (/prostudio/gaya, /prostudio/agent, …) supaya ia boleh
                di-bookmark, dikongsi, dan butang Back browser kembali ke
                senarai — bukan keluar terus daripada PRO Studio.
                Rupa LAMA tiada seksyen beralamat, jadi ia memapar halaman
                yang sama pada kedua-dua bentuk alamat. */}
            <Route path="/prostudio" element={uiV39
              ? <ProStudioV39View modSebenar readOnly={proStudioReadOnly} aksesCta={aksesCta} aksesSmart={!aksesSmart.isLocked} isAdmin={isAdmin} onNaikTaraf={() => navigate('/pricing')} />
              : <ProStudioView readOnly={proStudioReadOnly} aksesSmart={!aksesSmart.isLocked} onNaikTaraf={() => navigate('/pricing')} />} />
            <Route path="/prostudio/:seksyen" element={uiV39
              ? <ProStudioV39View modSebenar readOnly={proStudioReadOnly} aksesCta={aksesCta} aksesSmart={!aksesSmart.isLocked} isAdmin={isAdmin} onNaikTaraf={() => navigate('/pricing')} />
              : <ProStudioView readOnly={proStudioReadOnly} aksesSmart={!aksesSmart.isLocked} onNaikTaraf={() => navigate('/pricing')} />} />
            {/* Tingkat ketiga: satu agent, satu alamat
                (/prostudio/agent/<id>, /prostudio/agent/baharu). */}
            <Route path="/prostudio/:seksyen/:sub" element={uiV39
              ? <ProStudioV39View modSebenar readOnly={proStudioReadOnly} aksesCta={aksesCta} aksesSmart={!aksesSmart.isLocked} isAdmin={isAdmin} onNaikTaraf={() => navigate('/pricing')} />
              : <ProStudioView readOnly={proStudioReadOnly} aksesSmart={!aksesSmart.isLocked} onNaikTaraf={() => navigate('/pricing')} />} />
            {/* Alamat lama. Halaman ini sudah lama terbuka kepada semua
                pelanggan, jadi pautan dan bookmark ke /pro-studio memang
                wujud di luar sana — lencongan ini yang menghalangnya
                mendarat pada skrin kosong. Corak sama /pricing-lama. */}
            <Route path="/pro-studio" element={<Navigate to="/prostudio" replace />} />
            <Route path="/affiliate/*" element={<AffiliateView />} />
            <Route path="/affiliate/claim-missing" element={<AffiliateClaimView />} />
            <Route path="/payment/success" element={<CheckoutSuccessView />} />
            <Route path="/payment/return" element={<BillplzPaymentReturnView />} />
            <Route path="/upgrade-success" element={<UpgradeSuccessView />} />

          </Routes>
          </BingkaiIsi>
          </BingkaiHalaman>
        </main>
      </div>

      <TranscribeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onTranscribe={handleTranscribe}
      />

      <ManualProductModal
        isOpen={showManualProductModal}
        onClose={() => setShowManualProductModal(false)}
        onSubmit={handleManualProductsSubmit}
        nextProductIndex={products.length + 1}
        spaceName={spacesWithCounts.find(s => s.id === selectedSpaceId)?.name || ''}
        logicOptions={headerLogicOptions}
        defaultLogicId={headerSelectedLogicId}
        isLogicLocked={isLogicLocked}
        systemDefaultLogicName={systemDefaultLogicName}
      />

      <ProductLibraryModal
        isOpen={showLibraryModal}
        onClose={() => setShowLibraryModal(false)}
        onSelectProducts={handleLibrarySelect}
        uiV39={uiV39}
        selectedSpaceId={selectedSpaceId}
        spaces={spacesWithCounts}
        logicOptions={headerLogicOptions}
        defaultLogicId={headerSelectedLogicId}
        isLogicLocked={isLogicLocked}
        systemDefaultLogicName={systemDefaultLogicName}
      />

      <BulkImportModal
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
        activeModuleId={activeModuleId}
        onImport={(newProducts) => {
          workspaceViewRef.current?.handleBulkImport?.(newProducts);
          setShowBulkImportModal(false);
        }}
      />

      <StorageCleanupOverlay
        progress={cleanupProgress}
        onConfirm={handleConfirmCleanup}
        onClose={handleCloseCleanupOverlay}
      />

      {/* Create/Edit Avatar Modal */}
      {showAvatarModal && (
        <div
          onClick={() => { setShowAvatarModal(false); setEditingAvatar(null); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            /* Warna v3.9 dikenakan melalui SATU kelas berskop dalam index.css,
               bukan dengan mencabang setiap baris `style` di sini. Modal ini
               ada berpuluh nilai warna tersuai; mencabang setiap satu
               bermakna berpuluh tempat untuk tersilap, dan rupa lama (yang
               memakai modal SAMA di atas halaman terang) mesti kekal utuh.
               Corak sama seperti `.sesaat-barpukal-v39`. */
            className={uiV39 ? 'sesaat-avatarmodal-v39' : undefined}
            style={{
              background: '#ffffff', borderRadius: 16, padding: 24, width: 380,
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0'
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10, background: '#f5f3ff',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#6366f1' }}>person_add</span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{editingAvatar ? 'Edit Avatar' : 'Create New Avatar'}</div>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>{savedAvatars.length}/{avatarMaxSlots} slots used</div>
              </div>
              <button
                onClick={() => { setShowAvatarModal(false); setEditingAvatar(null); }}
                style={{
                  width: 28, height: 28, borderRadius: 8, border: 'none', background: '#f1f5f9',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  color: '#94a3b8', transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => { e.target.style.background = '#e2e8f0'; e.target.style.color = '#64748b'; }}
                onMouseLeave={(e) => { e.target.style.background = '#f1f5f9'; e.target.style.color = '#94a3b8'; }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
              </button>
            </div>

            {/* Image Upload */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Avatar Image *
              </label>
              <input ref={avatarModalFileRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => {
                    const modal = document.getElementById('avatar-modal-preview');
                    if (modal) { modal.src = reader.result; modal.dataset.base64 = reader.result; }
                    setAvatarAdaImej(true);
                  };
                  reader.readAsDataURL(file);
                  e.target.value = '';
                }}
              />
              <div
                onClick={() => avatarModalFileRef.current?.click()}
                style={{
                  width: '100%', height: 120, background: '#f8fafc', border: '2px dashed #e2e8f0', borderRadius: 10,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', overflow: 'hidden', transition: 'all 0.2s', position: 'relative'
                }}
              >
                <img
                  id="avatar-modal-preview"
                  src={editingAvatar?.imageUrl || ''}
                  data-base64=""
                  alt=""
                  style={{
                    position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
                    display: (editingAvatar?.imageUrl) ? 'block' : 'none'
                  }}
                  onLoad={(e) => { if (e.target.src) e.target.style.display = 'block'; }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <span className="material-symbols-outlined" style={{ fontSize: 28, color: '#cbd5e1' }}>cloud_upload</span>
                <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, marginTop: 4 }}>Click to upload image</span>

                {/* Butang ganti gambar — muncul di ATAS gambar.
                    Sebelum ini gambar menutupi seluruh kotak (position:absolute,
                    inset 0), jadi ayat "Click to upload image" terkambus di
                    belakangnya: kotak itu masih boleh ditekan, tetapi tiada
                    apa-apa di skrin yang memberitahu begitu. Pelanggan yang
                    hendak menukar gambar tidak tahu ia boleh ditukar. */}
                {(editingAvatar?.imageUrl || avatarAdaImej) && (
                  <span style={{
                    position: 'absolute', right: 8, bottom: 8,
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '5px 10px', borderRadius: 8,
                    background: 'rgba(15,23,42,0.78)', color: '#ffffff',
                    fontSize: 10, fontWeight: 700, pointerEvents: 'none',
                  }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 13 }}>swap_horiz</span>
                    Replace image
                  </span>
                )}

                {/* Bar kemajuan muat naik — menutupi kotak supaya jelas ia
                    sedang bekerja, dan supaya gambar tidak kelihatan seperti
                    sudah siap sedangkan ia baru separuh naik. */}
                {avatarUploadPct !== null && (
                  <span style={{
                    position: 'absolute', inset: 0,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 8,
                    background: 'rgba(15,23,42,0.72)', color: '#ffffff',
                  }}>
                    <span style={{ fontSize: 11, fontWeight: 700 }}>Uploading… {avatarUploadPct}%</span>
                    <span style={{ width: '70%', height: 5, borderRadius: 999, background: 'rgba(255,255,255,0.25)', overflow: 'hidden' }}>
                      <span style={{
                        display: 'block', height: '100%', width: `${avatarUploadPct}%`,
                        background: '#ffffff', transition: 'width .15s',
                      }} />
                    </span>
                  </span>
                )}
              </div>
            </div>

            {/* Name Input */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Avatar Name *
              </label>
              <input
                id="avatar-modal-name"
                type="text"
                defaultValue={editingAvatar?.name || ''}
                placeholder="e.g. Sarah Pink Hijab"
                style={{
                  width: '100%', height: 36, padding: '0 12px', boxSizing: 'border-box',
                  fontSize: 12, fontWeight: 600, color: '#1e293b',
                  background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
                  outline: 'none', fontFamily: 'inherit', transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#6366f1'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>

            {/* Gender Dropdown */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Gender *
              </label>
              <select
                id="avatar-modal-gender"
                value={avatarModalGender}
                onChange={(e) => setAvatarModalGender(e.target.value)}
                style={{
                  width: '100%', height: 36, padding: '0 12px',
                  fontSize: 12, fontWeight: 600, color: '#1e293b',
                  background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
                  outline: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  appearance: 'none', WebkitAppearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center'
                }}
              >
                <option value="" disabled>Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            {/* Age Dropdown */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Age *
              </label>
              <select
                value={avatarModalAge}
                onChange={(e) => setAvatarModalAge(e.target.value)}
                style={{
                  width: '100%', height: 36, padding: '0 12px',
                  fontSize: 12, fontWeight: 600, color: '#1e293b',
                  background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
                  outline: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  appearance: 'none', WebkitAppearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center'
                }}
              >
                <option value="18-25">18-25</option>
                <option value="26-35">26-35</option>
                <option value="36-45">36-45</option>
                <option value="46+">46+</option>
              </select>
            </div>

            {/* Hijab Toggle (Female only) */}
            {avatarModalGender === 'female' && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  Hijab *
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className={uiV39 ? 'sesaat-hijab' : undefined}
                    onClick={() => setAvatarModalHijab(true)}
                    style={{
                      flex: 1, height: 36, borderRadius: 8, fontSize: 12, cursor: 'pointer',
                      fontWeight: uiV39 ? 500 : 700, fontFamily: 'inherit',
                      /* v3.9 tiada warna jenama pada kawalan — pilihan aktif
                         ditandakan dengan permukaan terang + bingkai pilih. */
                      background: uiV39
                        ? (avatarModalHijab ? 'var(--s-select)' : 'transparent')
                        : (avatarModalHijab ? '#6366f1' : '#f8fafc'),
                      color: uiV39
                        ? (avatarModalHijab ? 'var(--s-select-ink)' : 'var(--s-ink-3)')
                        : (avatarModalHijab ? '#ffffff' : '#64748b'),
                      border: uiV39
                        ? `1px solid ${avatarModalHijab ? 'var(--s-select)' : 'var(--s-line-strong)'}`
                        : `1px solid ${avatarModalHijab ? '#6366f1' : '#e2e8f0'}`
                    }}
                  >Yes, with hijab</button>
                  <button
                    type="button"
                    className={uiV39 ? 'sesaat-hijab' : undefined}
                    onClick={() => setAvatarModalHijab(false)}
                    style={{
                      flex: 1, height: 36, borderRadius: 8, fontSize: 12, cursor: 'pointer',
                      fontWeight: uiV39 ? 500 : 700, fontFamily: 'inherit',
                      background: uiV39
                        ? (!avatarModalHijab ? 'var(--s-select)' : 'transparent')
                        : (!avatarModalHijab ? '#6366f1' : '#f8fafc'),
                      color: uiV39
                        ? (!avatarModalHijab ? 'var(--s-select-ink)' : 'var(--s-ink-3)')
                        : (!avatarModalHijab ? '#ffffff' : '#64748b'),
                      border: uiV39
                        ? `1px solid ${!avatarModalHijab ? 'var(--s-select)' : 'var(--s-line-strong)'}`
                        : `1px solid ${!avatarModalHijab ? '#6366f1' : '#e2e8f0'}`
                    }}
                  >No hijab</button>
                </div>
                <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 4 }}>
                  This info is sent to AI so the prompt matches the avatar image.
                </div>
              </div>
            )}

            {/* Limit warning */}
            {!editingAvatar && savedAvatars.length >= avatarMaxSlots && (
              <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, marginBottom: 16, fontSize: 10, color: '#dc2626', fontWeight: 600 }}>
                ⚠ Avatar slots full ({avatarMaxSlots}/{avatarMaxSlots}). Please delete an existing avatar.
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              {editingAvatar?.imageUrl && (
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch(editingAvatar.imageUrl);
                      const blob = await response.blob();
                      const ext = blob.type?.split('/')?.[1] || 'png';
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${editingAvatar.name || 'avatar'}.${ext}`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    } catch (err) {
                      console.error('Download failed:', err);
                      alert('Failed to download image.');
                    }
                  }}
                  style={{
                    padding: '8px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8,
                    fontSize: 12, fontWeight: 600, color: '#16a34a', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 4
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>download</span>
                  Download
                </button>
              )}
              <button
                disabled={!editingAvatar && savedAvatars.length >= avatarMaxSlots}
                onClick={async () => {
                  const nameEl = document.getElementById('avatar-modal-name');
                  const previewEl = document.getElementById('avatar-modal-preview');
                  const name = nameEl?.value?.trim();
                  const gender = avatarModalGender;
                  const age = avatarModalAge;
                  // hijab only meaningful for female; male → null
                  const hijab = gender === 'female' ? avatarModalHijab : null;
                  const newBase64 = previewEl?.dataset?.base64;

                  if (!name) { alert('Please enter an avatar name.'); return; }
                  if (!gender) { alert('Please select a gender.'); return; }
                  if (!age) { alert('Please select an age.'); return; }
                  if (!editingAvatar && !newBase64) { alert('Please upload an avatar image.'); return; }

                  try {
                    const avatarId = editingAvatar?.id || `avatar_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
                    let imageUrl = editingAvatar?.imageUrl || '';

                    // Upload image to Storage if new base64
                    if (newBase64) {
                      const blob = await fetch(newBase64).then(r => r.blob());
                      const storageRef = ref(storage, `avatars/${user.uid}/${avatarId}`);
                      /* Kiraan simetri (2026-08-26): EDIT menimpa fail yang sama,
                         jadi yang dikira ialah BEZA saiz lama↔baru — kalau terus
                         tambah penuh, setiap edit menggelembungkan kaunter. Avatar
                         BARU: saiz lama 0 → tambah penuh. Gagal baca saiz lama →
                         anggap 0 (recount Optimize betulkan). */
                      let saizLamaAv = 0;
                      if (editingAvatar) {
                        try { saizLamaAv = (await getMetadata(storageRef)).size || 0; } catch { /* fail belum wujud */ }
                      }
                      /* `uploadBytesResumable`, bukan `uploadBytes` — hanya yang
                         resumable memberi peristiwa kemajuan. Gambar avatar
                         boleh beberapa MB; tanpa bar kemajuan, modal nampak
                         beku dan pelanggan menekan Save berulang kali. */
                      setAvatarUploadPct(0);
                      await new Promise((selesai, gagal) => {
                        /* Path avatar TETAP (ditimpa bila edit) → jangan immutable; 1 jam
                           cukup — avatar di-fetch pada SETIAP flow run, edit avatar jarang. */
                        const tugas = uploadBytesResumable(storageRef, blob, { contentType: blob.type || 'image/jpeg', cacheControl: 'public, max-age=3600' });
                        tugas.on('state_changed',
                          (s) => setAvatarUploadPct(s.totalBytes
                            ? Math.round((s.bytesTransferred / s.totalBytes) * 100)
                            : 0),
                          gagal,
                          selesai);
                      });
                      imageUrl = await getDownloadURL(storageRef);
                      ubahKaunterStorage(user.uid, blob.size - saizLamaAv);
                      setAvatarUploadPct(null);
                    }

                    // Save to Firestore
                    await setDoc(doc(db, 'users', user.uid, 'avatars', avatarId), {
                      name,
                      gender,
                      age,
                      hijab,
                      imageUrl,
                      ...(editingAvatar ? {} : { createdAt: new Date() })
                    }, { merge: true });

                    setSelectedAvatarId(avatarId);
                    setShowAvatarModal(false);
                    setEditingAvatar(null);
                    console.log(`[Avatar] ${editingAvatar ? 'Updated' : 'Created'}: ${name} (${gender})`);
                  } catch (e) {
                    console.error('[Avatar] Save error:', e);
                    alert('Failed to save avatar: ' + e.message);
                  } finally {
                    /* WAJIB dalam `finally` — kalau muat naik gagal di tengah
                       jalan, bar kemajuan akan tersangkut pada 63% selamanya
                       dan butang Save kekal terkunci. */
                    setAvatarUploadPct(null);
                  }
                }}
                /* Terkunci semasa muat naik — tanpa ini, tekanan kedua
                   mencetuskan muat naik kedua ke path yang SAMA dan bar
                   kemajuan melompat-lompat antara dua tugasan. */
                disabled={avatarUploadPct !== null}
                style={{
                  padding: '8px 16px', border: 'none', borderRadius: 8,
                  fontSize: 12, fontWeight: 700, color: '#ffffff',
                  cursor: avatarUploadPct !== null ? 'wait' : 'pointer',
                  opacity: avatarUploadPct !== null ? 0.7 : 1,
                  background: (!editingAvatar && savedAvatars.length >= avatarMaxSlots) ? '#cbd5e1' : '#6366f1',
                  boxShadow: '0 2px 8px rgba(99,102,241,0.25)'
                }}
              >
                {avatarUploadPct !== null
                  ? `Uploading… ${avatarUploadPct}%`
                  : (editingAvatar ? 'Update' : 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Module AI Logic Settings Modal (Admin Only) */}
      {moduleSettingsModal.open && (
        <div
          onClick={() => setModuleSettingsModal({ open: false, moduleId: '', moduleLabel: '' })}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff', borderRadius: 16, padding: 24, width: 560, maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0'
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10, background: '#f5f3ff',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#6366f1' }}>tune</span>
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>Module Settings</div>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>{isAdmin ? (editingModuleName || moduleSettingsModal.moduleLabel) : moduleSettingsModal.moduleLabel}</div>
              </div>
            </div>

            {/* ★ MODULE DISPLAY NAME — Admin can rename */}
            {isAdmin && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  Nama Module
                </label>
                <input
                  type="text"
                  value={editingModuleName}
                  onChange={(e) => setEditingModuleName(e.target.value)}
                  placeholder="Nama module..."
                  style={{
                    width: '100%', padding: '10px 12px',
                    fontSize: 13, fontWeight: 600, color: '#1e293b',
                    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
                    outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#6366f1'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
                <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 4 }}>
                  Nama ini akan dipaparkan pada butang module untuk semua user.
                </div>
              </div>
            )}

            {/* ★ RUPA KAD FORMULA (arahan user 2026-08-18) — apa yang pelanggan
                baca di bawah gambar pratonton pada tab Generate. Sebelum ini ayat,
                lencana dan tempoh ditetapkan dalam kod, jadi menukar satu patah
                perkataan memerlukan build + deploy penuh.

                ⚠️ Kotak KOSONG bermakna "tiada", bukan "guna yang asal" — tetapi
                   HANYA selepas module ini pernah disimpan sekali. Module yang belum
                   pernah disimpan kekal memapar ayat asal, supaya tiada kad menjadi
                   kosong pada hari ciri ini naik. Lihat nota pada pemuat modules
                   (`kadNotaMs`) dan pada `notaFormula` dalam PromptComposerV39. */}
            {isAdmin && (
              <div style={{ marginBottom: 20, padding: 14, background: '#fafbfc', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  Rupa Kad Formula
                </label>
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: -2, marginBottom: 12, lineHeight: 1.5 }}>
                  Yang dilihat pelanggan pada tab Generate, di bawah gambar pratonton module ini.
                  Kosongkan mana-mana kotak untuk membuang bahagian itu daripada kad.
                </div>

                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', marginBottom: 5 }}>
                    Ayat bawah nama — Bahasa Melayu
                  </label>
                  <input
                    type="text"
                    value={editingKadNotaMs}
                    onChange={(e) => setEditingKadNotaMs(e.target.value)}
                    placeholder="cth: 1 scene · 3 set"
                    maxLength={60}
                    style={{ width: '100%', padding: '9px 11px', fontSize: 12.5, fontWeight: 500, color: '#1e293b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', marginBottom: 5 }}>
                    Ayat bawah nama — English
                  </label>
                  <input
                    type="text"
                    value={editingKadNotaEn}
                    onChange={(e) => setEditingKadNotaEn(e.target.value)}
                    placeholder="cth: 1 scene · 3 sets"
                    maxLength={60}
                    style={{ width: '100%', padding: '9px 11px', fontSize: 12.5, fontWeight: 500, color: '#1e293b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', marginBottom: 5 }}>
                      Lencana
                    </label>
                    <input
                      type="text"
                      value={editingKadBadge}
                      onChange={(e) => setEditingKadBadge(e.target.value)}
                      placeholder="cth: BETA"
                      maxLength={12}
                      style={{ width: '100%', padding: '9px 11px', fontSize: 12.5, fontWeight: 500, color: '#1e293b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', marginBottom: 5 }}>
                      Tempoh video
                    </label>
                    <input
                      type="text"
                      value={editingKadDurasi}
                      onChange={(e) => setEditingKadDurasi(e.target.value)}
                      placeholder="cth: 0:08"
                      maxLength={8}
                      style={{ width: '100%', padding: '9px 11px', fontSize: 12.5, fontWeight: 500, color: '#1e293b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                {/* ★ Generate V2 (23 Okt, arahan user) — tab Sell / Grow / Earn, tapisan format,
                    pill atas gambar kad. Hanya dipakai oleh rupa Generate V2 (admin). */}
                <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                  {[
                    { label: 'Goal (Generate V2)', nilai: editingKadGoal, set: setEditingKadGoal, pilihan: [{ v: '', n: 'None (All tab only)' }, ...GOAL_V2.map((g) => ({ v: g.id, n: g.nama }))] },
                    { label: 'Format', nilai: editingKadFormat, set: setEditingKadFormat, pilihan: FORMAT_V2.map((f) => ({ v: f.id, n: f.nama })) },
                    { label: 'Pill', nilai: editingKadPil, set: setEditingKadPil, pilihan: [{ v: '', n: 'None' }, ...PIL_V2.map((p) => ({ v: p, n: p }))] },
                  ].map((x) => (
                    <div key={x.label} style={{ flex: 1, minWidth: 0 }}>
                      <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', marginBottom: 5 }}>
                        {x.label}
                      </label>
                      <select
                        value={x.nilai}
                        onChange={(e) => x.set(e.target.value)}
                        style={{ width: '100%', padding: '9px 8px', fontSize: 12.5, fontWeight: 500, color: '#1e293b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                      >
                        {x.pilihan.map((o) => <option key={o.v || 'tiada'} value={o.v}>{o.n}</option>)}
                      </select>
                    </div>
                  ))}
                </div>

                {/* ★ Generate V2 (24 Okt, arahan user) — scene yang pelanggan BOLEH pilih untuk module ini dalam
                    Prompt Input. Sekurang-kurangnya satu. ⚠️ Rupa sahaja buat masa ini — Generate + robot masih
                    memakai bilangan scene module (belum bersambung). 1–2 scene = 3 set · 3–5 = 1 set. */}
                <div style={{ marginTop: 12 }}>
                  <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', marginBottom: 5 }}>
                    Scenes customer can pick (Generate V2)
                  </label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {Array.from({ length: MAKS_SCENE_V2 }, (_, i) => i + 1).map((n) => {
                      const on = editingKadScenes.includes(n);
                      return (
                        <button
                          key={n}
                          type="button"
                          aria-pressed={on}
                          onClick={() => setEditingKadScenes((v) => {
                            const baru = on ? v.filter((x) => x !== n) : [...v, n].sort((a, b) => a - b);
                            return baru.length ? baru : v;   // sekurang-kurangnya satu scene
                          })}
                          style={{
                            padding: '6px 10px', fontSize: 12, fontWeight: 600, borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
                            background: on ? '#1e293b' : '#f8fafc', color: on ? '#fff' : '#475569',
                            border: '1px solid ' + (on ? '#1e293b' : '#e2e8f0'),
                          }}
                        >
                          {n} scene{n > 1 ? 's' : ''} · {n >= 3 ? '1 set' : '3 sets'}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Pratonton baris teks kad — admin nampak hasilnya tanpa perlu
                    menyimpan dahulu kemudian pergi ke tab Generate untuk menyemak. */}
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px dashed #e2e8f0' }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                    Pratonton kad
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>
                      {editingModuleName || moduleSettingsModal.moduleLabel}
                    </span>
                    {editingKadBadge.trim() && (
                      <span style={{ fontSize: 9, fontWeight: 700, color: '#6366f1', background: '#eef2ff', padding: '1px 5px', borderRadius: 4 }}>
                        {editingKadBadge.trim()}
                      </span>
                    )}
                    {editingKadDurasi.trim() && (
                      <span style={{ fontSize: 9, fontWeight: 700, color: '#475569', background: '#e2e8f0', padding: '1px 5px', borderRadius: 4 }}>
                        {editingKadDurasi.trim()}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
                    BM: {editingKadNotaMs.trim() || '— tiada —'}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>
                    EN: {editingKadNotaEn.trim() || '— tiada —'}
                  </div>
                </div>
              </div>
            )}

            {/* ★ MODULE MASTER PROMPT — Admin can edit (3-tab), non-admin sees info only */}
            {isAdmin ? (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Master Prompt — 3 Mode
                </label>

                {/* ★ PAGAR MUATAN — bacaan peti besi GAGAL. Kotak di bawah kosong
                    kerana ralat, BUKAN kerana module ini tiada prompt. Butang
                    "Simpan Semua" dikunci selagi mesej ini terpapar. */}
                {tetapanMuatGagal ? (
                  <div style={{ display: 'flex', gap: 8, padding: '10px 12px', marginBottom: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#dc2626' }}>error</span>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#b91c1c' }}>Master prompt gagal dimuat — penyimpanan dikunci</div>
                      <div style={{ fontSize: 10, fontWeight: 500, color: '#b91c1c', marginTop: 3, lineHeight: 1.5 }}>
                        Kotak di bawah kosong kerana ralat bacaan, bukan kerana module ini tiada prompt.
                        Data dalam database masih selamat. Sila tutup modal ini dan buka semula.
                      </div>
                      <div style={{ fontSize: 9, fontWeight: 500, color: '#ef4444', marginTop: 4, fontFamily: 'monospace' }}>{tetapanMuatGagal}</div>
                    </div>
                  </div>
                ) : tetapanMeta ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10, fontSize: 9, fontWeight: 600, color: '#16a34a' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 12 }}>check_circle</span>
                    <span>
                      Dimuat daripada peti besi
                      {tetapanMeta.updatedAt instanceof Date
                        ? ` · disimpan ${tetapanMeta.updatedAt.toLocaleDateString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric' })}`
                        : ''}
                      {tetapanMeta.updatedBy ? ` oleh ${tetapanMeta.updatedBy}` : ''}
                    </span>
                  </div>
                ) : null}

                {/* Tabs */}
                <div style={{ display: 'flex', gap: 6, marginBottom: 8, borderBottom: '1px solid #e2e8f0', paddingBottom: 8 }}>
                  {[1, 2, 3].map(n => {
                    const isActive = activePromptTab === n;
                    /* Kiraan aksara per mode — supaya mode kosong ketara sebelum
                       Simpan ditekan, tanpa perlu klik masuk satu-satu tab. */
                    const kunciMode = n === 1 ? 'mode1_noAvatarNoGender'
                      : n === 2 ? 'mode2_noAvatarWithGender'
                      : 'mode3_withAvatar';
                    const bilAksara = (editingModulePrompts[kunciMode] || '').length;
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setActivePromptTab(n)}
                        style={{
                          flex: 1, padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                          fontSize: 10, fontWeight: 700, textAlign: 'left',
                          background: isActive ? '#eef2ff' : '#ffffff',
                          border: `1px solid ${isActive ? '#6366f1' : '#e2e8f0'}`,
                          color: isActive ? '#4f46e5' : '#64748b',
                          transition: 'all 0.15s'
                        }}
                      >
                        <div style={{ fontSize: 11, fontWeight: 800 }}>Mode {n}</div>
                        <div style={{ fontSize: 9, fontWeight: 600, marginTop: 2, opacity: 0.85 }}>{MODE_LABELS[n]}</div>
                        <div style={{ fontSize: 9, fontWeight: 700, marginTop: 3, color: bilAksara ? '#94a3b8' : '#f59e0b' }}>
                          {bilAksara ? `${bilAksara.toLocaleString('ms-MY')} aksara` : 'kosong'}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Mode hint */}
                <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600, marginBottom: 8, padding: '6px 10px', background: '#f8fafc', borderRadius: 6 }}>
                  💡 {MODE_HINTS[activePromptTab]}
                </div>

                {/* Textarea for active tab */}
                {(() => {
                  const slotKey = activePromptTab === 1 ? 'mode1_noAvatarNoGender'
                    : activePromptTab === 2 ? 'mode2_noAvatarWithGender'
                    : 'mode3_withAvatar';
                  return (
                    <textarea
                      id={`prompt-textarea-${activePromptTab}`}
                      value={editingModulePrompts[slotKey]}
                      onChange={(e) => setEditingModulePrompts(prev => ({ ...prev, [slotKey]: e.target.value }))}
                      placeholder={`Master prompt untuk ${MODE_LABELS[activePromptTab]}...`}
                      style={{
                        width: '100%', minHeight: 260, maxHeight: 500, padding: 12,
                        fontSize: 12, fontWeight: 500, color: '#1e293b', lineHeight: 1.6,
                        background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
                        outline: 'none', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#6366f1'}
                      onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                    />
                  );
                })()}

                <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 8, lineHeight: 1.5 }}>
                  💡 Data produk (nama, link, gender, avatar) auto-appended di hujung prompt oleh sistem. Tulis prompt pure — OpenAI prompt caching jalan (jimat ~50% cost).
                </div>

                {/* ★ MANDATORY APPEND — zero token cost (tak pergi ke OpenAI) */}
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px dashed #e2e8f0' }}>
                  <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                    Wajib Append — Image Prompt
                  </label>
                  <textarea
                    value={editingMandatoryImageAppend}
                    onChange={(e) => setEditingMandatoryImageAppend(e.target.value)}
                    placeholder="Text wajib append ke setiap promptImage (contoh: text overlay rules, product preservation rules)..."
                    style={{
                      width: '100%', minHeight: 90, maxHeight: 200, padding: 12,
                      fontSize: 11, fontWeight: 500, color: '#1e293b', lineHeight: 1.5,
                      background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
                      outline: 'none', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box'
                    }}
                  />

                  {(() => {
                    // Use registry promptSchema (covers ai16saat-half + homeniche + future 2-scene modules)
                    const isTwoScene = getModule(moduleSettingsModal.moduleId).promptSchema === 'two-scene';
                    return (
                      <>
                        <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 12, marginBottom: 6 }}>
                          {isTwoScene ? 'Wajib Append — Video Prompt (Scene1)' : 'Wajib Append — Video Prompt'}
                        </label>
                        <textarea
                          value={editingMandatoryVideoAppend}
                          onChange={(e) => setEditingMandatoryVideoAppend(e.target.value)}
                          placeholder="Text wajib append ke setiap promptVideo (contoh: tone delivery rules, caption overlay rules). Guna {{pronoun}} untuk She/He auto."
                          style={{
                            width: '100%', minHeight: 120, maxHeight: 240, padding: 12,
                            fontSize: 11, fontWeight: 500, color: '#1e293b', lineHeight: 1.5,
                            background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
                            outline: 'none', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box'
                          }}
                        />
                        {isTwoScene && (
                          <>
                            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 12, marginBottom: 6 }}>
                              Wajib Append — Video Prompt (Scene2)
                            </label>
                            <textarea
                              value={editingMandatoryVideoAppend2}
                              onChange={(e) => setEditingMandatoryVideoAppend2(e.target.value)}
                              placeholder="Text wajib append ke promptVideoScene2 (contoh: scene 2 continuation rules). Guna {{pronoun}} untuk She/He auto."
                              style={{
                                width: '100%', minHeight: 120, maxHeight: 240, padding: 12,
                                fontSize: 11, fontWeight: 500, color: '#1e293b', lineHeight: 1.5,
                                background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
                                outline: 'none', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box'
                              }}
                            />

                            {/* ★ SCENE 2 PREPEND — Veo abaikan arahan kat hujung (slow pace bug).
                                Toggle ON → admin paste ayat yang akan disuntik di ATAS Scene 2. */}
                            <div style={{ marginTop: 14, padding: 12, background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8 }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: editingPrependScene2Enabled ? 8 : 0 }}>
                                <input
                                  type="checkbox"
                                  checked={editingPrependScene2Enabled}
                                  onChange={(e) => setEditingPrependScene2Enabled(e.target.checked)}
                                  style={{ width: 14, height: 14, cursor: 'pointer', accentColor: '#6366f1' }}
                                />
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#3730a3' }}>
                                  ⚡ Aktifkan: Letak ayat di ATAS Scene 2 (prepend)
                                </span>
                              </label>
                              {!editingPrependScene2Enabled && (
                                <div style={{ fontSize: 9, color: '#6366f1', lineHeight: 1.5, paddingLeft: 22 }}>
                                  Veo selalu abaikan arahan tone/pace kalau di hujung — skrip jadi slow, tak habis cakap. Toggle ni paksa Veo baca arahan dulu.
                                </div>
                              )}
                              {editingPrependScene2Enabled && (
                                <>
                                  <textarea
                                    value={editingPrependScene2Text}
                                    onChange={(e) => setEditingPrependScene2Text(e.target.value)}
                                    placeholder="Contoh: Tone: Fast-paced, high-energy voice delivery. Confident TikTok-style selling tone. Speak clearly but quickly. No pauses, no slow emphasis. All lines must be spoken fully within 8 seconds..."
                                    style={{
                                      width: '100%', minHeight: 100, maxHeight: 220, padding: 10,
                                      fontSize: 11, fontWeight: 500, color: '#1e293b', lineHeight: 1.5,
                                      background: '#ffffff', border: '1px solid #c7d2fe', borderRadius: 6,
                                      outline: 'none', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box'
                                    }}
                                  />
                                  <div style={{ fontSize: 9, color: '#4f46e5', marginTop: 6, lineHeight: 1.5 }}>
                                    💡 Ayat ini disuntik di <b>ATAS</b> Scene 2 sebelum AI body, supaya Veo baca dulu. <i>Wajib Append (Scene2)</i> di atas tu kekal di hujung — kau boleh guna kedua-dua sekali.
                                  </div>
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </>
                    );
                  })()}

                  <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 6, lineHeight: 1.5 }}>
                    ⚡ <b>Zero cost:</b> Text ni auto-append ke output AI (untuk Veo/Gemini), tak pergi ke OpenAI langsung.
                  </div>
                </div>

                {/* ★ GOYANG-SPECIFIC — Preset Fragment Manager (Modular Composition).
                    Setiap fragment = description + examples. Server inject runtime ke prompt.
                    Replaces old Hook/BG presets textarea (legacy `hookPresets` array still preserved
                    for backward compat — `editingHookPresets` / `editingBgPresets` saved by Simpan Semua). */}
                {moduleSettingsModal.moduleId === 'goyang' && (() => {
                  const renderFragmentList = (label, icon, type, list) => (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {icon} {label} — GOYANG
                        </label>
                        <button
                          onClick={() => setAdminFragmentEditor({ open: true, type, fragment: null })}
                          style={{ padding: '4px 10px', fontSize: 10, fontWeight: 800, background: '#0891b2', color: '#ffffff', border: 'none', borderRadius: 6, cursor: 'pointer', boxShadow: '0 1px 3px rgba(8,145,178,0.25)' }}
                        >
                          + Tambah
                        </button>
                      </div>
                      <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: 8, maxHeight: 240, overflowY: 'auto' }}>
                        {list.length === 0 ? (
                          <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', padding: '12px 0', fontStyle: 'italic' }}>
                            (Belum ada — klik "Tambah" untuk buat satu)
                          </div>
                        ) : list.map(f => (
                          <div key={f.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', background: '#ffffff', border: '1px solid #e0f2fe', borderRadius: 6, marginBottom: 6, opacity: f.enabled === false ? 0.5 : 1 }}>
                            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#0c4a6e', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</span>
                                {f.enabled === false && (
                                  <span style={{ fontSize: 8, fontWeight: 800, color: '#64748b', background: '#e2e8f0', padding: '1px 5px', borderRadius: 999, flexShrink: 0 }}>HIDDEN</span>
                                )}
                              </div>
                              <div style={{ fontSize: 9, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {f.fragment?.slice(0, 90)}{f.fragment?.length > 90 ? '…' : ''}
                              </div>
                              <div style={{ fontSize: 8, color: '#94a3b8', marginTop: 2 }}>
                                {Array.isArray(f.examples) ? `${f.examples.length} examples` : 'no examples'}
                              </div>
                            </div>
                            <button
                              onClick={() => setAdminFragmentEditor({ open: true, type, fragment: f })}
                              style={{ padding: '4px 8px', fontSize: 9, fontWeight: 700, color: '#0369a1', background: '#e0f2fe', border: '1px solid #bae6fd', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}
                            >
                              ✏ Edit
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                  return (
                    <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px dashed #e2e8f0' }}>
                      <div style={{ fontSize: 9, color: '#0369a1', marginBottom: 10, lineHeight: 1.5, padding: 8, background: '#eff6ff', borderRadius: 6, border: '1px solid #bfdbfe' }}>
                        <b>📦 Modular Composition</b> — setiap preset jadi fragment (description + examples). Server inject hanya fragment yang user pilih masa generate, jadi master prompt body kekal pendek dan cache-friendly. Tambah preset baru di sini sahaja.
                      </div>
                      {renderFragmentList('Hook Fragments', '🪝', 'hooks', adminHookFragments)}
                      {renderFragmentList('Background Fragments', '📍', 'backgrounds', adminBgFragments)}
                    </div>
                  );
                })()}

                {/* ★ GOYANG-SPECIFIC — System Templates Manager (admin curates templates yang user nampak) */}
                {moduleSettingsModal.moduleId === 'goyang' && (
                  <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px dashed #e2e8f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        🌐 System Templates — GOYANG
                      </label>
                      <button
                        onClick={() => setAdminTemplateEditor({ open: true, template: null })}
                        style={{
                          padding: '5px 10px', fontSize: 10, fontWeight: 800,
                          background: '#0891b2', color: '#ffffff', border: 'none', borderRadius: 6,
                          cursor: 'pointer', boxShadow: '0 1px 3px rgba(8,145,178,0.25)'
                        }}
                      >
                        + Tambah
                      </button>
                    </div>
                    <div style={{ fontSize: 9, color: '#0369a1', marginBottom: 8, lineHeight: 1.5 }}>
                      Templates di sini akan muncul untuk SEMUA user dalam dropdown template selector. Templates user mereka sendiri (Save di workspace) tak terjejas.
                    </div>

                    <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: 8, maxHeight: 220, overflowY: 'auto' }}>
                      {adminSystemTemplates.length === 0 ? (
                        <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', padding: '12px 0', fontStyle: 'italic' }}>
                          (Belum ada system template — klik "Tambah" untuk buat satu)
                        </div>
                      ) : (
                        adminSystemTemplates.map(t => {
                          const formatSet = (s) => {
                            if (!s) return '—';
                            const hook = s.hook === 'Custom' ? (s.hookCustom || 'Custom') : (s.hook || 'Auto');
                            const bg = s.bg === 'Custom' ? (s.bgCustom || 'Custom') : (s.bg || 'Auto');
                            return `${hook} · ${bg}`;
                          };
                          return (
                            <div key={t.id} style={{
                              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8,
                              padding: '8px 10px', background: '#ffffff', border: '1px solid #e0f2fe',
                              borderRadius: 6, marginBottom: 6
                            }}>
                              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#0c4a6e', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {t.name}
                                </div>
                                <div style={{ fontSize: 9, color: '#64748b', lineHeight: 1.55, fontFamily: 'inherit' }}>
                                  <div><b style={{ color: '#0369a1' }}>Set 1:</b> {formatSet(t.set1)}</div>
                                  <div><b style={{ color: '#0369a1' }}>Set 2:</b> {formatSet(t.set2)}</div>
                                  <div><b style={{ color: '#0369a1' }}>Set 3:</b> {formatSet(t.set3)}</div>
                                </div>
                              </div>
                              <button
                                onClick={() => setAdminTemplateEditor({ open: true, template: t })}
                                style={{
                                  padding: '4px 8px', fontSize: 9, fontWeight: 700, color: '#0369a1',
                                  background: '#e0f2fe', border: '1px solid #bae6fd', borderRadius: 4,
                                  cursor: 'pointer', flexShrink: 0
                                }}
                              >
                                ✏ Edit
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ padding: 16, background: '#f8fafc', borderRadius: 10, marginBottom: 20 }}>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>
                  {modules.find(m => m.id === moduleSettingsModal.moduleId)?.description || 'Module ini dikonfigurasikan oleh admin.'}
                </div>
              </div>
            )}

            {/* ★ SUB-MODULE MANAGER (admin) */}
            {isAdmin && (
              <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px dashed #e2e8f0', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', flex: 1 }}>
                    Sub-Module (Variasi)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={editingHasSubModules}
                      onChange={(e) => setEditingHasSubModules(e.target.checked)}
                      style={{ width: 14, height: 14, accentColor: '#6366f1', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: 11, fontWeight: 700, color: editingHasSubModules ? '#6366f1' : '#94a3b8' }}>Aktifkan</span>
                  </label>
                </div>

                {editingHasSubModules && (
                  <div>
                    {/* Label untuk pilihan ini */}
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ display: 'block', fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                        Label pilihan (nama jenis)
                      </label>
                      <input
                        type="text"
                        value={editingSubModuleLabel}
                        onChange={(e) => setEditingSubModuleLabel(e.target.value)}
                        placeholder="cth: Teknik, Jenis, Kategori, Gaya..."
                        style={{
                          width: '100%', padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#1e293b',
                          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
                          outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box'
                        }}
                      />
                      <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 3 }}>
                        Ini nama yang user nampak di sidebar (cth: "Teknik: Supplement ▼")
                      </div>
                    </div>

                    {/* Senarai sub-module */}
                    <div style={{ marginBottom: 8 }}>
                      {editingSubModules.length === 0 ? (
                        <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', padding: '10px 0', fontStyle: 'italic' }}>
                          (Belum ada — klik "+ Tambah" untuk buat satu)
                        </div>
                      ) : editingSubModules.map((sm, idx) => (
                        <div key={sm.id} style={{ marginBottom: 8, background: '#f8fafc', border: `1px solid ${subModuleExpandedIdx === idx ? '#c7d2fe' : '#e2e8f0'}`, borderRadius: 8, overflow: 'hidden' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px' }}>
                            <input
                              type="text"
                              value={sm.name}
                              onChange={(e) => setEditingSubModules(prev => prev.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))}
                              placeholder="Nama..."
                              style={{
                                flex: 1, padding: '6px 8px', fontSize: 12, fontWeight: 600, color: '#1e293b',
                                background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 6,
                                outline: 'none', fontFamily: 'inherit'
                              }}
                            />
                            <button
                              onClick={() => setSubModuleExpandedIdx(subModuleExpandedIdx === idx ? null : idx)}
                              style={{
                                padding: '5px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', border: 'none', borderRadius: 6,
                                background: subModuleExpandedIdx === idx ? '#6366f1' : '#f1f5f9',
                                color: subModuleExpandedIdx === idx ? '#ffffff' : '#64748b'
                              }}
                            >
                              {subModuleExpandedIdx === idx ? '▲ Prompt' : '▼ Prompt'}
                            </button>
                            <button
                              onClick={() => {
                                setEditingSubModules(prev => prev.filter((_, i) => i !== idx));
                                if (subModuleExpandedIdx === idx) setSubModuleExpandedIdx(null);
                                else if (subModuleExpandedIdx > idx) setSubModuleExpandedIdx(i => i - 1);
                              }}
                              style={{ width: 28, height: 28, background: '#fee2e2', border: 'none', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626', fontSize: 14, flexShrink: 0 }}
                            >✕</button>
                          </div>
                          {subModuleExpandedIdx === idx && (
                            <div style={{ padding: '0 10px 10px' }}>
                              <textarea
                                value={sm.masterPrompt}
                                onChange={(e) => setEditingSubModules(prev => prev.map((x, i) => i === idx ? { ...x, masterPrompt: e.target.value } : x))}
                                placeholder={`Master prompt untuk "${sm.name || 'sub-module ini'}"...\n\nData produk (nama, link, harga) auto-append di hujung oleh sistem.`}
                                style={{
                                  width: '100%', minHeight: 200, maxHeight: 400, padding: 10,
                                  fontSize: 11, fontWeight: 500, color: '#1e293b', lineHeight: 1.6,
                                  background: '#ffffff', border: '1px solid #c7d2fe', borderRadius: 8,
                                  outline: 'none', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#6366f1'}
                                onBlur={(e) => e.target.style.borderColor = '#c7d2fe'}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={() => {
                        const id = 'sm_' + Math.random().toString(36).substring(2, 8);
                        setEditingSubModules(prev => [...prev, { id, name: '', masterPrompt: '' }]);
                        setSubModuleExpandedIdx(editingSubModules.length);
                      }}
                      style={{
                        width: '100%', padding: '8px 0', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                        background: '#eef2ff', color: '#6366f1', border: '1px dashed #c7d2fe', borderRadius: 8
                      }}
                    >
                      + Tambah Sub-Module
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ChatGPT scrape config — diletak di BAWAH modal (Smart Custom + FLOWNITY) */}
            {moduleSettingsModal.moduleId === 'smartcustom' && isAdmin && (
              <div style={{ marginBottom: 16 }}>
                <SmartCustomChatGPTTemplates />
              </div>
            )}
            {moduleSettingsModal.moduleId === 'flownity' && isAdmin && (
              <FlownityScrapeConfig moduleId="flownity" />
            )}

            {/* ★ SEJARAH & PULIH — versi lama diarkibkan setiap kali Simpan ditekan.
                Dimuat hanya bila diminta (satu bacaan senarai), jadi membuka modal
                kekal laju. "Pulih" mengisi kotak sahaja — tiada penulisan sehingga
                admin menekan Simpan sendiri. */}
            {isAdmin && (
              <div style={{ marginBottom: 16, padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 14, color: '#64748b' }}>history</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sejarah &amp; Pulih</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => muatSejarahTetapan(moduleSettingsModal.moduleId)}
                    disabled={sejarahMemuat}
                    style={{
                      padding: '5px 10px', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 6,
                      fontSize: 10, fontWeight: 700, color: '#475569', cursor: sejarahMemuat ? 'wait' : 'pointer'
                    }}
                  >
                    {sejarahMemuat ? 'Memuat…' : (sejarahTetapan ? 'Muat semula' : 'Lihat sejarah')}
                  </button>
                </div>

                {sejarahRalat && (
                  <div style={{ fontSize: 10, fontWeight: 600, color: '#b91c1c', marginTop: 8 }}>
                    Gagal muat sejarah: {sejarahRalat}
                  </div>
                )}

                {sejarahTetapan && sejarahTetapan.length === 0 && !sejarahRalat && (
                  <div style={{ fontSize: 10, fontWeight: 500, color: '#94a3b8', marginTop: 8, lineHeight: 1.5 }}>
                    Belum ada versi lama. Salinan pertama akan dibuat automatik pada kali seterusnya anda menekan Simpan.
                  </div>
                )}

                {sejarahTetapan && sejarahTetapan.length > 0 && (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                      {sejarahTetapan.map(v => {
                        const mp = v.masterPrompt;
                        const saiz = typeof mp === 'string'
                          ? [mp.length, 0, 0]
                          : [
                              (mp?.mode1_noAvatarNoGender || '').length,
                              (mp?.mode2_noAvatarWithGender || '').length,
                              (mp?.mode3_withAvatar || '').length
                            ];
                        const bila = new Date(v.ms);
                        return (
                          <div key={v.id} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                            padding: '7px 9px', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 6
                          }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#1e293b' }}>
                                {bila.toLocaleString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </div>
                              <div style={{ fontSize: 9, fontWeight: 500, color: '#94a3b8', marginTop: 2 }}>
                                Mode 1: {saiz[0].toLocaleString('ms-MY')} · Mode 2: {saiz[1].toLocaleString('ms-MY')} · Mode 3: {saiz[2].toLocaleString('ms-MY')} aksara
                                {v.savedBy ? ` · ${v.savedBy}` : ''}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                if (!window.confirm(`Pulih versi ${bila.toLocaleString('ms-MY')}?\n\nIa akan MENGISI kotak dalam modal ini sahaja. Tiada apa disimpan sehingga anda menekan "Simpan Semua".`)) return;
                                pulihVersiTetapan(v);
                              }}
                              style={{
                                flexShrink: 0, padding: '5px 10px', background: '#eef2ff', border: '1px solid #c7d2fe',
                                borderRadius: 6, fontSize: 10, fontWeight: 700, color: '#4f46e5', cursor: 'pointer'
                              }}
                            >
                              Pulih
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: 9, fontWeight: 500, color: '#94a3b8', marginTop: 8, lineHeight: 1.5 }}>
                      Menyimpan {SEJARAH_SIMPAN_VERSI} versi terakhir; apa-apa yang berumur kurang {SEJARAH_LINDUNG_HARI} hari tidak dibuang.
                      Tekan <strong>Pulih</strong> untuk isi kotak, semak dahulu, kemudian barulah <strong>Simpan Semua</strong>.
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Notice for admin */}
            {isAdmin && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', marginBottom: 16, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 14, color: '#16a34a' }}>info</span>
                <span style={{ fontSize: 9, fontWeight: 500, color: '#15803d' }}>Perubahan prompt akan affect semua user yang guna module ini.</span>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setModuleSettingsModal({ open: false, moduleId: '', moduleLabel: '' });
                }}
                style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, fontWeight: 600, color: '#64748b', cursor: 'pointer' }}
              >
                Batal
              </button>
              {isAdmin && (
                <button
                  disabled={!!tetapanMuatGagal}
                  title={tetapanMuatGagal ? 'Master prompt gagal dimuat — tutup modal dan buka semula sebelum menyimpan.' : undefined}
                  onClick={async () => {
                    /* Pagar terakhir: walaupun butang sudah dilumpuhkan, jangan
                       benarkan penulisan berlaku selepas muatan gagal — Simpan
                       menulis kotak kosong dan itu memadam resepi module. */
                    if (tetapanMuatGagal) return;
                    try {
                      const { updateDoc, setDoc, doc: fsDoc, deleteField, getDoc, collection, getDocs, deleteDoc } = await import('firebase/firestore');
                      const newName = editingModuleName.trim() || moduleSettingsModal.moduleLabel;
                      const modId = moduleSettingsModal.moduleId;

                      /* ★ 0) ARKIB VERSI LAMA — SEBELUM apa-apa ditimpa.
                         Merangkumi resepi (peti besi) DAN metadata doc utama
                         (Wajib Append, prepend Scene 2, preset, sub-module), kerana
                         Simpan menulis kedua-duanya. Gagal arkib TIDAK menghalang
                         Simpan — ia dilog sahaja; menghalang bermakna admin tak boleh
                         bekerja langsung bila pembersihan lama tersekat. */
                      try {
                        const [lamaVault, lamaUtama] = await Promise.all([
                          getDoc(fsDoc(db, 'modules', modId, 'private', 'prompt')),
                          getDoc(fsDoc(db, 'modules', modId))
                        ]);
                        const dv = lamaVault.exists() ? (lamaVault.data() || {}) : null;
                        // Tiada resepi lama = tiada apa yang boleh hilang. Langkau arkib.
                        if (dv && dv.masterPrompt != null) {
                          const du = lamaUtama.exists() ? (lamaUtama.data() || {}) : {};
                          const cap = Date.now();
                          await setDoc(fsDoc(db, 'modules', modId, 'private', `history_${cap}`), {
                            masterPrompt: dv.masterPrompt,
                            subModulePrompts: dv.subModulePrompts || {},
                            meta: {
                              name: du.name || '',
                              mandatoryImageAppend: du.mandatoryImageAppend || '',
                              mandatoryVideoAppend: du.mandatoryVideoAppend || '',
                              mandatoryVideoAppend2: du.mandatoryVideoAppend2 || '',
                              prependScene2Enabled: !!du.prependScene2Enabled,
                              prependScene2Text: du.prependScene2Text || '',
                              hookPresets: du.hookPresets || [],
                              bgPresets: du.bgPresets || [],
                              hasSubModules: !!du.hasSubModules,
                              subModuleLabel: du.subModuleLabel || 'Teknik',
                              subModules: du.subModules || []
                            },
                            // Cap jari simpanan ASAL versi ini (bukan masa ia diarkibkan)
                            savedAt: dv.updatedAt || null,
                            savedBy: dv.updatedBy || '',
                            archivedAt: new Date(),
                            archivedBy: user?.email || ''
                          });

                          /* Pembersihan: buang versi yang melebihi 20 TERBARU **dan**
                             sudah lebih 30 hari. Versi muda tidak pernah dibuang
                             walaupun senarai panjang — itu yang membolehkan undo
                             kepada versi semalam selepas satu petang penuh tuning. */
                          const semua = (await getDocs(collection(db, 'modules', modId, 'private'))).docs
                            .filter(d => d.id.startsWith('history_'))
                            .map(d => ({ id: d.id, ms: msDaripadaIdSejarah(d.id) }))
                            .sort((a, b) => b.ms - a.ms);
                          const hadUmur = cap - (SEJARAH_LINDUNG_HARI * 24 * 60 * 60 * 1000);
                          const buang = semua.slice(SEJARAH_SIMPAN_VERSI).filter(v => v.ms < hadUmur);
                          for (const v of buang) {
                            await deleteDoc(fsDoc(db, 'modules', modId, 'private', v.id)).catch(() => {});
                          }
                        }
                      } catch (errArkib) {
                        console.warn('[ModuleSettings] Gagal arkib versi lama (Simpan diteruskan):', errArkib);
                      }

                      // 1) Simpan RESEPI (master prompt) ke PETI BESI — admin + server sahaja boleh baca
                      const masterPromptObj = {
                        mode1_noAvatarNoGender: editingModulePrompts.mode1_noAvatarNoGender || '',
                        mode2_noAvatarWithGender: editingModulePrompts.mode2_noAvatarWithGender || '',
                        mode3_withAvatar: editingModulePrompts.mode3_withAvatar || ''
                      };
                      const subModulePrompts = {};
                      if (editingHasSubModules) {
                        editingSubModules.forEach(sm => { subModulePrompts[sm.id] = sm.masterPrompt || ''; });
                      }
                      await setDoc(fsDoc(db, 'modules', modId, 'private', 'prompt'), {
                        masterPrompt: masterPromptObj,
                        subModulePrompts,
                        updatedAt: new Date(),
                        updatedBy: user?.email || ''
                      }, { merge: true });
                      // 2) Simpan METADATA (bukan resepi) ke doc utama + PADAM field prompt lama
                      //    supaya tak lagi terdedah kepada user biasa yang boleh baca doc modules.
                      /* ⚠️ SMART (smartcustom) TIADA doc dalam koleksi `modules` — ia hidup
                         dalam registry kod sahaja (config/modules.js, `listRegistryOnlyModules`).
                         `updateDoc` pada doc yang tidak wujud MELEMPAR ralat, jadi "Simpan
                         Semua" untuk SMART tidak pernah berjaya (bug lama, ditemui 2026-08-18
                         semasa menambah kotak Rupa Kad Formula).

                         Penyelesaian yang dipilih user: CIPTA doc itu, tetapi disemai dengan
                         nilai registry yang SAMA supaya tiada apa berubah pada skrin. Tanpa
                         semaian ini doc kosong akan memberi `order: 99` (SMART melompat ke
                         hujung senarai) dan `badge: null` (lencana NEW hilang), kerana pemuat
                         modules memakai nilai lalai bila medan tiada.

                         ⛔ JANGAN tukar cabang `updateDoc` kepada `setDoc(merge)` secara
                            menyeluruh — doc yang WUJUD tidak sepatutnya menerima semaian
                            registry, kerana admin mungkin sudah mengubah nama/kedudukannya. */
                      const rujukanModul = fsDoc(db, 'modules', modId);
                      const metaKad = {
                        /* Rupa kad formula — ditulis SETIAP kali Simpan. Selepas simpanan
                           pertama, medan ini WUJUD, jadi kotak kosong bermakna "kad tanpa
                           bahagian itu" dan bukan lagi "guna nilai asal". */
                        kadNotaMs: editingKadNotaMs.trim(),
                        kadNotaEn: editingKadNotaEn.trim(),
                        kadBadge: editingKadBadge.trim(),
                        kadDurasi: editingKadDurasi.trim(),
                        /* Generate V2 — nilai dari <select> sahaja (senarai tetap), jadi tiada nilai luar. */
                        kadGoal: editingKadGoal,
                        kadFormat: editingKadFormat,
                        kadPil: editingKadPil,
                        kadScenes: editingKadScenes.length ? editingKadScenes : [1],
                      };
                      const adaDocModul = (await getDoc(rujukanModul)).exists();
                      if (!adaDocModul) {
                        const reg = getModule(modId);
                        await setDoc(rujukanModul, {
                          name: newName,
                          description: '',
                          icon: reg.icon || 'extension',
                          color: reg.color || '#94a3b8',
                          order: reg.order ?? 99,
                          locked: false,
                          jsonMode: false,
                          outputFormat: '3sets',
                          marketingName: reg.marketingName || newName,
                          badge: reg.badge || null,
                          scriptKey: reg.scriptKey ?? null,
                          promptSchema: reg.promptSchema || 'single-scene',
                          uploadVia: reg.uploadVia || 'webapp-or-extension',
                          adminOnly: reg.adminOnly === true,
                          isPremiumAddon: reg.isPremiumAddon === true,
                          releaseStatus: reg.releaseStatus || 'released',
                          sceneCount: reg.sceneCount ?? null,
                          createdAt: new Date(),
                        }, { merge: true });
                        console.log('[ModuleSettings] ★ doc modules/' + modId + ' DICIPTA (registry-only) — nilai registry disemai');
                      }
                      await updateDoc(rujukanModul, {
                        ...metaKad,
                        name: newName,
                        masterPrompt: deleteField(),   // buang resepi dari doc yang user biasa boleh baca
                        mandatoryImageAppend: editingMandatoryImageAppend || '',
                        mandatoryVideoAppend: editingMandatoryVideoAppend || '',
                        mandatoryVideoAppend2: editingMandatoryVideoAppend2 || '',
                        // Scene 2 prepend — admin-controlled per module
                        prependScene2Enabled: !!editingPrependScene2Enabled,
                        prependScene2Text: editingPrependScene2Text || '',
                        // GOYANG-specific presets — split textarea by newline, trim, drop empties
                        hookPresets: editingHookPresets.split('\n').map(s => s.trim()).filter(Boolean),
                        bgPresets: editingBgPresets.split('\n').map(s => s.trim()).filter(Boolean),
                        // Sub-module config — ID/NAMA sahaja di doc utama; RESEPI di peti besi
                        hasSubModules: editingHasSubModules,
                        subModuleLabel: editingSubModuleLabel || 'Teknik',
                        subModules: editingHasSubModules
                          ? editingSubModules.map(sm => ({ id: sm.id, name: sm.name }))
                          : [],
                        updatedAt: new Date()
                      });
                      /* Kemas kini state tempatan — pendengar Firestore akan menyusul,
                         tetapi kad formula patut berubah sebaik modal ditutup. */
                      setModules(prev => prev.map(m => m.id === modId ? { ...m, name: newName, ...metaKad } : m));
                      console.log('[ModuleSettings] ★ master prompt → PETI BESI modules/' + modId + '/private/prompt; metadata di doc utama (masterPrompt lama dipadam)');
                      setModuleSettingsModal({ open: false, moduleId: '', moduleLabel: '' });
                    } catch (err) {
                      console.error('[ModuleSettings] Save error:', err);
                      alert('Gagal simpan: ' + err.message);
                    }
                  }}
                  style={{
                    padding: '8px 16px', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#ffffff',
                    background: tetapanMuatGagal ? '#cbd5e1' : '#6366f1',
                    cursor: tetapanMuatGagal ? 'not-allowed' : 'pointer',
                    boxShadow: tetapanMuatGagal ? 'none' : '0 2px 8px rgba(99,102,241,0.25)'
                  }}
                >
                  {tetapanMuatGagal ? 'Simpan Dikunci' : 'Simpan Semua'}
                </button>
              )}
            </div>

            {/* ★ [2026-10-17] PADAM MODULE (admin, arahan user). Module asas + module yang dibuka kepada pelanggan
                TIDAK boleh dipadam (pagar dalam services/padamModuleService.js). Doc utama + peti besi dipadam. */}
            {isAdmin && (
              <PadamModuleSeksyen
                moduleId={moduleSettingsModal.moduleId}
                moduleLabel={moduleSettingsModal.moduleLabel}
                regModule={getModule(moduleSettingsModal.moduleId)}
                onPadam={async () => {
                  const modId = moduleSettingsModal.moduleId;
                  await padamModule(db, modId, getModule(modId));
                  console.log('[ModuleSettings] ★ module ' + modId + ' DIPADAM (doc + peti besi)');
                  setModules(prev => prev.filter(m => m.id !== modId));
                  // Module yang dipadam sedang dipilih → kembali ke lalai, supaya composer tidak memegang module hantu.
                  if (activeModuleId === modId) { setActiveModuleId('ai8saat'); setModulDipilih(false); }
                  setModuleSettingsModal({ open: false, moduleId: '', moduleLabel: '' });
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* ★ CREATE MODULE MODAL (admin) — cipta module baharu */}
      {createModuleModal && (
        <div
          onClick={() => { if (!creatingModule) setCreateModuleModal(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#ffffff', borderRadius: 16, padding: 24, width: 480, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0' }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#6366f1' }}>add</span>
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>Cipta module baharu</div>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>Kongsi engine sedia ada, prompt &amp; rupa tersendiri</div>
              </div>
            </div>

            {/* Module ID */}
            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Module ID</label>
            <input
              type="text" value={newModule.id}
              onChange={(e) => setNewModule(p => ({ ...p, id: e.target.value }))}
              placeholder="cth: ai-beauty"
              style={{ width: '100%', padding: '10px 12px', fontSize: 13, fontWeight: 600, color: '#1e293b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
            />
            <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 4 }}>Huruf kecil, tiada ruang. <b>Kekal selamanya</b> — distamp pada setiap produk.</div>

            {/* Nama paparan */}
            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '14px 0 6px' }}>Nama paparan</label>
            <input
              type="text" value={newModule.name}
              onChange={(e) => setNewModule(p => ({ ...p, name: e.target.value }))}
              placeholder="cth: AI BEAUTY"
              style={{ width: '100%', padding: '10px 12px', fontSize: 13, fontWeight: 600, color: '#1e293b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
            />

            {/* Engine */}
            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '14px 0 6px' }}>Engine automation</label>
            <select
              value={newModule.engine}
              onChange={(e) => setNewModule(p => ({ ...p, engine: e.target.value }))}
              style={{ width: '100%', padding: '10px 12px', fontSize: 13, fontWeight: 600, color: '#1e293b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
            >
              <option value="flow1">Flow 1-Scene (macam AI8SAAT)</option>
              <option value="flow2">Flow 2-Scene (macam AI16SAAT)</option>
              <option value="nscene">Flow N-Scene — AI auto, pilih babak</option>
              <option value="resipi">Flow Multi-Scene — Resipi</option>
              <option value="goyang">GOYANG</option>
              <option value="manual">Manual — tiada AI (macam Smart)</option>
            </select>
            <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 4 }}>Tentukan script extension + bilangan scene + cara upload. Tak boleh cipta engine baru di sini.</div>

            {/* ★ [2026-10-17] Flow Multi-Scene (enjin RESIPI) — bilangan scene 1–5. Admin UI = English. */}
            {newModule.engine === 'resipi' && (
              <div style={{ marginTop: 10, padding: 12, background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8 }}>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#3730a3', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Scene images</label>
                <select
                  value={['berlainan', 'tiada', 'sahaja', 'frame'].includes(newModule.resipiGambar) ? newModule.resipiGambar : 'sama'}
                  onChange={(e) => setNewModule(p => ({ ...p, resipiGambar: e.target.value, nscene: e.target.value === 'frame' ? '1' : (e.target.value === 'berlainan' && String(p.nscene) === '1') ? '2' : ((e.target.value === 'sahaja' && String(p.nscene) === '5') ? '4' : p.nscene) }))}
                  style={{ width: '100%', padding: '8px 10px', fontSize: 12, fontWeight: 600, color: '#1e293b', background: '#ffffff', border: '1px solid #c7d2fe', borderRadius: 6, outline: 'none', marginBottom: 10 }}
                >
                  <option value="sama">Same image — 1 AI image for all scenes</option>
                  <option value="berlainan">Different image — 1 AI image per scene</option>
                  <option value="tiada">No image — text to video</option>
                  <option value="sahaja">Images only — no video</option>
                  <option value="frame">Before and after — 2 images, 1 video</option>
                </select>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#3730a3', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{newModule.resipiGambar === 'sahaja' ? 'Number of images' : 'Number of scenes'}</label>
                <select
                  disabled={newModule.resipiGambar === 'frame'}
                  value={['1', '2', '3', '4', '5'].includes(String(newModule.nscene)) ? String(newModule.nscene) : '3'}
                  onChange={(e) => setNewModule(p => ({ ...p, nscene: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', fontSize: 12, fontWeight: 600, color: '#1e293b', background: '#ffffff', border: '1px solid #c7d2fe', borderRadius: 6, outline: 'none', fontFamily: 'inherit' }}
                >
                  {newModule.resipiGambar !== 'berlainan' && <option value="1">1 scene (~8 s) · up to 3 sets</option>}
                  <option value="2">2 scenes (~16 s) · up to 3 sets</option>
                  <option value="3">3 scenes (~24 s) · 1 set</option>
                  <option value="4">4 scenes (~32 s) · 1 set</option>
                  {newModule.resipiGambar !== 'sahaja' && <option value="5">5 scenes (~40 s) · 1 set</option>}
                </select>
                <div style={{ fontSize: 9, color: '#4f46e5', marginTop: 6, lineHeight: 1.5 }}>
                  {newModule.resipiGambar === 'berlainan'
                    ? 'One AI image per scene, each animated into its own video. Customer picks which scenes use the product photo in the Control Panel. '
                    : newModule.resipiGambar === 'frame'
                      ? 'Two AI images become the Start and End frames of one video. Customer picks which image uses the product photo in the Control Panel. '
                    : newModule.resipiGambar === 'tiada'
                      ? 'No image and no product photo: each video is generated straight from its prompt. '
                      : 'One AI image animated into every scene. '}
                  {newModule.resipiGambar === 'sahaja'
                    ? 'Images are saved to the customer\'s Downloads folder. Customer can also show them on the card (Control Panel). 1 set per card. '
                    : '3+ scenes = 1 set per card · 2+ scenes: customer picks "Combine" or "Download each scene" in the Control Panel. '}
                  Card fields: <b>{newModule.resipiGambar === 'berlainan' ? 'Image Prompt + Video Prompt per scene' : newModule.resipiGambar === 'frame' ? 'Before image + After image + Video Prompt' : newModule.resipiGambar === 'sahaja' ? 'Image Prompt per image' : newModule.resipiGambar === 'tiada' ? 'Video Prompt per scene' : 'Image Prompt + Video Prompt per scene (one image)'}</b>.
                </div>
              </div>
            )}

            {/* Flow N-Scene — pilih bilangan babak */}
            {newModule.engine === 'nscene' && (
              <div style={{ marginTop: 10, padding: 12, background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8 }}>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#3730a3', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Bilangan babak (scene)</label>
                <select
                  value={newModule.nscene}
                  onChange={(e) => setNewModule(p => ({ ...p, nscene: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', fontSize: 12, fontWeight: 600, color: '#1e293b', background: '#ffffff', border: '1px solid #c7d2fe', borderRadius: 6, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                >
                  <option value="3">3 babak (~24 saat)</option>
                  <option value="4">4 babak (~32 saat)</option>
                  <option value="5">5 babak (~40 saat)</option>
                  <option value="6">6 babak (~48 saat)</option>
                </select>
                <div style={{ fontSize: 9, color: '#4f46e5', marginTop: 6, lineHeight: 1.5 }}>AI tulis skrip setiap babak automatik (dah berfungsi). Robot flow automation N-babak akan <b>dibina oleh dev</b> — sehingga siap, module ni untuk jana skrip dahulu.</div>
              </div>
            )}

            {/* Icon + Badge */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Icon (Material)</label>
                <input
                  type="text" value={newModule.icon}
                  onChange={(e) => setNewModule(p => ({ ...p, icon: e.target.value }))}
                  placeholder="auto_awesome"
                  style={{ width: '100%', padding: '10px 12px', fontSize: 12, fontWeight: 600, color: '#1e293b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Badge</label>
                <select
                  value={newModule.badge}
                  onChange={(e) => setNewModule(p => ({ ...p, badge: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', fontSize: 12, fontWeight: 600, color: '#1e293b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                >
                  <option value="">Tiada</option>
                  <option value="BETA">BETA</option>
                  <option value="NEW">NEW</option>
                </select>
              </div>
            </div>

            {/* Warna */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Warna</label>
              <input
                type="color" value={newModule.color}
                onChange={(e) => setNewModule(p => ({ ...p, color: e.target.value }))}
                style={{ width: 44, height: 30, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', padding: 2 }}
              />
              <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{newModule.color}</span>
            </div>

            {/* Akses awal */}
            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '14px 0 6px' }}>Akses awal</label>
            <select
              value={newModule.access}
              onChange={(e) => setNewModule(p => ({ ...p, access: e.target.value }))}
              style={{ width: '100%', padding: '10px 12px', fontSize: 13, fontWeight: 600, color: '#1e293b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
            >
              <option value="admin">Admin sahaja (test dulu)</option>
              <option value="allpaid">Semua paid user</option>
              <option value="premium">Premium add-on (grant per-lesen)</option>
            </select>
            <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 4, lineHeight: 1.5 }}>Master prompt diisi lepas cipta — guna gear ⚙️ sedia ada. Module auto-muncul dalam checklist "Modul Akses" di panel admin.</div>

            {/* Error */}
            {createModuleError && (
              <div style={{ marginTop: 14, padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 11, color: '#b91c1c', fontWeight: 600 }}>{createModuleError}</div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20, paddingTop: 16, borderTop: '1px solid #f1f5f9' }}>
              <button
                onClick={() => { if (!creatingModule) setCreateModuleModal(false); }}
                style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, fontWeight: 600, color: '#64748b', cursor: 'pointer' }}
              >
                Batal
              </button>
              <button
                onClick={handleCreateModule}
                disabled={creatingModule}
                style={{ padding: '8px 16px', background: creatingModule ? '#a5b4fc' : '#6366f1', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#ffffff', cursor: creatingModule ? 'not-allowed' : 'pointer', boxShadow: '0 2px 8px rgba(99,102,241,0.25)', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>add</span>
                {creatingModule ? 'Mencipta...' : 'Cipta module'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ★ Admin Template Editor (system templates) — for GOYANG module settings */}
      <TemplateEditorModal
        open={adminTemplateEditor.open}
        onClose={() => setAdminTemplateEditor({ open: false, template: null })}
        onSaved={() => {
          // Reload admin list AND re-resolve selectedTemplate (kalau dia template yang baru di-edit).
          // Without this, leftpanel TemplateSelector akan tunjuk old cached version.
          listSystemTemplates('goyang').then(list => {
            setAdminSystemTemplates(list);
            if (selectedTemplateId) {
              const found = list.find(t => t.id === selectedTemplateId);
              if (found) setSelectedTemplate(found);
            }
            // Trigger TemplateSelector di leftpanel refetch — supaya dropdown + summary fresh
            setTemplatesRefreshKey(k => k + 1);
          }).catch(() => {});
        }}
        moduleId="goyang"
        hookPresets={adminHookFragments}
        bgPresets={adminBgFragments}
        template={adminTemplateEditor.template}
        forceSystem={true}
      />

      {/* ★ Admin Fragment Editor — Modular Composition (hooks + backgrounds) */}
      <FragmentEditorModal
        open={adminFragmentEditor.open}
        onClose={() => setAdminFragmentEditor({ open: false, type: null, fragment: null })}
        onSaved={() => {
          // Reload affected list + trigger TemplateSelector refetch (sebab dropdown options derive dari fragments)
          if (adminFragmentEditor.type === 'hooks') {
            listFragments('goyang', 'hooks').then(setAdminHookFragments).catch(() => {});
          } else if (adminFragmentEditor.type === 'backgrounds') {
            listFragments('goyang', 'backgrounds').then(setAdminBgFragments).catch(() => {});
          }
          setTemplatesRefreshKey(k => k + 1);
        }}
        moduleId="goyang"
        type={adminFragmentEditor.type}
        fragment={adminFragmentEditor.fragment}
      />
    </div>
  );
}

export default App;
